import React, { useState, useRef, useEffect, useMemo } from 'react';
import GoogleDriveService, {
  GoogleDriveFile,
  GoogleDriveFolder,
  GoogleDriveBreadcrumb,
  GoogleDrivePreviewInfo,
  GoogleDriveAuthState
} from '../api/googleDriveService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  ChipProps,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  Container,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  TablePagination,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  AppBar,
  Toolbar,
  Fade,
  LinearProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Tooltip as MuiTooltip,
  Tabs,
  Tab,
  Popover,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Slider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AnalysisIcon from '@mui/icons-material/Analytics';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import InsightsIcon from '@mui/icons-material/Insights';
import DescriptionIcon from '@mui/icons-material/Description';
import SummaryIcon from '@mui/icons-material/Summarize';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentAnalysisIcon from '@mui/icons-material/Psychology';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import botIcon from '../../assets/Chatbot.svg';
import { Chatbot } from './Chatbot/components/Chatbot';
import CloseIcon from '@mui/icons-material/Close';
import { ExportDialog } from './Chatbot/components/Chatbot/ExportDialog';
import { generatePDFFromGeneratedContent, generatePDFFromExportData, type GeneratedContent, type BrandDetails, type ContentPreferences } from './Chatbot/utils/pdfExport';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { environment } from '../../environments/environment';

// Google Drive service will be imported and used instead
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine,
  ReferenceArea,
  ComposedChart
} from 'recharts';
import { ResponsiveTreeMap } from '@nivo/treemap';
import UploadIcon from '@mui/icons-material/Upload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import FileText from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HomeIcon from '@mui/icons-material/Home';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import rbacApi from '../api/rbacApi';
import ReactMarkdown from 'react-markdown';
import ReactDOM from 'react-dom';

// Helper Functions
const getEntryValue = (entries: Entry[], key: string): string => {
  const entry = entries.find(e => e.key === key);
  if (!entry) {
    return '';
  }

  if (entry.value === null) {
    return '';
  }

  if (Array.isArray(entry.value)) {
    return entry.value.join(', ');
  }

  if (typeof entry.value === 'object') {
    return JSON.stringify(entry.value);
  }

  return String(entry.value);
};

const formatPublishedDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return dateStr;
  }
};

// Add this helper function after the existing helper functions
const convertNumericDate = (numericDate: number): Date => {
  const dateStr = numericDate.toString();
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)) - 1; // JavaScript months are 0-based
  const day = parseInt(dateStr.substring(6, 8));
  return new Date(year, month, day);
};

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(4),
  height: '100%',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
}));

const IconWrapper = styled(Box)({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  backgroundColor: '#4c3794',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ResultCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  },
}));

const StyledChip = styled(Chip)({
  margin: '4px',
  '& .MuiChip-label': {
    fontWeight: 500,
  }
});

const FloatingBotButton = styled(IconButton)({
  position: 'fixed',
  bottom: '24px',
  right: '24px',
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: '#4c3794',
  boxShadow: '0 4px 12px rgba(76, 55, 148, 0.3)',
  '&:hover': {
    backgroundColor: '#3a2a73',
  },
  '& svg': {
    color: '#FFFFFF',
    fontSize: '24px',
  },
});

const ChatDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '400px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

const MarkdownMessage = ({ text }: { text: string }) => {
  return (
    <ReactMarkdown
      components={{
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: 700, color: '#131F32' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        del: ({ children }) => (
          <Typography component="span" sx={{ textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="p" sx={{
            fontSize: '0.875rem',
            color: '#475569',
            lineHeight: 1.7,
            mb: 1.5,
            '&:last-child': { mb: 0 }
          }}>
            {children}
          </Typography>
        ),
        h1: ({ children }) => (
          <Typography
            component="div"
            variant="h5"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: '#131F32',
              mb: 1.5,
              mt: 2,
              '&:first-of-type': { mt: 0 }
            }}
          >
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography
            component="div"
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#131F32',
              mb: 1,
              mt: 2,
              '&:first-of-type': { mt: 0 }
            }}
          >
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography sx={{
            fontSize: '0.938rem',
            fontWeight: 600,
            color: '#131F32',
            mb: 0.75,
            mt: 1.5,
            '&:first-of-type': { mt: 0 }
          }}>
            {children}
          </Typography>
        ),
        ul: ({ children }) => (
          <Box component="ul" sx={{
            m: 0,
            pl: 2.5,
            mb: 1.5,
            listStyleType: 'disc'
          }}>
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box component="ol" sx={{
            m: 0,
            pl: 2.5,
            mb: 1.5,
            listStyleType: 'decimal'
          }}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Typography component="li" sx={{
            fontSize: '0.875rem',
            color: '#475569',
            lineHeight: 1.7,
            mb: 0.5
          }}>
            {children}
          </Typography>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'sender',
})<{ sender: 'user' | 'bot' }>(({ theme, sender }) => ({
  minWidth: sender === 'user' ? 'auto' : '85%',
  maxWidth: '85%',
  padding: theme.spacing(1, 1.5),
  borderRadius: sender === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
  backgroundColor: sender === 'user' ? '#4c3794' : '#F5F5F5',
  color: sender === 'user' ? '#ffffff' : '#1a1a1a',
  marginLeft: sender === 'user' ? 'auto' : '16px',
  marginRight: sender === 'user' ? '16px' : 'auto',
  marginBottom: theme.spacing(0.75),
  boxShadow: sender === 'user'
    ? '0 2px 8px rgba(76, 55, 148, 0.2)'
    : '0 1px 4px rgba(0,0,0,0.05)',
  position: 'relative',
  fontSize: '0.85rem',
  lineHeight: 1.4,

}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: '6px 0',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  borderRadius: '16px !important',
  border: '1px solid rgba(76, 55, 148, 0.1)',
  background: '#ffffff',
  transition: 'all 0.3s ease',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '16px 0',
    background: '#ffffff',
    borderColor: 'rgba(76, 55, 148, 0.2)',
    boxShadow: '0 8px 32px rgba(76, 55, 148, 0.12)',
  },
  '&.selected': {
    background: '#ffffff',
    borderColor: 'rgba(76, 55, 148, 0.2)',
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderRadius: '16px',
  padding: '16px 24px',
  background: '#ffffff',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    background: '#ffffff',
    borderBottom: '1px solid rgba(76, 55, 148, 0.1)',
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px 32px',
  background: '#ffffff',
  borderBottomLeftRadius: '16px',
  borderBottomRightRadius: '16px',
}));

const InfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  marginBottom: '24px',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  padding: '16px',
  borderRadius: '12px',
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(255, 152, 0, 0.2)',
    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.08)',
  }
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#1a1a1a',
  minWidth: '140px',
  fontSize: '0.8rem',
  letterSpacing: '0.3px',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: '#333333',
  flex: 1,
  fontSize: '0.95rem',
  lineHeight: 1.6,
}));

const TagContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '12px',
}));

const Tag = styled(Chip)(({ theme }) => ({
  background: 'rgba(76, 55, 148, 0.1)',
  color: '#4c3794',
  fontWeight: 500,
  borderRadius: '8px',
  padding: '4px 8px',
  height: '28px',
  fontSize: '0.85rem',
  '&:hover': {
    background: 'rgba(76, 55, 148, 0.15)',
  }
}));

const InsightChip = styled(({ backgroundColor, textColor, ...props }: InsightChipProps & Omit<ChipProps, keyof InsightChipProps>) => (
  <Chip {...props} />
))(({ backgroundColor = '#FFF3E0', textColor = '#FF9800' }) => ({
  backgroundColor,
  color: textColor,
  margin: '4px',
  '& .MuiChip-label': {
    fontWeight: 500,
  }
}));

const FullScreenContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  // padding: '24px'
});

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px 0',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  marginTop: '16px',
}));

const PaginationButton = styled(Button)(({ theme }) => ({
  minWidth: '36px',
  height: '36px',
  borderRadius: '8px',
  padding: '8px',
  color: '#666',
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(76, 55, 148, 0.08)',
    transform: 'translateY(-1px)',
  },
  '&.active': {
    backgroundColor: '#4c3794',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(76, 55, 148, 0.25)',
    '&:hover': {
      backgroundColor: '#3a2a73',
    }
  },
  '&.disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      transform: 'none',
      backgroundColor: 'transparent',
    }
  }
}));

const PaginationInfo = styled(Typography)(({ theme }) => ({
  color: '#666',
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const PaginationControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  '& .MuiTypography-root': {
    color: '#666',
    fontSize: '0.875rem',
    fontWeight: 500,
  }
}));

const UploadMethodCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(76, 55, 148, 0.15)',
  },
  '&.selected': {
    borderColor: '#4c3794',
    backgroundColor: 'rgba(76, 55, 148, 0.05)',
  }
}));

const UploadMethodIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(76, 55, 148, 0.1)',
  color: '#4c3794',
  marginBottom: theme.spacing(1),
}));

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  interactionUID?: string;
  visualization?: {
    type: 'table' | 'chart' | 'heatmap';
    data: MultiColumnData;
  };
}

interface InputField {
  key: string;
  label: string;
  type: string;
  value: string | string[] | undefined;
  info: string;
  options?: string[] | Array<{ id: string; label: string }>;
}

interface ContentSentiment {
  Pros: {
    sentimentClass: string;
    sentimentScore: number;
  };
  Cons: {
    sentimentClass: string;
    sentimentScore: number;
  };
}

interface Entry {
  key: string;
  label: string;
  value: string | number | string[] | ContentSentiment | null;
  info?: string;
}

interface SurveyItem {
  objectType: string;
  docUID: string;
  entries: Entry[];
}

interface MessageBubbleProps {
  sender: 'user' | 'bot';
  children?: React.ReactNode;
  sx?: any;
}

interface FormValues extends Record<string, string | string[] | undefined> {
  topic?: string;
  contentType?: string;
  keywords?: string[];
}

interface InsightChipProps {
  label: string;
  backgroundColor?: string;
  textColor?: string;
  size?: 'small' | 'medium';
}

interface ContentInsightItem {
  key: string;
  value: string;
  info: string;
  options?: string[];
  label: string;
}

interface SurveyResponse {
  objectType: 'DICTIONARY' | 'PARAGRAPH' | 'MULTITEXTOPTIONS';
  entries?: Entry[];
  value?: string;
  docUID?: string;
}

interface ResponseItem {
  objectType: string;
  key: string;
  value: any;
}

// Add these interfaces after the existing interfaces
interface ChartDataItem {
  name: string;
  value: number;
}

interface TimelineDataItem {
  date: string;
  value: number;
  sentimentScore: number;
}

// Add new interfaces for MULTICOLUMN support
interface MultiColumnData {
  headers: string[];
  data: Record<string, any>[];
  title: string;
  visual: boolean;
  dimension: number;
}

interface MultiColumnResponse {
  objectType: 'MULTICOLUMN';
  key: string;
  label: string;
  value: MultiColumnData;
}

interface Project {
  id: string;
  org_id: string;
  project_name: string;
  project_description: string;
  project_type: string;
  configuration: {
    period_start_date: string;
    period_end_date: string;
    period_type: string;
  };
  source: string;
  active: boolean;
  status: string;
  last_updated_date: string;
}

interface ProjectPermission {
  id: string;
  name: string;
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canInvite: boolean;
    canManageUsers: boolean;
  };
}

// Segment Analysis interfaces
interface SegmentEntry {
  key: string;
  label: string;
  value: string | number | string[] | null;
}

interface SegmentItem {
  objectType: 'DICTIONARY' | 'MULTICOLUMNDICT';
  docUID?: string;
  key?: string;
  value?: MultiColumnData;
  entries?: SegmentEntry[];
}

interface SegmentGroup {
  groupName: string;
  items: SegmentItem[];
}

interface SegmentAnalysisData {
  segmentName: string;
  segmentSummary: string;
  segmentSize: number;
  segmentPercentage: number;
  positiveTopics: string[];
  negativeTopics: string[];
  segmentRecommendations: string[];
  analysis: string;
  title?: string;
  drivers?: Array<{ driver: string; score: number }>;
  multicolumnData?: Array<{
    title: string;
    key?: string;
    headers: string[];
    data: Record<string, any>[];
    analysis?: string;
    recommendations?: string[];
    entries?: Array<{ key: string; label: string; value: any }>;
  }>;
  informationCategory?: string;
  characteristics?: Record<string, string>;
}

// Export Content interfaces
interface ConversationPair {
  id: string;
  question: {
    id: string;
    content: string;
    timestamp: string;
  };
  answer: {
    id: string;
    content: string;
    timestamp: string;
  };
}

// Using types from pdfExport utils - BrandDetails, ContentPreferences, and GeneratedContent are imported from './Chatbot/utils/pdfExport'







interface ProjectResponse {
  message: string;
  upsertCount: number;
  upsertRef: string[];
}

// Google Drive interfaces are now imported from the service

// Add this type after the existing interfaces
type TimeRange = 'day' | 'week' | 'month' | 'year';

// Add this helper function after the existing helper functions
const groupDataByTimeRange = (data: TimelineDataItem[], range: TimeRange): TimelineDataItem[] => {
  const groupedData: { [key: string]: { count: number; totalScore: number; entries: number } } = {};

  data.forEach(item => {
    const date = new Date(item.date);
    let key: string;
    let weekStart: Date;

    switch (range) {
      case 'day':
        key = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        break;
      case 'week':
        weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = `Week of ${weekStart.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })}`;
        break;
      case 'month':
        key = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short'
        });
        break;
      case 'year':
        key = date.toLocaleDateString('en-US', {
          year: 'numeric'
        });
        break;
      default:
        key = item.date;
    }

    if (!groupedData[key]) {
      groupedData[key] = { count: 0, totalScore: 0, entries: 0 };
    }
    groupedData[key].count += item.value;
    groupedData[key].totalScore += item.sentimentScore;
    groupedData[key].entries += 1;
  });

  return Object.entries(groupedData)
    .map(([date, data]) => ({
      date,
      value: data.count,
      sentimentScore: data.totalScore / data.entries // Calculate average sentiment score
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

// Process and clean MULTICOLUMN data to handle complex responses
const processMultiColumnData = (data: any, forChart: boolean = false): MultiColumnData | null => {
  if (!data || !data.headers || !data.data || !Array.isArray(data.data)) {
    return null;
  }

  let cleanHeaders = data.headers;

  // Only filter out problematic columns for charts, not for tables
  if (forChart) {
    // Dynamically detect columns that contain arrays
    const arrayColumns = new Set<string>();

    // Check each row to find columns that contain arrays
    data.data.forEach((row: any) => {
      data.headers.forEach((header: string) => {
        const value = row[header];
        if (Array.isArray(value) && value.length > 1) {
          arrayColumns.add(header);
        }
      });
    });

    // Filter out columns that contain arrays
    cleanHeaders = data.headers.filter((header: string) => !arrayColumns.has(header));
  }

  if (cleanHeaders.length === 0) {
    return null;
  }

  // Process each row to clean the data
  const cleanData = data.data.map((row: any) => {
    const cleanRow: any = {};
    cleanHeaders.forEach((header: string) => {
      const value = row[header];
      if (Array.isArray(value)) {
        if (forChart) {
          // For charts, show count and first item
          if (value.length === 0) {
            cleanRow[header] = 'No data';
          } else if (value.length === 1) {
            cleanRow[header] = String(value[0]);
          } else {
            cleanRow[header] = `${value.length} items`;
          }
        } else {
          // For tables, keep the full array for display
          cleanRow[header] = value;
        }
      } else if (typeof value === 'number') {
        cleanRow[header] = value;
      } else if (typeof value === 'object' && value !== null) {
        cleanRow[header] = forChart ? 'Complex data' : value;
      } else {
        cleanRow[header] = value;
      }
    });
    return cleanRow;
  });

  return {
    headers: cleanHeaders,
    data: cleanData,
    title: data.title || 'Data Analysis',
    visual: data.visual || false,
    dimension: data.dimension || 2
  };
};

// Add these styled components after the existing styled components
const SummaryCard = styled(Card)(({ theme }) => ({
  flex: 1,
  borderRadius: 12,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(76, 55, 148, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(76, 55, 148, 0.12)',
    borderColor: 'rgba(76, 55, 148, 0.2)',
  }
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  color: '#4c3794',
  marginBottom: theme.spacing(0.5),
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#666',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const ViewAllButton = styled(Button)(({ theme }) => ({

  boxShadow: 'none !important',
  textTransform: 'none',
  fontWeight: 500,
  color: '#4c3794',
  backgroundColor: 'transparent !important',
}));

// Define the purple color palette for charts
const CHART_COLORS = [
  '#4c3794', // Main deep purple
  // '#6a4caf', // Lighter purple
  // '#a084e8', // Pastel purple
  // '#b39ddb', // Soft lavender
  // '#ede7f6'  // Very light purple
];

interface DashboardData {
  sentimentDistribution: ChartDataItem[];
  topicDistribution: ChartDataItem[];
  timelineData: TimelineDataItem[];
  ratingDistribution: ChartDataItem[];
  experienceDistribution: ChartDataItem[];
  employmentStatusDistribution: ChartDataItem[];
}

// Visualization Components
const DataTable = ({ data }: { data: MultiColumnData }) => {
  // Don't render table if no data available
  if (!data.data || data.data.length === 0) {
    return null;
  }

  // Process data to handle complex arrays and improve display
  const processedData = data.data.map(row => {
    const processedRow: any = {};
    data.headers.forEach(header => {
      const value = row[header];
      if (Array.isArray(value)) {
        // Handle array values - show count and first few items
        if (value.length === 0) {
          processedRow[header] = 'No data';
        } else if (value.length <= 300) {
          processedRow[header] = value; // Keep as array for bullet point rendering
        } else {
          processedRow[header] = `${value.length} items (${value.slice(0, 2).join(', ')}...)`;
        }
      } else if (typeof value === 'number') {
        // Format numbers properly
        processedRow[header] = value.toFixed(2);
      } else if (typeof value === 'object' && value !== null) {
        // Handle object values
        processedRow[header] = JSON.stringify(value).substring(0, 50) + '...';
      } else {
        processedRow[header] = value;
      }
    });
    return processedRow;
  });

  return (
    <Box sx={{
      mt: 2,
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff',
      maxHeight: '400px',
      overflowY: 'auto'
    }}>
      <Box sx={{
        p: 2,
        backgroundColor: 'rgba(76, 55, 148, 0.05)',
        borderBottom: '1px solid rgba(76, 55, 148, 0.1)'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          fontSize: '0.9rem'
        }}>
          {data.title}
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.75rem',
          minWidth: '600px'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
              {data.headers.map((header, index) => (
                <th key={index} style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#4c3794',
                  borderBottom: '1px solid rgba(76, 55, 148, 0.1)',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : 'rgba(76, 55, 148, 0.02)'
                }}
              >
                {data.headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid rgba(18, 5, 62, 0.05)',
                      color: '#333',
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      wordWrap: 'break-word',
                      verticalAlign: 'top'
                    }}
                  >
                    {Array.isArray(row[header]) ? (
                      <ul style={{
                        margin: 0,
                        paddingLeft: '16px',
                        listStyleType: 'disc'
                      }}>
                        {row[header].map((item: any, index: number) => (
                          <li key={index} style={{
                            marginBottom: '4px',
                            fontSize: '0.7rem',
                            lineHeight: '1.3'
                          }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      row[header]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const DataChart = ({ data }: { data: MultiColumnData }) => {
  // Dynamically detect label column (first non-numeric column)
  const labelHeader = data.headers.find(h => {
    const val = data.data[0]?.[h];
    return typeof val !== 'number' && val !== null && val !== undefined;
  }) || data.headers[0];

  // Dynamically detect all numeric columns
  const numericHeaders = data.headers.filter(h => {
    if (h === labelHeader) return false;
    const val = data.data[0]?.[h];
    return typeof val === 'number';
  });

  // Find the primary numeric column for the bar (prioritize "count", then first numeric)
  const primaryNumericHeader = numericHeaders.find(h => {
    const headerLower = h.toLowerCase();
    return headerLower === 'count' || headerLower === 'total';
  }) || numericHeaders[0];

  // Convert data to chart format - store all values dynamically
  const chartData = data.data.map(row => {
    const chartItem: any = {};
    chartItem['name'] = String(row[labelHeader] || '-');

    // Store ALL column values (both numeric and non-numeric) for tooltip
    data.headers.forEach(header => {
      if (header !== labelHeader) { // Skip label header as it's already in 'name'
        const val = row[header];
        // Ensure numeric values are stored as numbers for proper display
        if (numericHeaders.includes(header) && val !== null && val !== undefined) {
          const numVal = typeof val === 'number' ? val : parseFloat(String(val));
          chartItem[header] = !isNaN(numVal) ? numVal : val;
        } else {
          chartItem[header] = val;
        }
      }
    });

    // Set primary value for bar chart (use primary numeric header)
    if (primaryNumericHeader) {
      chartItem['count'] = chartItem[primaryNumericHeader] || 0;
    }

    return chartItem;
  });

  // Custom tooltip component - dynamically shows ALL columns (all data)
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;

      // Get ALL columns that have values (excluding the label which is shown as title)
      const allFields = data.headers.filter(header => {
        if (header === labelHeader) return false; // Skip label header
        const val = dataPoint[header];
        return val !== null && val !== undefined && val !== '';
      });

      return (
        <Box sx={{
          backgroundColor: '#ede7f6',
          borderRadius: '8px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
          padding: '12px 16px',
          color: '#4c3794',
          maxWidth: '300px'
        }}>
          <Typography sx={{
            fontWeight: 700,
            fontSize: '0.813rem',
            color: '#4c3794',
            mb: 1.5
          }}>
            {label}
          </Typography>
          {allFields.map((header, index) => {
            const value = dataPoint[header];
            if (value === null || value === undefined || value === '') return null;

            // Format header name for display
            const headerLabel = header
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .trim();

            // Format value for display
            const isNumeric = typeof value === 'number';
            const isPercentage = isNumeric && (
              header.toLowerCase().includes('percent') ||
              header.toLowerCase().includes('%') ||
              header.toLowerCase() === 'percentage'
            );

            return (
              <Box
                key={header}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: index < allFields.length - 1 ? 0.5 : 0,
                  gap: 1
                }}
              >
                <Typography sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#4c3794',
                  flexShrink: 0
                }}>
                  {headerLabel}:
                </Typography>
                <Typography sx={{
                  fontSize: '0.75rem',
                  color: '#2c1810',
                  fontWeight: 500,
                  textAlign: 'right',
                  wordBreak: 'break-word'
                }}>
                  {isNumeric
                    ? (isPercentage
                      ? `${value}%`
                      : value.toLocaleString())
                    : String(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  // Determine chart type based on dimension
  if (data.dimension === 2) {
    // Check if this is a Key Drivers chart (for setting domain to 0-5)
    const isKeyDrivers = data.title && (
      data.title.toLowerCase().includes('key drivers') ||
      data.title.toLowerCase().includes('leverage drivers')
    );

    // Horizontal bar chart for 2D data
    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
            barCategoryGap={20}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
              domain={isKeyDrivers ? [0, 5] : ['auto', 'auto']}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={200}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="count"
              radius={[0, 8, 8, 0]}
              maxBarSize={10}
              fill="#4c3794"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  } else if (data.dimension === 3) {
    // Line chart for 3D data (time series with categories)
    const categories = Array.from(new Set(chartData.map(item => item[data.headers[0]])));

    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[1]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {categories.map((category, index) => {
              const categoryData = chartData.filter(item => item[data.headers[0]] === category);
              return (
                <Line
                  key={category}
                  type="monotone"
                  dataKey={data.headers[2]}
                  data={categoryData}
                  name={category}
                  stroke={`hsl(${240 + index * 60}, 70%, 50%)`}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    strokeWidth: 2,
                    stroke: '#fff'
                  }}
                  activeDot={{
                    r: 6,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  } else {
    // Default to table for other dimensions
    return <DataTable data={data} />;
  }
};

// Response Curve Chart Component for Driver Analysis
const ResponseCurveChart = ({
  title,
  data
}: {
  title: string;
  data: Array<{
    driver_value: number;
    outcome_value: number;
    driver_value_scaled?: number;
    driver_change_std?: number;
    outcome_type?: string;
  }>
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  // Prepare chart data - use driver_value (raw value) for X-axis
  const chartData = data.map(point => ({
    x: point.driver_value,
    y: point.outcome_value,
    driverValue: point.driver_value,
    driverChangeStd: point.driver_change_std ?? point.driver_value_scaled,
    outcomeType: point.outcome_type || 'continuous'
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid rgba(76, 55, 148, 0.2)',
          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.15)',
          padding: '12px 16px',
          color: '#4c3794'
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.813rem', color: '#4c3794', mb: 1 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
            Driver Value: {data.x != null ? data.x.toFixed(2) : 'N/A'}
          </Typography>
          {data.driverChangeStd !== undefined && (
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
              Driver Change (Std Dev): {data.driverChangeStd != null ? data.driverChangeStd.toFixed(2) : 'N/A'}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600 }}>
            Outcome Value: {data.y != null ? data.y.toFixed(2) : 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.5 }}>
            Type: {data.outcomeType}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{
      p: 3,
      borderRadius: '12px',
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      mb: 3
    }}>
      <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#4c3794',
        mb: 2,
        fontSize: '1rem'
      }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
          <XAxis
            dataKey="x"
            label={{ value: 'Driver Value', position: 'insideBottom', offset: -5, style: { fill: '#4c3794' } }}
            tick={{ fontSize: 12, fill: '#4c3794' }}
            tickLine={{ stroke: '#b39ddb' }}
            tickFormatter={(value) => (value != null ? value.toFixed(2) : '')}
          />
          <YAxis
            label={{ value: 'Outcome Value', angle: -90, position: 'insideLeft', style: { fill: '#4c3794' } }}
            tick={{ fontSize: 12, fill: '#4c3794' }}
            tickLine={{ stroke: '#b39ddb' }}
            tickFormatter={(value) => (value != null ? value.toFixed(2) : '')}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="y"
            stroke="#4c3794"
            strokeWidth={2}
            dot={{ r: 3, fill: '#4c3794', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 5, fill: '#4c3794', stroke: '#fff', strokeWidth: 2 }}
            animationDuration={1000}
          />
          <Area
            type="monotone"
            dataKey="y"
            fill="#4c3794"
            fillOpacity={0.1}
            stroke="none"
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

// Interactive What-If Analysis Component
const InteractiveWhatIfChart = ({
  dataset
}: {
  dataset: {
    title?: string;
    headers?: string[];
    data?: Array<{
      driver_value: number;
      outcome_value: number;
      driver_value_scaled?: number;
      driver_change_std?: number;
      outcome_type?: string;
    }>;
    baseline_metrics?: {
      current_outcome_value?: number;
      baseline_outcome_std?: number;
      baseline_driver_mean?: number;
      baseline_driver_std?: number;
      model_type?: string;
      is_binary?: boolean;
      coefficient?: number | null;
      odds_ratio?: number | null;
      correlation?: number;
      estimated_from_correlation?: boolean;
    };
    // Legacy support
    driver?: string;
    outcome?: string;
    summary?: any;
    response_curve?: Array<{
      driver_value: number;
      outcome_value: number;
      driver_value_scaled?: number;
      driver_change_std?: number;
      outcome_type?: string;
    }>;
  }
}) => {
  // State for baseline adjustment
  const [baselineAdjustment, setBaselineAdjustment] = useState(0);

  // Extract data - support both new and legacy formats
  const title = dataset.title || 'What-If Analysis';
  const headers = dataset.headers || ['Driver Value', 'Outcome Value'];
  const driverLabel = headers[0] || 'Driver Value';
  const outcomeLabel = headers[1] || 'Outcome Value';

  // Get data - prefer new format, fallback to legacy
  const rawData = dataset.data || dataset.response_curve || [];
  const baselineMetrics = dataset.baseline_metrics || dataset.summary || {};

  if (!rawData || rawData.length === 0) {
    return null;
  }

  // Get current outcome value - try multiple sources
  const currentOutcomeValue = baselineMetrics.current_outcome_value !== undefined
    ? baselineMetrics.current_outcome_value
    : (baselineMetrics.baseline_outcome_value !== undefined
      ? baselineMetrics.baseline_outcome_value
      : undefined);
  const isBinary = baselineMetrics.is_binary || false;
  const oddsRatio = baselineMetrics.odds_ratio;
  const baselineDriverMean = baselineMetrics.baseline_driver_mean;
  const modelType = baselineMetrics.model_type || 'linear';

  // Prepare chart data
  const originalData = rawData.map((point: any) => ({
    driver_value: point.driver_value,
    outcome_value: point.outcome_value,
    driver_value_scaled: point.driver_value_scaled,
    driver_change_std: point.driver_change_std,
    outcome_type: point.outcome_type || (isBinary ? 'probability' : 'continuous')
  }));

  // Calculate a default current outcome value from data if not provided
  const calculatedCurrentOutcome = useMemo(() => {
    if (currentOutcomeValue !== undefined) {
      return currentOutcomeValue;
    }
    // If no baseline provided, find the point where driver_change_std is closest to 0 (baseline)
    if (originalData.length > 0) {
      // First try to find point with driver_change_std closest to 0
      const hasDriverChangeStd = originalData.some(d => d.driver_change_std !== undefined && d.driver_change_std !== null);

      if (hasDriverChangeStd) {
        const baselinePoint = originalData.reduce((closest, point) => {
          const closestStd = Math.abs(closest.driver_change_std || 999);
          const pointStd = Math.abs(point.driver_change_std || 999);
          return pointStd < closestStd ? point : closest;
        });

        // Use this point if it's reasonably close to baseline (within 1 std dev)
        if (baselinePoint && Math.abs(baselinePoint.driver_change_std || 999) < 1.0) {
          return baselinePoint.outcome_value;
        }
      }

      // Fallback: use the median of outcome values (always works if we have data)
      const outcomeValues = originalData.map(d => d.outcome_value).filter(v => v !== undefined && v !== null).sort((a, b) => a - b);
      if (outcomeValues.length > 0) {
        const medianIndex = Math.floor(outcomeValues.length / 2);
        return outcomeValues[medianIndex];
      }
    }
    return undefined;
  }, [currentOutcomeValue, originalData]);

  // Calculate adjustment range (±50% of baseline, or ±0.5 for probabilities)
  const adjustmentRange = useMemo(() => {
    const baselineValue = calculatedCurrentOutcome;

    if (baselineValue === undefined) {
      // If no baseline, use data range
      const outcomeValues = originalData.map(d => d.outcome_value);
      const minOutcome = Math.min(...outcomeValues);
      const maxOutcome = Math.max(...outcomeValues);
      const range = maxOutcome - minOutcome;
      return {
        min: -range * 0.5,
        max: range * 0.5
      };
    }

    if (isBinary) {
      // For binary outcomes, clamp to valid probability range
      return {
        min: Math.max(-baselineValue, -0.5),
        max: Math.min(1 - baselineValue, 0.5)
      };
    }

    // For continuous outcomes, ±50% of baseline
    return {
      min: -baselineValue * 0.5,
      max: baselineValue * 0.5
    };
  }, [calculatedCurrentOutcome, isBinary, originalData]);

  // Helper function for proper binary outcome adjustment using log-odds
  const adjustBinaryOutcome = (
    originalOutcome: number,
    originalBaseline: number,
    newBaseline: number,
    oddsRatio?: number | null
  ): number => {
    // Clamp baselines to valid probability range
    const originalBaselineProb = Math.max(0.01, Math.min(0.99, originalBaseline));
    const newBaselineProb = Math.max(0.01, Math.min(0.99, newBaseline));

    // Calculate log-odds
    const originalLogOdds = Math.log(originalBaselineProb / (1 - originalBaselineProb));
    const newLogOdds = Math.log(newBaselineProb / (1 - newBaselineProb));
    const logOddsDiff = newLogOdds - originalLogOdds;

    // For each point, calculate its log-odds offset from baseline
    const originalPointProb = Math.max(0.01, Math.min(0.99, originalOutcome));
    const originalPointLogOdds = Math.log(originalPointProb / (1 - originalPointProb));
    const offsetFromBaseline = originalPointLogOdds - originalLogOdds;

    // Apply new baseline with same offset
    const newPointLogOdds = newLogOdds + offsetFromBaseline;
    const newPointProb = 1 / (1 + Math.exp(-newPointLogOdds));

    return Math.max(0, Math.min(1, newPointProb));
  };

  // Adjust response curve based on baseline change
  const adjustedData = useMemo(() => {
    if (calculatedCurrentOutcome === undefined || baselineAdjustment === 0) {
      return originalData;
    }

    const baselineValue = calculatedCurrentOutcome;
    const newBaselineOutcome = baselineValue + baselineAdjustment;

    return originalData.map(point => {
      // For binary outcomes: use proper logistic adjustment
      if (isBinary && oddsRatio && currentOutcomeValue !== undefined) {
        const adjustedOutcome = adjustBinaryOutcome(
          point.outcome_value,
          currentOutcomeValue,
          newBaselineOutcome,
          oddsRatio
        );
        return {
          ...point,
          outcome_value: adjustedOutcome
        };
      }

      // For continuous outcomes: simple linear shift
      return {
        ...point,
        outcome_value: point.outcome_value + baselineAdjustment
      };
    });
  }, [originalData, baselineAdjustment, calculatedCurrentOutcome, currentOutcomeValue, isBinary, oddsRatio]);

  // Calculate new baseline outcome value
  const newBaselineOutcome = calculatedCurrentOutcome !== undefined
    ? calculatedCurrentOutcome + baselineAdjustment
    : null;

  // Determine x-axis dataKey (use driver_value by default)
  const xAxisDataKey = "driver_value";

  return (
    <Card sx={{
      backgroundColor: '#fff',
      p: 3,
      borderRadius: '12px',
      border: '1px solid #e9ecef',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      width: '100%'
    }}>
      <Typography sx={{
        fontSize: '0.938rem',
        fontWeight: 600,
        color: '#131F32',
        mb: 2
      }}>
        {title}
      </Typography>

      {/* Baseline Adjustment Slider - Always show if we have data */}
      {originalData.length > 0 && (
        <Box sx={{ mb: 3, px: 1 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#131F32', mb: 0.5 }}>
              Current Performance Adjustment
            </Typography>
            <Typography sx={{ fontSize: '0.813rem', color: '#64748B', mb: 1 }}>
              {calculatedCurrentOutcome != null ? (
                currentOutcomeValue != null ? (
                  <>
                    Current Performance: <strong>{currentOutcomeValue.toFixed(2)}</strong>
                    {newBaselineOutcome != null && (
                      <> → New Performance: <strong>{newBaselineOutcome.toFixed(2)}</strong></>
                    )}
                    {baselineAdjustment != null && baselineAdjustment !== 0 && (
                      <> (Change: {baselineAdjustment > 0 ? '+' : ''}{baselineAdjustment.toFixed(2)})</>
                    )}
                  </>
                ) : (
                  <>
                    Estimated Baseline: <strong>{calculatedCurrentOutcome.toFixed(2)}</strong>
                    {newBaselineOutcome != null && (
                      <> → Adjusted Baseline: <strong>{newBaselineOutcome.toFixed(2)}</strong></>
                    )}
                    {baselineAdjustment != null && baselineAdjustment !== 0 && (
                      <> (Change: {baselineAdjustment > 0 ? '+' : ''}{baselineAdjustment.toFixed(2)})</>
                    )}
                  </>
                )
              ) : (
                <>
                  Baseline: <strong>Calculating...</strong>
                  {baselineAdjustment != null && baselineAdjustment !== 0 && (
                    <> (Adjustment: {baselineAdjustment > 0 ? '+' : ''}{baselineAdjustment.toFixed(2)})</>
                  )}
                </>
              )}
            </Typography>
          </Box>

          {calculatedCurrentOutcome != null ? (
            <Slider
              value={baselineAdjustment}
              onChange={(e, newValue) => setBaselineAdjustment(newValue as number)}
              min={adjustmentRange.min}
              max={adjustmentRange.max}
              step={(adjustmentRange.max - adjustmentRange.min) / 100}
              marks={[
                { value: adjustmentRange.min, label: adjustmentRange.min.toFixed(2) },
                { value: 0, label: '0' },
                { value: adjustmentRange.max, label: adjustmentRange.max.toFixed(2) }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => value.toFixed(3)}
              sx={{
                '& .MuiSlider-thumb': {
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(76, 55, 148, 0.16)',
                  },
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#4c3794',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#e9ecef',
                },
              }}
            />
          ) : (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.813rem', color: '#94A3B8' }}>
                Calculating baseline from data...
              </Typography>
            </Box>
          )}

          <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 1 }}>
            Adjust the current performance level to see how the response curve shifts.
            This helps explore "What if our current performance was different?"
          </Typography>
        </Box>
      )}

      {/* Chart */}
      <Box sx={{ width: '100%', height: 400 }}>
        <ResponsiveContainer>
          <ComposedChart data={adjustedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            <XAxis
              dataKey={xAxisDataKey}
              label={{
                value: driverLabel,
                position: 'insideBottom',
                offset: -5,
                style: { textAnchor: 'middle', fontSize: '0.75rem', fill: '#64748B' }
              }}
              stroke="#64748B"
              tick={{ fontSize: '0.75rem', fill: '#64748B' }}
              tickFormatter={(value) => (value != null ? value.toFixed(2) : '')}
            />
            <YAxis
              label={{
                value: outcomeLabel,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: '0.75rem', fill: '#64748B' }
              }}
              stroke="#64748B"
              tick={{ fontSize: '0.75rem', fill: '#64748B' }}
              tickFormatter={(value) => {
                if (value == null) return '';
                if (isBinary) {
                  return (value * 100).toFixed(0) + '%';
                }
                return value.toFixed(2);
              }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <Box sx={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      padding: '12px 16px',
                      minWidth: '200px'
                    }}>
                      <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, mb: 1 }}>
                        {driverLabel}: {data.driver_value != null ? data.driver_value.toFixed(2) : 'N/A'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>
                        {outcomeLabel}: {isBinary
                          ? (data.outcome_value != null ? (data.outcome_value * 100).toFixed(2) + '%' : 'N/A')
                          : (data.outcome_value != null ? data.outcome_value.toFixed(2) : 'N/A')}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Legend />

            {/* Original Response Curve (faded) - only show if adjusted */}
            {baselineAdjustment !== 0 && (
              <Line
                type="monotone"
                dataKey="outcome_value"
                data={originalData}
                stroke="#E0E0E0"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
                name="Original Baseline"
                strokeOpacity={0.6}
              />
            )}

            {/* Adjusted Response Curve */}
            <Line
              type="monotone"
              dataKey="outcome_value"
              stroke="#4c3794"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 6, fill: '#4c3794' }}
              name={baselineAdjustment !== 0 ? "Adjusted Baseline" : "Response Curve"}
            />

            {/* Baseline Driver Marker */}
            {baselineDriverMean != null && (
              <ReferenceLine
                x={baselineDriverMean}
                stroke="#7F8C8D"
                strokeDasharray="5 5"
                strokeWidth={1.5}
                label={{
                  value: "Current Driver",
                  position: "top",
                  style: { fontSize: '0.7rem', fill: '#7F8C8D', fontWeight: 500 }
                }}
              />
            )}

            {/* Original Current Performance Marker */}
            {calculatedCurrentOutcome != null && (
              <ReferenceLine
                y={calculatedCurrentOutcome}
                stroke="#95A5A6"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{
                  value: currentOutcomeValue != null ? "Current Performance" : "Estimated Baseline",
                  position: "right",
                  style: { fontSize: '0.7rem', fill: '#95A5A6', fontWeight: 500 }
                }}
              />
            )}

            {/* New Performance Marker - only show if adjusted */}
            {newBaselineOutcome != null && baselineAdjustment !== 0 && (
              <ReferenceLine
                y={newBaselineOutcome}
                stroke="#4A90E2"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{
                  value: "New Performance",
                  position: "right",
                  style: { fontSize: '0.7rem', fill: '#4A90E2', fontWeight: 500 }
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Summary Info */}
      {baselineMetrics && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <Grid container spacing={2}>
            {calculatedCurrentOutcome != null && (
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
                  {currentOutcomeValue != null ? 'Current Performance' : 'Estimated Baseline'}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#131F32' }}>
                  {calculatedCurrentOutcome.toFixed(2)}
                </Typography>
              </Grid>
            )}
            {baselineDriverMean != null && (
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
                  Driver Baseline
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#131F32' }}>
                  {baselineDriverMean.toFixed(2)}
                </Typography>
              </Grid>
            )}
            {modelType && (
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
                  Model Type
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#131F32', textTransform: 'capitalize' }}>
                  {modelType}
                </Typography>
              </Grid>
            )}
            {isBinary && oddsRatio != null && (
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>
                  Odds Ratio
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#131F32' }}>
                  {oddsRatio.toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Card>
  );
};

const DataHeatmap = ({ data }: { data: MultiColumnData }) => {
  // For 3D data, create a heatmap visualization
  const [hoveredCell, setHoveredCell] = useState<{
    x: any;
    y: any;
    value: any;
    xPos: number;
    yPos: number;
  } | null>(null);

  const prepareHeatmapData = (): { data: any[]; xValues: any[]; yValues: any[]; zHeader: string } | null => {
    if (!data.data || data.data.length === 0) return null;

    // For 3D data, we'll create a heatmap where:
    // - X-axis: First header (e.g., location, category)
    // - Y-axis: Second header (e.g., time period, subcategory) 
    // - Color intensity: Third header (e.g., sentiment score, count)

    const xValues = Array.from(new Set(data.data.map(row => row[data.headers[0]])));
    const yValues = Array.from(new Set(data.data.map(row => row[data.headers[1]])));

    // Create a matrix for the heatmap
    const heatmapData = yValues.map(yValue => {
      const row: any = { y: yValue };
      xValues.forEach(xValue => {
        const matchingRow = data.data.find(row =>
          row[data.headers[0]] === xValue && row[data.headers[1]] === yValue
        );
        row[xValue] = matchingRow ? matchingRow[data.headers[2]] : 0;
      });
      return row;
    });

    return {
      data: heatmapData,
      xValues,
      yValues,
      zHeader: data.headers[2]
    };
  };

  const heatmapConfig = prepareHeatmapData();

  // Get min and max values for color scaling
  const allValues = data.data.map(row => row[data.headers[2]]).filter(val => typeof val === 'number');
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const getColorIntensity = (value: number) => {
    if (typeof value !== 'number') return '#f0f0f0';
    const normalized = (value - minValue) / (maxValue - minValue);
    // Use a color gradient from light purple to dark purple to match theme
    const intensity = Math.floor(normalized * 255);
    return `rgb(${237 + intensity * 0.07}, ${231 + intensity * 0.09}, ${246 + intensity * 0.03})`;
  };

  const getTextColor = (backgroundColor: string) => {
    // Extract RGB values from the background color
    const rgbMatch = backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!rgbMatch) return '#333';

    const r = parseInt(rgbMatch[1]);
    const g = parseInt(rgbMatch[2]);
    const b = parseInt(rgbMatch[3]);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Use dark text for light backgrounds, light text for dark backgrounds
    return luminance > 0.6 ? '#333' : '#fff';
  };



  return (
    <Box sx={{
      mt: 2,
      borderRadius: 2,
      p: 2,
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff'
    }}>
      <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#4c3794',
        mb: 2,
        fontSize: '1rem'
      }}>
        {data.title} - Heatmap Visualization
      </Typography>

      {heatmapConfig && heatmapConfig.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{
            display: 'inline-block',
            minWidth: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            {/* Header row with X values */}
            <Box sx={{
              display: 'flex',
              borderBottom: '2px solid #4c3794',
              backgroundColor: '#f8f9fa'
            }}>
              <Box sx={{
                width: 120,
                p: 1,
                borderRight: '2px solid #4c3794',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: '#4c3794'
              }}>
                {data.headers[1]} / {data.headers[0]}
              </Box>
              {heatmapConfig.xValues.map((xValue: any, index: number) => (
                <Box key={index} sx={{
                  flex: 1,
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#4c3794',
                  borderRight: index < heatmapConfig.xValues.length - 1 ? '1px solid #e0e0e0' : 'none'
                }}>
                  {String(xValue)}
                </Box>
              ))}
            </Box>

            {/* Data rows */}
            {heatmapConfig.data.map((row: any, rowIndex: number) => (
              <Box key={rowIndex} sx={{
                display: 'flex',
                borderBottom: rowIndex < heatmapConfig.data.length - 1 ? '1px solid #e0e0e0' : 'none'
              }}>
                <Box sx={{
                  width: 120,
                  p: 1,
                  borderRight: '2px solid #4c3794',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: '#4c3794',
                  backgroundColor: '#f8f9fa'
                }}>
                  {String(row.y)}
                </Box>
                {heatmapConfig.xValues.map((xValue: any, colIndex: number) => {
                  const backgroundColor = getColorIntensity(row[xValue]);
                  const textColor = getTextColor(backgroundColor);

                  return (
                    <Box key={colIndex} sx={{
                      flex: 1,
                      p: 1,
                      textAlign: 'center',
                      fontSize: '0.75rem',
                      backgroundColor,
                      color: textColor,
                      fontWeight: 500,
                      borderRight: colIndex < heatmapConfig.xValues.length - 1 ? '1px solid #e0e0e0' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(76, 55, 148, 0.2)',
                        zIndex: 1,
                        position: 'relative'
                      }
                    }}
                      onMouseEnter={(e) => {
                        const cellData = {
                          x: xValue,
                          y: row.y,
                          value: row[xValue],
                          xPos: e.clientX,
                          yPos: e.clientY
                        };

                        setHoveredCell(cellData);
                      }}
                      onMouseLeave={() => {

                        setHoveredCell(null);
                      }}
                    >
                      {typeof row[xValue] === 'number' ? (row[xValue] % 1 === 0 ? row[xValue].toString() : row[xValue].toFixed(2)) : row[xValue]}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {/* Color legend */}
          <Box sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.75rem' }}>
              {data.headers[2]} Scale:
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 24,
              background: 'linear-gradient(to right, rgb(237, 231, 246), rgb(76, 55, 148))',
              borderRadius: 1,
              px: 2,
              border: '1px solid rgba(76, 55, 148, 0.1)'
            }}>
              <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.7rem' }}>
                {minValue.toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#4c3794', fontSize: '0.7rem' }}>
                →
              </Typography>
              <Typography variant="caption" sx={{ color: '#4c3794', fontWeight: 600, fontSize: '0.7rem' }}>
                {maxValue.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: 1
        }}>
          <Typography>No data available for heatmap visualization</Typography>
        </Box>
      )}

      {/* Custom tooltip using portal */}
      {hoveredCell && ReactDOM.createPortal(
        <Box
          sx={{
            position: 'fixed',
            left: hoveredCell.xPos + 10,
            top: hoveredCell.yPos - 10,
            backgroundColor: '#ede7f6',
            borderRadius: '8px',
            border: '2px solid #4c3794',
            boxShadow: '0 4px 12px rgba(76, 55, 148, 0.25)',
            padding: '12px 16px',
            color: '#4c3794',
            zIndex: 9999,
            minWidth: '180px',
            backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #4c3794'
            }
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[0]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {String(hoveredCell.x)}
            </Typography>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[1]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {String(hoveredCell.y)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'capitalize',
              color: '#4c3794',
              mb: 0.5
            }}>
              {data.headers[2]}:
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: '#2c1810',
              fontWeight: 500
            }}>
              {typeof hoveredCell.value === 'number' ? (hoveredCell.value % 1 === 0 ? hoveredCell.value.toString() : hoveredCell.value.toFixed(2)) : String(hoveredCell.value)}
            </Typography>
          </Box>
        </Box>,
        document.body
      )}
    </Box>
  );
};

// Folder Tree Item Component
const FolderTreeItem = ({
  folder,
  onNavigate,
  onCloseDialog,
  level = 0
}: {
  folder: GoogleDriveFolder;
  onNavigate: (folderId: string) => void;
  onCloseDialog: () => void;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    onNavigate(folder.id);
    onCloseDialog();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          pl: level * 2 + 1,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'rgba(76, 55, 148, 0.1)',
          }
        }}
        onClick={handleClick}
      >
        <Typography sx={{ fontSize: '1.2rem', mr: 1 }}>📁</Typography>
        <Typography sx={{ flex: 1, fontWeight: 500 }}>
          {folder.name}
        </Typography>
        {folder.children && folder.children.length > 0 && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ color: '#4c3794' }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </IconButton>
        )}
      </Box>
      {expanded && folder.children && (
        <Box>
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              onNavigate={onNavigate}
              onCloseDialog={onCloseDialog}
              level={level + 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface SurveyInsightsProps {
  context?: 'hr' | 'banking' | 'gov';
  projectType?: string; // e.g. 'DATA_ANAL'
}

const SurveyInsights = ({ context, projectType }: SurveyInsightsProps = {}) => {
  // Detect context from sessionStorage if not provided as prop
  const detectedContext = context || (sessionStorage.getItem('appContext') as 'hr' | 'banking' | 'gov' | null) || 'hr';
  // Detect project type (e.g., DATA_ANAL) from prop, sessionStorage or path
  const detectedProjectType = projectType || sessionStorage.getItem('projectType') || (typeof window !== 'undefined' && window.location.pathname.includes('data-analytics') ? 'DATA_ANAL' : 'SURVEY');

  // Google Drive service instance
  const googleDriveService = useRef<GoogleDriveService | null>(null);

  // Initialize Google Drive service with callbacks
  useEffect(() => {
    googleDriveService.current = new GoogleDriveService({
      onAuthStateChange: (state: GoogleDriveAuthState) => {
        setGoogleDriveAuthState(state);
      },
      onFilesLoaded: (files: GoogleDriveFile[], totalFiles: number) => {
        setGoogleDriveFiles(files);
        setGoogleDriveTotalFiles(totalFiles);
      },
      onError: (error: string) => {
        setError(error);
      },
      onSuccess: (message: string) => {
        setSuccess(message);
      },
      onDownloadProgress: (progress: number) => {
        setGoogleDriveDownloadProgress(progress);
      },
      onLoadingStateChange: (isLoading: boolean) => {
        setIsLoadingGoogleFiles(isLoading);
      },
      onFilePreviewLoaded: (previewInfo: GoogleDrivePreviewInfo, previewUrl: string, previewContent: string) => {
        setFilePreviewInfo(previewInfo);
        setPreviewUrl(previewUrl);
        setPreviewContent(previewContent);
        setShowFilePreview(true);
      },
      onFolderBreadcrumbLoaded: (breadcrumb: GoogleDriveBreadcrumb[]) => {
        setFolderBreadcrumb(breadcrumb);
      },
      onFolderTreeLoaded: (folderTree: GoogleDriveFolder[]) => {
        setFolderTree(folderTree);
        setShowFolderTree(true);
      },
      onFileInfoLoaded: (fileInfo: any) => {
        setFileInfo(fileInfo);
        setShowFileInfo(true);
      }
    });

    // Cleanup on unmount
    return () => {
      if (googleDriveService.current) {
        googleDriveService.current.destroy();
      }
    };
  }, []);

  const [formValues, setFormValues] = useState<FormValues>({
    contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK"  // Default value
  });
  const [surveyItems, setSurveyItems] = useState<SurveyItem[]>([]);
  const [selectedDocUID, setSelectedDocUID] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(detectedProjectType === 'DATA_ANAL');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you analyze the survey data?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isAddSurveyDialogOpen, setIsAddSurveyDialogOpen] = useState(false);
  const [addSurveyFormValues, setAddSurveyFormValues] = useState<string>('');
  const [addSurveyLoading, setAddSurveyLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    { text: "What are the key insights?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
    { text: "Show me positive feedback", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
    // { text: "Show me areas of improvement", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
    // { text: "What's the overall satisfaction?", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
    // { text: "Analyze employee sentiment", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
  ]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [addSurveyDialog, setAddSurveyDialog] = useState(false);

  // Export Content state
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [brandDetails, setBrandDetails] = useState<BrandDetails>({
    website: '',
    websiteNotApplicable: false,
    industryKeywords: '',
    targetAudience: [],
    brandVoice: ''
  });
  const [contentPreferences, setContentPreferences] = useState<ContentPreferences>({
    category: 'SOCIAL_POST_LINKEDIN',
    format: 'Text Only',
    length: 'Short (100-200 words)',
    style: 'Professional',
    instructions: ''
  });
  const [newAudience, setNewAudience] = useState('');
  const [showAudienceDropdown, setShowAudienceDropdown] = useState(false);
  const [showContentDisplay, setShowContentDisplay] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isExportingAsIs, setIsExportingAsIs] = useState(false);
  const [contentTypes, setContentTypes] = useState<any[]>([]);
  // Export dialog state for Survey Insights (separate from chatbot export)
  const [showSurveyInsightsExportDialog, setShowSurveyInsightsExportDialog] = useState(false);
  const [isExportingSurveyInsights, setIsExportingSurveyInsights] = useState(false);
  const [addSurveyFields, setAddSurveyFields] = useState<InputField[]>([]);
  const [addSurveyFormData, setAddSurveyFormData] = useState({
    topic: '',
    keywords: [] as string[]
  });
  const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(surveyItems.length / itemsPerPage);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    sentimentDistribution: [],
    topicDistribution: [],
    timelineData: [],
    ratingDistribution: [],
    experienceDistribution: [],
    employmentStatusDistribution: []
  });
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<'project' | 'upload'>('project');
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_description: '',
    period_start_date: new Date().toISOString().split('T')[0],
    period_end_date: new Date().toISOString().split('T')[0],
    period_type: 'D'
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRange>('day');
  const [totalResponses, setTotalResponses] = useState(0);

  // State for tracking active chart type per segment (for Driver Priority Matrix / Opportunity Heatmap toggle)
  const [activeChartTypes, setActiveChartTypes] = useState<Record<string, 'priorityMatrix' | 'heatmap'>>({});
  // State for tracking Segment Opportunity Map view type (TreeMap vs Bubble Chart)
  const [segmentOpportunityMapView, setSegmentOpportunityMapView] = useState<'treemap' | 'bubble'>('bubble');
  const [averageSentiment, setAverageSentiment] = useState(0);

  // State for tracking selected docUIDs for chatbot - separate for segments and drivers
  const [selectedSegmentDocUIDs, setSelectedSegmentDocUIDs] = useState<Set<string>>(new Set());
  const [selectedDriverDocUIDs, setSelectedDriverDocUIDs] = useState<Set<string>>(new Set());

  // Helper to get the active selection based on current tab
  const getActiveSelectedDocUIDs = (): Set<string> => {
    if (activeMainTab === 'segment') {
      return selectedSegmentDocUIDs;
    } else if (activeMainTab === 'driver') {
      return selectedDriverDocUIDs;
    }
    return new Set();
  };

  // Helper to get the setter for the active selection
  const getActiveSetSelectedDocUIDs = (): React.Dispatch<React.SetStateAction<Set<string>>> => {
    if (activeMainTab === 'segment') {
      return setSelectedSegmentDocUIDs;
    } else if (activeMainTab === 'driver') {
      return setSelectedDriverDocUIDs;
    }
    return setSelectedSegmentDocUIDs; // Default fallback
  };
  const [positiveResponses, setPositiveResponses] = useState(0);
  // Add state for the View All Responses dialog
  const [isViewAllDialogOpen, setIsViewAllDialogOpen] = useState(false);
  // Add state for multicolumn data expand dialog (for all tables, not just Key Themes)
  const [multicolumnDialogOpen, setMulticolumnDialogOpen] = useState<Record<string, boolean>>({});
  const [multicolumnDialogData, setMulticolumnDialogData] = useState<any>(null);
  // Add state for search query
  const [searchQuery, setSearchQuery] = useState('');
  // Add these new interfaces after the existing interfaces
  const [uploadMethod, setUploadMethod] = useState<'device' | 'url' | 'google-drive'>('device');
  const [fileUrl, setFileUrl] = useState('');
  const [isUrlValidating, setIsUrlValidating] = useState(false);
  const [urlValidationError, setUrlValidationError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  // Add new state variables for iframe authentication
  const [showAuthIframe, setShowAuthIframe] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);
  const [authRetryCount, setAuthRetryCount] = useState(0);
  // Add new state variables for SharePoint authentication
  const [showSharePointAuth, setShowSharePointAuth] = useState(false);
  const [sharePointUrl, setSharePointUrl] = useState('');
  const [authWindow, setAuthWindow] = useState<Window | null>(null);
  // Add new state variables for comprehensive URL handling
  const [urlType, setUrlType] = useState<'public' | 'authenticated' | 'complex' | 'unknown'>('unknown');
  const [authCredentials, setAuthCredentials] = useState<{ type: 'bearer' | 'basic', token: string } | null>(null);
  const [showAuthCredentialsDialog, setShowAuthCredentialsDialog] = useState(false);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [isLargeFile, setIsLargeFile] = useState(false);
  // Add new state variables for Google Drive authentication
  const [showGoogleAuth, setShowGoogleAuth] = useState(false);
  const [googleUrl, setGoogleUrl] = useState('');
  const [googleWindow, setGoogleWindow] = useState<Window | null>(null);
  // Add new state variables for Google Drive integration
  const [showGoogleDriveDialog, setShowGoogleDriveDialog] = useState(false);
  const [googleDriveAuthState, setGoogleDriveAuthState] = useState<GoogleDriveAuthState>({
    isAuthenticated: false,
    sessionId: ''
  });
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [isLoadingGoogleFiles, setIsLoadingGoogleFiles] = useState(false);
  const [selectedGoogleFile, setSelectedGoogleFile] = useState<GoogleDriveFile | null>(null);
  const [selectedGoogleFiles, setSelectedGoogleFiles] = useState<GoogleDriveFile[]>([]);
  const [isDownloadingGoogleFile, setIsDownloadingGoogleFile] = useState(false);
  const [isDownloadingMultipleFiles, setIsDownloadingMultipleFiles] = useState(false);
  const [googleDriveSearchQuery, setGoogleDriveSearchQuery] = useState('');
  const [googleDriveCurrentPage, setGoogleDriveCurrentPage] = useState(1);
  const [googleDriveTotalFiles, setGoogleDriveTotalFiles] = useState(0);
  const [googleDriveDownloadProgress, setGoogleDriveDownloadProgress] = useState(0);
  const [showGoogleDriveAuthDialog, setShowGoogleDriveAuthDialog] = useState(false);
  const [googleDriveAuthUrl, setGoogleDriveAuthUrl] = useState('');
  const [isGoogleDriveAuthInProgress, setIsGoogleDriveAuthInProgress] = useState(false);
  const [googleDriveAuthWindow, setGoogleDriveAuthWindow] = useState<Window | null>(null);
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [fileInfo, setFileInfo] = useState<any>(null);
  const [isLoadingFileInfo, setIsLoadingFileInfo] = useState(false);

  // New state variables for folder navigation and file preview
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [folderBreadcrumb, setFolderBreadcrumb] = useState<GoogleDriveBreadcrumb[]>([]);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [filePreviewInfo, setFilePreviewInfo] = useState<GoogleDrivePreviewInfo | null>(null);
  const [isLoadingFilePreview, setIsLoadingFilePreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewContent, setPreviewContent] = useState<string>('');
  const [showFolderTree, setShowFolderTree] = useState(false);
  const [folderTree, setFolderTree] = useState<GoogleDriveFolder[]>([]);
  const [isLoadingFolderTree, setIsLoadingFolderTree] = useState(false);

  // New state variables for tab functionality
  const [activeTab, setActiveTab] = useState<'myDrive' | 'shared' | 'teamDrives'>('myDrive');
  const [teamDrives, setTeamDrives] = useState<any[]>([]);
  const [selectedTeamDrive, setSelectedTeamDrive] = useState<string | null>(null);

  // Sidebar state
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const sidebarDropdownRef = useRef<HTMLDivElement>(null);

  // Tab state
  const [activeMainTab, setActiveMainTab] = useState<'executive' | 'dashboard' | 'segment' | 'driver'>('executive');

  // Executive Summary state
  const [executiveSummaryData, setExecutiveSummaryData] = useState<any[]>([]);
  const [loadingExecutiveSummary, setLoadingExecutiveSummary] = useState(false);
  // Chart dialog state - stores the chart data to display in dialog
  const [expandedChartDialog, setExpandedChartDialog] = useState<{
    open: boolean;
    title: string;
    chartData: any[];
    headers: string[];
    labelHeader: string;
    primaryNumericHeader: string | null;
    isKeyDrivers: boolean;
    isSegmentsOverview?: boolean;
    isDistributionOfSurveyOutcomes?: boolean;
    isDriverPriorityMatrix?: boolean;
    analysis?: string;
    recommendations?: string[];
  } | null>(null);

  // Segment Analysis state
  const [segmentGroups, setSegmentGroups] = useState<SegmentGroup[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);

  // Driver Analysis state
  const [driverGroups, setDriverGroups] = useState<any[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Unified loading state for initial project load
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  // Ref to track current project ID to prevent stale updates
  const currentProjectIdRef = useRef<string | null>(null);
  // Ref to track previous campaignId to prevent unnecessary Chatbot resets
  const previousCampaignIdRef = useRef<string | null>(null);

  const [expandedSegmentId, setExpandedSegmentId] = useState<string | null>(null);
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const [segmentFilter, setSegmentFilter] = useState<'all' | 'OUTCOME_BASED_SEGMENT' | 'PERSONA_BASED_SEGMENT'>('all');

  // View state for Segment Analysis (List vs Details)
  const [activeSegmentView, setActiveSegmentView] = useState<'list' | 'details'>('list');
  const [selectedSegmentData, setSelectedSegmentData] = useState<any>(null);

  const handleViewSegmentDetails = (data: any) => {
    setSelectedSegmentData(data);
    setActiveSegmentView('details');
  };

  const handleBackToSegmentList = () => {
    setActiveSegmentView('list');
    setSelectedSegmentData(null);
  };

  /**
   * Sub-component: SegmentCardList
   * Renders a list of segment cards with basic info.
   */
  const SegmentCardList = ({
    groups,
    onViewDetails,
    selectedUIDs,
    onSelectionChange,
    onAskAI
  }: {
    groups: SegmentGroup[];
    onViewDetails: (data: any) => void;
    selectedUIDs: Set<string>;
    onSelectionChange: (uids: string[], checked: boolean) => void;
    onAskAI: () => void;
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Ask AI / Action Header */}
      {groups.length > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          mb: 1
        }}>
          <Button
            variant="contained"
            startIcon={<ChatBubbleIcon />}
            onClick={onAskAI}
            sx={{
              backgroundColor: '#4c3794',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(76, 55, 148, 0.25)',
              '&:hover': {
                backgroundColor: '#3d2d7a',
                boxShadow: '0 6px 20px rgba(76, 55, 148, 0.35)'
              }
            }}
          >
            Ask AI
          </Button>
        </Box>
      )}

      {/* Segment Cards */}
      <Grid container spacing={3}>
        {groups.map((group, idx) => {
          const sData = processSegmentData(group);
          if (!sData) return null;
          const uids = getSegmentDocUIDs(group);
          const isSelected = uids.some(uid => selectedUIDs.has(uid));

          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e9ecef',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(76, 55, 148, 0.15)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <Box sx={{ p: 3, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => onSelectionChange(uids, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ color: '#4c3794', p: 0 }}
                    />
                    <Box sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '20px',
                      backgroundColor: 'rgba(76, 55, 148, 0.1)',
                      color: '#4c3794',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}>
                      {sData.segmentPercentage.toFixed(1)}%
                    </Box>
                  </Box>

                  <Typography sx={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#131F32',
                    mb: 1,
                    lineHeight: 1.3
                  }}>
                    {sData.segmentName}
                  </Typography>

                  <Typography sx={{
                    fontSize: '0.875rem',
                    color: '#64748B',
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {sData.segmentSummary}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<PersonIcon sx={{ fontSize: 14, color: '#4c3794 !important' }} />}
                      label={`${sData.segmentSize} responses`}
                      size="small"
                      sx={{ backgroundColor: '#fff', border: '1px solid rgba(76, 55, 148, 0.2)', color: '#4c3794', fontWeight: 600 }}
                    />
                    {sData.informationCategory && (
                      <Chip
                        label={sData.informationCategory.replace(/_/g, ' ').replace('SEGMENT', '').trim()}
                        size="small"
                        sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#64748B', fontWeight: 600, textTransform: 'capitalize' }}
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9', mt: 'auto' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onViewDetails(sData)}
                    sx={{
                      backgroundColor: '#4c3794',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: '#3d2d7a' }
                    }}
                  >
                    View Insights
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  /**
   * Sub-component: SegmentDetailsView
   * Renders the highly detailed view for a selected segment.
   */
  const SegmentDetailsView = ({
    data,
    onBack,
    onAskAI,
    hideHeader = false
  }: {
    data: any;
    onBack: () => void;
    onAskAI: () => void;
    hideHeader?: boolean;
  }) => {
    const [activeViz, setActiveViz] = useState<'matrix' | 'heatmap'>('matrix');

    // Micro-segment filter state
    const operationalVars = useMemo(() => {
      const overviewCategory = executiveSummaryData?.find((cat: any) => cat.groupName === 'Overview');
      if (overviewCategory && overviewCategory.items) {
        const dictionaryItem = overviewCategory.items.find((item: any) => item.objectType === 'DICTIONARY');
        if (dictionaryItem && dictionaryItem.entries) {
          const opVarsEntry = dictionaryItem.entries.find((e: any) => e.key === 'operationalVars');
          if (opVarsEntry && Array.isArray(opVarsEntry.value)) {
            return opVarsEntry.value.map((v: any) => ({
              key: v.key || v,
              label: v.label || v
            }));
          }
        }
      }
      // Fallback for demo if data is missing
      return [
        { key: 'departmentId', label: 'Department' },
        { key: 'operationalUnit', label: 'Operational Unit' }
      ];
    }, [executiveSummaryData]);

    const [microFilters, setMicroFilters] = useState<Record<string, string>>({});

    // Initialize microFilters when operationalVars changes
    useEffect(() => {
      if (operationalVars.length > 0) {
        const initialFilters: Record<string, string> = {};
        operationalVars.forEach((v: { key: string; label: string }) => {
          initialFilters[v.key] = '';
        });
        setMicroFilters(initialFilters);
      }
    }, [operationalVars]);

    const [distinctValuesMap, setDistinctValuesMap] = useState<Record<string, string[]>>({});
    const [isFetchingDistinct, setIsFetchingDistinct] = useState<Record<string, boolean>>({});
    const [isGeneratingMicroSegments, setIsGeneratingMicroSegments] = useState(false);
    const [microSegmentResults, setMicroSegmentResults] = useState<any[]>([]);
    const [microSegmentActiveView, setMicroSegmentActiveView] = useState<'filters' | 'list' | 'details'>('filters');
    const [selectedMicroSegmentData, setSelectedMicroSegmentData] = useState<any>(null);

    const handleViewMicroSegmentDetails = (sData: any) => {
      setSelectedMicroSegmentData(sData);
      setMicroSegmentActiveView('details');
    };

    const handleBackToMicroSegmentList = () => {
      setMicroSegmentActiveView('list');
      setSelectedMicroSegmentData(null);
    };

    const handleBackToMicroFilters = () => {
      setMicroSegmentActiveView('filters');
      setMicroSegmentResults([]);
    };

    // Derive Macro Segment ID
    const macroSegmentId = `${data.informationCategory || 'OUTCOME_BASED_SEGMENT'}_${data.id || 'Segment_0'}`;

    const fetchDistinctValues = async (fieldName: string) => {
      if (distinctValuesMap[fieldName]) return; // Already fetched

      setIsFetchingDistinct(prev => ({ ...prev, [fieldName]: true }));
      try {
        const response = await axios.post(`${environment.controlplaneUrl}/agent/flow/execute`, {
          data: {
            fieldName,
            pageSize: 50,
            pageNumber: 1,
            campaignId: selectedProject?.id
          },
          agent: {
            code: "QUERY_DISTINCT_IN_USER_FEEDBACK"
          }
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.status === 200 && response.data.data) {
          const distinctValueEntry = response.data.data.find((item: any) => item.key === 'distinct');
          if (distinctValueEntry && Array.isArray(distinctValueEntry.value)) {
            setDistinctValuesMap(prev => ({ ...prev, [fieldName]: distinctValueEntry.value }));
          }
        }
      } catch (error) {
        console.error(`Error fetching distinct values for ${fieldName}:`, error);
      } finally {
        setIsFetchingDistinct(prev => ({ ...prev, [fieldName]: false }));
      }
    };

    const handleGenerateMicroSegments = async () => {
      setIsGeneratingMicroSegments(true);
      setMicroSegmentResults([]); // Clear previous results
      try {
        const infoCat = data.informationCategory?.includes('PERSONA')
          ? 'PERSONA_BASED_MICRO_SEGMENT'
          : 'OUTCOME_BASED_MICRO_SEGMENT';

        const payload = {
          data: {
            topic: "",
            keywords: [],
            pageSize: 50,
            pageNumber: 1,
            campaignId: selectedProject?.id,
            informationCategory: infoCat,
            filters: {
              macroSegmentId,
              ...microFilters
            }
          },
          agent: {
            code: "QUERY_SURVEY_ANALYSIS_CATEGORY",
            groupBy: "segmentName"
          }
        };

        const response = await axios.post(`${environment.controlplaneUrl}/agent/flow/execute`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.status === 200 && response.data.data) {
          setMicroSegmentResults(response.data.data);
          setMicroSegmentActiveView('list');
          // setSuccess('Micro-segment analysis fetched successfully');
          // Auto-scroll to results
          setTimeout(() => {
            const resultsSection = document.getElementById('micro-segment-results');
            if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          setMicroSegmentResults([]);
          setError('No micro-segment analysis found for the selected criteria');
        }
      } catch (error) {
        console.error('Error fetching micro-segments:', error);
        setError('Failed to fetch micro-segment analysis');
      } finally {
        setIsGeneratingMicroSegments(false);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Back Button and Header */}
        {!hideHeader && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => {
                if (microSegmentActiveView === 'details') handleBackToMicroSegmentList();
                else if (microSegmentActiveView === 'list') handleBackToMicroFilters();
                else onBack();
              }} sx={{ color: '#4c3794', backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#131F32' }}>
                    {microSegmentActiveView === 'details' && selectedMicroSegmentData
                      ? selectedMicroSegmentData.segmentName
                      : data.segmentName}
                  </Typography>
                  {data.characteristics && Object.keys(data.characteristics).length > 0 && (
                    <MuiTooltip
                      title={
                        <Box sx={{ p: 1.5, minWidth: '280px' }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, mb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 1, color: '#fff' }}>
                            Segment Characteristics
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {Object.entries(data.characteristics).map(([key, value], idx) => (
                              <Box key={idx}>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.3 }}>{key}</Typography>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff', lineHeight: 1.4 }}>{String(value)}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="right"
                      PopperProps={{
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            backgroundColor: '#1e1e2d',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                            p: 0
                          },
                          '& .MuiTooltip-arrow': {
                            color: '#1e1e2d'
                          }
                        }
                      }}
                    >
                      <IconButton size="small" sx={{ ml: 1.5, color: '#4c3794', backgroundColor: 'rgba(76, 55, 148, 0.08)', '&:hover': { backgroundColor: 'rgba(76, 55, 148, 0.15)' } }}>
                        <InfoIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </MuiTooltip>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.875rem', color: '#64748B' }}>
                  {microSegmentActiveView === 'filters' ? 'Segment Deep Dive & Analysis' :
                    microSegmentActiveView === 'list' ? 'Micro-Segment Analysis Results' :
                      'Micro-Segment Deep Dive'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<ChatBubbleIcon />}
              onClick={onAskAI}
              sx={{
                backgroundColor: '#4c3794',
                '&:hover': { backgroundColor: '#3d2d7a' },
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px'
              }}
            >
              Ask AI
            </Button>
          </Box>
        )}

        {/* Micro-Segment Analysis Section */}
        {!(data.informationCategory?.includes('MICRO_SEGMENT')) && (
          <Box sx={{ mt: 2 }}>
            {microSegmentActiveView === 'filters' && (
              <Card sx={{ p: 3, borderRadius: '16px', border: '1px solid #e9ecef', backgroundColor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.03)' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon sx={{ color: '#4c3794' }} /> Micro-Segment Analysis
                </Typography>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Macro Segment ID"
                      value={macroSegmentId}
                      disabled
                      size="small"
                      variant="outlined"
                      sx={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Grid>
                  {operationalVars.map((v: { key: string; label: string }) => (
                    <Grid item xs={12} sm={3} key={v.key}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{v.label}</InputLabel>
                        <Select
                          value={microFilters[v.key]}
                          label={v.label}
                          onOpen={() => fetchDistinctValues(v.key)}
                          onChange={(e) => setMicroFilters(prev => ({ ...prev, [v.key]: e.target.value as string }))}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {isFetchingDistinct[v.key] ? (
                            <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading...</MenuItem>
                          ) : (
                            distinctValuesMap[v.key]?.map((val, idx) => (
                              <MenuItem key={idx} value={val}>{val}</MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleGenerateMicroSegments}
                      disabled={isGeneratingMicroSegments || !Object.values(microFilters).some(v => v)}
                      startIcon={isGeneratingMicroSegments ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                      sx={{
                        backgroundColor: '#4c3794',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        height: '40px',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#3d2d7a' }
                      }}
                    >
                      {isGeneratingMicroSegments ? 'Generating...' : 'Generate'}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            )}

            {microSegmentActiveView === 'list' && (
              <Box id="micro-segment-results" sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(76, 55, 148, 0.2)', backgroundColor: 'rgba(76, 55, 148, 0.02)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={handleBackToMicroFilters} sx={{ color: '#4c3794' }}>
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#4c3794' }}>
                      Micro-Segment Analysis Results
                    </Typography>
                  </Box>
                </Box>
                <SegmentCardList
                  groups={microSegmentResults}
                  onViewDetails={handleViewMicroSegmentDetails}
                  selectedUIDs={new Set()} // No selection for micro-segments yet
                  onSelectionChange={() => { }}
                  onAskAI={onAskAI}
                />
              </Box>
            )}

            {microSegmentActiveView === 'details' && selectedMicroSegmentData && (
              <Box>
                <SegmentDetailsView
                  data={selectedMicroSegmentData}
                  onBack={handleBackToMicroSegmentList}
                  onAskAI={onAskAI}
                  hideHeader={true}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Legacy Restoration: Render full segment details using EXACT same logic as dialog */}
        {(microSegmentActiveView === 'filters' || data.informationCategory?.includes('MICRO_SEGMENT')) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Segment Description */}
            {data.segmentSummary && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 1.5 }}>
                  Segment Description
                </Typography>
                <Card sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e9ecef' }}>
                  <Typography sx={{ fontSize: '0.938rem', color: '#475569', lineHeight: 1.7 }}>
                    {data.segmentSummary}
                  </Typography>
                </Card>
              </Box>
            )}


            {/* All Multicolumn Data */}
            {data.multicolumnData &&
              data.multicolumnData
                .filter((tableData: any) => {
                  const titleLower = tableData.title?.toLowerCase() || '';
                  const keyLower = tableData.key?.toLowerCase() || '';
                  const subcategory = tableData.entries?.find((e: any) => e.key === 'informationSubcategory')?.value || '';
                  const isWhatIfAnalysis = titleLower.includes('what-if') || titleLower.includes('what if') || keyLower.includes('what-if') || keyLower.includes('whatif') || subcategory.toLowerCase().includes('what-if');
                  return !isWhatIfAnalysis;
                })
                .map((tableData: any, tableIndex: number) => {
                  const titleLower = tableData.title?.toLowerCase() || '';
                  const keyLower = tableData.key?.toLowerCase() || '';
                  const subcategory = tableData.entries?.find((e: any) => e.key === 'informationSubcategory')?.value || '';

                  const isDriverPriorityMatrix = titleLower.includes('driver priority matrix') || titleLower.includes('strategic focus areas') || titleLower.includes('performance vs impact') || keyLower === 'driverprioritmatrix' || keyLower.includes('strategicfocusareas') || subcategory.toLowerCase().includes('driver priority matrix');
                  const isOpportunityHeatmap = titleLower.includes('opportunity heatmap') || titleLower.includes('top priority actions') || titleLower.includes('ranked improvement') || keyLower === 'opportunityheatmap' || keyLower.includes('toppriorityactions') || subcategory.toLowerCase().includes('opportunity heatmap');

                  // Toggle logic: Skip rendering if not the active visualization
                  if (isDriverPriorityMatrix && activeViz !== 'matrix') return null;
                  if (isOpportunityHeatmap && activeViz !== 'heatmap') return null;

                  return (
                    <Box key={tableIndex}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32' }}>
                          {tableData.title || 'Data Analysis'}
                        </Typography>
                        {(isDriverPriorityMatrix || isOpportunityHeatmap) && (
                          <ToggleButtonGroup
                            value={activeViz}
                            exclusive
                            onChange={(e, val) => val && setActiveViz(val)}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(76, 55, 148, 0.05)',
                              p: 0.3,
                              borderRadius: '8px',
                              border: 'none',
                              '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                fontSize: '0.75rem',
                                borderRadius: '6px !important',
                                border: 'none',
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#64748B',
                                '&.Mui-selected': {
                                  backgroundColor: '#fff',
                                  color: '#4c3794',
                                  boxShadow: '0 2px 4px rgba(76, 55, 148, 0.1)',
                                  '&:hover': { backgroundColor: '#fff' }
                                }
                              }
                            }}
                          >
                            <ToggleButton value="matrix">Matrix</ToggleButton>
                            <ToggleButton value="heatmap">Heatmap</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                      </Box>

                      {tableData.analysis && (
                        <Card sx={{ p: 2, mb: 2, borderRadius: '10px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                          <MarkdownMessage text={tableData.analysis} />
                        </Card>
                      )}

                      {/* Bubble Chart Restoration */}
                      {isDriverPriorityMatrix && tableData.data && tableData.data.length > 0 ? (
                        (() => {
                          const getValue = (row: any, possibleKeys: string[]): any => {
                            for (const key of possibleKeys) if (row[key] !== undefined && row[key] !== null) return row[key];
                            for (const pk of possibleKeys) {
                              const kl = pk.toLowerCase();
                              for (const rk in row) if (rk.toLowerCase() === kl) return row[rk];
                            }
                            return null;
                          };

                          const rawData = tableData.data.map((row: any) => {
                            const drv = getValue(row, ['driver', 'Driver']) || '';
                            const perf = getValue(row, ['current_performance_score', 'Current Performance Score', 'Current Performance', 'currentPerformanceScore']) || 0;
                            const imp = getValue(row, ['impact_score', 'Impact Score', 'impactScore']) || 0;
                            const gap_raw = getValue(row, ['performance_gap', 'Performance Gap', 'performanceGap']) || 0;
                            return { driver: drv, x: perf, y: imp, gap: Math.abs(gap_raw), direction: getValue(row, ['direction', 'Direction']) || 'positive' };
                          }).filter((it: any) => it.driver && !isNaN(it.x) && !isNaN(it.y));

                          const xv = rawData.map((d: any) => d.x);
                          const yv = rawData.map((d: any) => d.y);
                          const tXMin = Math.min(0, ...xv);
                          const tXMax = Math.max(100, ...xv);
                          const tYMin = Math.min(0, ...yv);
                          const tYMax = Math.max(...yv);
                          const tXP = 5;
                          const tYP = (tYMax - tYMin) * 0.1 || 5;
                          const fXMin = tXMin - tXP;
                          const fXMax = tXMax + tXP;
                          const fYMin = Math.max(0, tYMin - tYP);
                          const fYMax = tYMax + tYP;
                          const pThr = (fXMin + fXMax) / 2;
                          const cImp = (fYMin + fYMax) / 2;

                          const BubbleTooltipDialog = ({ active, payload }: any) => {
                            if (active && payload && payload.length) {
                              const d = payload[0].payload;
                              return (
                                <Box sx={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e9ecef', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', p: 1.5, minWidth: '200px' }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 1 }}>{d.driver}</Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance:</Typography>
                                      <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{d.x.toFixed(2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Impact:</Typography>
                                      <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{d.y.toFixed(2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Gap:</Typography>
                                      <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{d.gap.toFixed(2)}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            }
                            return null;
                          };

                          const bData = rawData.map((it: any) => ({ ...it, z: it.gap * 1000, quadrant: it.x < pThr && it.y >= cImp ? 'Priority Fix' : it.x >= pThr && it.y >= cImp ? 'Maintain Excellence' : it.x >= pThr && it.y < cImp ? 'Over-Investing' : 'Low Priority' }));
                          const qCols: Record<string, string> = { 'Priority Fix': '#f44336', 'Maintain Excellence': '#4caf50', 'Over-Investing': '#ff9800', 'Low Priority': '#9e9e9e' };

                          return (
                            <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #e9ecef', mb: 2 }}>
                              <Typography sx={{ fontSize: '0.875rem', color: '#64748B', fontStyle: 'italic', mb: 2 }}>
                                Bubble size = performance gap. Quadrants divided at center: X = {pThr.toFixed(1)}, Y = {cImp.toFixed(2)}.
                              </Typography>
                              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                                {Object.entries(qCols).map(([q, c]) => (
                                  <Box key={q} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: c }} />
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600 }}>{q}</Typography>
                                  </Box>
                                ))}
                              </Box>
                              <Box sx={{ width: '100%', height: '450px' }}>
                                <ResponsiveContainer>
                                  <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
                                    <ReferenceArea x1={fXMin} x2={pThr} y1={cImp} y2={fYMax} fill="rgba(244, 67, 54, 0.08)" />
                                    <ReferenceArea x1={pThr} x2={fXMax} y1={cImp} y2={fYMax} fill="rgba(76, 175, 80, 0.08)" />
                                    <ReferenceArea x1={fXMin} x2={pThr} y1={fYMin} y2={cImp} fill="rgba(158, 158, 158, 0.08)" />
                                    <ReferenceArea x1={pThr} x2={fXMax} y1={fYMin} y2={cImp} fill="rgba(255, 152, 0, 0.08)" />
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <ReferenceLine x={pThr} stroke="#4c3794" strokeWidth={2} strokeDasharray="5 5" />
                                    <ReferenceLine y={cImp} stroke="#4c3794" strokeWidth={2} strokeDasharray="5 5" />
                                    <XAxis type="number" dataKey="x" name="Performance" domain={[fXMin, fXMax]} tick={{ fontSize: 12 }} />
                                    <YAxis type="number" dataKey="y" name="Impact" domain={[fYMin, fYMax]} tick={{ fontSize: 12 }} />
                                    <ZAxis type="number" dataKey="z" range={[100, 800]} />
                                    <Tooltip content={<BubbleTooltipDialog />} />
                                    {Object.entries(qCols).map(([q, c]) => (
                                      <Scatter key={q} name={q} data={bData.filter((d: any) => d.quadrant === q)} fill={c} />
                                    ))}
                                  </ScatterChart>
                                </ResponsiveContainer>
                              </Box>
                            </Card>
                          );
                        })()
                      ) : isOpportunityHeatmap && tableData.data && tableData.data.length > 0 ? (
                        (() => {
                          const hData = tableData.data.map((r: any) => ({
                            rank: r.priority_rank || r['Priority Rank'] || 999,
                            driver: r.driver || r.Driver || '',
                            opp: r.opportunity_score || r['Opportunity Score'] || 0,
                            perf: r.current_performance_score || r.currentPerformanceScore || 0,
                            imp: r.impact_score || r.impactScore || 0,
                            dir: r.direction || r.Direction || 'positive'
                          })).sort((a: any, b: any) => a.rank - b.rank);

                          const getC = (s: number) => s > 25 ? { b: 'rgba(244, 67, 54, 0.15)', t: '#f44336' } : s >= 15 ? { b: 'rgba(255, 152, 0, 0.15)', t: '#ff9800' } : { b: 'rgba(76, 175, 80, 0.15)', t: '#4caf50' };

                          return (
                            <Card sx={{ p: 2, borderRadius: '12px', border: '1px solid #e9ecef', mb: 2, overflow: 'auto' }}>
                              <Table size="small">
                                <TableHead sx={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Rank</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794' }}>Driver</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Opportunity</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Performance</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Impact</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Trend</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {hData.map((r: any, i: number) => {
                                    const c = getC(r.opp);
                                    return (
                                      <TableRow key={i}>
                                        <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>{r.rank}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.driver}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}><Box sx={{ backgroundColor: c.b, color: c.t, p: 0.5, borderRadius: '4px', fontWeight: 700 }}>{r.opp.toFixed(1)}</Box></TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{r.perf.toFixed(1)}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{r.imp.toFixed(2)}</TableCell>
                                        <TableCell sx={{ textAlign: 'center', color: r.dir === 'negative' ? '#ef4444' : '#22c55e', fontSize: '1.2rem', fontWeight: 700 }}>{r.dir === 'negative' ? '↓' : '↑'}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Card>
                          );
                        })()
                      ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e9ecef', boxShadow: 'none', mb: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
                              <TableRow>
                                {tableData.headers?.map((h: string, i: number) => {
                                  const isP = h.toLowerCase().includes('positive');
                                  const isN = h.toLowerCase().includes('negative');
                                  return (
                                    <TableCell key={i} sx={{
                                      fontWeight: 700,
                                      color: '#4c3794',
                                      backgroundColor: isP ? 'rgba(16, 185, 129, 0.08)' : isN ? 'rgba(249, 115, 22, 0.08)' : 'transparent'
                                    }}>
                                      {h}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tableData.data?.map((r: any, i: number) => (
                                <TableRow key={i}>
                                  {tableData.headers?.map((h: string, j: number) => {
                                    const isP = h.toLowerCase().includes('positive');
                                    const isN = h.toLowerCase().includes('negative');
                                    return (
                                      <TableCell key={j} sx={{
                                        backgroundColor: isP ? 'rgba(16, 185, 129, 0.05)' : isN ? 'rgba(249, 115, 22, 0.05)' : 'transparent'
                                      }}>
                                        {String(r[h] ?? '-')}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  );
                })}

            {/* What-If Analysis */}
            {(() => {
              const wData = data.multicolumnData?.find((t: any) => t.title?.toLowerCase().includes('what-if') || t.key?.toLowerCase().includes('whatif'));
              if (!wData || !wData.data) return null;
              return (
                <Box>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChartIcon sx={{ color: '#4c3794' }} /> What-If Analysis
                  </Typography>
                  <Grid container spacing={3}>
                    {wData.data.map((d: any, i: number) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <InteractiveWhatIfChart dataset={{ title: d.title || wData.title, headers: d.headers || wData.headers || ['Driver', 'Outcome'], data: d.data || d.response_curve || [], baseline_metrics: d.baseline_metrics || d.summary || {} }} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })()}

            {/* Sentiment Topics - Legacy Styles */}
            <Grid container spacing={3}>
              {data.positiveTopics?.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#10b981', mb: 1.5 }}>Positive Topics</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, borderRadius: '12px', backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                    {data.positiveTopics.map((t: string, i: number) => <Chip key={i} label={t} sx={{ backgroundColor: '#fff', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)', fontWeight: 600 }} />)}
                  </Box>
                </Grid>
              )}
              {data.negativeTopics?.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#ef4444', mb: 1.5 }}>Negative Topics</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, borderRadius: '12px', backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    {data.negativeTopics.map((t: string, i: number) => <Chip key={i} label={t} sx={{ backgroundColor: '#fff', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', fontWeight: 600 }} />)}
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Recommendations */}
            {data.segmentRecommendations?.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 1.5 }}>Strategic Recommendations</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {data.segmentRecommendations.map((r: string, i: number) => (
                    <Card key={i} sx={{ p: 2, borderRadius: '10px', border: '1px solid rgba(59, 130, 246, 0.2)', backgroundColor: 'rgba(59, 130, 246, 0.03)' }}>
                      <MarkdownMessage text={r.trim()} />
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };


  // Reasoning log state - support multiple connections (one per interactionId)
  // Store only the latest reasoning entry per interactionId with progress
  const [reasoningEntries, setReasoningEntries] = useState<Map<string, {
    reasoning: string;
    timestamp: Date;
    progressPercent?: number;
    geography?: string;
    language?: string;
    interactionName?: string;
  }>>(new Map());
  const [reasoningSockets, setReasoningSockets] = useState<Map<string, WebSocket>>(new Map());
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLButtonElement | null>(null);
  const notificationButtonRef = useRef<HTMLButtonElement | null>(null); // Ref for notification button
  const lastShownReasoningRef = useRef<Map<string, string>>(new Map()); // Track last shown reasoning per interactionId using ref
  const timedOutInteractionsRef = useRef<Set<string>>(new Set()); // Track interaction IDs that have timed out
  const REASONING_LOG_TIMEOUT = 3600000; // 1 hour timeout for reasoning logs

  // Frontend-only search - filter files based on search query
  const filteredGoogleDriveFiles = googleDriveFiles.filter(file => {
    if (!googleDriveSearchQuery.trim()) return true;

    const query = googleDriveSearchQuery.toLowerCase();
    const fileName = file.name.toLowerCase();
    const fileType = file.mimeType.toLowerCase();

    return fileName.includes(query) ||
      fileType.includes(query) ||
      (file.size && file.size.toLowerCase().includes(query));
  });

  // Handle clicking outside sidebar project dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarDropdownRef.current && !sidebarDropdownRef.current.contains(event.target as Node)) {
        setIsProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchContentTypes();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      // Store current project ID in ref to track which project we're loading
      const projectId = selectedProject.id;
      currentProjectIdRef.current = projectId;

      // Clear selected docUIDs when project changes
      setSelectedSegmentDocUIDs(new Set());
      setSelectedDriverDocUIDs(new Set());

      // Reset loading states immediately when project changes
      setIsInitialLoading(true);
      setLoading(true);
      setLoadingExecutiveSummary(true);
      setLoadingSegments(true);
      setIsDashboardLoading(true);

      // Execute only common API calls that are needed regardless of tab
      // Tab-specific calls (fetchSegmentAnalysis, fetchExecutiveSummary) are handled by activeMainTab useEffect
      Promise.all([
        fetchSurveyItems(false).catch(() => { }),
        fetchDashboardData().catch(() => { })
      ]).finally(() => {
        // Only update loading states if project hasn't changed
        if (currentProjectIdRef.current === projectId) {
          setIsInitialLoading(false);
          setLoading(false);
          setIsDashboardLoading(false);
        }
      });
    } else {
      // Reset loading states when no project is selected
      currentProjectIdRef.current = null;
      setIsInitialLoading(false);
      setLoading(false);
      setLoadingExecutiveSummary(false);
      setLoadingSegments(false);
      setIsDashboardLoading(false);
    }

    // Cleanup: reset loading states if project changes while loading
    return () => {
      // This cleanup runs when selectedProject changes or component unmounts
      // Reset loading states to prevent stale updates
      if (currentProjectIdRef.current !== selectedProject?.id) {
        setIsInitialLoading(false);
        setLoading(false);
        setLoadingExecutiveSummary(false);
        setLoadingSegments(false);
        setIsDashboardLoading(false);
      }
    };
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;

    const projectId = selectedProject.id;

    // Clear selections when switching away from a tab
    if (activeMainTab !== 'segment') {
      setSelectedSegmentDocUIDs(new Set());
    }
    if (activeMainTab !== 'driver') {
      setSelectedDriverDocUIDs(new Set());
    }

    if (activeMainTab === 'segment') {
      setLoadingSegments(true);

      // Wait for the API call and then ensure data is set before hiding loader
      fetchSegmentAnalysis().then(() => {
        // Use requestAnimationFrame to ensure React has processed state updates
        return new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Additional small delay to ensure state updates are complete
              setTimeout(() => {
                resolve();
              }, 50);
            });
          });
        });
      }).finally(() => {
        // Only update loading state if project and tab haven't changed
        if (currentProjectIdRef.current === projectId && activeMainTab === 'segment') {
          setLoadingSegments(false);
        }
      });
    } else if (activeMainTab === 'executive') {
      setLoadingExecutiveSummary(true);
      fetchExecutiveSummary().finally(() => {
        // Only update loading state if project and tab haven't changed
        if (currentProjectIdRef.current === projectId && activeMainTab === 'executive') {
          setLoadingExecutiveSummary(false);
        }
      });
    } else if (activeMainTab === 'driver') {
      setLoadingDrivers(true);

      // Wait for the API call and then ensure data is set before hiding loader
      fetchDriverAnalysis().then(() => {
        // Use requestAnimationFrame to ensure React has processed state updates
        return new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Additional small delay to ensure state updates are complete
              setTimeout(() => {
                resolve();
              }, 50);
            });
          });
        });
      }).finally(() => {
        // Only update loading state if project and tab haven't changed
        if (currentProjectIdRef.current === projectId && activeMainTab === 'driver') {
          setLoadingDrivers(false);
        }
      });
    } else {
      // Dashboard or other tab - reset loading states
      setLoadingSegments(false);
      setLoadingExecutiveSummary(false);
      setLoadingDrivers(false);
    }
  }, [activeMainTab, segmentFilter, selectedProject?.id]);

  // Load files when dialog opens or tab changes
  useEffect(() => {
    if (showGoogleDriveDialog && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [showGoogleDriveDialog, activeTab, googleDriveAuthState.isAuthenticated, googleDriveAuthState.sessionId]);

  // Load files when team drive selection changes
  useEffect(() => {
    if (activeTab === 'myDrive' && selectedTeamDrive && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [selectedTeamDrive]);

  useEffect(() => {
    setMessages([
      {
        id: 1,
        text: "Hello! I'm your AI assistant. How can I help you analyze the survey data?",
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
  }, [selectedDocId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Add keyboard event listener for Escape key to exit full screen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Exit full screen with Escape
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }

      // Toggle chat with Ctrl/Cmd + K
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setIsChatOpen(!isChatOpen);
      }

      // Focus chat input with Ctrl/Cmd + L when chat is open
      if ((event.ctrlKey || event.metaKey) && event.key === 'l' && isChatOpen) {
        event.preventDefault();
        const chatInput = document.querySelector('input[aria-label="Chat message input"]') as HTMLInputElement;
        if (chatInput) {
          chatInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen, isChatOpen]);

  // Auto-enable full screen for data analytics chat
  useEffect(() => {
    if (isChatOpen && detectedProjectType === 'DATA_ANAL') {
      setIsFullScreen(true);
    }
  }, [isChatOpen, detectedProjectType]);

  // Ensure valid activeMainTab for data analytics
  useEffect(() => {
    if (detectedProjectType === 'DATA_ANAL' && ['executive', 'segment', 'driver'].includes(activeMainTab)) {
      setActiveMainTab('dashboard');
    }
  }, [detectedProjectType, activeMainTab]);

  // Keep reasoning WebSocket connection open - no cleanup on unmount
  // Connection will remain active throughout component lifecycle

  // Remove the automatic session ID generation - let it come from the auth response
  // useEffect(() => {
  //   // Generate a unique session ID for this user session
  //   const sessionId = `survey_insights_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  //   setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({ ...prev, sessionId }));
  // }, []);

  const fetchSurveyItems = async (manageLoading: boolean = true) => {
    if (manageLoading) {
      setLoading(true);
    }
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: "",
            keywords: [],
            contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
            pageNumber: page + 1,
            campaignId: selectedProject?.id || ""
          },
          agent: {
            code: detectedProjectType === 'DATA_ANAL' ? "QUERY_GENERAL_DATASET" : "QUERY_USER_FEEDBACK"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Check if project hasn't changed before updating state
      if (currentProjectIdRef.current === selectedProject?.id) {
        if (response.data.data) {
          setSurveyItems(response.data.data);
          setTotalCount(response.data.totalCount || 0);
          calculateSummaryMetrics(response.data.data);
          const chartData = processSurveyData(response.data.data);
          setDashboardData(chartData);
          setLastFetchedTime(new Date()); // Update timestamp on successful fetch
        } else {
          setSurveyItems([]);
          setTotalCount(0);
          setLastFetchedTime(new Date()); // Update timestamp even if no data
        }
      }
    } catch (err: any) {
      if (err?.response?.data?.error === 'jwt malformed' || err?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      console.error('Error fetching surveys:', err);
    } finally {
      if (manageLoading) {
        setLoading(false);
      }
      setIsRefreshing(false); // Reset refresh state
    }
  };

  const handleInputChange = (key: string, value: string) => {
    if (key === 'keywords') {
      setFormValues((prev: FormValues) => ({
        ...prev,
        keywords: [...(prev.keywords || []), value]
      }));
    } else {
      setFormValues((prev: FormValues) => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleDeleteKeyword = (keywordToDelete: string) => {
    setFormValues((prev: FormValues) => ({
      ...prev,
      keywords: (prev.keywords || []).filter(keyword => keyword !== keywordToDelete)
    }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const keywords = Array.isArray(formValues.keywords) ? formValues.keywords : [];
      const topic = typeof formValues.topic === 'string' ? formValues.topic : '';

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic,
            keywords,
            contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
            context: detectedContext, // Add context information
            // pageSize: rowsPerPage,
            pageNumber: page + 1
          },
          agent: {
            code: detectedProjectType === 'DATA_ANAL' ? "QUERY_GENERAL_DATASET" : "QUERY_USER_FEEDBACK"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.data) {
        setSurveyItems(response.data.data);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setSurveyItems([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        navigate('/login');
      }
      setError('Failed to search surveys. Please try again.');
      console.error('Error searching surveys:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAnalysisContext = (docUID: string | null) => {
    if (!docUID) return '';

    const surveyItem = surveyItems?.find(item => item.docUID === docUID);
    if (!surveyItem) return '';

    const context = surveyItem.entries.map(entry => {
      const value = Array.isArray(entry.value)
        ? entry.value.join(', ')
        : entry.value;
      return `${entry.label}: ${value}`;
    }).join('\n');

    return `Selected Survey Item:\n${context}`;
  };

  // Generate unique interactionId in format: xxxx-xxxx-xxxx-xxxx-xxxx-xxxx
  const generateInteractionId = (): string => {
    const segments = [];
    for (let i = 0; i < 6; i++) {
      segments.push(Math.random().toString(36).substring(2, 6));
    }
    return segments.join('-');
  };

  // Connect to reasoning log WebSocket and display in Snackbar
  // NEVER closes connections from frontend - connections stay open indefinitely
  const connectReasoningLog = (interactionId: string) => {
    if (!interactionId) return;

    // Check if we already have an open connection for this interactionId
    const existingSocket = reasoningSockets.get(interactionId);

    // If it's already timed out, don't try to connect again
    if (timedOutInteractionsRef.current.has(interactionId)) {
      console.log(`Connection for ${interactionId} has previously timed out. Skipping reconnection.`);
      return;
    }

    if (existingSocket) {
      // Connection already exists - check if it's still open
      if (existingSocket.readyState === WebSocket.OPEN || existingSocket.readyState === WebSocket.CONNECTING) {
        // Connection is open or connecting, no need to create a new one
        return;
      }
      // If closed, we'll create a new one below (but never call close() on the old one)
    }

    // Reset last shown reasoning for this interactionId to allow showing new entries
    lastShownReasoningRef.current.delete(interactionId);

    // Build WebSocket URL
    const params = new URLSearchParams({ interactionId });
    if (selectedProject?.id) {
      params.set('campaignId', selectedProject.id);
    }
    const orgId = localStorage.getItem('org_id');
    if (orgId) {
      params.set('orgCode', orgId);
    }

    const wsUrl = `${environment.reasoningWsUrl}/ws/reasoning-logs?${params.toString()}`;
    console.log('Connecting to reasoning log WebSocket:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);

      // Set a timeout to close the connection if it stays open too long (e.g. hung job)
      const timeoutId = setTimeout(() => {
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          console.log(`Reasoning log timeout reached for ${interactionId}, closing connection.`);
          timedOutInteractionsRef.current.add(interactionId);
          socket.close(4000, 'Reasoning Log Timeout'); // Use custom code for timeout

          setReasoningEntries(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(interactionId);
            if (existing) {
              newMap.set(interactionId, {
                ...existing,
                reasoning: `${existing.reasoning}\n\n[System: Connection timed out after 1 hour]`
              });
            }
            return newMap;
          });
        }
      }, REASONING_LOG_TIMEOUT);

      // Store socket in Map - NEVER close it from frontend (except on timeout or 100% completion)
      setReasoningSockets(prev => new Map(prev).set(interactionId, socket));

      socket.onopen = () => {
        console.log('Reasoning log WebSocket connected for interactionId:', interactionId);
        // Update latest entry for this interactionId
        setReasoningEntries(prev => new Map(prev).set(interactionId, {
          reasoning: '',
          timestamp: new Date(),
          progressPercent: 0
        }));
      };

      socket.onmessage = (event) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          console.log('Reasoning log message received:', data);

          // Only process delta updates (new entries) - update only the latest entry per interactionId
          if (data.type === 'delta') {
            const appendedReasonings = data.appendedReasonings || [];

            // Extract interactionName from the message (could be in data or fullDocument)
            const interactionName = data.interactionName || data.fullDocument?.interactionName;

            // Always update interactionName if it's available
            if (interactionName) {
              setReasoningEntries(prev => {
                const existing = prev.get(interactionId);
                if (existing && existing.interactionName !== interactionName) {
                  return new Map(prev).set(interactionId, {
                    ...existing,
                    interactionName
                  });
                }
                return prev;
              });
            }

            if (appendedReasonings.length > 0) {
              // Get the latest (last) reasoning entry from the delta
              const latestReasoning = appendedReasonings[appendedReasonings.length - 1];
              const reasoningText = latestReasoning.reasoning || '';
              const progressPercent = latestReasoning.progressPercent !== undefined ? latestReasoning.progressPercent : undefined;
              const geography = latestReasoning.geography;
              const language = latestReasoning.language;
              const datetime = latestReasoning.datetime ? new Date(latestReasoning.datetime) : new Date();

              // Only update if this is a new reasoning we haven't shown before
              const lastShown = lastShownReasoningRef.current.get(interactionId) || '';
              if (reasoningText && reasoningText !== lastShown) {
                // Update the latest entry for this interactionId (replace, don't append)
                setReasoningEntries(prev => {
                  const existing = prev.get(interactionId);
                  return new Map(prev).set(interactionId, {
                    reasoning: reasoningText,
                    timestamp: datetime,
                    progressPercent,
                    geography,
                    language,
                    interactionName: interactionName || existing?.interactionName
                  });
                });
                lastShownReasoningRef.current.set(interactionId, reasoningText);

                // Close WebSocket connection if progress is 100% (upload complete)
                if (progressPercent === 100) {
                  console.log(`Upload complete for interactionId: ${interactionId}, closing WebSocket connection`);
                  // Close the socket if it's still open (using closure variable - safe for this specific interactionId)
                  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                    socket.close(1000, 'Upload complete');
                  }
                  // Remove from sockets Map - only this specific interactionId
                  setReasoningSockets(prev => {
                    const newMap = new Map(prev);
                    // Double-check: only remove if it matches the current interactionId
                    if (newMap.has(interactionId)) {
                      newMap.delete(interactionId);
                    }
                    return newMap;
                  });
                }
              }
            }
          } else if (data.type === 'snapshot' || data.type === 'update') {
            // For snapshot/update, only update the latest if we haven't seen it before
            const reasonings = data.reasonings || data.fullDocument?.reasonings || [];

            // Extract interactionName from the message (could be in data or fullDocument)
            const interactionName = data.interactionName || data.fullDocument?.interactionName;

            // Extract progressPercent from fullDocument if available
            const progressPercent = data.progressPercent !== undefined
              ? data.progressPercent
              : (data.fullDocument?.progressPercent !== undefined
                ? data.fullDocument.progressPercent
                : undefined);

            // Always update interactionName if it's available, even if there are no reasonings yet
            if (interactionName) {
              setReasoningEntries(prev => {
                const existing = prev.get(interactionId);
                if (existing && existing.interactionName !== interactionName) {
                  return new Map(prev).set(interactionId, {
                    ...existing,
                    interactionName
                  });
                } else if (!existing) {
                  return new Map(prev).set(interactionId, {
                    reasoning: '',
                    timestamp: new Date(),
                    progressPercent,
                    interactionName
                  });
                }
                return prev;
              });
            }

            if (reasonings.length > 0) {
              const latestReasoning = reasonings[reasonings.length - 1];
              const reasoningText = latestReasoning.reasoning || '';
              const geography = latestReasoning.geography;
              const language = latestReasoning.language;
              const datetime = latestReasoning.datetime ? new Date(latestReasoning.datetime) : new Date();

              // Only update if this is a new reasoning we haven't seen it before
              const lastShown = lastShownReasoningRef.current.get(interactionId) || '';
              if (reasoningText && reasoningText !== lastShown) {
                // Update the latest entry for this interactionId (replace, don't append)
                setReasoningEntries(prev => {
                  const existing = prev.get(interactionId);
                  return new Map(prev).set(interactionId, {
                    reasoning: reasoningText,
                    timestamp: datetime,
                    progressPercent: progressPercent !== undefined ? progressPercent : (latestReasoning.progressPercent !== undefined ? latestReasoning.progressPercent : undefined),
                    geography,
                    language,
                    interactionName: interactionName || existing?.interactionName
                  });
                });
                lastShownReasoningRef.current.set(interactionId, reasoningText);

                // Close WebSocket connection if progress is 100% (upload complete)
                if (progressPercent === 100) {
                  console.log(`Upload complete for interactionId: ${interactionId}, closing WebSocket connection`);
                  // Close the socket if it's still open (using closure variable - safe for this specific interactionId)
                  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                    socket.close(1000, 'Upload complete');
                  }
                  // Remove from sockets Map - only this specific interactionId
                  setReasoningSockets(prev => {
                    const newMap = new Map(prev);
                    // Double-check: only remove if it matches the current interactionId
                    if (newMap.has(interactionId)) {
                      newMap.delete(interactionId);
                    }
                    return newMap;
                  });
                }
              }
            }
          } else if (data.type === 'error') {
            // Update the latest entry for this interactionId with error
            setReasoningEntries(prev => new Map(prev).set(interactionId, {
              reasoning: `Error: ${data.error || 'Unknown error'}`,
              timestamp: new Date(),
              progressPercent: undefined
            }));
          }
        } catch (err) {
          console.error('Failed to parse reasoning message:', err);
        }
      };

      socket.onerror = (event) => {
        console.error('Reasoning log WebSocket error:', event);
        setReasoningEntries(prev => new Map(prev).set(interactionId, {
          reasoning: 'Error connecting to reasoning log',
          timestamp: new Date(),
          progressPercent: undefined
        }));
      };

      socket.onclose = (event) => {
        console.log('Reasoning log WebSocket closed:', event.code, event.reason);

        // Clear the timeout when the socket closes (either normally or via timeout)
        clearTimeout(timeoutId);

        // Server closed the connection - do NOT remove from Map or call close() unless it was our timeout
        // Connection reference is kept for potential reconnection
        if (event.code !== 1000 && event.code !== 4000) {
          // Unexpected closure - attempt to reconnect after a delay
          console.log('Unexpected closure, will attempt to reconnect...');
          setTimeout(() => {
            if (interactionId && !timedOutInteractionsRef.current.has(interactionId)) {
              connectReasoningLog(interactionId);
            }
          }, 3000);
        }
      };
    } catch (err) {
      console.error('Failed to create reasoning log WebSocket:', err);
      setReasoningEntries(prev => new Map(prev).set(interactionId, {
        reasoning: 'Failed to connect to reasoning log',
        timestamp: new Date(),
        progressPercent: undefined
      }));
    }
  };

  const callCustomAPI = async (userMessage: string, docId: string) => {
    try {
      // Generate unique interactionId for this query
      const interactionId = generateInteractionId();
      console.log('Generated interactionId for query:', interactionId);

      const payload = {
        data: {
          query: userMessage,
          campaignId: selectedProject?.id || ""
        },
        user: {
          interactionId: interactionId
        },
        agent: {
          code: detectedProjectType === 'DATA_ANAL' ? 'QUERY_GENERAL_DATASET_AND_RESPOND' : 'QUERY_USER_FEEDBACK_AND_RESPOND'
        }
      };


      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );


      if (response.data?.data) {
        const responseData = response.data.data;
        const paragraphResponse = responseData.find((item: any) => item.objectType === 'PARAGRAPH');
        const followupQuestions = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions');
        const sourceLinks = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks');
        const dictionaryResponse = responseData.find((item: any) => item.objectType === 'DICTIONARY');
        const multiColumnResponse = responseData.find((item: any) => item.objectType === 'MULTICOLUMN');

        // Handle MULTICOLUMN visualization data with improved processing
        let visualization = undefined;

        // Check for MULTICOLUMN data in DICTIONARY structure first
        if (dictionaryResponse?.entries) {
          const datasetEntry = dictionaryResponse.entries.find((entry: any) => entry.key === 'dataset');
          if (datasetEntry?.value) {
            const data = datasetEntry.value;
            // Process and clean the data before creating visualization
            const processedData = processMultiColumnData(data, data.visual);
            if (processedData) {
              let type: 'table' | 'chart' | 'heatmap' = 'table';
              if (processedData.visual) {
                if (processedData.dimension === 2) {
                  type = 'chart';
                } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                  type = 'heatmap';
                }
              }
              visualization = {
                type,
                data: processedData
              };
            }
          }
        }

        // Check for direct MULTICOLUMN structure if not found in DICTIONARY
        if (!visualization && multiColumnResponse?.value) {
          const data = multiColumnResponse.value;
          // Process and clean the data before creating visualization
          const processedData = processMultiColumnData(data, data.visual);
          if (processedData) {
            let type: 'table' | 'chart' | 'heatmap' = 'table';
            if (processedData.visual) {
              if (processedData.dimension === 2) {
                type = 'chart';
              } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                type = 'heatmap';
              }
            }
            visualization = {
              type,
              data: processedData
            };
          }
        }

        // Extract interaction UID from the response
        let interactionUID = undefined;

        // First try to find it in DICTIONARY entries
        if (dictionaryResponse?.entries) {
          const uidEntry = dictionaryResponse.entries.find((entry: any) => entry.key === 'interactionUID');
          if (uidEntry) {
            interactionUID = uidEntry.value;
          }
        }

        // If not found in DICTIONARY, look for IDENTIFIER object type
        if (!interactionUID) {
          const identifierResponse = responseData.find((item: any) => item.objectType === 'IDENTIFIER');
          if (identifierResponse && identifierResponse.key === 'interactionId') {
            interactionUID = identifierResponse.value;
          }
        }

        return {
          response: paragraphResponse?.value || 'No response available.',
          followupQuestions: followupQuestions?.value || [],
          sourceLinks: sourceLinks?.value || [],
          visualization,
          interactionUID
        };
      }

      return {
        response: 'No relevant response received from the server.',
        followupQuestions: [],
        sourceLinks: [],
        visualization: undefined
      };
    } catch (error: any) {
      console.error('Error calling custom API:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      return {
        response: 'I encountered an error processing your request. Please try again later.',
        followupQuestions: [],
        sourceLinks: [],
        visualization: undefined
      };
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;


    const userMessage: ChatMessage = {
      id: Math.floor(Date.now()),
      text: newMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const { response, followupQuestions, sourceLinks, visualization, interactionUID } = await callCustomAPI(newMessage.trim(), selectedDocId || '');

      const botMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: response,
        sender: 'bot',
        timestamp: new Date(),
        interactionUID,
        visualization
      };

      setMessages(prev => [...prev, botMessage]);

      if (followupQuestions && followupQuestions.length > 0) {
        const icons = [
          <TrendingUpIcon sx={{ fontSize: 16 }} />,
          <ShowChartIcon sx={{ fontSize: 16 }} />,
          <TrendingDownIcon sx={{ fontSize: 16 }} />,
          <MonetizationOnIcon sx={{ fontSize: 16 }} />,
          <SentimentAnalysisIcon sx={{ fontSize: 16 }} />
        ];

        const newSuggestedQuestions = followupQuestions.map((question: string, index: number) => ({
          text: question,
          icon: icons[index % icons.length]
        }));

        setSuggestedQuestions(newSuggestedQuestions);
      } else {
        setSuggestedQuestions([
          { text: "What are the key insights?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
          { text: "Show me positive feedback", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
          // { text: "Show me areas of improvement", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
          // { text: "What's the overall satisfaction?", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
          // { text: "Analyze employee sentiment", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
        ]);
      }
    } catch (error) {
      console.error('Error in chat:', error);
      const errorMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: 'I apologize, but I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAccordionChange = (uuid: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setSelectedDocUID(isExpanded ? uuid : null);
  };

  const getFieldValue = (field: InputField): string => {
    const value = formValues[field.key];
    if (!value) return '';
    if (field.type === 'MULTITEXT' && Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const handleAddSurvey = async () => {
    if (!addSurveyFormValues.trim()) {
      setError('Please enter survey content');
      return;
    }

    setAddSurveyLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentText: addSurveyFormValues
          },
          agent: {
            code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      await fetchSurveyItems();
      setIsAddSurveyDialogOpen(false);
      setAddSurveyFormValues('');
    } catch (err: any) {
      if (err?.response?.data?.error === 'jwt malformed' || err?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError('Failed to add survey. Please try again.');
      console.error('Error adding survey:', err);
    } finally {
      setAddSurveyLoading(false);
    }
  };

  const fetchAddSurveyFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: { code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET" }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );
      setAddSurveyFields(response.data.data.primaryInput);
    } catch (error: any) {
      console.error('Error fetching add survey fields:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
    }
  };

  const handleSubmitSurvey = async () => {
    setIsSubmittingSurvey(true);
    try {
      await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: addSurveyFormData.topic,
            keywords: addSurveyFormData.keywords
          },
          agent: { code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET" }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      setAddSurveyFormData({ topic: '', keywords: [] });
      setAddSurveyDialog(false);

      fetchSurveyItems();
    } catch (error: any) {
      console.error('Error submitting survey:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
    } finally {
      setIsSubmittingSurvey(false);
    }
  };

  const handleAddSurveyInputChange = (key: string, value: any) => {
    setAddSurveyFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return surveyItems.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedDocUID(null);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1
      }}>
        {/* Page Info */}
        <Typography sx={{
          fontSize: '0.7rem',
          color: '#64748B',
          fontWeight: 500
        }}>
          {currentPage} of {totalPages}
        </Typography>

        {/* Compact Pagination */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Previous */}
          <IconButton
            size="small"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{
              width: 28,
              height: 28,
              color: currentPage === 1 ? '#cbd5e1' : '#64748B',
              '&:hover': {
                backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(76, 55, 148, 0.08)',
              }
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Page Numbers - Compact */}
          {totalPages <= 7 ? (
            // Show all pages if 7 or fewer
            Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <IconButton
                key={page}
                size="small"
                onClick={() => handlePageChange(page)}
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: currentPage === page ? 'white' : '#64748B',
                  backgroundColor: currentPage === page ? '#4c3794' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentPage === page ? '#3a2a73' : 'rgba(76, 55, 148, 0.08)',
                  }
                }}
              >
                {page}
              </IconButton>
            ))
          ) : (
            // Smart pagination for more pages
            <>
              {/* First page */}
              <IconButton
                size="small"
                onClick={() => handlePageChange(1)}
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: currentPage === 1 ? 'white' : '#64748B',
                  backgroundColor: currentPage === 1 ? '#4c3794' : 'transparent',
                  '&:hover': {
                    backgroundColor: currentPage === 1 ? '#3a2a73' : 'rgba(76, 55, 148, 0.08)',
                  }
                }}
              >
                1
              </IconButton>

              {/* Ellipsis or pages around current */}
              {currentPage > 3 && (
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', px: 0.5 }}>...</Typography>
              )}

              {/* Current page and neighbors */}
              {Array.from(
                { length: Math.min(3, totalPages - 2) },
                (_, i) => Math.max(2, currentPage - 1 + i)
              ).filter(page => page < totalPages).map(page => (
                <IconButton
                  key={page}
                  size="small"
                  onClick={() => handlePageChange(page)}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: currentPage === page ? 'white' : '#64748B',
                    backgroundColor: currentPage === page ? '#4c3794' : 'transparent',
                    '&:hover': {
                      backgroundColor: currentPage === page ? '#3a2a73' : 'rgba(76, 55, 148, 0.08)',
                    }
                  }}
                >
                  {page}
                </IconButton>
              ))}

              {/* Ellipsis or last page */}
              {currentPage < totalPages - 2 && (
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B', px: 0.5 }}>...</Typography>
              )}

              {/* Last page */}
              {totalPages > 1 && (
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(totalPages)}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: currentPage === totalPages ? 'white' : '#64748B',
                    backgroundColor: currentPage === totalPages ? '#4c3794' : 'transparent',
                    '&:hover': {
                      backgroundColor: currentPage === totalPages ? '#3a2a73' : 'rgba(76, 55, 148, 0.08)',
                    }
                  }}
                >
                  {totalPages}
                </IconButton>
              )}
            </>
          )}

          {/* Next */}
          <IconButton
            size="small"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{
              width: 28,
              height: 28,
              color: currentPage === totalPages ? '#cbd5e1' : '#64748B',
              '&:hover': {
                backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(76, 55, 148, 0.08)',
              }
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const fetchExecutiveSummary = async () => {
    if (!selectedProject) return;

    try {
      const requestPayload: any = {
        data: {
          topic: "",
          keywords: [],
          pageSize: 50,
          pageNumber: 1,
          campaignId: selectedProject.id
        },
        agent: {
          code: "QUERY_SURVEY_ANALYSIS_SUMMARY",
          groupBy: "informationCategory"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Check if project hasn't changed before updating state
      if (currentProjectIdRef.current === selectedProject?.id) {
        if (response.data.status === 200 && response.data.data) {
          setExecutiveSummaryData(response.data.data);
        } else {
          setExecutiveSummaryData([]);
        }
      }
    } catch (error: any) {
      console.error('Error fetching executive summary:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      // Only update state if project hasn't changed
      if (currentProjectIdRef.current === selectedProject?.id) {
        setExecutiveSummaryData([]);
      }
    }
    // Note: Loading state is managed by the calling useEffect
  };

  const fetchSegmentAnalysis = async (): Promise<void> => {
    if (!selectedProject) {
      return Promise.resolve();
    }

    try {
      const requestPayload: any = {
        data: {
          topic: "",
          keywords: [],
          pageSize: 50,
          pageNumber: 1,
          campaignId: selectedProject.id,
          informationCategory: "OUTCOME_BASED_SEGMENT"
        },
        agent: {
          code: "QUERY_SURVEY_ANALYSIS_CATEGORY",
          groupBy: "segmentName"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Check if project hasn't changed before updating state
      if (currentProjectIdRef.current === selectedProject?.id) {
        if (response.data.status === 200 && response.data.data) {
          // Set the data
          setSegmentGroups(response.data.data);

          // Wait for React to process the state update before resolving
          return new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  resolve();
                }, 100);
              });
            });
          });
        } else {
          setSegmentGroups([]);
          return Promise.resolve();
        }
      }
      return Promise.resolve();
    } catch (error: any) {
      console.error('Error fetching segment analysis:', error);
      // Only update state if project hasn't changed
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      if (currentProjectIdRef.current === selectedProject?.id) {
        setSegmentGroups([]);
      }
      return Promise.resolve();
    }
  };

  const fetchDriverAnalysis = async (): Promise<void> => {
    if (!selectedProject) {
      return Promise.resolve();
    }

    try {
      const requestPayload: any = {
        data: {
          topic: "",
          keywords: [],
          pageSize: 50,
          pageNumber: 1,
          campaignId: selectedProject.id,
          informationCategory: "METRIC_BASED_DRIVER"
        },
        agent: {
          code: "QUERY_SURVEY_ANALYSIS_CATEGORY",
          groupBy: "outcome"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Check if project hasn't changed before updating state
      if (currentProjectIdRef.current === selectedProject?.id) {
        if (response.data.status === 200 && response.data.data) {
          // Set the data
          setDriverGroups(response.data.data);

          // Wait for React to process the state update before resolving
          return new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  resolve();
                }, 100);
              });
            });
          });
        } else {
          setDriverGroups([]);
          return Promise.resolve();
        }
      }
      return Promise.resolve();
    } catch (error: any) {
      console.error('Error fetching driver analysis:', error);
      // Only update state if project hasn't changed
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      if (currentProjectIdRef.current === selectedProject?.id) {
        setDriverGroups([]);
      }
      return Promise.resolve();
    }
  };

  const processSegmentData = (group: SegmentGroup): SegmentAnalysisData | null => {
    // Try to find DICTIONARY item first
    let dictionaryItem = group.items.find(item => item.objectType === 'DICTIONARY');
    let entries: Array<{ key: string; label: string; value: any }> = [];

    // If no DICTIONARY item, try to extract from MULTICOLUMNDICT items' entries
    if (!dictionaryItem) {
      const multicolumnItem = group.items.find(item => item.objectType === 'MULTICOLUMNDICT' && item.entries);
      if (multicolumnItem && multicolumnItem.entries) {
        entries = multicolumnItem.entries;
      } else {
        // If still no entries, create a minimal entry structure from groupName
        entries = [{
          key: 'segmentName',
          label: 'Segment Name',
          value: group.groupName || 'Unknown Segment'
        }];
      }
    } else {
      entries = dictionaryItem.entries || [];
    }

    const getEntryValue = (key: string): any => {
      const entry = entries.find(e => e.key === key);
      return entry?.value || null;
    };

    // Extract MULTICOLUMNDICT data - preserve title, key, headers, data, and metadata from entries
    const multicolumnItems = group.items.filter(item => item.objectType === 'MULTICOLUMNDICT' && item.value);
    const multicolumnData: Array<{
      title: string;
      key?: string;
      headers: string[];
      data: Record<string, any>[];
      analysis?: string;
      recommendations?: string[];
      entries?: Array<{ key: string; label: string; value: any }>;
    }> = [];

    multicolumnItems.forEach(item => {
      if (item.value && item.value.headers && Array.isArray(item.value.headers)) {
        // Include item even if data array is empty - preserve structure and headers
        const dataArray = Array.isArray(item.value.data) ? item.value.data : [];

        // Extract analysis and recommendations from MULTICOLUMNDICT item's entries
        let multicolumnAnalysis: string | undefined;
        let multicolumnRecommendations: string[] | undefined;

        if (item.entries && Array.isArray(item.entries)) {
          const analysisEntry = item.entries.find(e => e.key === 'analysis');
          const recommendationsEntry = item.entries.find(e => e.key === 'recommendations');

          if (analysisEntry && analysisEntry.value) {
            multicolumnAnalysis = typeof analysisEntry.value === 'string' ? analysisEntry.value : String(analysisEntry.value);
          }

          if (recommendationsEntry && recommendationsEntry.value) {
            if (Array.isArray(recommendationsEntry.value)) {
              multicolumnRecommendations = recommendationsEntry.value.map(v => String(v));
            } else {
              multicolumnRecommendations = [String(recommendationsEntry.value)];
            }
          }
        }

        multicolumnData.push({
          title: item.value.title || item.key || 'Data Table',
          key: item.key,
          headers: item.value.headers,
          data: dataArray,
          analysis: multicolumnAnalysis,
          recommendations: multicolumnRecommendations,
          entries: item.entries
        });
      }
    });

    // Keep legacy drivers for backward compatibility (if Driver/Score format exists)
    const drivers: Array<{ driver: string; score: number }> = [];
    multicolumnItems.forEach(item => {
      if (item.value?.data && Array.isArray(item.value.data) && item.value.data.length > 0) {
        item.value.data.forEach((row: any) => {
          // Handle both uppercase and lowercase keys for legacy support
          const driver = row.Driver || row.driver || row['Driver'] || row['driver'];
          const score = row.Score !== undefined ? row.Score : (row.score !== undefined ? row.score : undefined);

          if (driver && score !== undefined) {
            drivers.push({
              driver: String(driver),
              score: typeof score === 'number' ? score : parseFloat(String(score)) || 0
            });
          }
        });
      }
    });

    // Extract title from entries
    const title = getEntryValue('title');

    // Extract recommendations - can be array or string
    const recommendationsValue = getEntryValue('recommendations');
    let recommendations: string[] = [];
    if (Array.isArray(recommendationsValue)) {
      recommendations = recommendationsValue.map(v => String(v));
    } else if (recommendationsValue) {
      recommendations = [String(recommendationsValue)];
    }

    // If no DICTIONARY item, try to get segment info from MULTICOLUMNDICT entries
    let segmentName = getEntryValue('segmentName') || group.groupName;
    let informationCategory = getEntryValue('informationCategory');

    // If we still don't have segmentName, try getting it from any MULTICOLUMNDICT item's entries
    if (!segmentName || segmentName === group.groupName) {
      for (const item of group.items) {
        if (item.objectType === 'MULTICOLUMNDICT' && item.entries) {
          const segmentNameEntry = item.entries.find(e => e.key === 'segmentName');
          if (segmentNameEntry?.value) {
            segmentName = String(segmentNameEntry.value);
          }
          if (!informationCategory) {
            const categoryEntry = item.entries.find(e => e.key === 'informationCategory');
            if (categoryEntry?.value) {
              informationCategory = String(categoryEntry.value);
            }
          }
          break; // Use first item's entries
        }
      }
    }

    // Extract characteristics - this is an object with key-value pairs
    const characteristicsValue = getEntryValue('characteristics');
    let characteristics: Record<string, string> | undefined = undefined;
    if (characteristicsValue && typeof characteristicsValue === 'object' && !Array.isArray(characteristicsValue)) {
      characteristics = characteristicsValue as Record<string, string>;
    }

    return {
      segmentName: segmentName || group.groupName || 'Unknown Segment',
      segmentSummary: getEntryValue('description') || getEntryValue('segmentSummary') || '',
      segmentSize: getEntryValue('size') || getEntryValue('segmentSize') || 0,
      segmentPercentage: getEntryValue('percentage') || getEntryValue('segmentPercentage') || 0,
      positiveTopics: Array.isArray(getEntryValue('positiveTopics')) ? getEntryValue('positiveTopics') : [],
      negativeTopics: Array.isArray(getEntryValue('negativeTopics')) ? getEntryValue('negativeTopics') : [],
      segmentRecommendations: recommendations.length > 0 ? recommendations : (Array.isArray(getEntryValue('segmentRecommendations')) ? getEntryValue('segmentRecommendations') : []),
      analysis: getEntryValue('analysis') || '',
      title: title || undefined,
      drivers: drivers.length > 0 ? drivers : undefined,
      multicolumnData: multicolumnData.length > 0 ? multicolumnData : undefined,
      informationCategory: informationCategory || getEntryValue('informationCategory') || undefined,
      characteristics: characteristics
    };
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
            analysisType: "DASHBOARD",
            context: detectedContext // Add context information
          },
          agent: {
            code: detectedProjectType === 'DATA_ANAL' ? "QUERY_GENERAL_DATASET" : "QUERY_USER_FEEDBACK"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Check if project hasn't changed before updating state
      if (currentProjectIdRef.current === selectedProject?.id) {
        if (response.data.data) {
          setDashboardData({
            sentimentDistribution: response.data.data.contentSentiment || [],
            topicDistribution: response.data.data.highlights || [],
            timelineData: response.data.data.timelineData || [],
            ratingDistribution: response.data.data.ratingDistribution || [],
            experienceDistribution: response.data.data.experienceDistribution || [],
            employmentStatusDistribution: response.data.data.employmentStatusDistribution || []
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      // Only update state if project hasn't changed
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      if (currentProjectIdRef.current === selectedProject?.id) {
        // Set default data in case of error
        setDashboardData({
          sentimentDistribution: [],
          topicDistribution: [],
          timelineData: [],
          ratingDistribution: [],
          experienceDistribution: [],
          employmentStatusDistribution: []
        });
      }
    }
    // Note: Loading state is managed by the calling useEffect
  };

  // Update processSurveyData function
  const processSurveyData = (surveys: SurveyItem[]): DashboardData => {
    const sentimentCounts: { [key: string]: number } = {};
    const topicCounts: { [key: string]: number } = {};
    const timelineData: TimelineDataItem[] = [];
    const ratingCounts: { [key: string]: number } = {};
    const experienceCounts: { [key: string]: number } = {};
    const employmentStatusCounts: { [key: string]: number } = {};

    surveys.forEach((survey: SurveyItem) => {
      // Process sentiment
      const sentimentData = survey.entries.find(e => e.key === 'contentSentiment');
      if (sentimentData?.value) {
        const sentimentValue = (sentimentData.value as any).sentiment || 'Neutral';
        sentimentCounts[sentimentValue] = (sentimentCounts[sentimentValue] || 0) + 1;
      }

      // Process topics from highlights
      const highlightsEntry = survey.entries.find(e => e.key === 'highlights');
      if (highlightsEntry?.value && Array.isArray(highlightsEntry.value)) {
        highlightsEntry.value.forEach(topic => {
          // Extract key phrases from the highlight
          const phrases = topic.split(/[.,]/).map(p => p.trim()).filter(p => p);
          phrases.forEach(phrase => {
            if (phrase) {
              topicCounts[phrase] = (topicCounts[phrase] || 0) + 1;
            }
          });
        });
      }

      // Process timeline using publishedDate and sentiment score
      const dateEntry = survey.entries.find(e => e.key === 'publishedDate');
      const sentimentScoreEntry = survey.entries.find(e => e.key === 'contentSentiment');
      if (dateEntry?.value && sentimentScoreEntry?.value) {
        try {
          const dateStr = dateEntry.value.toString();
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          const date = new Date(`${year}-${month}-${day}`);

          const sentimentValue = sentimentScoreEntry.value as any;
          // Fix: Extract sentiment score properly from the contentSentiment structure
          let sentimentScore = 0;

          // Handle different possible structures
          if (typeof sentimentValue === 'object') {
            if (sentimentValue.overall_score !== undefined) {
              sentimentScore = sentimentValue.overall_score;
            } else if (sentimentValue.Pros && sentimentValue.Cons) {
              // Calculate average from Pros and Cons sentiment scores
              const prosScore = sentimentValue.Pros.sentimentScore || 0;
              const consScore = sentimentValue.Cons.sentimentScore || 0;
              sentimentScore = (prosScore + consScore) / 2;
            } else if (sentimentValue.sentimentScore !== undefined) {
              sentimentScore = sentimentValue.sentimentScore;
            } else if (sentimentValue.sentiment) {
              // Convert sentiment string to numeric score
              const sentimentStr = sentimentValue.sentiment.toLowerCase();
              if (sentimentStr.includes('positive') || sentimentStr.includes('good')) {
                sentimentScore = 0.8;
              } else if (sentimentStr.includes('neutral') || sentimentStr.includes('mixed')) {
                sentimentScore = 0.5;
              } else if (sentimentStr.includes('negative') || sentimentStr.includes('poor')) {
                sentimentScore = 0.2;
              }
            }
          } else if (typeof sentimentValue === 'string') {
            // If it's a JSON string, try to parse it
            try {
              const parsed = JSON.parse(sentimentValue);
              if (parsed.overall_score !== undefined) {
                sentimentScore = parsed.overall_score;
              } else if (parsed.Pros && parsed.Cons) {
                const prosScore = parsed.Pros.sentimentScore || 0;
                const consScore = parsed.Cons.sentimentScore || 0;
                sentimentScore = (prosScore + consScore) / 2;
              } else if (parsed.sentimentScore !== undefined) {
                sentimentScore = parsed.sentimentScore;
              } else if (parsed.sentiment) {
                // Convert sentiment string to numeric score
                const sentimentStr = parsed.sentiment.toLowerCase();
                if (sentimentStr.includes('positive') || sentimentStr.includes('good')) {
                  sentimentScore = 0.8;
                } else if (sentimentStr.includes('neutral') || sentimentStr.includes('mixed')) {
                  sentimentScore = 0.5;
                } else if (sentimentStr.includes('negative') || sentimentStr.includes('poor')) {
                  sentimentScore = 0.2;
                }
              }
            } catch (parseError) {
              // If parsing fails, try to extract sentiment from string
              const sentimentStr = sentimentValue.toLowerCase();
              if (sentimentStr.includes('positive') || sentimentStr.includes('good')) {
                sentimentScore = 0.8;
              } else if (sentimentStr.includes('neutral') || sentimentStr.includes('mixed')) {
                sentimentScore = 0.5;
              } else if (sentimentStr.includes('negative') || sentimentStr.includes('poor')) {
                sentimentScore = 0.2;
              }
            }
          }

          timelineData.push({
            date: date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }),
            value: 1,
            sentimentScore: sentimentScore
          });
        } catch (error) {
          console.error('Error converting date or processing sentiment:', error);
        }
      }

      // Process ratings from various survey questions
      const ratingQuestions = [
        'How satisfied are you with your onboarding experience?',
        'How effectively did your manager support you during your initial days?',
        'How comfortable do you feel with the team culture and collaboration?',
        'How satisfied are you with the training and resources provided to perform your job?',
        'How well were your job roles and responsibilities explained to you?',
        'On a scale of 0 to 10, how likely are you to recommend ABC Consulting as a great place to work to your friends or family?'
      ];

      ratingQuestions.forEach(question => {
        const ratingEntry = survey.entries.find(e => e.key === question);
        if (ratingEntry?.value) {
          const ratingValue = ratingEntry.value.toString();
          const questionKey = question.split('?')[0].trim();
          if (!ratingCounts[questionKey]) {
            ratingCounts[questionKey] = 0;
          }
          ratingCounts[questionKey] += Number(ratingValue);
        }
      });

      // Process experience (using tenure)
      const tenureEntry = survey.entries.find(e => e.key === 'Tenure');
      if (tenureEntry?.value) {
        const tenure = tenureEntry.value.toString();
        experienceCounts[tenure] = (experienceCounts[tenure] || 0) + 1;
      }

      // Process department as employment status
      const departmentEntry = survey.entries.find(e => e.key === 'Department');
      if (departmentEntry?.value) {
        const department = departmentEntry.value.toString();
        employmentStatusCounts[department] = (employmentStatusCounts[department] || 0) + 1;
      }
    });

    // Convert to chart data format and sort by value
    const sentimentData: ChartDataItem[] = Object.entries(sentimentCounts)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }))
      .sort((a, b) => b.value - a.value);

    const topicData: ChartDataItem[] = Object.entries(topicCounts)
      .map(([name, value]) => ({
        name,
        value
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Show top 10 topics

    // Calculate average ratings for each question
    const ratingData = Object.entries(ratingCounts)
      .map(([name, total]) => ({
        name: name.length > 30 ? name.substring(0, 30) + '...' : name,
        value: total / surveys.length
      }))
      .sort((a, b) => b.value - a.value);

    const experienceData = Object.entries(experienceCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const employmentStatusData = Object.entries(employmentStatusCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Sort timeline data by date
    timelineData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Aggregate timeline data by date
    const aggregatedTimelineData: TimelineDataItem[] = [];
    timelineData.forEach(item => {
      const existingItem = aggregatedTimelineData.find(d => d.date === item.date);
      if (existingItem) {
        existingItem.value += 1;
      } else {
        aggregatedTimelineData.push({ ...item });
      }
    });

    return {
      sentimentDistribution: sentimentData,
      topicDistribution: topicData,
      timelineData: aggregatedTimelineData,
      ratingDistribution: ratingData,
      experienceDistribution: experienceData,
      employmentStatusDistribution: employmentStatusData
    };
  };

  // Update the chat functionality
  // Function to handle docUID selection - uses active tab's selection
  const handleDocUIDToggle = (docUID: string) => {
    const setActiveSelected = getActiveSetSelectedDocUIDs();
    setActiveSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docUID)) {
        newSet.delete(docUID);
      } else {
        newSet.add(docUID);
      }
      return newSet;
    });
  };

  // Function to get all docUIDs from a segment group
  // For segment items, the UUID is in item.entries where key === "uuid"
  const getSegmentDocUIDs = (group: SegmentGroup): string[] => {
    const docUIDs: string[] = [];
    group.items.forEach(item => {
      // First try to get UUID from entries array
      const uuidEntry = item.entries?.find((e: any) => e.key === 'uuid');
      const uid = uuidEntry?.value || item.docUID || item.key;
      if (uid) {
        // Convert to string to ensure type safety
        docUIDs.push(String(uid));
      }
    });
    return docUIDs;
  };

  // Function to get all docUIDs from a driver group
  // For driver items, the UUID is in item.entries where key === "uuid"
  const getDriverDocUIDs = (group: any): string[] => {
    const docUIDs: string[] = [];
    if (group.items && Array.isArray(group.items)) {
      group.items.forEach((item: any) => {
        // First try to get UUID from entries array
        const uuidEntry = item.entries?.find((e: any) => e.key === 'uuid');
        const uid = uuidEntry?.value || item.docUID || item.key;
        if (uid) {
          // Convert to string to ensure type safety
          docUIDs.push(String(uid));
        }
      });
    }
    return docUIDs;
  };

  const handleChatClick = () => {
    setIsChatOpen(true);
    // Reset messages when opening chat
    setMessages([
      {
        id: 1,
        text: "Hello! I'm your AI assistant. How can I help you analyze the survey data?",
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
  };

  const handleFullScreenToggle = () => {
    setIsFullScreen(!isFullScreen);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setIsFullScreen(false); // Exit full screen when closing chat
  };

  // Export functions for Survey Insights (Segment Analysis / Driver Analysis)
  const handleExportDialogGenerate = async (brandDetails: BrandDetails, contentPreferences: ContentPreferences) => {
    if (!selectedProject) {
      // setError('Please select a project first');
      return;
    }

    const activeSelectedDocUIDs = getActiveSelectedDocUIDs();
    if (activeSelectedDocUIDs.size === 0) {
      // setError('Please select at least one segment or driver to export');
      return;
    }

    setIsGeneratingContent(true);
    // setError(null);

    try {
      const contentTypeMap: { [key: string]: string } = {
        'SOCIAL_POST_INSTAGRAM': 'SOCIAL_POST_INSTAGRAM',
        'SOCIAL_POST_FACEBOOK': 'SOCIAL_POST_FACEBOOK',
        'SOCIAL_POST_LINKEDIN': 'SOCIAL_POST_LINKEDIN',
        'SOCIAL_POST_TWITTER': 'SOCIAL_POST_TWITTER',
        'BLOG_POST': 'BLOG_POST',
        'EMAIL': 'EMAIL',
        'VIDEO': 'VIDEO',
        'SHORT_MESSAGE': 'SHORT_MESSAGE',
        'JOB_DESCRIPTION': 'JOB_DESCRIPTION',
        'CUSTOM': 'CUSTOM'
      };

      const writingStyleMap: { [key: string]: string } = {
        'Professional': 'professional',
        'Casual': 'casual',
        'Technical': 'technical',
        'Creative': 'creative'
      };

      const payload = {
        data: {
          campaignId: selectedProject.id,
          docUIDs: Array.from(activeSelectedDocUIDs),
          instructions: {
            keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
            writing_style: writingStyleMap[contentPreferences.style] || 'professional',
            tone: 'warm',
            audience: brandDetails.targetAudience.length > 0 ? brandDetails.targetAudience[0] : 'general',
            guidelines: contentPreferences.instructions || ''
          },
          contentType: contentTypeMap[contentPreferences.category] || 'SOCIAL_POST_LINKEDIN'
        },
        agent: {
          code: "GENERATE_CONTENT_FROM_USER_FEEDBACK_ANALYTICS"
        }
      };

      const response = await axios.post(
        `${environment.apiUrl}/agent/scheduleFlow`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status === 200 && response.data.data) {
        // Import processMultiColumnData for visualization handling
        const { processMultiColumnData } = await import('./Chatbot/utils/pdfExport');

        const responseData = response.data.data;
        const paragraphResponse = responseData.find((item: any) => item.objectType === 'PARAGRAPH');
        const followupQuestions = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions');
        const sourceLinks = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks');
        const multiColumnResponse = responseData.find((item: any) => item.objectType === 'MULTICOLUMN');
        const dictionaryResponses = responseData.filter((item: any) => item.objectType === 'DICTIONARY');

        // Extract title from DICTIONARY entries
        let title = `Generated Content from ${activeMainTab === 'segment' ? 'Segment Analysis' : 'Driver Analysis'}`;
        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const titleEntry = dict.entries.find((entry: any) => entry.key === 'title');
            if (titleEntry?.value) {
              title = titleEntry.value;
              break;
            }
          }
        }

        // Extract text content
        let textContent = '';
        if (paragraphResponse?.value) {
          textContent = paragraphResponse.value;
        } else {
          for (const dict of dictionaryResponses) {
            if (dict.entries) {
              const textEntry = dict.entries.find((entry: any) => entry.key === 'text');
              if (textEntry?.value) {
                textContent = textEntry.value;
                break;
              }
            }
          }
        }

        if (!textContent) {
          textContent = 'Content generated successfully, but no text content was returned.';
        }

        // Handle visualization data (similar to chatbot export)
        let visualization: any = undefined;

        // Check DICTIONARY for dataset
        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const datasetEntry = dict.entries.find((entry: any) => entry.key === 'dataset');
            if (datasetEntry?.value) {
              const data = datasetEntry.value;
              const processedData = processMultiColumnData(data, data.visual);

              if (processedData) {
                let type: 'table' | 'chart' | 'heatmap' = 'table';

                if (processedData.visual === true) {
                  if (processedData.dimension === 2) {
                    type = 'chart';
                  } else if (processedData.dimension >= 3) {
                    type = 'heatmap';
                  } else {
                    type = 'chart';
                  }
                } else {
                  type = 'table';
                }

                visualization = { type, data: processedData };
                break;
              }
            }
          }
        }

        // Check standalone MULTICOLUMN
        if (!visualization && multiColumnResponse) {
          const processedData = processMultiColumnData(multiColumnResponse, multiColumnResponse.visual);

          if (processedData) {
            let type: 'table' | 'chart' | 'heatmap' = 'table';

            if (processedData.visual === true) {
              if (processedData.dimension === 2) {
                type = 'chart';
              } else if (processedData.dimension >= 3) {
                type = 'heatmap';
              } else {
                type = 'chart';
              }
            } else {
              type = 'table';
            }

            visualization = { type, data: processedData };
          }
        }

        const generatedContent: GeneratedContent = {
          title,
          content: textContent,
          format: contentPreferences.format,
          category: contentPreferences.category,
          followupQuestions: followupQuestions?.value || [],
          sourceLinks: sourceLinks?.value || [],
          visualization: visualization
        };

        await generatePDFFromGeneratedContent(generatedContent);
        setSuccess('Content generated and downloaded successfully!');
      } else {
        throw new Error('No content generated from API');
      }
    } catch (error: any) {
      console.error('Error generating content:', error);
      // setError(error?.response?.data?.error || error?.message || 'Failed to generate content');
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleExportAsIsFromSurveyInsights = async () => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    const activeSelectedDocUIDs = getActiveSelectedDocUIDs();
    if (activeSelectedDocUIDs.size === 0) {
      setError('Please select at least one segment or driver to export');
      return;
    }

    setIsExportingSurveyInsights(true);
    setError(null);

    try {
      const payload = {
        data: {
          campaignId: selectedProject.id,
          docUIDs: Array.from(activeSelectedDocUIDs)
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_USER_FEEDBACK_ANALYTICS"
        }
      };

      const response = await axios.post(
        `${environment.apiUrl}/agent/scheduleFlow`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.status === 200 && response.data.data) {
        // Process the export data similar to chatbot export
        await generatePDFFromExportData(response.data.data);
        setSuccess('Content exported successfully!');
      } else {
        throw new Error('Export failed');
      }
    } catch (error: any) {
      console.error('Error exporting content:', error);
      // setError(error?.response?.data?.error || error?.message || 'Failed to export content');
    } finally {
      setIsExportingSurveyInsights(false);
    }
  };

  // Export Content helper functions
  const audienceOptions = [
    'Retail bank users',
    'Corporate clients',
    'Small business owners',
    'Internal employees',
    'Investment clients',
    'Insurance customers',
    'Loan applicants',
    'Credit card users'
  ];

  const handleExportMode = () => {
    setIsExportMode(true);
    setSelectedPairs(new Set());
  };

  const handleCancelExport = () => {
    setIsExportMode(false);
    setSelectedPairs(new Set());
  };

  const handlePairSelection = (pairId: string) => {
    const newSelected = new Set(selectedPairs);
    if (newSelected.has(pairId)) {
      newSelected.delete(pairId);
    } else {
      newSelected.add(pairId);
    }
    setSelectedPairs(newSelected);
  };

  const handleExport = () => {
    if (selectedPairs.size === 0) return;

    setIsExportMode(false);
    setShowExportDialog(true);
    setCurrentStep(1);
  };

  const handleCloseExportDialog = () => {
    setShowExportDialog(false);
    setSelectedPairs(new Set());
    setCurrentStep(1);
  };

  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBrandDetailsChange = (field: keyof BrandDetails, value: string) => {
    setBrandDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAudienceToggle = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience]
    }));
  };

  const handleAddCustomAudience = () => {
    if (newAudience.trim() && !brandDetails.targetAudience.includes(newAudience.trim())) {
      setBrandDetails(prev => ({
        ...prev,
        targetAudience: [...prev.targetAudience, newAudience.trim()]
      }));
      setNewAudience('');
    }
  };

  const handleRemoveAudience = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter(a => a !== audience)
    }));
  };

  const handleContentPreferenceChange = (field: keyof ContentPreferences, value: string) => {
    setContentPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isContentLengthApplicable = () => {
    const textBasedFormats = ['Text Only'];
    return textBasedFormats.includes(contentPreferences.format);
  };

  // Helper function to map content preferences to API format
  const mapContentPreferencesToAPI = () => {
    // Content types are now fetched from API and already in correct format
    const contentTypeMap: { [key: string]: string } = {
      'SOCIAL_POST_INSTAGRAM': 'SOCIAL_POST_INSTAGRAM',
      'SOCIAL_POST_FACEBOOK': 'SOCIAL_POST_FACEBOOK',
      'SOCIAL_POST_LINKEDIN': 'SOCIAL_POST_LINKEDIN',
      'SOCIAL_POST_TWITTER': 'SOCIAL_POST_TWITTER',
      'BLOG_POST': 'BLOG_POST',
      'EMAIL': 'EMAIL',
      'VIDEO': 'VIDEO',
      'SHORT_MESSAGE': 'SHORT_MESSAGE',
      'JOB_DESCRIPTION': 'JOB_DESCRIPTION',
      'CUSTOM': 'CUSTOM'
    };

    const writingStyleMap: { [key: string]: string } = {
      'Professional': 'professional',
      'Casual': 'casual',
      'Technical': 'technical',
      'Creative': 'creative'
    };

    return {
      contentType: contentTypeMap[contentPreferences.category] || 'SOCIAL_POST_LINKEDIN',
      writingStyle: writingStyleMap[contentPreferences.style] || 'professional',
      tone: 'warm', // Default tone
      audience: brandDetails.targetAudience.length > 0 ? brandDetails.targetAudience[0] : 'general',
      guidelines: contentPreferences.instructions || ''
    };
  };

  // Helper function to extract interaction UIDs from selected pairs
  const getInteractionUIDs = (): string[] => {
    const uids: string[] = [];
    selectedPairs.forEach(pairId => {
      const index = parseInt(pairId.replace('pair_', ''));
      if (messages[index] && messages[index + 1]) {
        const botMessage = messages[index + 1];
        // Use the interaction UID from the bot message if available
        if (botMessage.interactionUID) {
          uids.push(botMessage.interactionUID);
        }
      }
    });
    return uids;
  };

  const generateContentFromAPI = async (): Promise<GeneratedContent> => {
    try {
      const interactionUIDs = getInteractionUIDs();
      const apiConfig = mapContentPreferencesToAPI();

      const payload = {
        data: {
          interactionUIDs: interactionUIDs,
          instructions: {
            keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
            writing_style: apiConfig.writingStyle,
            tone: apiConfig.tone,
            audience: apiConfig.audience,
            guidelines: apiConfig.guidelines
          },
          contentType: apiConfig.contentType
        },
        agent: {
          code: "GENERATE_CONTENT_FROM_CONVERSATION"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        const responseData = response.data.data;
        const paragraphResponse = responseData.find((item: any) => item.objectType === 'PARAGRAPH');
        const followupQuestions = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions');
        const sourceLinks = responseData.find((item: any) => item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks');
        const multiColumnResponse = responseData.find((item: any) => item.objectType === 'MULTICOLUMN');

        // Find all DICTIONARY objects
        const dictionaryResponses = responseData.filter((item: any) => item.objectType === 'DICTIONARY');

        // Extract text content from PARAGRAPH or DICTIONARY with text key
        let textContent = '';
        if (paragraphResponse?.value) {
          textContent = paragraphResponse.value;
        } else {
          // Look through all DICTIONARY objects for text content
          for (const dict of dictionaryResponses) {
            if (dict.entries) {
              const textEntry = dict.entries.find((entry: any) => entry.key === 'text');
              if (textEntry) {
                textContent = textEntry.value;
                break;
              }
            }
          }
        }

        // Handle MULTICOLUMN visualization data with improved processing
        let visualization = undefined;

        // Check for MULTICOLUMN data in DICTIONARY structure first
        for (const dict of dictionaryResponses) {
          if (dict.entries) {
            const datasetEntry = dict.entries.find((entry: any) => entry.key === 'dataset');
            if (datasetEntry?.value) {
              const data = datasetEntry.value;
              // Process and clean the data before creating visualization
              const processedData = processMultiColumnData(data, data.visual);
              if (processedData) {
                let type: 'table' | 'chart' | 'heatmap' = 'table';
                if (processedData.visual) {
                  if (processedData.dimension === 2) {
                    type = 'chart';
                  } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                    type = 'heatmap';
                  }
                }
                visualization = {
                  type,
                  data: processedData
                };
                break; // Found visualization data, no need to continue
              }
            }
          }
        }

        // Check for standalone MULTICOLUMN data
        if (!visualization && multiColumnResponse) {
          const processedData = processMultiColumnData(multiColumnResponse, multiColumnResponse.visual);
          if (processedData) {
            let type: 'table' | 'chart' | 'heatmap' = 'table';
            if (processedData.visual) {
              if (processedData.dimension === 2) {
                type = 'chart';
              } else if (processedData.dimension === 3 || processedData.dimension === 4) {
                type = 'heatmap';
              }
            }
            visualization = {
              type,
              data: processedData
            };
          }
        }

        return {
          title: `${contentPreferences.category} - Generated Content`,
          content: textContent || 'Content generated successfully, but no text content was returned.',
          format: contentPreferences.format,
          category: contentPreferences.category,
          followupQuestions: followupQuestions?.value || [],
          sourceLinks: sourceLinks?.value || [],
          visualization: visualization
        };
      } else {
        throw new Error('No content generated from API');
      }
    } catch (error: any) {
      console.error('Error generating content from API:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      throw error;
    }
  };

  const handleGenerateContent = async () => {
    try {
      setIsGeneratingContent(true);

      // Show loading state
      setGeneratedContent({
        title: 'Generating Content...',
        content: 'Please wait while we generate your content...',
        format: contentPreferences.format,
        category: contentPreferences.category
      });
      setShowExportDialog(false);
      setShowContentDisplay(true);

      // Generate content using API
      const content = await generateContentFromAPI();
      setGeneratedContent(content);
    } catch (error: any) {
      console.error('Error generating content:', error);
      setError(error?.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsGeneratingContent(false);
    }
  };

  // Helper function to generate text content (fallback)
  const generateTextContent = (generatedContent: GeneratedContent): string => {
    let fullContent = '';

    // Add title
    fullContent += `# ${generatedContent.title}\n\n`;
    fullContent += `**Format:** ${generatedContent.format}\n`;
    fullContent += `**Category:** ${generatedContent.category}\n\n`;
    fullContent += `---\n\n`;

    // Add main text content
    fullContent += `## Content\n\n`;
    fullContent += generatedContent.content + '\n\n';

    // Add visualization data if present
    if (generatedContent.visualization) {
      fullContent += `## Data Visualization\n\n`;
      fullContent += `**Type:** ${generatedContent.visualization.type}\n`;
      fullContent += `**Title:** ${generatedContent.visualization.data.title || 'N/A'}\n\n`;

      // Add headers
      if (generatedContent.visualization.data.headers) {
        fullContent += `**Headers:** ${generatedContent.visualization.data.headers.join(', ')}\n\n`;
      }

      // Add data table
      if (generatedContent.visualization.data.data) {
        fullContent += `**Data:**\n`;
        const headers = generatedContent.visualization.data.headers || [];
        const data = generatedContent.visualization.data.data || [];

        // Create table header
        if (headers.length > 0) {
          fullContent += headers.join('\t') + '\n';
          fullContent += headers.map(() => '---').join('\t') + '\n';
        }

        // Add data rows
        data.forEach((row: any) => {
          const values = headers.map((header: string) => {
            const value = row[header];
            return typeof value === 'object' ? JSON.stringify(value) : String(value || '');
          });
          fullContent += values.join('\t') + '\n';
        });
        fullContent += '\n';
      }
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      fullContent += `## Source Links\n\n`;
      generatedContent.sourceLinks.forEach((link, index) => {
        fullContent += `${index + 1}. ${link}\n`;
      });
      fullContent += '\n';
    }

    // Add metadata
    fullContent += `---\n\n`;
    fullContent += `**Generated on:** ${new Date().toLocaleString()}\n`;
    fullContent += `**Export Type:** Complete Content Export\n`;

    return fullContent;
  };

  // Helper function to convert chart data to image
  const chartDataToImage = async (visualization: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        // Create a canvas element
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 600;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Set background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add title
        ctx.fillStyle = '#1a1a1a';
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(visualization.data.title || 'Data Visualization', canvas.width / 2, 40);

        // Add chart type
        ctx.font = '16px Arial';
        ctx.fillText(`Type: ${visualization.type}`, canvas.width / 2, 70);

        // Create a simple table visualization
        const headers = visualization.data.headers || [];
        const data = visualization.data.data || [];

        if (headers.length > 0 && data.length > 0) {
          const cellWidth = canvas.width / headers.length;
          const cellHeight = 30;
          const startY = 120;

          // Draw headers
          ctx.fillStyle = '#4c3794';
          ctx.fillRect(0, startY, canvas.width, cellHeight);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';

          headers.forEach((header: string, index: number) => {
            const x = index * cellWidth + cellWidth / 2;
            ctx.fillText(header, x, startY + 20);
          });

          // Draw data rows
          ctx.fillStyle = '#1a1a1a';
          ctx.font = '12px Arial';

          data.forEach((row: any, rowIndex: number) => {
            const y = startY + cellHeight + (rowIndex * cellHeight);

            // Alternate row colors
            if (rowIndex % 2 === 0) {
              ctx.fillStyle = '#f8f9fa';
            } else {
              ctx.fillStyle = '#ffffff';
            }
            ctx.fillRect(0, y, canvas.width, cellHeight);

            // Draw cell content
            ctx.fillStyle = '#1a1a1a';
            headers.forEach((header: string, colIndex: number) => {
              const x = colIndex * cellWidth + cellWidth / 2;
              const value = row[header];
              const text = typeof value === 'object' ? JSON.stringify(value) : String(value || '');
              ctx.fillText(text, x, y + 20);
            });
          });
        }

        // Convert canvas to data URL
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Helper function to generate PDF with charts
  const generatePDFWithCharts = async (generatedContent: GeneratedContent): Promise<string> => {
    // For now, we'll use a simple approach with HTML to PDF conversion
    // In a real implementation, you'd use a library like jsPDF or Puppeteer

    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${generatedContent.title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 30px; }
          .title { font-size: 24px; font-weight: bold; color: #1a1a1a; margin-bottom: 10px; }
          .meta { color: #666; font-size: 14px; }
          .content { margin: 20px 0; }
          .chart-container { margin: 20px 0; text-align: center; }
          .chart-image { max-width: 100%; height: auto; border: 1px solid #ddd; }
          .source-links { margin: 20px 0; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">${generatedContent.title}</div>
          <div class="meta">Format: ${generatedContent.format} | Category: ${generatedContent.category}</div>
        </div>
        
        <div class="content">
          <h2>Content</h2>
          <div>${generatedContent.content.replace(/\n/g, '<br>')}</div>
        </div>
    `;

    // Add visualization if present
    if (generatedContent.visualization) {
      try {
        const chartImage = await chartDataToImage(generatedContent.visualization);
        htmlContent += `
          <div class="chart-container">
            <h2>Data Visualization</h2>
            <p><strong>Type:</strong> ${generatedContent.visualization.type}</p>
            <p><strong>Title:</strong> ${generatedContent.visualization.data.title || 'N/A'}</p>
            <img src="${chartImage}" alt="Data Visualization" class="chart-image" />
          </div>
        `;
      } catch (error) {
        console.error('Error generating chart image:', error);
        htmlContent += `
          <div class="chart-container">
            <h2>Data Visualization</h2>
            <p>Chart could not be generated. Data available in text format.</p>
          </div>
        `;
      }
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      htmlContent += `
        <div class="source-links">
          <h2>Source Links</h2>
          <ul>
      `;
      generatedContent.sourceLinks.forEach((link, index) => {
        htmlContent += `<li><a href="${link}">${link}</a></li>`;
      });
      htmlContent += `
          </ul>
        </div>
      `;
    }

    htmlContent += `
        <div class="footer">
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <p>Export Type: Complete Content Export with Charts</p>
        </div>
      </body>
      </html>
    `;

    // For now, return the HTML content
    // In a real implementation, you'd convert this to PDF using a library
    return htmlContent;
  };

  const handleDownloadContent = async () => {
    if (!generatedContent) return;

    const format = generatedContent.format.toLowerCase();
    let fileName = '';
    let mimeType = '';
    let content = '';

    if (format.includes('image') || format === 'infographic') {
      const link = document.createElement('a');
      link.href = generatedContent.content;
      link.download = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    } else if (format === 'video') {
      fileName = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}.mp4`;
      content = generatedContent.content;
      mimeType = 'text/plain';
      fileName = fileName.replace('.mp4', '_script.txt');
    } else {
      fileName = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      mimeType = 'application/pdf';

      // Generate PDF with charts using the same logic as export
      try {
        await generatePDFFromGeneratedContent(generatedContent);

        return; // PDF is saved directly, no need to create blob
      } catch (error) {
        console.error('Error generating PDF:', error);
        // Fallback to text if PDF generation fails
        fileName = fileName.replace('.pdf', '.txt');
        mimeType = 'text/plain';
        content = generateTextContent(generatedContent);
      }
    }

    // Only create blob for non-PDF content
    if (mimeType !== 'application/pdf') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCopyContent = () => {
    if (generatedContent) {
      // Build comprehensive content for copying
      let fullContent = '';

      // Add title
      fullContent += `${generatedContent.title}\n\n`;
      fullContent += `Format: ${generatedContent.format}\n`;
      fullContent += `Category: ${generatedContent.category}\n\n`;
      fullContent += `---\n\n`;

      // Add main text content
      fullContent += `Content:\n\n`;
      fullContent += generatedContent.content + '\n\n';

      // Add visualization data if present
      if (generatedContent.visualization) {
        fullContent += `Data Visualization:\n`;
        fullContent += `Type: ${generatedContent.visualization.type}\n`;
        fullContent += `Title: ${generatedContent.visualization.data.title || 'N/A'}\n\n`;

        // Add headers
        if (generatedContent.visualization.data.headers) {
          fullContent += `Headers: ${generatedContent.visualization.data.headers.join(', ')}\n\n`;
        }

        // Add data table
        if (generatedContent.visualization.data.data) {
          fullContent += `Data:\n`;
          const headers = generatedContent.visualization.data.headers || [];
          const data = generatedContent.visualization.data.data || [];

          // Create table header
          if (headers.length > 0) {
            fullContent += headers.join('\t') + '\n';
            fullContent += headers.map(() => '---').join('\t') + '\n';
          }

          // Add data rows
          data.forEach((row: any) => {
            const values = headers.map((header: string) => {
              const value = row[header];
              return typeof value === 'object' ? JSON.stringify(value) : String(value || '');
            });
            fullContent += values.join('\t') + '\n';
          });
          fullContent += '\n';
        }
      }


      // Add source links if present
      if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
        fullContent += `Source Links:\n\n`;
        generatedContent.sourceLinks.forEach((link, index) => {
          fullContent += `${index + 1}. ${link}\n`;
        });
        fullContent += '\n';
      }

      // Add metadata
      fullContent += `---\n\n`;
      fullContent += `Generated on: ${new Date().toLocaleString()}\n`;
      fullContent += `Export Type: Complete Content Export\n`;

      navigator.clipboard.writeText(fullContent);
    }
  };

  const handleCloseContentDisplay = () => {
    setShowContentDisplay(false);
    setGeneratedContent(null);
    setSelectedPairs(new Set());
  };

  // Export as-is functionality with PDF generation
  const exportAsIs = async () => {
    try {
      setIsExportingAsIs(true);

      const interactionUIDs = getInteractionUIDs();
      const apiConfig = mapContentPreferencesToAPI();

      const payload = {
        data: {
          interactionUIDs: interactionUIDs,
          instructions: {
            keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
            writing_style: apiConfig.writingStyle,
            tone: apiConfig.tone,
            audience: apiConfig.audience,
            guidelines: apiConfig.guidelines
          },
          contentType: apiConfig.contentType
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_CONVERSATION"
        }
      };

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        // Generate PDF with the response data
        await generatePDFFromExportData(response.data.data);
        setSuccess('PDF exported successfully!');
      } else {
        throw new Error('No export data received from API');
      }
    } catch (error) {
      console.error('Error exporting conversation:', error);
      // setError('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingAsIs(false);
    }
  };

  // Generate PDF from GeneratedContent (for download content)
  const generatePDFFromGeneratedContent = async (generatedContent: GeneratedContent) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Add header with styling
    pdf.setFillColor(156, 39, 176); // Purple background
    pdf.rect(0, 0, pageWidth, 30, 'F');

    pdf.setTextColor(255, 255, 255); // White text
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Generated Content Export', margin, 20);

    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    const exportProjectType = (typeof window !== 'undefined' && sessionStorage.getItem('projectType')) || (typeof window !== 'undefined' && window.location.pathname.includes('data-analytics') ? 'DATA_ANAL' : 'SURVEY');
    pdf.text(exportProjectType === 'DATA_ANAL' ? 'Data Analytics Export' : 'Survey Insights Export', margin, 20);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    yPosition = 40;

    // Add title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(156, 39, 176);
    pdf.text(generatedContent.title, margin, yPosition);
    yPosition += 15;

    // Skip metadata (Format and Category)
    // pdf.setFontSize(12);
    // pdf.setFont('helvetica', 'normal');
    // pdf.setTextColor(0, 0, 0);
    // pdf.text(`Format: ${generatedContent.format}`, margin, yPosition);
    // yPosition += 8;
    // pdf.text(`Category: ${generatedContent.category}`, margin, yPosition);
    // yPosition += 15;

    // Add content
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Content:', margin, yPosition);
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const contentLines = pdf.splitTextToSize(generatedContent.content, contentWidth);
    for (const line of contentLines) {
      if (yPosition > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    }

    // Add visualization if present
    if (generatedContent.visualization) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Data Visualization:', margin, yPosition);
      yPosition += 10;

      // Generate chart image
      try {
        const chartImage = await generateChartImage(generatedContent.visualization.data);
        if (chartImage) {
          const imgWidth = Math.min(contentWidth, 160);
          const imgHeight = (imgWidth * 0.75);

          if (yPosition + imgHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        }
      } catch (error) {
        console.error('Error generating chart image:', error);
        // Fallback to table
        yPosition += 5;
        pdf.setFontSize(9);

        const headers = generatedContent.visualization.data.headers || [];
        const data = generatedContent.visualization.data.data || [];

        if (headers.length > 0 && data.length > 0) {
          const colWidth = contentWidth / headers.length;
          let xPos = margin;

          // Add headers
          for (const header of headers) {
            pdf.setFont('helvetica', 'bold');
            pdf.text(header.substring(0, 15), xPos + 2, yPosition + 2);
            xPos += colWidth;
          }
          yPosition += 8;

          // Add data rows
          const maxRows = Math.min(8, data.length);
          for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }

            const row = data[rowIndex];
            xPos = margin;
            for (const header of headers) {
              pdf.setFont('helvetica', 'normal');
              const cellValue = String(row[header] || '').substring(0, 15);
              pdf.text(cellValue, xPos + 2, yPosition + 2);
              xPos += colWidth;
            }
            yPosition += 5;
          }
        }
      }
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Source Links:', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      for (const link of generatedContent.sourceLinks) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`• ${link}`, margin, yPosition);
        yPosition += 5;
      }
    }

    // Add footer
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
      pdf.text('Generated Content Export', margin, pageHeight - 10);
    }

    // Save the PDF
    const fileName = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  // Generate PDF from export data with charts
  const generatePDFFromExportData = async (exportData: any[]) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Add header with styling
    pdf.setFillColor(156, 39, 176); // Purple background
    pdf.rect(0, 0, pageWidth, 30, 'F');

    pdf.setTextColor(255, 255, 255); // White text
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    const exportProjectType = (typeof window !== 'undefined' && sessionStorage.getItem('projectType')) || (typeof window !== 'undefined' && window.location.pathname.includes('data-analytics') ? 'DATA_ANAL' : 'SURVEY');
    pdf.text(exportProjectType === 'DATA_ANAL' ? 'Data Analytics Export' : 'Survey Insights Export', margin, 20);

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, 25);

    // Reset text color
    pdf.setTextColor(0, 0, 0);
    yPosition = 40;

    // Process each data item
    for (let i = 0; i < exportData.length; i++) {
      const item = exportData[i];

      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      // Add section header with background
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPosition - 5, contentWidth, 15, 'F');

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Section ${i + 1}`, margin + 5, yPosition + 5);
      yPosition += 20;

      // Process entries
      if (item.entries && Array.isArray(item.entries)) {
        for (const entry of item.entries) {
          // Skip Geography and Language fields
          if (entry.key === 'geography' || entry.key === 'language' ||
            entry.label === 'Geography' || entry.label === 'Language') {
            continue;
          }

          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = 20;
          }

          // Add entry label
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(156, 39, 176); // Purple color
          const labelText = `${entry.label}:`;
          pdf.text(labelText, margin, yPosition);
          yPosition += 8;

          // Reset text color
          pdf.setTextColor(0, 0, 0);

          // Add entry value
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');

          let valueText = '';
          if (typeof entry.value === 'string') {
            valueText = entry.value;
          } else if (typeof entry.value === 'object' && entry.value !== null) {
            // Handle dataset objects (charts)
            if (entry.value.headers && entry.value.data) {
              // Generate chart image
              try {
                const chartImage = await generateChartImage(entry.value);
                if (chartImage) {
                  // Add chart title
                  pdf.setFontSize(11);
                  pdf.setFont('helvetica', 'bold');
                  pdf.text(`${entry.value.title || 'Chart'}`, margin, yPosition);
                  yPosition += 8;

                  // Add chart image with better sizing
                  const imgWidth = Math.min(contentWidth, 160);
                  const imgHeight = (imgWidth * 0.75); // Better aspect ratio for charts

                  if (yPosition + imgHeight > pageHeight - 20) {
                    pdf.addPage();
                    yPosition = 20;
                  }

                  pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
                  yPosition += imgHeight + 15;

                  // Add data summary
                  pdf.setFontSize(9);
                  pdf.setFont('helvetica', 'normal');
                  pdf.text(`Data points: ${entry.value.data.length}`, margin, yPosition);
                  yPosition += 5;

                  continue; // Skip the normal text processing
                }
              } catch (error) {
                console.error('Error generating chart image:', error);
              }

              // Fallback to table if chart generation fails
              valueText = `Chart Data: ${entry.value.title || 'Untitled Chart'}`;

              // Add chart data as table
              yPosition += 5;
              pdf.setFontSize(9);

              // Add headers with background
              pdf.setFillColor(248, 249, 250);
              pdf.rect(margin, yPosition - 3, contentWidth, 8, 'F');

              const colWidth = contentWidth / entry.value.headers.length;
              let xPos = margin;
              for (const header of entry.value.headers) {
                pdf.setFont('helvetica', 'bold');
                pdf.text(header.substring(0, 15), xPos + 2, yPosition + 2);
                xPos += colWidth;
              }
              yPosition += 8;

              // Add data rows (limit to first 8 rows to avoid overflow)
              const maxRows = Math.min(8, entry.value.data.length);
              for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
                if (yPosition > pageHeight - 20) {
                  pdf.addPage();
                  yPosition = 20;
                }

                const row = entry.value.data[rowIndex];
                xPos = margin;
                for (const header of entry.value.headers) {
                  pdf.setFont('helvetica', 'normal');
                  const cellValue = String(row[header] || '').substring(0, 15);
                  pdf.text(cellValue, xPos + 2, yPosition + 2);
                  xPos += colWidth;
                }
                yPosition += 5;
              }

              if (entry.value.data.length > 8) {
                pdf.text(`... and ${entry.value.data.length - 8} more rows`, margin, yPosition);
                yPosition += 5;
              }

              yPosition += 5;
              continue; // Skip the normal text processing
            } else {
              valueText = JSON.stringify(entry.value, null, 2);
            }
          } else {
            valueText = String(entry.value || '');
          }

          // Split long text into multiple lines
          const lines = pdf.splitTextToSize(valueText, contentWidth);
          for (const line of lines) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          }

          yPosition += 8; // Add spacing between entries
        }
      }

      // Add separator between sections
      yPosition += 10;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 15;
    }

    // Add footer
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
      pdf.text(exportProjectType === 'DATA_ANAL' ? 'Data Analytics Export' : 'Survey Insights Export', margin, pageHeight - 10);
    }

    // Save the PDF
    const fileName = `${exportProjectType === 'DATA_ANAL' ? 'data_analytics_export' : 'survey_insights_export'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  // Generate chart image from data using the same components as chatbot
  const generateChartImage = async (chartData: any): Promise<string | null> => {
    try {
      // Validate chart data
      if (!chartData || !chartData.headers || !chartData.data || chartData.data.length === 0) {
        console.warn('Invalid chart data provided:', chartData);
        return await generateTableChartImage(chartData);
      }

      // Create a temporary container for the chart
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '600px';
      tempContainer.style.height = '400px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      document.body.appendChild(tempContainer);

      // Create a React root to render the chart component
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);

      // Render the same DataChart component used in chatbot
      const ChartComponent = () => {
        return (
          <div style={{ width: '100%', height: '100%' }}>
            {chartData.visual ? (
              <DataChart data={chartData} />
            ) : chartData.title && chartData.title.toLowerCase().includes('sentiment') ? (
              // Special handling for sentiment charts - use pie chart
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <h3 style={{ color: '#4c3794', marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                  {chartData.title}
                </h3>
                <div style={{ width: '300px', height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.data}
                        dataKey={chartData.headers[1]}
                        nameKey={chartData.headers[0]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        fill="#4c3794"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {chartData.data.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry[chartData.headers[0]] === 'Positive' ? '#4c3794' :
                                entry[chartData.headers[0]] === 'Negative' ? '#b19cd9' :
                                  '#d8bfd8'
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [`${value}`, name]}
                        contentStyle={{
                          backgroundColor: '#ede7f6',
                          borderRadius: '8px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                          padding: '8px 12px',
                          color: '#4c3794'
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span style={{ color: '#4c3794', fontSize: '12px' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <DataTable data={chartData} />
            )}
          </div>
        );
      };

      // Render the chart
      root.render(<ChartComponent />);

      // Wait a bit for the chart to render
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to image using html2canvas
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: 'white',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: 600,
        height: 400
      });

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);

      return canvas.toDataURL('image/png');
    } catch (error: any) {
      console.error('Error generating chart image:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      // Fallback to table if chart generation fails
      return await generateTableChartImage(chartData);
    }
  };

  // Fallback function to generate table chart image
  const generateTableChartImage = async (chartData: any): Promise<string | null> => {
    try {
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '600px';
      tempContainer.style.height = '400px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      document.body.appendChild(tempContainer);

      const chartElement = createTableChart(chartData);
      tempContainer.appendChild(chartElement);

      const canvas = await html2canvas(tempContainer, {
        backgroundColor: 'white',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      document.body.removeChild(tempContainer);
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating table chart image:', error);
      return null;
    }
  };

  // Create a table-based chart
  const createTableChart = (chartData: any): HTMLElement => {
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.fontFamily = 'Arial, sans-serif';
    table.style.fontSize = '12px';

    // Add title
    if (chartData.title) {
      const titleRow = document.createElement('tr');
      const titleCell = document.createElement('td');
      titleCell.colSpan = chartData.headers.length;
      titleCell.style.textAlign = 'center';
      titleCell.style.fontWeight = 'bold';
      titleCell.style.fontSize = '14px';
      titleCell.style.padding = '10px';
      titleCell.style.backgroundColor = '#f8f9fa';
      titleCell.textContent = chartData.title;
      titleRow.appendChild(titleCell);
      table.appendChild(titleRow);
    }

    // Add headers
    const headerRow = document.createElement('tr');
    chartData.headers.forEach((header: string) => {
      const th = document.createElement('th');
      th.style.border = '1px solid #ddd';
      th.style.padding = '8px';
      th.style.backgroundColor = '#f8f9fa';
      th.style.fontWeight = 'bold';
      th.textContent = header;
      headerRow.appendChild(th);
    });
    table.appendChild(headerRow);

    // Add data rows
    chartData.data.slice(0, 10).forEach((row: any) => {
      const tr = document.createElement('tr');
      chartData.headers.forEach((header: string) => {
        const td = document.createElement('td');
        td.style.border = '1px solid #ddd';
        td.style.padding = '8px';
        td.textContent = String(row[header] || '');
        tr.appendChild(td);
      });
      table.appendChild(tr);
    });

    return table;
  };

  // Create a text-based chart
  const createTextChart = (chartData: any): HTMLElement => {
    const container = document.createElement('div');
    container.style.padding = '20px';
    container.style.fontFamily = 'Arial, sans-serif';

    if (chartData.title) {
      const title = document.createElement('h3');
      title.style.margin = '0 0 15px 0';
      title.style.color = '#333';
      title.textContent = chartData.title;
      container.appendChild(title);
    }

    const dataList = document.createElement('ul');
    dataList.style.listStyle = 'none';
    dataList.style.padding = '0';
    dataList.style.margin = '0';

    chartData.data.slice(0, 10).forEach((row: any) => {
      const li = document.createElement('li');
      li.style.padding = '5px 0';
      li.style.borderBottom = '1px solid #eee';
      li.textContent = `${row[chartData.headers[0]]}: ${row[chartData.headers[1]]}`;
      dataList.appendChild(li);
    });

    container.appendChild(dataList);
    return container;
  };

  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await axios.post<Project[]>(
        `${environment.apiUrl}/workspace/getProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            project_type: detectedProjectType === 'DATA_ANAL' ? ["DATA_ANAL"] :
              detectedContext === "banking" ? ["CUST_SRVY"] :
                detectedContext === "gov" ? ["CTZN_SRVY"] :
                  ["EMPL_SRVY"]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setProjects(response.data);

      // If there are projects, select the first one and fetch its details
      if (response.data.length > 0) {
        const firstProject = response.data[0];
        // Only update if project ID is different
        if (selectedProject?.id !== firstProject.id) {
          setSelectedProject(firstProject);
        }

        // Fetch detailed data for the first project
        try {
          const projectDetails = await axios.post<Project>(
            `${environment.apiUrl}/workspace/getProjectDetails`,
            {
              data: {
                id: firstProject.id
              }
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );
          // Only update if project ID is different
          if (selectedProject?.id !== projectDetails.data.id) {
            setSelectedProject(projectDetails.data);
          }
        } catch (error: any) {
          console.error('Error fetching first project details:', error);
          if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
            navigate('/login');
          }
        }
      }
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Fetch content types from API
  const fetchContentTypes = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/account/getContentTypes`,
        {
          data: {}
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setContentTypes(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching content types:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      // Set fallback content types if API fails
      setContentTypes([
        { contentType: 'SOCIAL_POST_LINKEDIN', contentTypeDescription: 'LinkedIn Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_FACEBOOK', contentTypeDescription: 'Facebook Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_TWITTER', contentTypeDescription: 'Twitter Post', class: 'Semi Formal' },
        { contentType: 'BLOG_POST', contentTypeDescription: 'Blog', class: 'Semi Formal' },
        { contentType: 'EMAIL', contentTypeDescription: 'Email', class: 'Semi Formal' },
        { contentType: 'CUSTOM', contentTypeDescription: 'Custom', class: 'Semi Formal' }
      ]);
    }
  };

  // Simple project-specific permission check
  const [canEditProject, setCanEditProject] = useState(false);
  const [canViewProject, setCanViewProject] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Refresh state
  const [lastFetchedTime, setLastFetchedTime] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchProjectPermissions = async () => {
    if (!selectedProject) return;

    const userId = localStorage.getItem('user_email') || 'demouser01@bahwancybertek.com';
    const organisationId = localStorage.getItem('org_id') || 'bctglobal';

    setLoadingPermissions(true);

    try {
      // Call the correct RBAC endpoint: /analytics/rbac/users/{userId}/project-permissions/{projectId}
      const response = await fetch(
        `${environment.apiUrl}/analytics/rbac/users/${userId}/project-permissions/${selectedProject.id}?organisationId=${organisationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check if user can view this project
        const canView = data.permissions?.canView || false;
        const canEdit = data.permissions?.canEdit || false;

        setCanViewProject(canView);
        setCanEditProject(canEdit);

      } else {
        console.error('❌ Project permission fetch failed:', response.status);
        setCanViewProject(false);
        setCanEditProject(false);
      }
    } catch (error) {
      console.error('❌ Project permission fetch error:', error);
      // Set default to false if fetch fails
      setCanViewProject(false);
      setCanEditProject(false);
    } finally {
      setLoadingPermissions(false);
    }
  };







  // Fetch project permissions when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProjectPermissions();
    }
  }, [selectedProject]);

  // Close chat when user doesn't have view permission
  useEffect(() => {
    if (canViewProject && detectedProjectType === 'DATA_ANAL') {
      setIsChatOpen(true);
    } else if (!canViewProject) {
      setIsChatOpen(false);
      setIsFullScreen(false);
    }
  }, [canViewProject, detectedProjectType]);

  // Ensure full screen state is synchronized with chat state
  useEffect(() => {
    if (!isChatOpen) {
      setIsFullScreen(false);
    }
  }, [isChatOpen]);



  const handleCreateProject = async () => {
    try {
      const response = await axios.post<ProjectResponse>(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            source: "10_org",
            project_items: [
              {
                project_name: newProject.project_name,
                project_description: newProject.project_description,
                project_type: detectedProjectType === 'DATA_ANAL' ? "DATA_ANAL" :
                  detectedContext === "banking" ? "CUST_SRVY" :
                    detectedContext === "gov" ? "CTZN_SRVY" :
                      "EMPL_SRVY",
                configuration: {
                  period_start_date: newProject.period_start_date,
                  period_end_date: newProject.period_end_date,
                  period_type: newProject.period_type
                }
              }
            ]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.upsertRef && response.data.upsertRef.length > 0) {
        // Fetch project details
        const projectDetails = await axios.post<Project>(
          `${environment.apiUrl}/workspace/getProjectDetails`,
          {
            data: {
              id: response.data.upsertRef[0]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        setProjects(prev => [...prev, projectDetails.data]);
        // Only update if project ID is different
        if (selectedProject?.id !== projectDetails.data.id) {
          setSelectedProject(projectDetails.data);
        }
        setDialogStep('upload');

        // Auto-assign permissions for the new project
        try {
          const autoAssignResponse = await axios.post(
            `${environment.apiUrl}/manage/project-permissions/auto-assign`,
            {
              projectId: projectDetails.data.id,
              userId: localStorage.getItem('user_email') || 'admin@example.com',
              organisationId: localStorage.getItem('org_id') || 'org123'
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );

          console.log('✅ Auto-assign permissions successful:', autoAssignResponse.data);

          // Refresh project permissions after auto-assign
          await fetchProjectPermissions();

        } catch (autoAssignError: any) {
          console.error('⚠️ Auto-assign permissions failed:', autoAssignError);
          // Don't show error to user as project creation was successful
          // Auto-assign is a convenience feature, not critical
        }
      }
    } catch (error: any) {
      console.error('Error creating project:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
    }
  };

  const handleProjectChange = async (projectId: string) => {
    try {
      // Check if project is actually changing
      const isProjectChanging = selectedProject?.id !== projectId;

      const response = await axios.post<Project>(
        `${environment.apiUrl}/workspace/getProjectDetails`,
        {
          data: {
            id: projectId
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Only update if project ID is different
      if (isProjectChanging) {
        setSelectedProject(response.data);
      }

      // Reset chat messages only when project actually changes
      if (isProjectChanging) {
        setMessages([
          {
            id: 1,
            text: "Hello! I'm your AI assistant. How can I help you analyze the survey data?",
            sender: 'bot',
            timestamp: new Date(),
          },
        ]);

        // Reset suggested questions to default
        setSuggestedQuestions([
          { text: "What are the key insights?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
          { text: "Show me positive feedback", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
          // { text: "Show me areas of improvement", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
          // { text: "What's the overall satisfaction?", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
          // { text: "Analyze employee sentiment", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
        ]);
      }
    } catch (error: any) {
      console.error('Error fetching project details:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return {
          background: 'rgba(76, 175, 80, 0.1)',
          color: '#2E7D32',
          border: '1px solid rgba(76, 175, 80, 0.2)'
        };
      case 'negative':
        return {
          background: 'rgba(244, 67, 54, 0.1)',
          color: '#C62828',
          border: '1px solid rgba(244, 67, 54, 0.2)'
        };
      default:
        return {
          background: 'rgba(255, 152, 0, 0.1)',
          color: '#FF9800',
          border: '1px solid rgba(255, 152, 0, 0.2)'
        };
    }
  };

  const getSentimentScoreColor = (score: number) => {
    if (score >= 0.6) return '#2E7D32';
    if (score >= 0.3) return '#FF9800';
    return '#C62828';
  };

  const SentimentMeter = ({ sentiment }: { sentiment: string }) => {
    const getMeterColor = (sentiment: string) => {
      switch (sentiment?.toLowerCase()) {
        case 'positive':
          return '#2E7D32';
        case 'negative':
          return '#C62828';
        default:
          return '#FF9800';
      }
    };

    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Box sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: getMeterColor(sentiment),
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }} />
        <Typography sx={{
          fontSize: '0.875rem',
          color: 'text.secondary',
          textTransform: 'capitalize'
        }}>
          {sentiment}
        </Typography>
      </Box>
    );
  };

  const CircularGauge = ({ sentiment }: { sentiment: string }) => {
    const getGaugeColor = (sentiment: string) => {
      switch (sentiment?.toLowerCase()) {
        case 'positive':
          return '#2E7D32';
        case 'negative':
          return '#C62828';
        default:
          return '#FF9800';
      }
    };

    const getGaugePercentage = (sentiment: string) => {
      switch (sentiment?.toLowerCase()) {
        case 'positive':
          return '60%';
        case 'negative':
          return '100%';
        default:
          return '80%';
      }
    };

    return (
      <Box sx={{
        position: 'relative',
        width: 100,
        height: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Background circle */}
        <Box sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `conic-gradient(
            #2E7D32 0% 60%,
            #FF9800 60% 80%,
            #C62828 80% 100%
          )`,
          opacity: 0.2
        }} />

        {/* Sentiment circle */}
        <Box sx={{
          position: 'absolute',
          width: '90%',
          height: '90%',
          borderRadius: '50%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            background: getGaugeColor(sentiment),
            opacity: 0.9
          }} />
        </Box>
      </Box>
    );
  };

  const handleDeviceFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;

    setUploadLoading(true);
    const file = event.target.files[0];

    // Generate unique interactionId for this upload
    const interactionId = generateInteractionId();
    console.log('Generated interactionId for file upload:', interactionId);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', `Bearer ${localStorage.getItem('token')}`);
    formData.append('type', 'docs');
    formData.append('org_id', localStorage.getItem("org_id") || '');
    formData.append('campaignId', selectedProject?.id || '');
    try {
      // Upload file
      const uploadResponse = await axios.post(
        `${environment.apiUrl}/account/uploadFileForOrg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (uploadResponse.data.status === 'success') {
        // Index the document with interactionId
        const indexResponse = await axios.post(
          `${environment.controlplaneUrl}/agent/flow/execute`,
          {
            data: {
              filePath: uploadResponse.data.path,
              sourceName: detectedProjectType === 'DATA_ANAL' ? "Data Analytics Dataset" : "Survey Data",
              fileType: "csv",
              contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
              campaignId: selectedProject?.id || "",
              org_id: localStorage.getItem("org_id")
            },
            user: {
              interactionId: interactionId
            },
            agent: {
              code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET"
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          }
        );

        // Wait 1 second before connecting to reasoning log
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Connect to reasoning log WebSocket
        connectReasoningLog(interactionId);

        // Close upload dialog and open notification popover
        setIsProjectDialogOpen(false);
        setDialogStep('project');
        // Open notification popover after a short delay to ensure it's rendered
        setTimeout(() => {
          if (notificationButtonRef.current) {
            setNotificationAnchorEl(notificationButtonRef.current);
          }
        }, 100);

        // setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
        // Refresh survey list
        fetchSurveyItems();
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        navigate('/login');
      }
      // setError('Failed to upload and index survey data');
      console.error('Error uploading survey data:', err);
    } finally {
      setUploadLoading(false);
    }
  };

  // Helper function to analyze URL type and determine handling strategy
  const analyzeUrl = (url: string) => {
    const urlObj = new URL(url);

    // Check for public file hosting services
    const publicServices = [
      'githubusercontent.com',
      'raw.githubusercontent.com',
      'gist.githubusercontent.com',
      'jsdelivr.net',
      'unpkg.com',
      'cdn.jsdelivr.net',
      'cdnjs.cloudflare.com',
      'stackpath.bootstrapcdn.com'
    ];

    const isPublicService = publicServices.some(service => url.includes(service));

    // Check for complex authentication services
    const complexAuthServices = [
      'sharepoint.com',
      'office365.com',
      'onedrive.live.com',
      'drive.google.com',
      'docs.google.com',
      'sheets.google.com',
      'dropbox.com',
      'box.com'
    ];

    const isComplexAuth = complexAuthServices.some(service => url.includes(service));

    // Check for simple token-based auth patterns
    const hasTokenAuth = url.includes('token=') || url.includes('access_token=') || url.includes('bearer=');

    if (isPublicService) {
      return { type: 'public', strategy: 'direct-fetch' };
    } else if (hasTokenAuth) {
      return { type: 'authenticated', strategy: 'token-auth' };
    } else if (isComplexAuth) {
      return { type: 'complex', strategy: 'manual-download' };
    } else {
      return { type: 'unknown', strategy: 'try-direct' };
    }
  };

  // Helper function to check file size before downloading
  const checkFileSize = async (url: string, headers?: HeadersInit): Promise<number | null> => {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        return contentLength ? parseInt(contentLength) : null;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const validateAndDownloadFile = async (url: string): Promise<File | null> => {
    setIsUrlValidating(true);
    setUrlValidationError('');

    try {
      // First, validate the URL
      const urlObj = new URL(url);
      if (!urlObj.protocol.startsWith('http')) {
        throw new Error('Invalid URL protocol. Please provide a valid HTTP/HTTPS URL.');
      }

      // Analyze the URL to determine handling strategy
      const urlAnalysis = analyzeUrl(url);
      setUrlType(urlAnalysis.type as 'public' | 'authenticated' | 'complex' | 'unknown');


      // Handle different URL types
      switch (urlAnalysis.strategy) {
        case 'direct-fetch':
          return await handlePublicUrl(url);

        case 'token-auth':
          return await handleTokenAuthUrl(url);

        case 'manual-download':
          return await handleComplexAuthUrl(url);

        case 'try-direct':
          return await handleUnknownUrl(url);

        default:
          throw new Error('Unsupported URL type');
      }

    } catch (error: any) {
      console.error('Error downloading file from URL:', error);
      handleDownloadError(error, url);
      return null;
    } finally {
      setIsUrlValidating(false);
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Handle public URLs (GitHub, CDN, etc.)
  const handlePublicUrl = async (url: string): Promise<File | null> => {

    // Check file size first
    const size = await checkFileSize(url);
    setFileSize(size);

    if (size && size > 100 * 1024 * 1024) { // 100MB limit
      setIsLargeFile(true);
      setUrlValidationError('File is too large (>100MB). Please download manually and upload from device.');
      return null;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = url.split('/').pop() || 'downloaded-file.csv';
      const file = new File([blob], fileName, { type: blob.type });

      setDownloadProgress(100);
      return await processDownloadedFile(file);

    } catch (error: any) {
      console.error('Error downloading public URL:', error);
      throw error;
    }
  };

  // Handle token-based authenticated URLs
  const handleTokenAuthUrl = async (url: string): Promise<File | null> => {

    // Show credentials dialog if not already provided
    if (!authCredentials) {
      setShowAuthCredentialsDialog(true);
      return new Promise((resolve, reject) => {
        window.addEventListener('message', function authHandler(event) {
          if (event.data.type === 'AUTH_CREDENTIALS_PROVIDED') {
            window.removeEventListener('message', authHandler);
            setShowAuthCredentialsDialog(false);

            if (event.data.credentials) {
              setAuthCredentials(event.data.credentials);
              // Retry with credentials
              handleTokenAuthUrl(url).then(resolve).catch(reject);
            } else {
              reject(new Error('Authentication cancelled'));
            }
          }
        });
      });
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const headers: HeadersInit = {
        'Accept': '*/*',
      };

      if (authCredentials.type === 'bearer') {
        headers['Authorization'] = `Bearer ${authCredentials.token}`;
      } else if (authCredentials.type === 'basic') {
        headers['Authorization'] = `Basic ${authCredentials.token}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = url.split('/').pop() || 'downloaded-file.csv';
      const file = new File([blob], fileName, { type: blob.type });

      setDownloadProgress(100);
      return await processDownloadedFile(file);

    } catch (error: any) {
      console.error('Error downloading authenticated URL:', error);
      throw error;
    }
  };

  // Handle complex authentication (SharePoint, Google Drive, etc.)
  const handleComplexAuthUrl = async (url: string): Promise<File | null> => {

    const isSharePoint = url.includes('sharepoint.com') || url.includes('office365.com');
    const isGoogleDrive = googleDriveService.current ? googleDriveService.current.isGoogleDriveUrl(url) : (url.includes('drive.google.com') || url.includes('docs.google.com') || url.includes('sheets.google.com'));

    if (isSharePoint) {
      setSharePointUrl(url);
      setShowSharePointAuth(true);

      // Open SharePoint in a new tab for authentication
      const newWindow = window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      setAuthWindow(newWindow);

      return new Promise((resolve, reject) => {
        window.addEventListener('message', function authHandler(event) {
          if (event.data.type === 'SHAREPOINT_AUTH_COMPLETED') {
            window.removeEventListener('message', authHandler);
            setShowSharePointAuth(false);
            if (authWindow) {
              authWindow.close();
              setAuthWindow(null);
            }

            if (event.data.success) {
              resolve(null); // Return null to indicate manual upload needed
            } else {
              reject(new Error('SharePoint authentication cancelled by user'));
            }
          }
        });
      });
    } else if (isGoogleDrive) {
      // For Google Drive/Sheets, first try direct download for public files

      try {
        // Convert Google Drive URL to direct download format
        const directDownloadUrl = convertGoogleDriveUrl(url);

        const response = await fetch(directDownloadUrl, {
          method: 'GET',
          headers: {
            'Accept': '*/*',
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const fileName = getFileNameFromGoogleUrl(url) || 'google-drive-file.csv';
          const file = new File([blob], fileName, { type: blob.type });

          setDownloadProgress(100);
          return await processDownloadedFile(file);
        } else {
          throw new Error('Direct download failed');
        }
      } catch (error) {
        // Fall back to manual authentication
        setGoogleUrl(url);
        setShowGoogleAuth(true);

        // Open Google Drive/Sheets in a new tab for authentication
        const newWindow = window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
        setGoogleWindow(newWindow);

        return new Promise((resolve, reject) => {
          window.addEventListener('message', function authHandler(event) {
            if (event.data.type === 'GOOGLE_AUTH_COMPLETED') {
              window.removeEventListener('message', authHandler);
              setShowGoogleAuth(false);
              if (googleWindow) {
                googleWindow.close();
                setGoogleWindow(null);
              }

              if (event.data.success) {
                resolve(null); // Return null to indicate manual upload needed
              } else {
                reject(new Error('Google authentication cancelled by user'));
              }
            }
          });
        });
      }
    } else {
      // For other complex auth services, show manual download instructions
      setUrlValidationError('This URL requires complex authentication. Please download the file manually and upload from device.');
      return null;
    }
  };

  // Helper function to convert Google Drive URL to direct download format
  const convertGoogleDriveUrl = (url: string): string => {
    if (!googleDriveService.current) return url;
    return googleDriveService.current.convertGoogleDriveUrl(url);
  };

  // Helper function to extract filename from Google URL
  const getFileNameFromGoogleUrl = (url: string): string => {
    if (!googleDriveService.current) return 'google-file';
    return googleDriveService.current.getFileNameFromGoogleUrl(url);
  };

  // Handle unknown URLs (try direct download)
  const handleUnknownUrl = async (url: string): Promise<File | null> => {

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': '*/*',
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // Authentication required - show credentials dialog
          setShowAuthCredentialsDialog(true);
          return new Promise((resolve, reject) => {
            window.addEventListener('message', function authHandler(event) {
              if (event.data.type === 'AUTH_CREDENTIALS_PROVIDED') {
                window.removeEventListener('message', authHandler);
                setShowAuthCredentialsDialog(false);

                if (event.data.credentials) {
                  setAuthCredentials(event.data.credentials);
                  // Retry with credentials
                  handleTokenAuthUrl(url).then(resolve).catch(reject);
                } else {
                  reject(new Error('Authentication cancelled'));
                }
              }
            });
          });
        } else {
          throw new Error(`Failed to download file: ${response.status}`);
        }
      }

      const blob = await response.blob();
      const fileName = url.split('/').pop() || 'downloaded-file.csv';
      const file = new File([blob], fileName, { type: blob.type });

      setDownloadProgress(100);
      return await processDownloadedFile(file);

    } catch (error: any) {
      console.error('Error downloading unknown URL:', error);
      throw error;
    }
  };

  // Handle download errors with specific guidance
  const handleDownloadError = (error: any, url: string) => {
    if (error.message?.includes('Authentication cancelled')) {
      setUrlValidationError('Download cancelled. Please try again when you\'re ready to authenticate.');
    } else if (error.response?.status === 403) {
      setUrlValidationError('Access denied. Please ensure you have permission to access this file and try again.');
    } else if (error.response?.status === 404) {
      setUrlValidationError('File not found. Please check if the URL is correct and the file exists.');
    } else if (error.code === 'ECONNABORTED') {
      setUrlValidationError('Download timeout. The file might be too large or the server is slow. Please try again.');
    } else if (error.message?.includes('CORS')) {
      setUrlValidationError('CORS error. Please try a different URL or download the file locally and upload from device.');
    } else if (error.message?.includes('Failed to fetch')) {
      setUrlValidationError('Network error. Please check your internet connection and try again.');
    } else {
      setUrlValidationError(error.message || 'Failed to download file from URL. Please check the URL and try again.');
    }
  };

  const handleUrlUpload = async () => {
    if (!fileUrl.trim()) {
      // setError('Please enter a valid file URL');
      return;
    }

    setUploadLoading(true);
    setError(null);
    setSuccess('');

    try {
      // Download and index the file using our backend
      const result = await validateAndDownloadFile(fileUrl);

      if (result === null && !urlValidationError) {
        // Success - the backend handled everything
        setFileUrl('');
        // Refresh survey list
        fetchSurveyItems();
      } else if (result) {
        // If we got a file back (fallback case), process it
        const formData = new FormData();
        formData.append('file', result);
        formData.append('token', `Bearer ${localStorage.getItem('token')}`);
        formData.append('type', 'docs');
        formData.append('org_id', localStorage.getItem("org_id") || '');

        // Upload file
        const uploadResponse = await axios.post(
          `${environment.apiUrl}/account/uploadFileForOrg`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        );

        if (uploadResponse.data.status === 'success') {
          // Generate unique interactionId for this upload
          const interactionId = generateInteractionId();
          console.log('Generated interactionId for URL upload:', interactionId);

          // Index the document with interactionId
          const indexResponse = await axios.post(
            `${environment.controlplaneUrl}/agent/flow/execute`,
            {
              data: {
                filePath: uploadResponse.data.path,
                sourceName: detectedProjectType === 'DATA_ANAL' ? "Data Analytics Dataset" : "Survey Data",
                fileType: "csv",
                contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
                campaignId: selectedProject?.id || "",
                org_id: localStorage.getItem("org_id")
              },
              user: {
                interactionId: interactionId
              },
              agent: {
                code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET"
              }
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              }
            }
          );

          // Wait 1 second before connecting to reasoning log
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Connect to reasoning log WebSocket
          connectReasoningLog(interactionId);

          // Close upload dialog and open notification popover
          setIsProjectDialogOpen(false);
          setDialogStep('project');
          // Open notification popover after a short delay to ensure it's rendered
          setTimeout(() => {
            if (notificationButtonRef.current) {
              setNotificationAnchorEl(notificationButtonRef.current);
            }
          }, 100);

          // setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
          setFileUrl('');
          // Refresh survey list
          fetchSurveyItems();
        }
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        navigate('/login');
      }
      // setError('Failed to upload and index survey data from URL');
      console.error('Error uploading survey data from URL:', err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsProjectDialogOpen(false);
    setDialogStep('project');
    setNewProject({
      project_name: '',
      project_description: '',
      period_start_date: new Date().toISOString().split('T')[0],
      period_end_date: new Date().toISOString().split('T')[0],
      period_type: 'D'
    });
    setSuccess('');
    setError('');
    setUploadMethod('device');
    setFileUrl('');
    setUrlValidationError('');
    setDownloadProgress(0);
  };

  // Add this function to calculate summary metrics
  const calculateSummaryMetrics = (surveys: SurveyItem[]) => {
    setTotalResponses(surveys.length);

    // Get sentiment distribution from dashboard data
    const sentimentData = processSurveyData(surveys).sentimentDistribution;

    // Calculate total responses from sentiment distribution
    const totalSentimentResponses = sentimentData.reduce((sum, item) => sum + item.value, 0);

    // Find positive responses count
    const positiveItem = sentimentData.find(item => item.name === 'Positive');
    const positiveCount = positiveItem ? positiveItem.value : 0;

    // Calculate percentages
    if (totalSentimentResponses > 0) {
      setPositiveResponses(Math.round((positiveCount / totalSentimentResponses) * 100));
    } else {
      setPositiveResponses(0);
    }

    // Calculate average sentiment score
    let totalScore = 0;
    let validResponses = 0;

    surveys.forEach(survey => {
      const sentimentEntry = survey.entries.find(e => e.key === 'contentSentiment');
      if (sentimentEntry?.value) {
        const sentimentData = sentimentEntry.value as any;
        if (sentimentData.overall_score !== undefined) {
          totalScore += sentimentData.overall_score;
          validResponses++;
        }
      }
    });

    if (validResponses > 0) {
      setAverageSentiment(Math.round((totalScore / validResponses) * 100));
    } else {
      setAverageSentiment(0);
    }
  };

  // Helper to filter survey items by search query
  const getFilteredSurveyItems = () => {
    if (!searchQuery.trim()) return surveyItems;
    const query = searchQuery.trim().toLowerCase();
    return surveyItems.filter(item => {
      const title = getEntryValue(item.entries, 'title').toLowerCase();
      const email = getEntryValue(item.entries, 'email').toLowerCase();
      const highlights = getEntryValue(item.entries, 'highlights').toLowerCase();
      return (
        title.includes(query) ||
        email.includes(query) ||
        highlights.includes(query)
      );
    });
  };

  // Update getCurrentPageItems to use filtered items in dialog
  const getCurrentPageDialogItems = () => {
    const filtered = getFilteredSurveyItems();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Helper function to process downloaded files
  const processDownloadedFile = async (file: File): Promise<File | null> => {
    setDownloadProgress(100);
    setSuccess('File downloaded successfully! Now uploading...');

    // Generate unique interactionId for this upload
    const interactionId = generateInteractionId();
    console.log('Generated interactionId for downloaded file upload:', interactionId);

    // Upload the downloaded file using the same process as device upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', `Bearer ${localStorage.getItem('token')}`);
    formData.append('type', 'docs');
    formData.append('org_id', localStorage.getItem("org_id") || '');

    const uploadResponse = await axios.post(
      `${environment.apiUrl}/account/uploadFileForOrg`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }
    );

    if (uploadResponse.data.status === 'success') {
      // Index the document using the same API as device upload with interactionId
      const indexResponse = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            filePath: uploadResponse.data.path,
            sourceName: detectedProjectType === 'DATA_ANAL' ? "Data Analytics Dataset from URL" : "Survey Data from URL",
            fileType: file.name.toLowerCase().includes('.xlsx') ? 'excel' : 'csv',
            contentType: detectedProjectType === 'DATA_ANAL' ? "DATA_RECORD" : "USER_FEEDBACK",
            campaignId: selectedProject?.id || "",
            org_id: localStorage.getItem("org_id")
          },
          user: {
            interactionId: interactionId
          },
          agent: {
            code: detectedProjectType === 'DATA_ANAL' ? "INDEX_GENERAL_DATASET" : "INDEX_USER_FEEDBACK_DATASET"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Wait 1 second before connecting to reasoning log
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Connect to reasoning log WebSocket
      connectReasoningLog(interactionId);

      // Close upload dialog if open and open notification popover
      setIsProjectDialogOpen(false);
      setDialogStep('project');
      // Open notification popover after a short delay to ensure it's rendered
      setTimeout(() => {
        if (notificationButtonRef.current) {
          setNotificationAnchorEl(notificationButtonRef.current);
        }
      }, 100);

      // setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
      return null;
    } else {
      throw new Error('Failed to upload downloaded file');
    }
  };

  // Google Drive Authentication Functions
  const initiateGoogleDriveAuth = async () => {
    try {
      setIsGoogleDriveAuthInProgress(true);
      setError(''); // Clear any previous errors

      if (!googleDriveService.current) {
        setError('Google Drive service not initialized');
        setIsGoogleDriveAuthInProgress(false);
        return;
      }

      const { sessionId, authUrl } = await googleDriveService.current.initiateAuth();

      setGoogleDriveAuthUrl(authUrl);
      setShowGoogleDriveAuthDialog(true);

      const authWindow = googleDriveService.current.openAuthWindow(authUrl);

      if (!authWindow) {
        setError('Popup blocked! Please allow popups for this site and try again.');
        setIsGoogleDriveAuthInProgress(false);
        return;
      }
      setGoogleDriveAuthWindow(authWindow);

      // Check for authentication completion
      const checkAuthStatus = setInterval(async () => {
        try {
          let isWindowClosed = false;
          let isOnCallbackPage = false;

          try {
            isWindowClosed = authWindow?.closed || false;
          } catch (error) {
            isWindowClosed = false;
          }

          // Check if window is on callback page (indicating successful auth)
          try {
            const currentUrl = authWindow?.location?.href || '';
            isOnCallbackPage = currentUrl.includes('/analytics/google-drive/auth/callback');
          } catch (error) {
            // Ignore cross-origin restrictions
          }

          if (isWindowClosed || isOnCallbackPage) {
            clearInterval(checkAuthStatus);
            setShowGoogleDriveAuthDialog(false);
            setGoogleDriveAuthWindow(null);
            setIsGoogleDriveAuthInProgress(false);

            // Test if authentication was successful using auth status endpoint
            try {
              // Get the sessionId we stored earlier
              const sessionId = localStorage.getItem('googleDriveSessionId');

              if (!sessionId) {
                setError('Authentication failed: No session ID found. Please try again.');
                return;
              }

              // Add a small delay to allow backend to process tokens
              setSuccess('Processing authentication...');
              await new Promise(resolve => setTimeout(resolve, 2000));

              // Try auth status check first
              if (!googleDriveService.current) {
                setError('Google Drive service not initialized');
                return;
              }
              let isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);

              if (isAuthenticated) {
                setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                  ...prev,
                  isAuthenticated: true,
                  sessionId: sessionId
                }));

                // Load files and open dialog
                await loadGoogleDriveFilesWithSessionId(sessionId);
                setShowGoogleDriveDialog(true);
              } else {
                // If auth status says not authenticated but we have a sessionId, 
                // try to load files directly to see if authentication actually worked
                try {
                  setSuccess('Verifying authentication by loading files...');
                  // Try to load files directly to see if authentication actually worked
                  try {
                    const result = await googleDriveService.current.loadFiles(sessionId, undefined, undefined, 1, 5);

                    // Authentication actually worked, just the status endpoint is lagging
                    setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                      ...prev,
                      isAuthenticated: true,
                      sessionId: sessionId
                    }));

                    // Load files and open dialog
                    await loadGoogleDriveFilesWithSessionId(sessionId);
                    setShowGoogleDriveDialog(true);
                  } catch (fileError) {
                    // Try one more time with a longer delay
                    setSuccess('Authentication processing, please wait...');
                    await new Promise(resolve => setTimeout(resolve, 3000));

                    isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);
                    if (isAuthenticated) {
                      setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                        ...prev,
                        isAuthenticated: true,
                        sessionId: sessionId
                      }));

                      // Load files and open dialog
                      await loadGoogleDriveFilesWithSessionId(sessionId);
                      setShowGoogleDriveDialog(true);
                    } else {
                      setError('Authentication was cancelled or failed. Please try again.');
                    }
                  }
                } catch (fileError) {
                  setError('Authentication was cancelled or failed. Please try again.');
                }
              }
            } catch (error) {
              setError('Authentication was cancelled or failed. Please try again.');
            }
          }
        } catch (error: any) {
          // Auth not complete yet
        }
      }, 2000);

      // Cleanup interval after 5 minutes
      setTimeout(() => {
        clearInterval(checkAuthStatus);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
          setGoogleDriveAuthWindow(null);
        }
        setShowGoogleDriveAuthDialog(false);
        setIsGoogleDriveAuthInProgress(false);
      }, 300000); // 5 minutes timeout
    } catch (error: any) {
      setIsGoogleDriveAuthInProgress(false);

      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        setError(`Google Drive authentication failed: ${error?.message}. Please try again.`);
      }
    }
  };

  // Add function to clear Google Drive authentication
  const clearGoogleDriveAuth = async () => {
    try {
      // Clear any stored files first
      setGoogleDriveFiles([]);
      setSelectedGoogleFile(null);
      setSelectedGoogleFiles([]);

      // Call backend to revoke tokens if needed
      if (googleDriveService.current && googleDriveAuthState.sessionId) {
        await googleDriveService.current.clearAuth(googleDriveAuthState.sessionId);
      }

      // Clear the authentication state with a new session ID
      const newSessionId = `survey_insights_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      setGoogleDriveAuthState({
        isAuthenticated: false,
        sessionId: newSessionId
      });

      setError(''); // Clear any previous errors
      setSuccess('Google Drive authentication cleared. You can now authenticate with a different account.');
    } catch (error) {
      console.error('Error clearing Google Drive auth:', error);
    }
  };

  const loadGoogleDriveFilesWithSessionId = async (sessionId: string, searchQuery?: string, folderId?: string) => {
    if (!sessionId) {
      setError('Session ID is required. Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      const result = await googleDriveService.current.loadFiles(
        sessionId,
        searchQuery,
        folderId,
        googleDriveCurrentPage,
        1000

      );

      // Update current folder ID if available
      if (result.folderId) {
        setCurrentFolderId(result.folderId);
      }

      setError(''); // Clear any previous errors
    } catch (error: any) {
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else if (error?.message === 'Session not authenticated') {
        setError('Session expired or invalid. Click "Recover Session" to re-authenticate.');
        // Don't reset state immediately, let user choose recovery option
      } else {
        setError(error?.message || 'Failed to load Google Drive files');
      }
    }
  };

  const loadGoogleDriveFiles = async (searchQuery?: string) => {
    // Check if authenticated first
    if (!googleDriveAuthState.isAuthenticated) {
      setError('Please authenticate with Google Drive first. Click "Connect to Google Drive" to get started.');
      return;
    }

    // If we have a session ID, use it
    if (googleDriveAuthState.sessionId) {
      await loadGoogleDriveFilesWithSessionId(googleDriveAuthState.sessionId, searchQuery);
    } else {
      setError('No session ID available. Please authenticate with Google Drive first.');
    }
  };

  // Navigate to a specific folder
  const navigateToFolder = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    setCurrentFolderId(folderId);
    setGoogleDriveCurrentPage(1); // Reset to first page

    // Load files and get breadcrumb for the folder
    await Promise.all([
      loadGoogleDriveFilesWithSessionId(googleDriveAuthState.sessionId, undefined, folderId),
      getFolderBreadcrumb(folderId)
    ]);
  };

  // Navigate to breadcrumb item
  const navigateToBreadcrumb = async (folderId: string) => {
    await navigateToFolder(folderId);
  };

  // Get folder breadcrumb
  const getFolderBreadcrumb = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return [];
    }

    try {
      const breadcrumb = await googleDriveService.current.getFolderBreadcrumb(
        googleDriveAuthState.sessionId,
        folderId
      );
      return breadcrumb;
    } catch (error: any) {
      console.error('Error getting breadcrumb:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else if (error?.message === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
        setGoogleDriveAuthState({
          isAuthenticated: false,
          sessionId: ''
        });
      }
      return [];
    }
  };

  // Get file preview information
  const getFilePreviewInfo = async (fileId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.getFilePreviewInfo(
        googleDriveAuthState.sessionId,
        fileId
      );
    } catch (error: any) {
      console.error('Error getting file preview info:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to get file preview information');
    }
  };

  // Check authentication status
  const checkGoogleDriveAuthStatus = async (sessionId: string) => {
    if (!googleDriveService.current) {
      return false;
    }

    try {
      return await googleDriveService.current.checkAuthStatus(sessionId);
    } catch (error: any) {
      return false;
    }
  };

  // Load folder tree structure
  const loadFolderTree = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.loadFolderTree(
        googleDriveAuthState.sessionId,
        'root',
        3
      );
    } catch (error: any) {
      console.error('Error loading folder tree:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to load folder tree');
    }
  };

  const searchGoogleDriveFiles = async () => {
    // Frontend-only search - no API calls needed
    // Files are filtered in real-time as user types
  };

  const downloadGoogleDriveFile = async (file: GoogleDriveFile) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsDownloadingGoogleFile(true);
    setGoogleDriveDownloadProgress(0);
    setSelectedGoogleFile(file);

    try {
      // Get download URL and download file
      const downloadUrl = await googleDriveService.current.getDownloadUrl(
        googleDriveAuthState.sessionId,
        file.id
      );

      const downloadedFile = await googleDriveService.current.downloadFile(
        downloadUrl,
        file.name,
        (progress) => setGoogleDriveDownloadProgress(progress)
      );

      // Process the downloaded file using existing upload flow
      await processDownloadedFile(downloadedFile);

      // setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
      setShowGoogleDriveDialog(false);
      setSelectedGoogleFile(null);
    } catch (error: any) {
      console.error('Error downloading Google Drive file:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError(`Failed to download ${file.name}`);
    } finally {
      setIsDownloadingGoogleFile(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  // Add function to check download status (for large files)
  const checkDownloadStatus = async (fileId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      return null;
    }

    if (!googleDriveService.current) {
      return null;
    }

    try {
      return await googleDriveService.current.checkDownloadStatus(
        googleDriveAuthState.sessionId,
        fileId
      );
    } catch (error) {
      console.error('Error checking download status:', error);
      return null;
    }
  };

  // Add function to get file info
  const getFileInfo = async (file: GoogleDriveFile) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.getFileInfo(
        googleDriveAuthState.sessionId,
        file.id
      );
    } catch (error: any) {
      console.error('Error getting file info:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to get file information');
    }
  };

  // Add function to download multiple files
  const downloadMultipleGoogleDriveFiles = async (files: GoogleDriveFile[]) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsDownloadingMultipleFiles(true);
    setGoogleDriveDownloadProgress(0);

    try {
      const downloadedFiles = await googleDriveService.current.downloadMultipleFiles(
        googleDriveAuthState.sessionId,
        files,
        (progress) => setGoogleDriveDownloadProgress(progress)
      );

      if (downloadedFiles.length > 0) {
        setGoogleDriveDownloadProgress(100);

        // Process the first file (or you could implement a way to handle multiple files)
        await processDownloadedFile(downloadedFiles[0]);

        // setSuccess('Document uploaded successfully, AI system is processing your document. Refresh the data in a few minutes to view the extracted data.');
        setShowGoogleDriveDialog(false);
        setSelectedGoogleFiles([]);
      } else {
        setError('No files were successfully downloaded');
      }
    } catch (error: any) {
      console.error('Error downloading multiple Google Drive files:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError(`Failed to download ${files.length} files`);
    } finally {
      setIsDownloadingMultipleFiles(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  // Helper functions for file type detection and image preview
  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return '📈';
    if (mimeType?.includes('text')) return '📄';
    if (mimeType?.includes('video')) return '🎥';
    if (mimeType?.includes('audio')) return '🎵';
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return '📦';
    if (mimeType?.includes('code') || mimeType?.includes('javascript') || mimeType?.includes('python')) return '💻';
    return '📄';
  };

  const getFileTypeLabel = (mimeType: string) => {
    if (mimeType?.includes('image')) return 'IMAGE';
    if (mimeType?.includes('pdf')) return 'PDF';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'DOC';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return 'XLS';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return 'PPT';
    if (mimeType?.includes('text')) return 'TXT';
    if (mimeType?.includes('video')) return 'VIDEO';
    if (mimeType?.includes('audio')) return 'AUDIO';
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return 'ZIP';
    if (mimeType?.includes('code') || mimeType?.includes('javascript') || mimeType?.includes('python')) return 'CODE';
    return 'FILE';
  };

  // Enhanced image preview component with fallback
  const ImagePreview = ({ file }: { file: GoogleDriveFile }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    // Try to load thumbnail if available
    const hasThumbnail = file.thumbnailLink && !imageError;

    // Get the same background gradient as other file types
    const getImageBackground = (mimeType: string) => {
      if (mimeType?.includes('image')) {
        // Use a beautiful gradient for images
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      }
      return 'linear-gradient(135deg, #4c3794 0%, #6B46C1 100%)';
    };

    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        background: getImageBackground(file.mimeType),
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(1px)'
        }
      }}>
        {hasThumbnail && imageLoading ? (
          <Box
            component="img"
            src={file.thumbnailLink}
            alt={file.name}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              position: 'relative',
              zIndex: 1
            }}
          />
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            height: '100%',
            width: '100%',
            zIndex: 1,
            position: 'relative'
          }}>
            <Typography sx={{
              fontSize: '2.5rem',
              mb: 0.5,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }}>
              {getFileTypeIcon(file.mimeType)}
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.9,
              textAlign: 'center'
            }}>
              {getFileTypeLabel(file.mimeType)}
            </Typography>
          </Box>
        )}

        {/* Loading overlay */}
        {imageLoading && hasThumbnail && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '8px',
            zIndex: 2
          }}>
            <CircularProgress size={24} sx={{ color: '#4c3794' }} />
          </Box>
        )}
      </Box>
    );
  };

  // Tab handling functions
  const handleTabChange = (newTab: 'myDrive' | 'shared' | 'teamDrives') => {
    setActiveTab(newTab);
    setSelectedTeamDrive(null);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
  };

  const handleTeamDriveSelect = (teamDriveId: string) => {
    setSelectedTeamDrive(teamDriveId);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
  };

  // Load files for active tab
  const loadFilesForActiveTab = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    try {
      setIsLoadingGoogleFiles(true);
      setError('');

      let result;

      switch (activeTab) {
        case 'myDrive':
          if (selectedTeamDrive) {
            result = await googleDriveService.current!.loadTeamDriveFiles(
              googleDriveAuthState.sessionId,
              selectedTeamDrive,
              50
            );
          } else {
            result = await googleDriveService.current!.loadMyDriveFiles(
              googleDriveAuthState.sessionId,
              50
            );
          }
          break;
        case 'shared':
          result = await googleDriveService.current!.loadSharedFiles(
            googleDriveAuthState.sessionId,
            50
          );
          break;
        case 'teamDrives':
          result = await googleDriveService.current!.loadTeamDrives(
            googleDriveAuthState.sessionId,
            50
          );
          setTeamDrives(result.drives);
          return; // Don't set files for team drives tab
      }

      setGoogleDriveFiles(result.files);
      setGoogleDriveTotalFiles(result.totalFiles);
    } catch (error: any) {
      console.error('Error loading files for tab:', error);
      if (error.message === 'jwt_expired') {
        navigate('/login');
      } else if (error.message === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
      } else {
        setError(error.message || 'Failed to load files');
      }
    } finally {
      setIsLoadingGoogleFiles(false);
    }
  };

  const handleGoogleDriveFileSelect = (file: GoogleDriveFile, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select mode
      setSelectedGoogleFiles(prev => {
        const isSelected = prev.some(f => f.id === file.id);
        if (isSelected) {
          return prev.filter(f => f.id !== file.id);
        } else {
          return [...prev, file];
        }
      });
      setSelectedGoogleFile(null);
    } else {
      // Single select mode
      setSelectedGoogleFile(file);
      setSelectedGoogleFiles([]);
    }
  };

  const openGoogleDriveDialog = () => {
    // Clear any previous errors when starting fresh
    setError('');

    if (!googleDriveAuthState.isAuthenticated) {
      // If not authenticated, start the authentication process
      initiateGoogleDriveAuth();
    } else {
      // If already authenticated, show the file selection dialog
      setShowGoogleDriveDialog(true);
      loadGoogleDriveFiles();
    }
  };

  // Handle session recovery and re-authentication
  const handleSessionRecovery = async () => {
    // Clear the current session
    const currentSessionId = googleDriveAuthState.sessionId || localStorage.getItem('googleDriveSessionId');
    if (currentSessionId) {
      try {
        // Try to revoke the current session
        if (googleDriveService.current) {
          await googleDriveService.current.clearAuth(currentSessionId);
        }
      } catch (error) {
        // Ignore revocation errors
      }
    }

    // Clear local state
    setGoogleDriveAuthState({
      isAuthenticated: false,
      sessionId: ''
    });
    localStorage.removeItem('googleDriveSessionId');

    // Start fresh authentication
    setError('');
    setSuccess('Starting fresh authentication...');
    await initiateGoogleDriveAuth();
  };

  const closeGoogleDriveDialog = () => {
    setShowGoogleDriveDialog(false);
    setSelectedGoogleFile(null);
    setSelectedGoogleFiles([]);
    setGoogleDriveSearchQuery('');
    setGoogleDriveCurrentPage(1);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
    setShowFilePreview(false);
    setFilePreviewInfo(null);
    setPreviewUrl('');
    setPreviewContent('');
    setShowFolderTree(false);
    setFolderTree([]);
  };

  const formatFileSize = (bytes: string) => {
    if (!googleDriveService.current) return '0 Bytes';
    return googleDriveService.current.formatFileSize(bytes);
  };

  const formatDate = (dateString: string) => {
    if (!googleDriveService.current) return dateString;
    return googleDriveService.current.formatDate(dateString);
  };

  const getFileIcon = (mimeType: string) => {
    if (!googleDriveService.current) return '📁';
    return googleDriveService.current.getFileIcon(mimeType);
  };

  const isDataAnal = detectedProjectType === 'DATA_ANAL';

  const menuSections = [
    {
      label: 'Documents',
      items: [
        { id: 'files', label: 'All Documents', icon: FileText },
      ]
    },
    {
      label: 'Analysis',
      items: [
        {
          id: 'executive',
          label: isDataAnal ? 'Chat' : 'Executive Summary',
          icon: SummaryIcon,
          mainTab: 'executive'
        },
        ...(!isDataAnal ? [
          { id: 'segment', label: 'Segment Analysis', icon: TrendingUpIcon, mainTab: 'segment' },
          { id: 'driver', label: 'Driver Analysis', icon: ShowChartIcon, mainTab: 'driver' },
        ] : []),
      ]
    },
    {
      label: 'Actions',
      items: [
        ...(!isDataAnal ? [
          { id: 'viewAll', label: 'View All Responses', icon: OpenInNewIcon, action: 'viewAll' },
        ] : []),
        { id: 'refresh', label: 'Refresh Data', icon: RefreshIcon, action: 'refresh' },
        { id: 'add', label: 'Add Document', icon: AddIcon, action: 'add' },
      ]
    }
  ];


  // Ref to store the apiConfig object to prevent unnecessary Chatbot resets
  const chatbotApiConfigRef = useRef({
    baseUrl: environment.controlplaneUrl,
    queryEndpoint: '/agent/flow/execute',
    campaignId: '',
    agentCode: detectedProjectType === 'DATA_ANAL' ? 'QUERY_GENERAL_DATASET_AND_RESPOND' : 'QUERY_USER_FEEDBACK_AND_RESPOND',
    contentTypesEndpoint: '/account/getContentTypes',
    generateContentEndpoint: '/agent/flow/execute',
    customQueryParams: {}
  });

  // Memoize apiConfig to prevent unnecessary Chatbot resets
  // Only update when campaignId or selectedDocUIDs actually changes
  const currentCampaignId = selectedProject?.id || '';
  // Get the active selection based on current tab
  const activeSelectedDocUIDs = getActiveSelectedDocUIDs();
  const docUIDsArray = Array.from(activeSelectedDocUIDs);
  const chatbotApiConfig = useMemo(() => {
    // Create customQueryParams with docUIDs if any are selected
    const customQueryParams: any = {};
    if (docUIDsArray.length > 0) {
      customQueryParams.docUIDs = docUIDsArray;
    }

    // Only create new object if campaignId or docUIDs changed
    const configKey = `${currentCampaignId}-${activeMainTab}-${docUIDsArray.join(',')}`;
    const currentDocUIDs = (chatbotApiConfigRef.current.customQueryParams as any)?.docUIDs || [];
    if (previousCampaignIdRef.current === currentCampaignId &&
      chatbotApiConfigRef.current.campaignId === currentCampaignId &&
      currentDocUIDs.join(',') === docUIDsArray.join(',')) {
      // Return previous config object but update customQueryParams
      chatbotApiConfigRef.current.customQueryParams = customQueryParams;
      return chatbotApiConfigRef.current;
    }

    // CampaignId or docUIDs changed, update refs and return new config
    previousCampaignIdRef.current = currentCampaignId;
    const newConfig = {
      baseUrl: environment.controlplaneUrl,
      queryEndpoint: '/agent/flow/execute',
      campaignId: currentCampaignId,
      agentCode: detectedProjectType === 'DATA_ANAL' ? 'QUERY_GENERAL_DATASET_AND_RESPOND' : 'QUERY_USER_FEEDBACK_AND_RESPOND',
      contentTypesEndpoint: '/account/getContentTypes',
      generateContentEndpoint: '/agent/flow/execute',
      customQueryParams: customQueryParams
    };
    chatbotApiConfigRef.current = newConfig;
    return newConfig;
  }, [currentCampaignId, activeMainTab, docUIDsArray.join(','), detectedProjectType]);

  return (
    <FullScreenContainer>
      <Box sx={{ display: 'flex', height: '100%' }}>
        {/* Sidebar */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 5,
            width: isSidebarExpanded ? 350 : 85,
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            transition: 'width 0.3s ease',
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onMouseEnter={() => setIsSidebarExpanded(true)}
          onMouseLeave={() => setIsSidebarExpanded(false)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', py: 2 }}>
            {/* Logo / Profile Section */}
            <Box sx={{ px: 1.5, mb: 2 }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: isSidebarExpanded ? 1 : 0,
                justifyContent: isSidebarExpanded ? 'flex-start' : 'center'
              }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <AnalysisIcon sx={{
                    color: '#fff',
                    fontSize: 20,
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} />
                </Box>
                {isSidebarExpanded && (
                  <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                    <Typography variant="h5" sx={{
                      color: '#131F32',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      background: 'linear-gradient(45deg, #131F32, #4c3794)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {detectedProjectType === 'DATA_ANAL' ? 'Data Analytics' : (
                        detectedContext === 'banking' ? 'Customer Survey Insights' :
                          detectedContext === 'gov' ? 'Citizen Survey Insights' :
                            'Employee Survey Insights')}
                    </Typography>

                  </Box>
                )}
              </Box>
            </Box>

            {/* Project Controls */}
            {isSidebarExpanded && (
              <Box sx={{ px: 1.5, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ position: 'relative' }} ref={sidebarDropdownRef}>
                  <Button
                    onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                    fullWidth
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 1.5,
                      py: 1.25,
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', minWidth: 0 }}>
                      <Box sx={{
                        width: 28,
                        height: 28,
                        background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 700
                      }}>
                        {selectedProject?.project_name?.substring(0, 2).toUpperCase() || 'SP'}
                      </Box>
                      <Box sx={{ textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#131F32',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {selectedProject?.project_name || 'Select Project'}
                        </Typography>
                        {selectedProject?.project_description && (
                          <Typography sx={{
                            fontSize: '0.75rem',
                            color: '#64748B',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {selectedProject.project_description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <FullscreenIcon sx={{
                      fontSize: 16,
                      color: '#64748B',
                      flexShrink: 0,
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }} />
                  </Button>
                  {isProjectDropdownOpen && (
                    <Box sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      mt: 0.5,
                      height: '500px',
                      width: '325px',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                      zIndex: 9999,
                      py: 1,

                    }}>
                      <Box sx={{ px: 1.5, pb: 1, mb: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#64748B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Projects
                        </Typography>
                      </Box>
                      {projects.map((project) => (
                        <Button
                          key={project.id}
                          onClick={() => {
                            handleProjectChange(project.id);
                            setIsProjectDropdownOpen(false);
                          }}
                          fullWidth
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1.25,
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            }
                          }}
                        >
                          <Box sx={{
                            width: 32,
                            height: 32,
                            background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: '#fff',
                            fontSize: '0.75rem',
                            fontWeight: 700
                          }}>
                            {project.project_name.substring(0, 2).toUpperCase()}
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', minWidth: 0 }}>
                            <Typography sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#131F32',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {project.project_name}
                            </Typography>
                            {project.project_description && (
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: '#64748B',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {project.project_description}
                              </Typography>
                            )}
                          </Box>
                          {selectedProject?.id === project.id && (
                            <CheckIcon sx={{
                              fontSize: 16,
                              color: '#4c3794',
                              flexShrink: 0,
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }} />
                          )}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
                <Button
                  onClick={() => setIsProjectDialogOpen(true)}
                  fullWidth
                  startIcon={<AddIcon sx={{
                    fontSize: 16,
                    width: 16,
                    height: 16
                  }} />}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    backgroundColor: '#4c3794',
                    color: '#fff',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    borderRadius: '8px',
                    textTransform: 'none',
                    minHeight: '40px',
                    '&:hover': {
                      backgroundColor: '#3a2a73',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: '8px',
                      marginLeft: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }
                  }}
                >
                  NEW PROJECT
                </Button>
              </Box>
            )}

            {/* Navigation Sections */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pt: 2 }}>
              {menuSections.map((section: any, sectionIndex: number) => (
                <Box key={sectionIndex} sx={{ mb: 3 }}>
                  {/* Section Label */}
                  {isSidebarExpanded && section.label && (
                    <Typography sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#9CA3AF',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      px: 1.5,
                      mb: 1
                    }}>
                      {section.label}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {section.items.map((item: any) => {
                      const Icon = item.icon;
                      const isActiveMainTab = item.mainTab && activeMainTab === item.mainTab;
                      const isActiveView = !item.mainTab && !item.action && currentView === item.id;
                      const isActive = isActiveMainTab || isActiveView;

                      const handleClick = () => {
                        if (item.mainTab) {
                          setActiveMainTab(item.mainTab);
                        } else if (item.action === 'viewAll') {
                          if (detectedProjectType !== 'DATA_ANAL') {
                            setIsViewAllDialogOpen(true);
                          }
                        } else if (item.action === 'refresh') {
                          setIsRefreshing(true);
                          fetchSurveyItems();
                        } else if (item.action === 'add') {
                          if (selectedProject && canEditProject) {
                            setDialogStep('upload');
                            setIsProjectDialogOpen(true);
                          }
                        } else {
                          setCurrentView(item.id);
                        }
                      };

                      const isDisabled = item.action === 'refresh'
                        ? (isRefreshing || loading)
                        : item.action === 'add'
                          ? (!selectedProject || !canEditProject)
                          : false;

                      return (
                        <MuiTooltip
                          key={item.id}
                          title={item.action === 'add' && !canEditProject ? "Edit permission required" : (!isSidebarExpanded ? item.label : "")}
                          placement="right"
                        >
                          <span style={{ display: 'block' }}>
                            <Button
                              onClick={handleClick}
                              disabled={isDisabled}
                              fullWidth
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: isSidebarExpanded ? 2 : 1.5,
                                py: 1.25,
                                borderRadius: '8px',
                                textTransform: 'none',
                                justifyContent: isSidebarExpanded ? 'flex-start' : 'center',
                                backgroundColor: isActive ? 'rgba(76, 55, 148, 0.1)' : 'transparent',
                                color: isActive ? '#4c3794' : '#64748B',
                                minHeight: '40px',
                                '&:hover': {
                                  backgroundColor: isActive ? 'rgba(76, 55, 148, 0.15)' : 'rgba(76, 55, 148, 0.05)',
                                  color: '#4c3794',
                                },
                                '&.Mui-disabled': {
                                  color: '#D1D5DB'
                                }
                              }}
                            >
                              <Icon sx={{
                                fontSize: 20,
                                flexShrink: 0,
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: item.action === 'refresh' && isRefreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' }
                                }
                              }} />
                              {isSidebarExpanded && (
                                <Typography sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: isActive ? 600 : 500,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: 1,
                                  flex: 1,
                                  textAlign: 'left'
                                }}>
                                  {item.action === 'refresh' && isRefreshing ? 'Refreshing...' : item.label}
                                </Typography>
                              )}
                            </Button>
                          </span>
                        </MuiTooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}

              {/* Last Refreshed Timestamp */}
              {isSidebarExpanded && lastFetchedTime && (
                <Typography sx={{
                  fontSize: '0.65rem',
                  color: '#9CA3AF',
                  fontStyle: 'italic',
                  px: 1.5,
                  mt: -1
                }}>
                  Last refreshed: {new Date(lastFetchedTime).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </Typography>
              )}
            </Box>

            {/* Footer Section */}
            {isSidebarExpanded && (
              <Box sx={{ px: 1.5, mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Box sx={{
                  px: 1.5,
                  py: 1,
                  background: 'linear-gradient(135deg, rgba(76, 55, 148, 0.1) 0%, rgba(107, 91, 154, 0.1) 100%)',
                  borderRadius: '12px'
                }}>
                  <Typography sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#4c3794'
                  }}>
                    Need Help?
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.75rem',
                    color: '#6b5b9a',
                    mt: 0.5
                  }}>
                    Check documentation
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Content Area */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          marginLeft: isSidebarExpanded ? '272px' : '80px',
          transition: 'margin-left 0.3s ease',
          width: `calc(100% - ${isSidebarExpanded ? '272px' : '80px'})`,
        }}>
          {/* Header - Always full width, unaffected by chat */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flexShrink: 0
          }}>
            {/* Header */}
            <Box
              component="header"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                px: { xs: 2, sm: 3 },
                py: 1.5
              }}
            >
              {/* Left: Back Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={() => {
                    const context = sessionStorage.getItem('appsNavigationContext');
                    navigate('/Apps');
                  }}
                  startIcon={<ArrowBackIosIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: '#64748B',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 55, 148, 0.04)',
                      color: '#4c3794'
                    }
                  }}
                >
                  Back
                </Button>
                <Typography variant="h5" sx={{
                  color: '#131F32',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  background: 'linear-gradient(45deg, #131F32, #4c3794)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {detectedProjectType === 'DATA_ANAL' ? 'Data Analytics' : (
                    detectedContext === 'banking' ? 'Customer Survey Insights' :
                      detectedContext === 'gov' ? 'Citizen Survey Insights' :
                        'Employee Survey Insights')}
                </Typography>
              </Box>

              {/* Right: Notifications */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  ref={notificationButtonRef}
                  onClick={(e) => setNotificationAnchorEl(e.currentTarget)}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 55, 148, 0.08)',
                      color: '#4c3794'
                    }
                  }}
                >
                  <Badge badgeContent={reasoningEntries.size} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Box>
            {/* End Header */}
          </Box>

          {/* Content Area - Below header, can be split with chat */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            overflow: 'hidden'
          }}>
            {/* Main Scrollable Content */}
            <Box sx={{
              flex: 1,
              width: isChatOpen ? (isFullScreen ? '0%' : '52%') : '100%',
              overflowY: 'auto',
              backgroundColor: '#fff',
              transition: 'width 0.3s ease-in-out',
              // p: 3
            }}>
              {activeMainTab === 'executive' && detectedProjectType !== 'DATA_ANAL' && (
                <Box sx={{ p: 4, mx: 'auto' }}>
                  {/* Executive Summary Header */}


                  {/* Executive Summary Content */}
                  {loadingExecutiveSummary ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '400px',
                      gap: 2
                    }}>
                      <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={48} />
                      <Typography sx={{
                        color: '#64748B',
                        fontSize: '0.938rem',
                        fontWeight: 500
                      }}>
                        Loading executive summary...
                      </Typography>
                    </Box>
                  ) : executiveSummaryData.length === 0 ? (
                    <Card sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      border: '1px solid #e9ecef'
                    }}>
                      <SummaryIcon sx={{ fontSize: 64, color: '#E5E9EF', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
                        No Executive Summary Available
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        Executive summary data will appear here once available
                      </Typography>
                    </Card>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {/* Process API Data */}
                      {(() => {
                        // Extract data from grouped API response
                        let totalRecords: number | string | null = null;
                        let averageNpsScore: number | string | null = null;
                        let percentagePromoters: number | string | null = null;
                        let percentagePassives: number | string | null = null;
                        let percentageDetractors: number | string | null = null;
                        let topDrivers: string[] = [];
                        const groupedData: Array<{ groupName: string; items: Array<{ title: string; headers: string[]; data: any[]; analysis?: string; recommendations?: string[]; key?: string }> }> = [];

                        // Check if data is grouped (new structure) or flat (old structure)
                        const isGrouped = executiveSummaryData.length > 0 && executiveSummaryData[0].groupName !== undefined;

                        if (isGrouped) {
                          // New grouped structure
                          executiveSummaryData.forEach((group: any) => {
                            const groupItems: Array<{ title: string; headers: string[]; data: any[]; analysis?: string; recommendations?: string[]; key?: string }> = [];

                            group.items?.forEach((item: any) => {
                              if (item.objectType === 'DICTIONARY' && item.entries) {
                                const totalRecordsEntry = item.entries.find((e: any) => e.key === 'totalRecords');
                                if (totalRecordsEntry) {
                                  const value: any = totalRecordsEntry.value;
                                  if (typeof value === 'number') {
                                    totalRecords = value;
                                  } else if (value != null) {
                                    const parsed = Number(value);
                                    if (!isNaN(parsed)) {
                                      totalRecords = parsed;
                                    } else {
                                      totalRecords = String(value);
                                    }
                                  }
                                }

                                // Extract Average NPS Score
                                const averageNpsEntry = item.entries.find((e: any) => e.key === 'averageNpsScore');
                                if (averageNpsEntry) {
                                  const value: any = averageNpsEntry.value;
                                  if (typeof value === 'number') {
                                    averageNpsScore = value;
                                  } else if (value != null) {
                                    const parsed = Number(value);
                                    if (!isNaN(parsed)) {
                                      averageNpsScore = parsed;
                                    } else {
                                      averageNpsScore = String(value);
                                    }
                                  }
                                }

                                // Extract Percentage Promoters
                                const percentagePromotersEntry = item.entries.find((e: any) => e.key === 'percentagePromoters');
                                if (percentagePromotersEntry) {
                                  const value: any = percentagePromotersEntry.value;
                                  if (typeof value === 'number') {
                                    percentagePromoters = value;
                                  } else if (value != null) {
                                    const parsed = Number(value);
                                    if (!isNaN(parsed)) {
                                      percentagePromoters = parsed;
                                    } else {
                                      percentagePromoters = String(value);
                                    }
                                  }
                                }

                                // Extract Percentage Passives
                                const percentagePassivesEntry = item.entries.find((e: any) => e.key === 'percentagePassives');
                                if (percentagePassivesEntry) {
                                  const value: any = percentagePassivesEntry.value;
                                  if (typeof value === 'number') {
                                    percentagePassives = value;
                                  } else if (value != null) {
                                    const parsed = Number(value);
                                    if (!isNaN(parsed)) {
                                      percentagePassives = parsed;
                                    } else {
                                      percentagePassives = String(value);
                                    }
                                  }
                                }

                                // Extract Percentage Detractors
                                const percentageDetractorsEntry = item.entries.find((e: any) => e.key === 'percentageDetractors');
                                if (percentageDetractorsEntry) {
                                  const value: any = percentageDetractorsEntry.value;
                                  if (typeof value === 'number') {
                                    percentageDetractors = value;
                                  } else if (value != null) {
                                    const parsed = Number(value);
                                    if (!isNaN(parsed)) {
                                      percentageDetractors = parsed;
                                    } else {
                                      percentageDetractors = String(value);
                                    }
                                  }
                                }

                                const topDriversEntry = item.entries.find((e: any) => e.key === 'topDrivers');
                                if (topDriversEntry && Array.isArray(topDriversEntry.value)) {
                                  topDrivers = topDriversEntry.value;
                                }
                              } else if (item.objectType === 'MULTICOLUMNDICT' && item.value) {
                                const { title, headers, data } = item.value;

                                // Extract analysis and recommendations from item entries
                                let analysis: string | undefined;
                                let recommendations: string[] | undefined;

                                if (item.entries && Array.isArray(item.entries)) {
                                  const analysisEntry = item.entries.find((e: any) => e.key === 'analysis');
                                  const recommendationsEntry = item.entries.find((e: any) => e.key === 'recommendations');

                                  if (analysisEntry && analysisEntry.value) {
                                    analysis = typeof analysisEntry.value === 'string' ? analysisEntry.value : String(analysisEntry.value);
                                  }

                                  if (recommendationsEntry && recommendationsEntry.value) {
                                    if (Array.isArray(recommendationsEntry.value)) {
                                      recommendations = recommendationsEntry.value.map((v: any) => String(v));
                                    } else {
                                      recommendations = [String(recommendationsEntry.value)];
                                    }
                                  }
                                }

                                groupItems.push({
                                  title: title || item.label || 'Distribution',
                                  headers,
                                  data,
                                  analysis,
                                  recommendations,
                                  key: item.key // Preserve the key for detection
                                });
                              }
                            });

                            if (groupItems.length > 0) {
                              groupedData.push({
                                groupName: group.groupName || 'Data',
                                items: groupItems
                              });
                            }
                          });
                        } else {
                          // Old flat structure (backward compatibility)
                          const distributions: Array<{ title: string; headers: string[]; data: any[]; analysis?: string; recommendations?: string[]; key?: string }> = [];

                          executiveSummaryData.forEach((item: any) => {
                            if (item.objectType === 'DICTIONARY' && item.entries) {
                              const totalRecordsEntry = item.entries.find((e: any) => e.key === 'totalRecords');
                              if (totalRecordsEntry) {
                                const value: any = totalRecordsEntry.value;
                                if (typeof value === 'number') {
                                  totalRecords = value;
                                } else if (value != null) {
                                  const parsed = Number(value);
                                  if (!isNaN(parsed)) {
                                    totalRecords = parsed;
                                  } else {
                                    totalRecords = String(value);
                                  }
                                }
                              }
                              const topDriversEntry = item.entries.find((e: any) => e.key === 'topDrivers');
                              if (topDriversEntry && Array.isArray(topDriversEntry.value)) {
                                topDrivers = topDriversEntry.value;
                              }
                            } else if (item.objectType === 'MULTICOLUMNDICT' && item.value) {
                              const { title, headers, data } = item.value;

                              // Extract analysis and recommendations from item entries
                              let analysis: string | undefined;
                              let recommendations: string[] | undefined;

                              if (item.entries && Array.isArray(item.entries)) {
                                const analysisEntry = item.entries.find((e: any) => e.key === 'analysis');
                                const recommendationsEntry = item.entries.find((e: any) => e.key === 'recommendations');

                                if (analysisEntry && analysisEntry.value) {
                                  analysis = typeof analysisEntry.value === 'string' ? analysisEntry.value : String(analysisEntry.value);
                                }

                                if (recommendationsEntry && recommendationsEntry.value) {
                                  if (Array.isArray(recommendationsEntry.value)) {
                                    recommendations = recommendationsEntry.value.map((v: any) => String(v));
                                  } else {
                                    recommendations = [String(recommendationsEntry.value)];
                                  }
                                }
                              }

                              distributions.push({
                                title: title || item.label || 'Distribution',
                                headers,
                                data,
                                analysis,
                                recommendations,
                                key: item.key // Preserve the key for detection
                              });
                            }
                          });

                          if (distributions.length > 0) {
                            groupedData.push({
                              groupName: 'Data',
                              items: distributions
                            });
                          }
                        }

                        return (
                          <>
                            {/* Survey Overview & Key Metrics */}

                            <Grid container spacing={3}>
                              {/* Total Responses */}
                              {totalRecords && (
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{
                                    p: 2.5,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '1px solid #e9ecef',
                                    height: '100%'
                                  }}>
                                    <Typography variant="caption" sx={{
                                      color: '#64748B',
                                      display: 'block',
                                      mb: 1,
                                      fontWeight: 500,
                                      fontSize: '0.813rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Total Responses
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                      <Typography variant="h4" sx={{
                                        color: '#131F32',
                                        fontWeight: 700,
                                        fontSize: '1.3rem !important'
                                      }}>
                                        {(() => {
                                          if (totalRecords === null) return '--';
                                          const numValue = typeof totalRecords === 'number' ? totalRecords : Number(totalRecords);
                                          if (!isNaN(numValue)) {
                                            return numValue.toLocaleString();
                                          }
                                          return String(totalRecords);
                                        })()}
                                      </Typography>
                                    </Box>

                                  </Box>
                                </Grid>
                              )}

                              {/* Avg NPS Score */}
                              {averageNpsScore !== null && (
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{
                                    p: 2.5,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '1px solid #e9ecef',
                                    height: '100%'
                                  }}>
                                    <Typography variant="caption" sx={{
                                      color: '#64748B',
                                      display: 'block',
                                      mb: 1,
                                      fontWeight: 500,
                                      fontSize: '0.813rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Avg NPS Score
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                      <Typography variant="h4" sx={{
                                        color: '#131F32',
                                        fontWeight: 700,
                                        fontSize: '1.3rem !important'
                                      }}>
                                        {(() => {
                                          const numValue = typeof averageNpsScore === 'number' ? averageNpsScore : Number(averageNpsScore);
                                          if (!isNaN(numValue)) {
                                            return numValue.toFixed(2);
                                          }
                                          return String(averageNpsScore);
                                        })()}
                                      </Typography>
                                    </Box>

                                  </Box>
                                </Grid>
                              )}

                              {/* Percentage Promoters */}
                              {percentagePromoters !== null && (
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{
                                    p: 2.5,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '1px solid #e9ecef',
                                    height: '100%'
                                  }}>
                                    <Typography variant="caption" sx={{
                                      color: '#64748B',
                                      display: 'block',
                                      mb: 1,
                                      fontWeight: 500,
                                      fontSize: '0.813rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Promoters
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                      <Typography variant="h4" sx={{
                                        color: '#10b981',
                                        fontWeight: 700,
                                        fontSize: '1.3rem !important'
                                      }}>
                                        {(() => {
                                          const numValue = typeof percentagePromoters === 'number' ? percentagePromoters : Number(percentagePromoters);
                                          if (!isNaN(numValue)) {
                                            return numValue.toFixed(1);
                                          }
                                          return String(percentagePromoters);
                                        })()}%
                                      </Typography>
                                    </Box>

                                  </Box>
                                </Grid>
                              )}

                              {/* Percentage Detractors */}
                              {percentageDetractors !== null && (
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{
                                    p: 2.5,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '12px',
                                    border: '1px solid #e9ecef',
                                    height: '100%'
                                  }}>
                                    <Typography variant="caption" sx={{
                                      color: '#64748B',
                                      display: 'block',
                                      mb: 1,
                                      fontWeight: 500,
                                      fontSize: '0.813rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Detractors
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                      <Typography variant="h4" sx={{
                                        color: '#ef4444',
                                        fontWeight: 700,
                                        fontSize: '1.3rem !important'
                                      }}>
                                        {(() => {
                                          const numValue = typeof percentageDetractors === 'number' ? percentageDetractors : Number(percentageDetractors);
                                          if (!isNaN(numValue)) {
                                            return numValue.toFixed(1);
                                          }
                                          return String(percentageDetractors);
                                        })()}%
                                      </Typography>
                                    </Box>

                                  </Box>
                                </Grid>
                              )}
                            </Grid>


                            {/* Grouped Data Distributions - Two items per row */}
                            {groupedData.map((group, groupIndex) => {
                              // Skip "Performance Distribution Across Key Segments" group
                              const normalizedGroupName = group.groupName
                                ? group.groupName.toLowerCase().replace(/\s+/g, ' ').trim()
                                : '';
                              const isPerformanceDistribution = normalizedGroupName.includes('performance distribution') &&
                                normalizedGroupName.includes('key segments');

                              // Comment out Performance Distribution Across Key Segments
                              if (isPerformanceDistribution) {
                                return null;
                              }

                              return (
                                <Box key={groupIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 3, overflow: 'visible' }}>
                                  <Typography variant="h5" sx={{
                                    color: '#131F32',
                                    fontWeight: 700,
                                    fontSize: '1.25rem',
                                    mb: 1
                                  }}>
                                    {group.groupName}
                                  </Typography>
                                  <Grid container spacing={3} sx={{ overflow: 'visible' }}>
                                    {group.items.map((item, itemIndex) => {
                                      // Check if this is Segments Overview to make it full width
                                      const normalizedGroupName = group.groupName
                                        ? group.groupName.toLowerCase().replace(/\s+/g, ' ').trim()
                                        : '';
                                      const normalizedItemTitle = item.title
                                        ? item.title.toLowerCase().replace(/\s+/g, ' ').trim()
                                        : '';
                                      const itemKey = (item as any).key || '';
                                      const hasSegmentHeaders = item.headers && (
                                        item.headers.some((h: string) => h.toLowerCase().includes('segment name')) ||
                                        item.headers.some((h: string) => h.toLowerCase().includes('segment') && h.toLowerCase().includes('name'))
                                      );
                                      const titleMatches = normalizedItemTitle === 'segments overview' || normalizedItemTitle === 'segments  overview';
                                      const keyMatches = itemKey === 'segmentsOverview' || itemKey.toLowerCase() === 'segmentsoverview';
                                      const isSegmentsOverview =
                                        keyMatches ||
                                        titleMatches ||
                                        (normalizedGroupName.includes('segments') && normalizedGroupName.includes('overview')) ||
                                        (normalizedItemTitle.includes('segments') && normalizedItemTitle.includes('overview')) ||
                                        itemKey.toLowerCase().includes('segments') ||
                                        (hasSegmentHeaders && item.headers && item.headers.some((h: string) => h.toLowerCase().includes('performance'))) ||
                                        (normalizedItemTitle.replace(/\s+/g, ' ') === 'segments overview') ||
                                        (normalizedItemTitle.includes('segments') && normalizedItemTitle.includes('overview'));

                                      return (
                                        <Grid item xs={12} md={isSegmentsOverview ? 12 : 6} key={itemIndex} sx={{ overflow: 'visible' }}>
                                          <Card sx={{
                                            p: 3.5,
                                            borderRadius: '16px',
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'visible'
                                          }}>
                                            {/* Title */}
                                            <Box sx={{
                                              mb: item.analysis || (item.recommendations && item.recommendations.length > 0) ? 2.5 : 3
                                            }}>
                                              <Typography variant="h6" sx={{
                                                color: '#131F32',
                                                fontWeight: 700,
                                                fontSize: '1.125rem !important',
                                                lineHeight: 1.3
                                              }}>
                                                {item.title}
                                              </Typography>
                                            </Box>

                                            {/* Display Analysis below title if available */}
                                            {item.analysis && (
                                              <Card sx={{
                                                // backgroundColor: '#f8f9fa', 
                                                // p: 0.5, 
                                                // borderRadius: '12px',
                                                // border: '1px solid #FFF',
                                                boxShadow: 'none',
                                                border: 'none',
                                                mb: 3
                                              }}>
                                                <MarkdownMessage text={item.analysis} />
                                              </Card>
                                            )}

                                            {(() => {
                                              // Prepare chart data
                                              // Helper function to get value from row by header name (case-insensitive, handles spaces)
                                              const getValue = (row: any, header: string): any => {
                                                if (!row || !header) return null;

                                                // Try exact match first
                                                if (row[header] !== undefined) return row[header];

                                                // Normalize header for comparison (lowercase, trim spaces)
                                                const normalizedHeader = header.toLowerCase().trim().replace(/\s+/g, ' ');

                                                // Try case-insensitive match by iterating through all keys
                                                for (const key in row) {
                                                  if (row.hasOwnProperty(key)) {
                                                    const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, ' ');
                                                    if (normalizedKey === normalizedHeader) {
                                                      return row[key];
                                                    }
                                                  }
                                                }

                                                // Try variations
                                                const variations = [
                                                  header.toLowerCase(),
                                                  header.toUpperCase(),
                                                  header.charAt(0).toUpperCase() + header.slice(1).toLowerCase(),
                                                  header.replace(/\s+/g, ''),
                                                  header.replace(/\s+/g, '_'),
                                                  header.replace(/\s+/g, '-')
                                                ];

                                                for (const variation of variations) {
                                                  if (row[variation] !== undefined) return row[variation];
                                                }

                                                return null;
                                              };

                                              // Dynamically detect label column (first non-numeric column)
                                              const labelHeader = item.headers.find(h => {
                                                const val = getValue(item.data[0], h);
                                                return typeof val !== 'number' && val !== null && val !== undefined;
                                              }) || item.headers[0];

                                              // Dynamically detect all numeric columns
                                              const numericHeaders = item.headers.filter(h => {
                                                if (h === labelHeader) return false;
                                                const val = getValue(item.data[0], h);
                                                return typeof val === 'number';
                                              });

                                              // Find the primary numeric column for the bar (prioritize "count", then first numeric)
                                              const primaryNumericHeader = numericHeaders.find(h => {
                                                const headerLower = h.toLowerCase();
                                                return headerLower === 'count' || headerLower === 'total';
                                              }) || numericHeaders[0];

                                              // Prepare chart data - store ALL values dynamically
                                              const chartData = item.data.map((row: any) => {
                                                const chartItem: any = {
                                                  name: String(getValue(row, labelHeader) || '-')
                                                };

                                                // Store ALL column values (both numeric and non-numeric) for tooltip
                                                item.headers.forEach(header => {
                                                  if (header !== labelHeader) { // Skip label header as it's already in 'name'
                                                    const val = getValue(row, header);
                                                    // Ensure numeric values are stored as numbers for proper display
                                                    if (numericHeaders.includes(header) && val !== null && val !== undefined) {
                                                      const numVal = typeof val === 'number' ? val : parseFloat(String(val));
                                                      chartItem[header] = !isNaN(numVal) ? numVal : val;
                                                    } else {
                                                      chartItem[header] = val;
                                                    }
                                                  }
                                                });

                                                // Set primary value for bar chart (use primary numeric header)
                                                if (primaryNumericHeader) {
                                                  chartItem['count'] = chartItem[primaryNumericHeader] || 0;
                                                }

                                                return chartItem;
                                              });

                                              // Custom tooltip - dynamically shows ALL columns (all data)
                                              const CustomTooltip = ({ active, payload, label }: any) => {
                                                if (active && payload && payload.length) {
                                                  const dataPoint = payload[0].payload;

                                                  // Get ALL columns that have values (excluding the label which is shown as title)
                                                  const allFields = item.headers.filter(header => {
                                                    if (header === labelHeader) return false; // Skip label header
                                                    const val = dataPoint[header];
                                                    return val !== null && val !== undefined && val !== '';
                                                  });

                                                  return (
                                                    <Box sx={{
                                                      backgroundColor: '#fff',
                                                      borderRadius: '8px',
                                                      border: '1px solid #e9ecef',
                                                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                      padding: '12px 16px',
                                                      minWidth: '150px',
                                                      maxWidth: '300px'
                                                    }}>
                                                      <Typography sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.875rem',
                                                        color: '#131F32',
                                                        mb: 1.5
                                                      }}>
                                                        {label}
                                                      </Typography>
                                                      {allFields.map((header, index) => {
                                                        const value = dataPoint[header];
                                                        if (value === null || value === undefined || value === '') return null;

                                                        // Format header name for display
                                                        const headerLabel = header
                                                          .replace(/([A-Z])/g, ' $1')
                                                          .replace(/^./, str => str.toUpperCase())
                                                          .trim();

                                                        // Format value for display
                                                        const isNumeric = typeof value === 'number';
                                                        const isPercentage = isNumeric && (
                                                          header.toLowerCase().includes('percent') ||
                                                          header.toLowerCase().includes('%') ||
                                                          header.toLowerCase() === 'percentage'
                                                        );

                                                        return (
                                                          <Box
                                                            key={header}
                                                            sx={{
                                                              display: 'flex',
                                                              justifyContent: 'space-between',
                                                              alignItems: 'flex-start',
                                                              mb: index < allFields.length - 1 ? 0.5 : 0,
                                                              gap: 1
                                                            }}
                                                          >
                                                            <Typography sx={{
                                                              fontSize: '0.813rem',
                                                              color: '#64748B',
                                                              fontWeight: 500,
                                                              flexShrink: 0
                                                            }}>
                                                              {headerLabel}:
                                                            </Typography>
                                                            <Typography sx={{
                                                              fontSize: '0.813rem',
                                                              color: '#131F32',
                                                              fontWeight: 600,
                                                              textAlign: 'right',
                                                              wordBreak: 'break-word'
                                                            }}>
                                                              {isNumeric
                                                                ? (isPercentage
                                                                  ? `${value}%`
                                                                  : value.toLocaleString())
                                                                : String(value)}
                                                            </Typography>
                                                          </Box>
                                                        );
                                                      })}
                                                    </Box>
                                                  );
                                                }
                                                return null;
                                              };

                                              // Check if this is a Key Drivers group (for setting domain to 0-5)
                                              const isKeyDrivers = group.groupName && (
                                                group.groupName.toLowerCase().includes('key drivers') ||
                                                group.groupName.toLowerCase().includes('leverage drivers')
                                              );

                                              // Check if this is Distribution of Survey Outcomes group (for percentage 0-100)
                                              const isDistributionOfSurveyOutcomes = group.groupName &&
                                                group.groupName.toLowerCase().replace(/\s+/g, ' ').includes('distribution of survey outcomes');

                                              // Check if this is Segments Overview group - use treemap
                                              // Normalize group name by trimming and replacing multiple spaces with single space
                                              const normalizedGroupName = group.groupName
                                                ? group.groupName.toLowerCase().replace(/\s+/g, ' ').trim()
                                                : '';
                                              // Check both group name and item key/title
                                              const normalizedItemTitle = item.title
                                                ? item.title.toLowerCase().replace(/\s+/g, ' ').trim()
                                                : '';
                                              const itemKey = (item as any).key || '';

                                              // Check for Segment Opportunity Map
                                              const isSegmentOpportunityMap =
                                                (normalizedGroupName.includes('segment') && normalizedGroupName.includes('opportunity') && normalizedGroupName.includes('map')) ||
                                                (normalizedItemTitle.includes('segment') && normalizedItemTitle.includes('opportunity')) ||
                                                (normalizedItemTitle.includes('segment') && normalizedItemTitle.includes('comparison')) ||
                                                itemKey.toLowerCase().includes('segmentcomparison') ||
                                                itemKey.toLowerCase().includes('performancevsbusinessimpact') ||
                                                itemKey.toLowerCase().includes('segmentcomparison:performancevsbusinessimpact') ||
                                                (item.headers && item.headers.some((h: string) => h.toLowerCase().includes('business impact')) &&
                                                  item.headers.some((h: string) => h.toLowerCase().includes('performance score'))) ||
                                                (item.headers && item.headers.some((h: string) => h.toLowerCase().includes('business impact')) &&
                                                  item.headers.some((h: string) => h.toLowerCase().includes('aggregate performance score')));

                                              // Check if headers contain segment-related fields (strong indicator)
                                              const hasSegmentHeaders = item.headers && (
                                                item.headers.some((h: string) => h.toLowerCase().includes('segment name')) ||
                                                item.headers.some((h: string) => h.toLowerCase().includes('segment') && h.toLowerCase().includes('name'))
                                              );

                                              // More flexible check - look for "segments" and "overview" separately, or check headers
                                              // Also check if title exactly matches "Segments Overview" (case insensitive)
                                              const titleMatches = normalizedItemTitle === 'segments overview' || normalizedItemTitle === 'segments  overview';

                                              // Check item key - this is the most reliable indicator
                                              const keyMatches = itemKey === 'segmentsOverview' || itemKey.toLowerCase() === 'segmentsoverview';

                                              // Very permissive check - prioritize key match, then title/group name
                                              // Exclude if it's Segment Opportunity Map
                                              const isSegmentsOverview =
                                                !isSegmentOpportunityMap && (
                                                  keyMatches || // Most reliable - check key first
                                                  titleMatches ||
                                                  (normalizedGroupName.includes('segments') && normalizedGroupName.includes('overview')) ||
                                                  (normalizedItemTitle.includes('segments') && normalizedItemTitle.includes('overview')) ||
                                                  itemKey.toLowerCase().includes('segments') ||
                                                  (hasSegmentHeaders && item.headers && item.headers.some((h: string) => h.toLowerCase().includes('performance'))) ||
                                                  // Fallback: if title is exactly "Segments Overview" (with any spacing)
                                                  (normalizedItemTitle.replace(/\s+/g, ' ') === 'segments overview')
                                                );



                                              // If Segments Overview, render treemap
                                              if (isSegmentsOverview) {

                                                // Prepare treemap data - filter out invalid rows
                                                const treemapData = item.data
                                                  .filter((row: any) => row != null) // Filter out null/undefined rows
                                                  .map((row: any, index: number) => {
                                                    const segmentName = getValue(row, 'Segment Name') || getValue(row, 'Segment') || 'Unknown';
                                                    const size = getValue(row, 'Size') || 0;
                                                    const percentage = getValue(row, 'Percentage') || 0;
                                                    const performanceScore = getValue(row, 'Aggregate Performance Score');
                                                    const performanceCategory = getValue(row, 'Performance Category') || 'N/A';
                                                    const summary = getValue(row, 'Summary') || '';
                                                    // Extract Outcomes - this is an object with key-value pairs
                                                    const outcomes = getValue(row, 'Outcomes') || null;

                                                    // Use index-based color assignment from 10 distinct purple/violet shades
                                                    // Rich, varied colors for visual distinction
                                                    const richColors = [
                                                      '#4c3794', // Brand purple
                                                      '#1e3a5f', // Deep navy blue
                                                      '#2d5a4a', // Forest green
                                                      '#6b3a3a', // Burgundy
                                                      '#3d5a80', // Steel blue
                                                      '#5c4033', // Dark brown
                                                      '#2f4858', // Charcoal blue
                                                      '#4a3728', // Coffee
                                                      '#1f4e5f', // Teal
                                                      '#553555'  // Plum
                                                    ];

                                                    // Assign color based on index for distinct shades
                                                    const colorIndex = index % richColors.length;
                                                    const fillColor = richColors[colorIndex];

                                                    return {
                                                      name: segmentName,
                                                      size: typeof size === 'number' ? size : parseFloat(String(size)) || 0,
                                                      percentage: typeof percentage === 'number' ? percentage : parseFloat(String(percentage)) || 0,
                                                      performanceScore,
                                                      performanceCategory,
                                                      summary,
                                                      outcomes,
                                                      fill: fillColor
                                                    };
                                                  })
                                                  .filter((item: any) => item != null && item.name); // Ensure all items have required fields

                                                // Only render treemap if we have valid data
                                                if (!treemapData || treemapData.length === 0) {
                                                  return (
                                                    <Box sx={{ width: '100%', height: '500px', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <Typography sx={{ color: '#64748B' }}>No segment data available</Typography>
                                                    </Box>
                                                  );
                                                }

                                                // Filter out segments that are too small (less than 2% or size < 5)
                                                // This avoids tiny strips that don't add value
                                                const filteredTreemapData = treemapData.filter((item: any) => {
                                                  const minPercentage = 2; // Minimum 2% to display
                                                  const minSize = 5; // Minimum size of 5
                                                  return item.percentage >= minPercentage && item.size >= minSize;
                                                });

                                                if (filteredTreemapData.length === 0) {
                                                  return (
                                                    <Box sx={{ width: '100%', height: '500px', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <Typography sx={{ color: '#64748B' }}>No significant segment data available</Typography>
                                                    </Box>
                                                  );
                                                }

                                                // Transform data for @nivo/treemap format
                                                const nivoData = {
                                                  name: 'Segments',
                                                  children: filteredTreemapData.map((item: any) => ({
                                                    id: item.name,
                                                    value: item.size,
                                                    color: item.fill,
                                                    percentage: item.percentage,
                                                    performanceScore: item.performanceScore,
                                                    performanceCategory: item.performanceCategory,
                                                    summary: item.summary,
                                                    outcomes: item.outcomes
                                                  }))
                                                };

                                                // Custom tooltip for @nivo treemap
                                                const NivoTooltip = ({ node }: any) => {
                                                  if (!node) return null;
                                                  const data = node.data;
                                                  return (
                                                    <Box sx={{
                                                      backgroundColor: '#fff',
                                                      borderRadius: '8px',
                                                      border: '1px solid #e9ecef',
                                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                                      padding: '12px 16px',
                                                      minWidth: '400px',
                                                      maxWidth: '450px',
                                                      zIndex: 99999,
                                                      position: 'relative'
                                                    }}>
                                                      <Typography sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.875rem',
                                                        color: '#131F32',
                                                        mb: 1.5
                                                      }}>
                                                        {node.id || 'Unknown'}
                                                      </Typography>
                                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                          <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Size:</Typography>
                                                          <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{node.value}</Typography>
                                                        </Box>
                                                        {data.percentage !== undefined && (
                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Percentage:</Typography>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.percentage.toFixed(1)}%</Typography>
                                                          </Box>
                                                        )}
                                                        {data.performanceScore !== null && data.performanceScore !== undefined && (
                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Score:</Typography>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>
                                                              {typeof data.performanceScore === 'number' ? data.performanceScore.toFixed(1) : data.performanceScore}
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                        {data.performanceCategory && (
                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Category:</Typography>
                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.performanceCategory}</Typography>
                                                          </Box>
                                                        )}
                                                        {data.summary && (
                                                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e9ecef' }}>
                                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 }}>
                                                              {data.summary}
                                                            </Typography>
                                                          </Box>
                                                        )}
                                                        {/* Outcomes Section */}
                                                        {data.outcomes && typeof data.outcomes === 'object' && Object.keys(data.outcomes).length > 0 && (
                                                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(76, 55, 148, 0.3)' }}>
                                                            <Typography sx={{
                                                              fontSize: '0.75rem',
                                                              fontWeight: 700,
                                                              color: '#4c3794',
                                                              mb: 1,
                                                              textTransform: 'uppercase',
                                                              letterSpacing: '0.5px'
                                                            }}>
                                                              Outcomes
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                              {Object.entries(data.outcomes).map(([key, value], idx) => (
                                                                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                  <Typography sx={{
                                                                    fontSize: '0.7rem',
                                                                    color: '#64748B',
                                                                    flex: 1,
                                                                    lineHeight: 1.4
                                                                  }}>
                                                                    {key}:
                                                                  </Typography>
                                                                  <Typography sx={{
                                                                    fontSize: '0.75rem',
                                                                    color: '#131F32',
                                                                    fontWeight: 600,
                                                                    minWidth: '50px',
                                                                    textAlign: 'right'
                                                                  }}>
                                                                    {String(value)}
                                                                  </Typography>
                                                                </Box>
                                                              ))}
                                                            </Box>
                                                          </Box>
                                                        )}
                                                      </Box>
                                                    </Box>
                                                  );
                                                };

                                                // Use squarify for balanced layout (same as dialog for consistency)
                                                return (
                                                  <Box sx={{
                                                    width: '65%',
                                                    height: '450px',
                                                    mt: 2,
                                                    borderRadius: '16px',
                                                    boxShadow: '0 4px 20px rgba(76, 55, 148, 0.15)',
                                                    border: '1px solid rgba(76, 55, 148, 0.1)',
                                                    alignItems: 'center',
                                                    alignSelf: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                    // Use clip-path instead of overflow: hidden to allow tooltip to overflow
                                                    '& > div': {
                                                      borderRadius: '16px',
                                                      overflow: 'hidden'
                                                    },
                                                    // Ensure tooltips can overflow
                                                    '& .nivo-tooltip': {
                                                      zIndex: 9999
                                                    }
                                                  }}>
                                                    <ResponsiveTreeMap
                                                      data={nivoData}
                                                      value="value"
                                                      valueFormat=".0s"
                                                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                                                      tile="squarify"
                                                      enableLabel={false}
                                                      theme={{
                                                        labels: {
                                                          text: {
                                                            fontSize: 14,
                                                            fontWeight: 600
                                                          }
                                                        }
                                                      }}
                                                      parentLabelPosition="left"
                                                      parentLabelTextColor="black"
                                                      colors={{ scheme: 'oranges' }}
                                                      borderWidth={3}
                                                      borderColor="black"
                                                      tooltip={NivoTooltip}
                                                      animate={true}
                                                      motionConfig="gentle"
                                                      enableParentLabel={false}
                                                      orientLabel={false}

                                                      layers={['nodes', ({ nodes }: any) => {
                                                        // Custom layer for all labels with multi-line support
                                                        if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return null;

                                                        return (
                                                          <g>
                                                            {nodes.filter((node: any) => !node.isParent).map((node: any) => {
                                                              // Skip invalid nodes or very small ones
                                                              if (!node || node.width < 60 || node.height < 35) return null;

                                                              // Get the name
                                                              const name = String(node.id || node.data?.name || '');
                                                              if (!name || name === 'undefined' || name === 'null' || name === 'Segments') return null;

                                                              // Calculate characters per line based on node width (~8px per char for 14px font)
                                                              const charsPerLine = Math.max(8, Math.floor((node.width - 16) / 8));
                                                              const lineHeight = 18;
                                                              const maxLines = Math.max(1, Math.floor((node.height - 12) / lineHeight));

                                                              // Break text into lines by words
                                                              const words = name.split(' ');
                                                              const lines: string[] = [];
                                                              let currentLine = '';

                                                              for (const word of words) {
                                                                if (lines.length >= maxLines) break;

                                                                const testLine = currentLine ? `${currentLine} ${word}` : word;

                                                                if (testLine.length <= charsPerLine) {
                                                                  currentLine = testLine;
                                                                } else {
                                                                  if (currentLine) {
                                                                    lines.push(currentLine);
                                                                    if (lines.length >= maxLines) break;
                                                                    currentLine = word.length > charsPerLine ? word.substring(0, charsPerLine - 2) + '..' : word;
                                                                  } else {
                                                                    currentLine = word.substring(0, charsPerLine - 2) + '..';
                                                                  }
                                                                }
                                                              }

                                                              if (currentLine && lines.length < maxLines) {
                                                                lines.push(currentLine);
                                                              }

                                                              if (lines.length === 0) return null;

                                                              // Calculate vertical position to center the text block
                                                              const totalHeight = lines.length * lineHeight;
                                                              const startY = node.y + (node.height - totalHeight) / 2 + lineHeight / 2;

                                                              return (
                                                                <g key={`label-${node.id}`}>
                                                                  {lines.map((line, index) => (
                                                                    <text
                                                                      key={index}
                                                                      x={node.x + node.width / 2}
                                                                      y={startY + index * lineHeight}
                                                                      textAnchor="middle"
                                                                      dominantBaseline="middle"
                                                                      fill="#000000"
                                                                      fontSize={14}
                                                                      fontWeight={600}
                                                                      style={{ pointerEvents: 'none' }}
                                                                    >
                                                                      {line}
                                                                    </text>
                                                                  ))}
                                                                </g>
                                                              );
                                                            })}
                                                          </g>
                                                        );
                                                      }]}
                                                    />
                                                  </Box>
                                                );
                                              }

                                              // If Segment Opportunity Map, render Bubble Chart or TreeMap based on toggle
                                              if (isSegmentOpportunityMap) {
                                                // Debug logging
                                                console.log('Segment Opportunity Map detected:', {
                                                  groupName: group.groupName,
                                                  itemTitle: item.title,
                                                  itemKey: itemKey,
                                                  headers: item.headers,
                                                  dataLength: item.data?.length,
                                                  firstRow: item.data?.[0],
                                                  allRowKeys: item.data?.[0] ? Object.keys(item.data[0]) : []
                                                });

                                                // Prepare bubble chart data
                                                if (!item.data || item.data.length === 0) {
                                                  console.warn('No data available for Segment Opportunity Map');
                                                  return (
                                                    <Box sx={{ width: '100%', height: '500px', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <Typography sx={{ color: '#64748B' }}>No data available for Segment Opportunity Map</Typography>
                                                    </Box>
                                                  );
                                                }

                                                const bubbleData = item.data
                                                  .filter((row: any) => row != null)
                                                  .map((row: any, index: number) => {
                                                    const segmentName = getValue(row, 'Segment Name') || getValue(row, 'Segment') || 'Unknown';
                                                    const businessImpact = getValue(row, 'Business Impact');
                                                    const performanceScore = getValue(row, 'Aggregate Performance Score') || getValue(row, 'Performance Score');
                                                    const segmentSize = getValue(row, 'Segment Size') || getValue(row, 'Size') || 0;
                                                    const opportunityScore = getValue(row, 'Opportunity Score');
                                                    const quadrant = getValue(row, 'Quadrant') || 'Unknown';
                                                    const performanceCategory = getValue(row, 'Performance Category') || 'N/A';
                                                    const percentage = getValue(row, 'Percentage') || 0;
                                                    const outcomes = getValue(row, 'Outcomes') || null;

                                                    // Convert to numbers
                                                    const businessImpactNum = typeof businessImpact === 'number' ? businessImpact : parseFloat(String(businessImpact)) || 0;
                                                    const performanceScoreNum = typeof performanceScore === 'number' ? performanceScore : parseFloat(String(performanceScore)) || 0;
                                                    const sizeNum = typeof segmentSize === 'number' ? segmentSize : parseFloat(String(segmentSize)) || 0;
                                                    const opportunityScoreNum = typeof opportunityScore === 'number' ? opportunityScore : parseFloat(String(opportunityScore)) || 0;

                                                    // Debug first row
                                                    if (index === 0) {
                                                      console.log('First bubble data row:', {
                                                        originalRow: row,
                                                        segmentName,
                                                        businessImpact,
                                                        businessImpactNum,
                                                        performanceScore,
                                                        performanceScoreNum,
                                                        segmentSize,
                                                        sizeNum,
                                                        quadrant,
                                                        allRowKeys: Object.keys(row)
                                                      });
                                                    }

                                                    // Color based on quadrant
                                                    const quadrantColors: Record<string, string> = {
                                                      'Priority Fix': '#f44336',      // Red
                                                      'Maintain Excellence': '#4caf50', // Green
                                                      'Over-Investing': '#ff9800',     // Orange
                                                      'Low Priority': '#9e9e9e',       // Gray
                                                      'Unknown': '#4c3794'             // Purple (default)
                                                    };

                                                    const fillColor = quadrantColors[quadrant] || quadrantColors['Unknown'];

                                                    return {
                                                      name: segmentName,
                                                      x: performanceScoreNum,
                                                      y: businessImpactNum,
                                                      size: sizeNum,
                                                      opportunityScore: opportunityScoreNum,
                                                      quadrant,
                                                      performanceCategory,
                                                      percentage,
                                                      outcomes,
                                                      fill: fillColor
                                                    };
                                                  })
                                                  .filter((item: any) => {
                                                    const isValid = item != null && item.name && item.name !== 'Unknown' && !isNaN(item.x) && !isNaN(item.y) && item.x !== 0 && item.y !== 0;
                                                    if (!isValid && item) {
                                                      console.log('Filtered out invalid bubble data:', item);
                                                    }
                                                    return isValid;
                                                  });

                                                console.log('Processed bubble data:', bubbleData, 'Length:', bubbleData.length);

                                                if (bubbleData.length === 0) {
                                                  return (
                                                    <Box sx={{ width: '100%', height: '500px', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <Typography sx={{ color: '#64748B' }}>No valid segment opportunity data available. Please check console for details.</Typography>
                                                    </Box>
                                                  );
                                                }

                                                // Calculate ranges for axes
                                                const xValues = bubbleData.map(d => d.x).filter(v => !isNaN(v) && v != null);
                                                const yValues = bubbleData.map(d => d.y).filter(v => !isNaN(v) && v != null);

                                                if (xValues.length === 0 || yValues.length === 0) {
                                                  console.error('No valid x or y values found in bubble data');
                                                  return (
                                                    <Box sx={{ width: '100%', height: '500px', mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                      <Typography sx={{ color: '#64748B' }}>No valid data points available</Typography>
                                                    </Box>
                                                  );
                                                }

                                                const xMin = Math.min(...xValues);
                                                const xMax = Math.max(...xValues);
                                                const yMin = Math.min(...yValues);
                                                const yMax = Math.max(...yValues);
                                                const xRange = xMax - xMin || 1; // Prevent division by zero
                                                const yRange = yMax - yMin || 1; // Prevent division by zero

                                                console.log('Chart domain calculation:', {
                                                  xMin,
                                                  xMax,
                                                  yMin,
                                                  yMax,
                                                  xRange,
                                                  yRange,
                                                  bubbleDataLength: bubbleData.length
                                                });

                                                // Calculate size range for scaling bubbles
                                                const sizes = bubbleData.map(d => d.size).filter(s => !isNaN(s) && s > 0);
                                                const minSizeValue = sizes.length > 0 ? Math.min(...sizes) : 1;
                                                const maxSizeValue = sizes.length > 0 ? Math.max(...sizes) : 100;
                                                const sizeRangeValue = maxSizeValue - minSizeValue || 1;

                                                return (
                                                  <Box sx={{ width: '100%', mt: 2 }}>
                                                    {/* Toggle Switch - matching Priority Matrix/Heatmap style */}
                                                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5 }}>
                                                      <Typography sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: segmentOpportunityMapView === 'bubble' ? '#4c3794' : '#64748B',
                                                        transition: 'color 0.3s ease'
                                                      }}>
                                                        Bubble Chart
                                                      </Typography>
                                                      <Box
                                                        onClick={() => setSegmentOpportunityMapView(segmentOpportunityMapView === 'bubble' ? 'treemap' : 'bubble')}
                                                        sx={{
                                                          position: 'relative',
                                                          display: 'inline-flex',
                                                          height: '24px',
                                                          width: '44px',
                                                          alignItems: 'center',
                                                          borderRadius: '12px',
                                                          backgroundColor: segmentOpportunityMapView === 'treemap' ? '#4c3794' : '#D1D5DB',
                                                          cursor: 'pointer',
                                                          transition: 'background-color 0.3s ease',
                                                          '&:hover': {
                                                            opacity: 0.9
                                                          },
                                                          '&:focus': {
                                                            outline: 'none',
                                                            boxShadow: '0 0 0 2px rgba(76, 55, 148, 0.2)'
                                                          }
                                                        }}
                                                      >
                                                        <Box
                                                          sx={{
                                                            display: 'inline-block',
                                                            height: '16px',
                                                            width: '16px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#fff',
                                                            transform: segmentOpportunityMapView === 'treemap' ? 'translateX(24px)' : 'translateX(4px)',
                                                            transition: 'transform 0.3s ease',
                                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                                          }}
                                                        />
                                                      </Box>
                                                      <Typography sx={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: segmentOpportunityMapView === 'treemap' ? '#4c3794' : '#64748B',
                                                        transition: 'color 0.3s ease'
                                                      }}>
                                                        TreeMap
                                                      </Typography>
                                                    </Box>

                                                    {segmentOpportunityMapView === 'bubble' ? (
                                                      // Bubble Chart View
                                                      <Box sx={{
                                                        width: '100%',
                                                        minHeight: '600px',
                                                        // borderRadius: '16px',
                                                        // boxShadow: '0 4px 20px rgba(76, 55, 148, 0.15)',
                                                        // border: '1px solid rgba(76, 55, 148, 0.1)',
                                                        p: 3,
                                                        backgroundColor: '#fff',
                                                        position: 'relative'
                                                      }}>


                                                        <Box sx={{
                                                          width: '100%',
                                                          height: '700px',
                                                          position: 'relative',
                                                          mb: 2
                                                        }}>
                                                          <ResponsiveContainer width="100%" height="100%">
                                                            <ScatterChart
                                                              margin={{ top: 30, right: 30, bottom: 30, left: 60 }}
                                                            >
                                                              {/* Quadrant Background Colors - matching Priority Matrix style */}
                                                              {/* Top-left: Priority Fix - Light Red (Low Performance Score, High Business Impact) */}
                                                              <ReferenceArea
                                                                x1={xMin - xRange * 0.1}
                                                                x2={(xMin + xMax) / 2}
                                                                y1={(yMin + yMax) / 2}
                                                                y2={yMax + yRange * 0.1}
                                                                fill="rgba(244, 67, 54, 0.08)"
                                                                fillOpacity={1}
                                                              />
                                                              {/* Top-right: Maintain Excellence - Light Green (High Performance Score, High Business Impact) */}
                                                              <ReferenceArea
                                                                x1={(xMin + xMax) / 2}
                                                                x2={xMax + xRange * 0.1}
                                                                y1={(yMin + yMax) / 2}
                                                                y2={yMax + yRange * 0.1}
                                                                fill="rgba(76, 175, 80, 0.08)"
                                                                fillOpacity={1}
                                                              />
                                                              {/* Bottom-left: Low Priority - Light Gray (Low Performance Score, Low Business Impact) */}
                                                              <ReferenceArea
                                                                x1={xMin - xRange * 0.1}
                                                                x2={(xMin + xMax) / 2}
                                                                y1={yMin - yRange * 0.1}
                                                                y2={(yMin + yMax) / 2}
                                                                fill="rgba(158, 158, 158, 0.08)"
                                                                fillOpacity={1}
                                                              />
                                                              {/* Bottom-right: Over-Investing - Light Yellow/Amber (High Performance Score, Low Business Impact) */}
                                                              <ReferenceArea
                                                                x1={(xMin + xMax) / 2}
                                                                x2={xMax + xRange * 0.1}
                                                                y1={yMin - yRange * 0.1}
                                                                y2={(yMin + yMax) / 2}
                                                                fill="rgba(255, 152, 0, 0.08)"
                                                                fillOpacity={1}
                                                              />

                                                              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                                                              <XAxis
                                                                type="number"
                                                                dataKey="x"
                                                                name="Aggregate Performance Score"
                                                                label={{
                                                                  value: 'Aggregate Performance Score',
                                                                  position: 'insideBottom',
                                                                  offset: -5,
                                                                  style: { textAnchor: 'middle', fontSize: '0.875rem', fill: '#64748B' }
                                                                }}
                                                                domain={[xMin - xRange * 0.1, xMax + xRange * 0.1]}
                                                                tick={{ fontSize: 12, fill: '#64748B' }}
                                                                tickFormatter={(value) => typeof value === 'number' ? value.toFixed(2) : String(value)}
                                                              />
                                                              <YAxis
                                                                type="number"
                                                                dataKey="y"
                                                                name="Business Impact"
                                                                label={{
                                                                  value: 'Business Impact',
                                                                  angle: -90,
                                                                  position: 'insideLeft',
                                                                  style: { textAnchor: 'middle', fontSize: '0.875rem', fill: '#64748B' }
                                                                }}
                                                                domain={[yMin - yRange * 0.1, yMax + yRange * 0.1]}
                                                                tick={{ fontSize: 12, fill: '#64748B' }}
                                                                tickFormatter={(value) => typeof value === 'number' ? value.toFixed(2) : String(value)}
                                                              />

                                                              {/* Labels inside chart */}
                                                              {/* Top label - High Impact */}
                                                              <ReferenceLine
                                                                y={yMax + yRange * 0.05}
                                                                stroke="transparent"
                                                                label={{
                                                                  value: 'High Impact',
                                                                  position: 'top',
                                                                  fill: '#64748B',
                                                                  fontSize: 11,
                                                                  fontWeight: 600,
                                                                  offset: 5
                                                                }}
                                                              />

                                                              {/* Bottom label - Low Impact */}
                                                              <ReferenceLine
                                                                y={yMin - yRange * 0.05}
                                                                stroke="transparent"
                                                                label={{
                                                                  value: 'Low Impact',
                                                                  position: 'bottom',
                                                                  fill: '#64748B',
                                                                  fontSize: 11,
                                                                  fontWeight: 600,
                                                                  offset: 5
                                                                }}
                                                              />

                                                              {/* Left label - Low Performance - Vertical, centered */}
                                                              <ReferenceLine
                                                                x={xMin - xRange * 0.08}
                                                                stroke="transparent"
                                                                label={({ viewBox }: any) => {
                                                                  if (!viewBox) return <g />;
                                                                  // Use the center of the viewBox for vertical centering
                                                                  const centerY = viewBox.y + (viewBox.height || 0) / 2;
                                                                  return (
                                                                    <text
                                                                      x={viewBox.x}
                                                                      y={centerY}
                                                                      fill="#64748B"
                                                                      fontSize={11}
                                                                      fontWeight={600}
                                                                      textAnchor="middle"
                                                                      dominantBaseline="middle"
                                                                      transform={`rotate(-90 ${viewBox.x} ${centerY})`}
                                                                      style={{ pointerEvents: 'none' }}
                                                                    >
                                                                      Low Performance
                                                                    </text>
                                                                  );
                                                                }}
                                                              />

                                                              {/* Right label - High Performance - Vertical, centered */}
                                                              <ReferenceLine
                                                                x={xMax + xRange * 0.08}
                                                                stroke="transparent"
                                                                label={({ viewBox }: any) => {
                                                                  if (!viewBox) return <g />;
                                                                  // Use the center of the viewBox for vertical centering
                                                                  const centerY = viewBox.y + (viewBox.height || 0) / 2;
                                                                  return (
                                                                    <text
                                                                      x={viewBox.x}
                                                                      y={centerY}
                                                                      fill="#64748B"
                                                                      fontSize={11}
                                                                      fontWeight={600}
                                                                      textAnchor="middle"
                                                                      dominantBaseline="middle"
                                                                      transform={`rotate(-90 ${viewBox.x} ${centerY})`}
                                                                      style={{ pointerEvents: 'none' }}
                                                                    >
                                                                      High Performance
                                                                    </text>
                                                                  );
                                                                }}
                                                              />
                                                              <Tooltip
                                                                content={({ active, payload }) => {
                                                                  if (active && payload && payload.length) {
                                                                    const data = payload[0].payload;
                                                                    return (
                                                                      <Box sx={{
                                                                        backgroundColor: '#fff',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid #e9ecef',
                                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                                        padding: '12px 16px',
                                                                        minWidth: '250px'
                                                                      }}>
                                                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1.5, color: '#131F32' }}>
                                                                          {data.name}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Business Impact:</Typography>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.y === 'number' ? data.y.toFixed(2) : data.y}</Typography>
                                                                          </Box>
                                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Score:</Typography>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.x === 'number' ? data.x.toFixed(2) : data.x}</Typography>
                                                                          </Box>
                                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Segment Size:</Typography>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.size === 'number' ? data.size.toFixed(2) : data.size}</Typography>
                                                                          </Box>
                                                                          {data.opportunityScore !== undefined && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                              <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Opportunity Score:</Typography>
                                                                              <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.opportunityScore === 'number' ? data.opportunityScore.toFixed(2) : data.opportunityScore}</Typography>
                                                                            </Box>
                                                                          )}
                                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Quadrant:</Typography>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.quadrant}</Typography>
                                                                          </Box>
                                                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Percentage:</Typography>
                                                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.percentage === 'number' ? data.percentage.toFixed(2) : data.percentage}%</Typography>
                                                                          </Box>
                                                                          {data.outcomes && typeof data.outcomes === 'object' && Object.keys(data.outcomes).length > 0 && (
                                                                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(76, 55, 148, 0.3)' }}>
                                                                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4c3794', mb: 0.5 }}>
                                                                                Outcomes
                                                                              </Typography>
                                                                              {Object.entries(data.outcomes).map(([key, value], idx) => (
                                                                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                                                                                  <Typography sx={{ color: '#64748B' }}>{key}:</Typography>
                                                                                  <Typography sx={{ color: '#131F32', fontWeight: 600 }}>{String(value)}</Typography>
                                                                                </Box>
                                                                              ))}
                                                                            </Box>
                                                                          )}
                                                                        </Box>
                                                                      </Box>
                                                                    );
                                                                  }
                                                                  return null;
                                                                }}
                                                              />
                                                              <Scatter
                                                                name="Segments"
                                                                data={bubbleData}
                                                                shape={(props: any): React.ReactElement => {
                                                                  const { cx, cy, payload } = props;

                                                                  // Debug logging for first bubble
                                                                  if (bubbleData.indexOf(payload) === 0) {
                                                                    console.log('Rendering first bubble:', {
                                                                      cx,
                                                                      cy,
                                                                      payload,
                                                                      sizeRangeValue,
                                                                      minSizeValue,
                                                                      maxSizeValue
                                                                    });
                                                                  }

                                                                  // Scale bubble size (min 20px, max 80px)
                                                                  const minRadius = 20;
                                                                  const maxRadius = 80;
                                                                  const radiusRange = maxRadius - minRadius;

                                                                  // Normalize size value (0-1 range)
                                                                  const normalizedSize = sizeRangeValue > 0
                                                                    ? (payload.size - minSizeValue) / sizeRangeValue
                                                                    : 0.5;

                                                                  const radius = minRadius + (radiusRange * normalizedSize);

                                                                  // Ensure valid coordinates
                                                                  if (!cx || !cy || isNaN(cx) || isNaN(cy)) {
                                                                    console.warn('Invalid coordinates for bubble:', { cx, cy, payload });
                                                                    return React.createElement('g', {}); // Return empty group
                                                                  }

                                                                  return React.createElement('circle', {
                                                                    cx: cx,
                                                                    cy: cy,
                                                                    r: radius,
                                                                    fill: payload.fill || '#4c3794',
                                                                    fillOpacity: 0.7,
                                                                    stroke: '#fff',
                                                                    strokeWidth: 2
                                                                  });
                                                                }}
                                                              />

                                                              {/* Quadrant Reference Lines */}
                                                              <ReferenceLine
                                                                x={(xMin + xMax) / 2}
                                                                stroke="#94A3B8"
                                                                strokeDasharray="3 3"
                                                                strokeWidth={1}
                                                              />
                                                              <ReferenceLine
                                                                y={(yMin + yMax) / 2}
                                                                stroke="#94A3B8"
                                                                strokeDasharray="3 3"
                                                                strokeWidth={1}
                                                              />

                                                            </ScatterChart>
                                                          </ResponsiveContainer>
                                                        </Box>

                                                        {/* Legend */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
                                                          {['Priority Fix', 'Maintain Excellence', 'Over-Investing', 'Low Priority'].map((quadrant) => {
                                                            const color = {
                                                              'Priority Fix': '#f44336',
                                                              'Maintain Excellence': '#4caf50',
                                                              'Over-Investing': '#ff9800',
                                                              'Low Priority': '#9e9e9e'
                                                            }[quadrant] || '#4c3794';

                                                            return (
                                                              <Box key={quadrant} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: color }} />
                                                                <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>
                                                                  {quadrant}
                                                                </Typography>
                                                              </Box>
                                                            );
                                                          })}
                                                        </Box>
                                                      </Box>
                                                    ) : (
                                                      // TreeMap View (reuse existing treemap logic)
                                                      <Box sx={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        mt: 2
                                                      }}>
                                                        <Box sx={{
                                                          width: '65%',
                                                          height: '450px',
                                                          borderRadius: '16px',
                                                          boxShadow: '0 4px 20px rgba(76, 55, 148, 0.15)',
                                                          border: '1px solid rgba(76, 55, 148, 0.1)',
                                                          position: 'relative',
                                                          '& > div': {
                                                            borderRadius: '16px',
                                                            overflow: 'hidden'
                                                          },
                                                          '& .nivo-tooltip': {
                                                            zIndex: 9999
                                                          }
                                                        }}>
                                                          <ResponsiveTreeMap
                                                            data={{
                                                              name: 'Segments',
                                                              children: bubbleData.map((item: any) => ({
                                                                id: item.name,
                                                                value: item.size,
                                                                color: item.fill,
                                                                percentage: item.percentage,
                                                                performanceScore: item.x,
                                                                businessImpact: item.y,
                                                                opportunityScore: item.opportunityScore,
                                                                quadrant: item.quadrant,
                                                                performanceCategory: item.performanceCategory,
                                                                outcomes: item.outcomes
                                                              }))
                                                            }}
                                                            value="value"
                                                            valueFormat=".0s"
                                                            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                                                            tile="squarify"
                                                            enableLabel={false}
                                                            theme={{
                                                              labels: {
                                                                text: {
                                                                  fontSize: 14,
                                                                  fontWeight: 600
                                                                }
                                                              }
                                                            }}
                                                            colors={{ scheme: 'oranges' }}
                                                            borderWidth={3}
                                                            borderColor="black"
                                                            animate={true}
                                                            motionConfig="gentle"
                                                            enableParentLabel={false}
                                                            orientLabel={false}
                                                            tooltip={({ node }: any) => {
                                                              if (!node) return null;
                                                              const data = node.data;
                                                              return (
                                                                <Box sx={{
                                                                  backgroundColor: '#fff',
                                                                  borderRadius: '8px',
                                                                  border: '1px solid #e9ecef',
                                                                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                                                  padding: '12px 16px',
                                                                  minWidth: '300px',
                                                                  zIndex: 99999
                                                                }}>
                                                                  <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#131F32', mb: 1.5 }}>
                                                                    {node.id || 'Unknown'}
                                                                  </Typography>
                                                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                      <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Size:</Typography>
                                                                      <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{node.value}</Typography>
                                                                    </Box>
                                                                    {data.percentage !== undefined && (
                                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Percentage:</Typography>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.percentage === 'number' ? data.percentage.toFixed(2) : data.percentage}%</Typography>
                                                                      </Box>
                                                                    )}
                                                                    {data.businessImpact !== undefined && (
                                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Business Impact:</Typography>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.businessImpact === 'number' ? data.businessImpact.toFixed(2) : data.businessImpact}</Typography>
                                                                      </Box>
                                                                    )}
                                                                    {data.performanceScore !== undefined && (
                                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Score:</Typography>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.performanceScore === 'number' ? data.performanceScore.toFixed(2) : data.performanceScore}</Typography>
                                                                      </Box>
                                                                    )}
                                                                    {data.opportunityScore !== undefined && (
                                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Opportunity Score:</Typography>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.opportunityScore === 'number' ? data.opportunityScore.toFixed(2) : data.opportunityScore}</Typography>
                                                                      </Box>
                                                                    )}
                                                                    {data.quadrant && (
                                                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Quadrant:</Typography>
                                                                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.quadrant}</Typography>
                                                                      </Box>
                                                                    )}
                                                                    {data.outcomes && typeof data.outcomes === 'object' && Object.keys(data.outcomes).length > 0 && (
                                                                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(76, 55, 148, 0.3)' }}>
                                                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4c3794', mb: 0.5 }}>
                                                                          Outcomes
                                                                        </Typography>
                                                                        {Object.entries(data.outcomes).map(([key, value], idx) => (
                                                                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                                                                            <Typography sx={{ color: '#64748B' }}>{key}:</Typography>
                                                                            <Typography sx={{ color: '#131F32', fontWeight: 600 }}>{String(value)}</Typography>
                                                                          </Box>
                                                                        ))}
                                                                      </Box>
                                                                    )}
                                                                  </Box>
                                                                </Box>
                                                              );
                                                            }}
                                                            layers={['nodes', ({ nodes }: any) => {
                                                              if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return null;
                                                              return (
                                                                <g>
                                                                  {nodes.filter((node: any) => !node.isParent).map((node: any) => {
                                                                    if (!node || node.width < 60 || node.height < 35) return null;
                                                                    const name = String(node.id || node.data?.name || '');
                                                                    if (!name || name === 'undefined' || name === 'null' || name === 'Segments') return null;
                                                                    const charsPerLine = Math.max(8, Math.floor((node.width - 16) / 8));
                                                                    const lineHeight = 18;
                                                                    const maxLines = Math.max(1, Math.floor((node.height - 12) / lineHeight));
                                                                    const words = name.split(' ');
                                                                    const lines: string[] = [];
                                                                    let currentLine = '';
                                                                    for (const word of words) {
                                                                      if (lines.length >= maxLines) break;
                                                                      const testLine = currentLine ? `${currentLine} ${word}` : word;
                                                                      if (testLine.length <= charsPerLine) {
                                                                        currentLine = testLine;
                                                                      } else {
                                                                        if (currentLine) {
                                                                          lines.push(currentLine);
                                                                          if (lines.length >= maxLines) break;
                                                                          currentLine = word.length > charsPerLine ? word.substring(0, charsPerLine - 2) + '..' : word;
                                                                        } else {
                                                                          currentLine = word.substring(0, charsPerLine - 2) + '..';
                                                                        }
                                                                      }
                                                                    }
                                                                    if (currentLine && lines.length < maxLines) {
                                                                      lines.push(currentLine);
                                                                    }
                                                                    if (lines.length === 0) return null;
                                                                    const totalHeight = lines.length * lineHeight;
                                                                    const startY = node.y + (node.height - totalHeight) / 2 + lineHeight / 2;
                                                                    return (
                                                                      <g key={`label-${node.id}`}>
                                                                        {lines.map((line, index) => (
                                                                          <text
                                                                            key={index}
                                                                            x={node.x + node.width / 2}
                                                                            y={startY + index * lineHeight}
                                                                            textAnchor="middle"
                                                                            dominantBaseline="middle"
                                                                            fill="#000000"
                                                                            fontSize={14}
                                                                            fontWeight={600}
                                                                            style={{ pointerEvents: 'none' }}
                                                                          >
                                                                            {line}
                                                                          </text>
                                                                        ))}
                                                                      </g>
                                                                    );
                                                                  })}
                                                                </g>
                                                              );
                                                            }]}
                                                          />
                                                        </Box>
                                                      </Box>
                                                    )}
                                                  </Box>
                                                );
                                              }

                                              // Sort chart data by count (descending) and take top 4 for small chart view
                                              // Determine the data key to use for sorting and display
                                              // For Distribution of Survey Outcomes, use percentage; otherwise use count
                                              const barDataKey = isDistributionOfSurveyOutcomes ? 'percentage' : 'count';

                                              const sortedChartData = [...chartData].sort((a, b) => {
                                                const aCount = a[barDataKey] || 0;
                                                const bCount = b[barDataKey] || 0;
                                                return bCount - aCount;
                                              });
                                              const top3ChartData = sortedChartData.slice(0, 3);

                                              // Calculate max value from ALL data for consistent scale
                                              const allDataMax = Math.max(...sortedChartData.map(d => d[barDataKey] || 0));

                                              return (
                                                <Box sx={{ width: '100%', height: '250px', mt: 2 }}>
                                                  <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                      data={top3ChartData}
                                                      layout="vertical"
                                                      margin={{ top: 5, right: 30, left: -10, bottom: 5 }}
                                                      barCategoryGap={20}
                                                    >
                                                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                                      <XAxis
                                                        type="number"
                                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                                        domain={isKeyDrivers ? [0, 5] : isDistributionOfSurveyOutcomes ? [0, 100] : [0, Math.ceil(allDataMax * 1.1)]}
                                                        tickCount={6}
                                                        allowDecimals={false}
                                                        tickFormatter={isDistributionOfSurveyOutcomes ? (value: number) => `${value}%` : undefined}
                                                      />
                                                      <YAxis
                                                        type="category"
                                                        dataKey="name"
                                                        width={200}
                                                        tick={{ fontSize: 12, fill: '#64748B' }}
                                                      />
                                                      <Tooltip content={<CustomTooltip />} />
                                                      <Bar
                                                        dataKey={barDataKey}
                                                        fill="#4c3794"
                                                        radius={[0, 8, 8, 0]}
                                                        maxBarSize={15}
                                                      />
                                                    </BarChart>
                                                  </ResponsiveContainer>
                                                </Box>
                                              );
                                            })()}

                                            {/* Display Recommendations below chart if available */}
                                            {item.recommendations && item.recommendations.length > 0 && (
                                              <Box sx={{ mt: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                  <Box sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}>
                                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>💡</Typography>
                                                  </Box>
                                                  <Typography sx={{
                                                    fontSize: '0.938rem',
                                                    fontWeight: 700,
                                                    color: '#131F32',
                                                    letterSpacing: '-0.01em'
                                                  }}>
                                                    Recommendations
                                                  </Typography>
                                                  {/* <Chip
                                              label={item.recommendations.length}
                                              size="small"
                                              sx={{
                                                height: '20px',
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                                color: '#3b82f6',
                                                ml: 'auto'
                                              }}
                                            /> */}
                                                </Box>
                                                {/* <Card sx={{ 
                                            backgroundColor: '#fff', 
                                            p: 2.5, 
                                            borderRadius: '12px',
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                          }}> */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                  {item.recommendations.map((rec: string, idx: number) => {
                                                    // Always render recommendations using MarkdownMessage to handle markdown formatting
                                                    return (
                                                      <Box
                                                        key={idx}

                                                      >
                                                        <Box sx={{
                                                          fontSize: '1rem !important',
                                                          color: '#495057',
                                                          lineHeight: 1.6
                                                        }}>
                                                          <MarkdownMessage text={rec.trim()} />
                                                        </Box>
                                                      </Box>
                                                    );
                                                  })}
                                                </Box>
                                                {/* </Card> */}
                                              </Box>
                                            )}

                                            {/* Expand Button at Bottom Center - Hide for Treemap/Segments Overview */}
                                            {!isSegmentsOverview && (
                                              <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mt: 'auto',
                                                pt: 2
                                              }}>
                                                <Button
                                                  variant="contained"
                                                  // startIcon={<FullscreenIcon sx={{ fontSize: 18 }} />}
                                                  onClick={() => {
                                                    // Prepare chart data for dialog
                                                    const getValue = (row: any, header: string): any => {
                                                      if (row[header] !== undefined) return row[header];
                                                      if (row[header.toLowerCase()] !== undefined) return row[header.toLowerCase()];
                                                      if (row[header.toUpperCase()] !== undefined) return row[header.toUpperCase()];
                                                      const capitalized = header.charAt(0).toUpperCase() + header.slice(1).toLowerCase();
                                                      if (row[capitalized] !== undefined) return row[capitalized];
                                                      return null;
                                                    };
                                                    const labelHeader = item.headers.find((h: string) => {
                                                      const val = getValue(item.data[0], h);
                                                      return typeof val !== 'number' && val !== null && val !== undefined;
                                                    }) || item.headers[0];
                                                    const numericHeaders = item.headers.filter((h: string) => {
                                                      if (h === labelHeader) return false;
                                                      const val = getValue(item.data[0], h);
                                                      return typeof val === 'number';
                                                    });
                                                    const primaryNumericHeader = numericHeaders.find((h: string) => {
                                                      const headerLower = h.toLowerCase();
                                                      return headerLower === 'count' || headerLower === 'total';
                                                    }) || numericHeaders[0] || null;
                                                    const chartData = item.data.map((row: any) => {
                                                      const chartItem: any = {
                                                        name: String(getValue(row, labelHeader) || '-')
                                                      };
                                                      item.headers.forEach((header: string) => {
                                                        if (header !== labelHeader) {
                                                          const val = getValue(row, header);
                                                          if (numericHeaders.includes(header) && val !== null && val !== undefined) {
                                                            const numVal = typeof val === 'number' ? val : parseFloat(String(val));
                                                            chartItem[header] = !isNaN(numVal) ? numVal : val;
                                                          } else {
                                                            chartItem[header] = val;
                                                          }
                                                        }
                                                      });
                                                      if (primaryNumericHeader) {
                                                        chartItem['count'] = chartItem[primaryNumericHeader] || 0;
                                                      }
                                                      return chartItem;
                                                    });

                                                    // Check if this is Segments Overview for treemap
                                                    const normalizedGroupName = group.groupName
                                                      ? group.groupName.toLowerCase().replace(/\s+/g, ' ').trim()
                                                      : '';
                                                    const normalizedItemTitle = item.title
                                                      ? item.title.toLowerCase().replace(/\s+/g, ' ').trim()
                                                      : '';
                                                    const itemKey = (item as any).key || '';
                                                    const hasSegmentHeaders = item.headers && (
                                                      item.headers.some((h: string) => h.toLowerCase().includes('segment name')) ||
                                                      item.headers.some((h: string) => h.toLowerCase().includes('segment') && h.toLowerCase().includes('name'))
                                                    );
                                                    const isSegmentsOverviewCheck =
                                                      (normalizedGroupName.includes('segments') && normalizedGroupName.includes('overview')) ||
                                                      (normalizedItemTitle.includes('segments') && normalizedItemTitle.includes('overview')) ||
                                                      itemKey === 'segmentsOverview' ||
                                                      itemKey.toLowerCase().includes('segments') ||
                                                      (hasSegmentHeaders && item.headers.some((h: string) => h.toLowerCase().includes('performance')));

                                                    let dialogChartData: any[] = [];
                                                    if (isSegmentsOverviewCheck) {
                                                      dialogChartData = item.data.map((row: any) => {
                                                        const segmentName = getValue(row, 'Segment Name') || getValue(row, 'Segment') || 'Unknown';
                                                        const size = getValue(row, 'Size') || 0;
                                                        const percentage = getValue(row, 'Percentage') || 0;
                                                        const performanceScore = getValue(row, 'Aggregate Performance Score');
                                                        const performanceCategory = getValue(row, 'Performance Category') || 'N/A';
                                                        const summary = getValue(row, 'Summary') || '';
                                                        // Extract Outcomes for dialog
                                                        const outcomes = getValue(row, 'Outcomes') || null;

                                                        let fillColor = '#4c3794';
                                                        if (performanceScore !== null && performanceScore !== undefined) {
                                                          const score = typeof performanceScore === 'number' ? performanceScore : parseFloat(String(performanceScore));
                                                          if (!isNaN(score)) {
                                                            if (score >= 70) fillColor = '#4caf50';
                                                            else if (score >= 40) fillColor = '#ff9800';
                                                            else fillColor = '#f44336';
                                                          }
                                                        } else if (performanceCategory) {
                                                          const category = String(performanceCategory).toLowerCase();
                                                          if (category.includes('high') || category.includes('excellent')) fillColor = '#4caf50';
                                                          else if (category.includes('moderate') || category.includes('medium')) fillColor = '#ff9800';
                                                          else if (category.includes('low') || category.includes('poor')) fillColor = '#f44336';
                                                        }

                                                        return {
                                                          name: segmentName,
                                                          size: typeof size === 'number' ? size : parseFloat(String(size)) || 0,
                                                          percentage: typeof percentage === 'number' ? percentage : parseFloat(String(percentage)) || 0,
                                                          performanceScore,
                                                          performanceCategory,
                                                          summary,
                                                          outcomes,
                                                          fill: fillColor
                                                        };
                                                      });
                                                    } else {
                                                      const sortedChartData = [...chartData].sort((a, b) => {
                                                        const aCount = a.count || 0;
                                                        const bCount = b.count || 0;
                                                        return bCount - aCount;
                                                      });
                                                      dialogChartData = sortedChartData;
                                                    }

                                                    // Check if this is Driver Priority Matrix / Strategic Focus Areas
                                                    const titleLower = item.title?.toLowerCase() || '';
                                                    const isDriverPriorityMatrixDialog =
                                                      titleLower.includes('driver priority matrix') ||
                                                      titleLower.includes('strategic focus areas') ||
                                                      titleLower.includes('performance vs impact');

                                                    setExpandedChartDialog({
                                                      open: true,
                                                      title: item.title,
                                                      chartData: dialogChartData,
                                                      headers: item.headers,
                                                      labelHeader,
                                                      primaryNumericHeader,
                                                      isKeyDrivers: !!(group.groupName && (
                                                        group.groupName.toLowerCase().includes('key drivers') ||
                                                        group.groupName.toLowerCase().includes('leverage drivers')
                                                      )),
                                                      isSegmentsOverview: isSegmentsOverviewCheck,
                                                      isDistributionOfSurveyOutcomes: !!(group.groupName &&
                                                        group.groupName.toLowerCase().replace(/\s+/g, ' ').includes('distribution of survey outcomes')),
                                                      isDriverPriorityMatrix: isDriverPriorityMatrixDialog,
                                                      analysis: item.analysis,
                                                      recommendations: item.recommendations
                                                    });
                                                  }}
                                                  sx={{
                                                    backgroundColor: '#4c3794',
                                                    color: '#fff',
                                                    width: '100%',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '0.813rem',
                                                    px: 3,
                                                    py: 1,
                                                    borderRadius: '8px',
                                                    boxShadow: '0 2px 8px rgba(76, 55, 148, 0.25)',
                                                    '&:hover': {
                                                      backgroundColor: '#3d2d7a',
                                                      boxShadow: '0 4px 12px rgba(76, 55, 148, 0.35)'
                                                    }
                                                  }}
                                                >
                                                  View More Details
                                                </Button>
                                              </Box>
                                            )}
                                          </Card>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </Box>
                              );
                            })}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              )}

              {/* Project Creation Dialog - Always available regardless of active tab */}
              <Dialog
                open={isProjectDialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
              >
                {/* <DialogTitle>
            {dialogStep === 'project' ? 'Create New Project' : 'Upload Survey Data'}
          </DialogTitle> */}
                <DialogContent>
                  {dialogStep === 'project' ? (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Project Name"
                        value={newProject.project_name}
                        onChange={(e) => setNewProject(prev => ({ ...prev, project_name: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="Project Description"
                        value={newProject.project_description}
                        onChange={(e) => setNewProject(prev => ({ ...prev, project_description: e.target.value }))}
                        multiline
                        rows={3}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Start Date"
                          type="date"
                          value={newProject.period_start_date}
                          onChange={(e) => setNewProject(prev => ({ ...prev, period_start_date: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          fullWidth
                          label="End Date"
                          type="date"
                          value={newProject.period_end_date}
                          onChange={(e) => setNewProject(prev => ({ ...prev, period_end_date: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      {/* <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Choose how you would like to upload your survey data:
                  </Typography> */}

                      {/* Upload Method Selection */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                          Select Upload Method
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4} p={1}>
                            <UploadMethodCard
                              className={uploadMethod === 'device' ? 'selected' : ''}
                              onClick={() => setUploadMethod('device')}
                            >
                              <CardContent sx={{ textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <UploadMethodIcon>
                                  <UploadIcon sx={{ fontSize: 24 }} />
                                </UploadMethodIcon>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                  Upload from Device
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Select a file from your computer or device
                                </Typography>
                              </CardContent>
                            </UploadMethodCard>
                          </Grid>
                          <Grid item xs={12} md={4} p={1}>
                            <UploadMethodCard
                              className={uploadMethod === 'url' ? 'selected' : ''}
                              onClick={() => setUploadMethod('url')}
                            >
                              <CardContent sx={{ textAlign: 'center', p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <UploadMethodIcon>
                                  <OpenInNewIcon sx={{ fontSize: 24 }} />
                                </UploadMethodIcon>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                  Provide File Link
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Download from Public URL (supports SharePoint, Google Drive, etc.)
                                </Typography>
                              </CardContent>
                            </UploadMethodCard>
                          </Grid>
                          <Grid item xs={12} md={4} p={1}>
                            <UploadMethodCard
                              className={uploadMethod === 'google-drive' ? 'selected' : ''}
                              onClick={() => setUploadMethod('google-drive')}
                            >
                              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
                                <UploadMethodIcon>
                                  <AssessmentIcon sx={{ fontSize: 24 }} />
                                </UploadMethodIcon>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                  Google Drive
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Select a file from your Google Drive
                                </Typography>
                              </CardContent>
                            </UploadMethodCard>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Upload Method Specific Content */}
                      {uploadMethod === 'device' ? (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                            Upload CSV File from Device
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Please select a file containing your survey data. The file should include all required survey fields.
                          </Typography>
                          <input
                            accept=".csv, .xlsx"
                            style={{ display: 'none' }}
                            id="survey-file-upload"
                            type="file"
                            onChange={handleDeviceFileUpload}
                          />
                          <label htmlFor="survey-file-upload">
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<UploadIcon />}
                              disabled={uploadLoading}
                              sx={{
                                backgroundColor: '#4c3794',
                                '&:hover': {
                                  backgroundColor: '#3a2a73',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              {uploadLoading ? 'Uploading...' : 'Select CSV File'}
                            </Button>
                          </label>
                        </Box>
                      ) : uploadMethod === 'url' ? (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                            Download from File URL
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Provide a direct link to your CSV file. We support public URLs and authenticated URLs (SharePoint, Google Drive, etc.).
                          </Typography>

                          {/* SharePoint-specific guidance */}
                          {fileUrl.includes('sharepoint.com') && (
                            <Alert
                              severity="info"
                              sx={{
                                mb: 2,
                                '& .MuiAlert-icon': {
                                  color: '#4c3794'
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                SharePoint File Detected
                              </Typography>
                              <Typography variant="body2">
                                This appears to be a SharePoint file. The system will open SharePoint in a new tab for secure authentication.
                              </Typography>
                              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                <li>SharePoint will open in a new tab for login</li>
                                <li>Download the file from SharePoint to your computer</li>
                                <li>Then upload the downloaded file using the device upload option</li>
                                <li>This approach avoids iframe restrictions and ensures security</li>
                              </ul>
                            </Alert>
                          )}

                          {/* Google Drive/Sheets guidance */}
                          {(fileUrl.includes('drive.google.com') || fileUrl.includes('docs.google.com') || fileUrl.includes('sheets.google.com')) && (
                            <Alert
                              severity="info"
                              sx={{
                                mb: 2,
                                '& .MuiAlert-icon': {
                                  color: '#4c3794'
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Google Drive/Sheets File Detected
                              </Typography>
                              <Typography variant="body2">
                                This appears to be a Google Drive or Google Sheets file. The system will attempt automatic download for public files.
                              </Typography>
                              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                <li>For public files: Automatic download and processing</li>
                                <li>For private files: Manual authentication in new tab</li>
                                <li>Supports Google Sheets, Docs, and Drive files</li>
                                <li>Secure handling with proper authentication fallback</li>
                              </ul>
                            </Alert>
                          )}

                          <TextField
                            fullWidth
                            label="File URL"
                            placeholder="https://example.com/survey-data.csv"
                            value={fileUrl}
                            onChange={(e) => setFileUrl(e.target.value)}
                            disabled={uploadLoading || isUrlValidating || isDownloading}
                            sx={{ mb: 2 }}
                            error={!!urlValidationError}
                            helperText={urlValidationError}
                          />

                          <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleUrlUpload}
                            disabled={!fileUrl.trim() || uploadLoading || isUrlValidating || isDownloading}
                            sx={{
                              backgroundColor: '#4c3794',
                              '&:hover': {
                                backgroundColor: '#3a2a73',
                              },
                              '&.Mui-disabled': {
                                backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                color: '#ffffff'
                              }
                            }}
                          >
                            {isUrlValidating ? 'Validating URL...' :
                              isDownloading ? 'Downloading & Processing...' :
                                uploadLoading ? 'Uploading...' : 'Download & Process'}
                          </Button>

                          {/* Progress indicators */}
                          {(isUrlValidating || isDownloading || uploadLoading) && (
                            <Box sx={{ mt: 2 }}>
                              <LinearProgress
                                variant={isDownloading || uploadLoading ? "determinate" : "indeterminate"}
                                value={isDownloading || uploadLoading ? downloadProgress : undefined}
                                sx={{
                                  color: '#4c3794',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#4c3794'
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                                {isUrlValidating ? 'Validating file URL and checking access...' :
                                  isDownloading ? `Downloading and processing file... ${Math.round(downloadProgress)}%` :
                                    uploadLoading ? 'Processing and uploading file...' : ''}
                              </Typography>
                              {isDownloading && downloadProgress > 0 && downloadProgress < 100 && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                                  {fileUrl.includes('githubusercontent.com') ? 'Downloading from GitHub...' :
                                    fileUrl.includes('sharepoint.com') ? 'If authentication is required, you\'ll be prompted to log in' :
                                      'Processing file on server...'}
                                </Typography>
                              )}
                            </Box>
                          )}


                        </Box>
                      ) : null}

                      {uploadMethod === 'google-drive' && (
                        <Box sx={{ mb: 3 }}>
                          {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                      Select a file from Google Drive
                    </Typography> */}
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Authenticate with Google Drive and select a file to upload.
                          </Typography>

                          {googleDriveAuthState.isAuthenticated && (
                            <Box sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: 'rgba(16, 185, 129, 0.05)',
                              borderRadius: 2,
                              border: '1px solid rgba(16, 185, 129, 0.1)'
                            }}>
                              <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mb: 1 }}>
                                ✅ Connected to Google Drive
                              </Typography>
                              {/* <Typography variant="body2" sx={{ color: '#10B981', fontSize: '0.875rem', mb: 2 }}>
                          You're currently connected. If you need to switch accounts, click "Switch Account" below.
                        </Typography> */}
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={clearGoogleDriveAuth}
                                sx={{
                                  borderColor: '#10B981',
                                  color: '#10B981',
                                  '&:hover': {
                                    borderColor: '#059669',
                                    backgroundColor: 'rgba(16, 185, 129, 0.05)'
                                  }
                                }}
                              >
                                Switch Account
                              </Button>
                            </Box>
                          )}

                          <Button
                            variant="contained"
                            startIcon={<AssessmentIcon />}
                            onClick={openGoogleDriveDialog}
                            sx={{
                              backgroundColor: '#4c3794',
                              '&:hover': {
                                backgroundColor: '#3a2a73',
                              },
                              '&.Mui-disabled': {
                                backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                color: '#ffffff'
                              }
                            }}
                          >
                            {googleDriveAuthState.isAuthenticated ? 'Browse & Select File' : 'Connect to Google Drive'}
                          </Button>
                        </Box>
                      )}

                      {/* Status Messages */}
                      {success && (
                        <Alert
                          severity="success"
                          sx={{
                            mt: 2,
                            '& .MuiAlert-icon': {
                              color: '#4c3794'
                            }
                          }}
                        >
                          {success}
                        </Alert>
                      )}
                      {error && !googleDriveAuthState.isAuthenticated && (
                        <Alert
                          severity="error"
                          sx={{
                            mt: 2,
                            '& .MuiAlert-icon': {
                              color: '#4c3794'
                            }
                          }}
                        >
                          {error}
                        </Alert>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  {dialogStep === 'project' ? (
                    <>
                      <Button
                        onClick={handleCloseDialog}
                        sx={{ color: '#4c3794' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateProject}
                        variant="contained"
                        disabled={!newProject.project_name || !newProject.project_description || !newProject.period_start_date || !newProject.period_end_date}
                        sx={{
                          backgroundColor: '#4c3794',
                          '&:hover': {
                            backgroundColor: '#3a2a73',
                          }
                        }}
                      >
                        Next
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleCloseDialog}
                      sx={{ color: '#4c3794' }}
                    >
                      Close
                    </Button>
                  )}
                </DialogActions>
              </Dialog>

              {/* Main Content */}
              {activeMainTab === 'dashboard' && (
                <Box>
                  {!canViewProject && selectedProject ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexGrow: 1,
                      backgroundColor: '#f5f5f5',
                      p: 4
                    }}>
                      <Box sx={{
                        textAlign: 'center',
                        maxWidth: 500,
                        p: 4,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}>
                        <LockIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                        <Typography variant="h4" sx={{ mb: 2, color: '#333' }}>
                          Access Denied
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
                          You don't have access to this project
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
                          Please create a new project or select another project that you have access to.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setIsProjectDialogOpen(true)}
                            sx={{
                              backgroundColor: '#4c3794',
                              '&:hover': {
                                backgroundColor: '#3a2a73',
                              }
                            }}
                          >
                            Create New Project
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => window.location.reload()}
                            sx={{
                              borderColor: '#4c3794',
                              color: '#4c3794',
                              '&:hover': {
                                borderColor: '#3a2a73',
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                              }
                            }}
                          >
                            Refresh
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{
                      display: isFullScreen ? 'none' : 'flex',
                      flexGrow: 1,
                      overflow: 'hidden',
                      backgroundColor: '#f5f5f5',
                      transition: 'width 0.3s ease-in-out',
                      width: isChatOpen ? '60%' : '100%'
                    }}>
                      {/* Dashboard Content */}
                      <Box sx={{
                        width: '100%',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        p: 3
                      }}>





                        {isDashboardLoading ? (
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            gap: 2
                          }}>
                            <CircularProgress size={40} sx={{ color: '#4c3794' }} />
                            <Typography variant="body2" sx={{
                              color: '#64748B',
                              fontSize: '0.8rem',
                              fontWeight: 500
                            }}>
                              {detectedProjectType === 'DATA_ANAL' ? 'Loading data analytics...' : 'Loading survey insights...'}
                            </Typography>
                            <LinearProgress
                              sx={{
                                width: '200px',
                                height: '4px',
                                borderRadius: '2px',
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#4c3794'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Grid container spacing={3}>

                            {/* Summary Cards */}
                            <Grid item xs={12}>
                              <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                  <AssessmentIcon sx={{ color: '#4c3794' }} />
                                  <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: '#1a1a1a',
                                    fontSize: '1.1rem'
                                  }}>
                                    Survey Overview
                                  </Typography>
                                </Box>

                                {/* Refresh and Add Document Buttons */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {/* Help Text - Left of Refresh Button */}


                                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Typography variant="caption" sx={{
                                      color: '#9CA3AF',
                                      fontSize: '0.75rem !important',
                                      display: 'block',
                                      lineHeight: 1.2
                                    }}>
                                      Click refresh to see latest documents
                                    </Typography>
                                    {lastFetchedTime && (
                                      <Typography variant="caption" sx={{
                                        color: '#9CA3AF',
                                        fontSize: '0.65rem !important',
                                        display: 'block',
                                        fontStyle: 'italic',
                                        lineHeight: 1.2
                                      }}>
                                        Last refreshed: {new Date(lastFetchedTime).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          hour12: true
                                        })}
                                      </Typography>
                                    )}
                                  </Box>
                                  <ViewAllButton

                                    variant="contained"

                                    startIcon={<OpenInNewIcon />}

                                    onClick={() => {
                                      if (detectedProjectType !== 'DATA_ANAL') {
                                        setIsViewAllDialogOpen(true);
                                      }
                                    }}

                                    sx={{

                                      backgroundColor: '#fff',

                                      // color: '#ffffff',

                                      // '&:hover': {

                                      // backgroundColor: '#3a2a73',

                                      // }

                                    }}

                                  >

                                    View All Responses

                                  </ViewAllButton>
                                  {/* Refresh Button */}
                                  <MuiTooltip title={lastFetchedTime ? `Last refreshed: ${lastFetchedTime.toLocaleString()}` : 'Refresh data'}>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={
                                        <RefreshIcon sx={{
                                          fontSize: 18,
                                          animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                          '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' }
                                          }
                                        }} />
                                      }
                                      onClick={async () => {
                                        setIsRefreshing(true);
                                        await fetchSurveyItems();
                                      }}
                                      disabled={isRefreshing || loading}
                                      sx={{
                                        color: '#4c3794',
                                        borderColor: '#E5E9EF',
                                        borderRadius: '8px',
                                        height: '40px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 1.5,
                                        backgroundColor: 'white',
                                        '&:hover': {
                                          backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                          borderColor: '#4c3794'
                                        },
                                        '&.Mui-disabled': {
                                          color: '#D1D5DB',
                                          borderColor: '#E5E9EF'
                                        }
                                      }}
                                    >
                                      {isRefreshing ? 'Refreshing...' : 'Refresh'}
                                    </Button>
                                  </MuiTooltip>

                                  {/* Add Document Button - Disabled if user lacks edit permission */}
                                  <MuiTooltip title={!canEditProject ? "Edit permission required to add documents" : ""}>
                                    <span>
                                      <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                          setDialogStep('upload');
                                          setIsProjectDialogOpen(true);
                                        }}
                                        disabled={!selectedProject || !canEditProject}
                                        sx={{
                                          backgroundColor: canEditProject ? '#4c3794' : 'rgba(76, 55, 148, 0.3)',
                                          '&:hover': {
                                            backgroundColor: canEditProject ? '#3a2a73' : 'rgba(76, 55, 148, 0.3)',
                                          },
                                          '&.Mui-disabled': {
                                            backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                            color: '#ffffff'
                                          },
                                          borderRadius: '8px',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          px: 3,
                                          py: 1
                                        }}
                                      >
                                        Add Document
                                      </Button>
                                    </span>
                                  </MuiTooltip>
                                </Box>
                              </Box>



                            </Grid>

                            {/* Sentiment Distribution Chart */}
                            <Grid item xs={12} md={6}>
                              <Card sx={{
                                height: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',

                              }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                    <SentimentAnalysisIcon sx={{ color: '#4c3794' }} />
                                    <Typography variant="h6" sx={{
                                      fontWeight: 600,
                                      color: '#1a1a1a',
                                      fontSize: '1.1rem'
                                    }}>
                                      Sentiment Distribution
                                    </Typography>
                                  </Box>
                                  {dashboardData.sentimentDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                      <PieChart>
                                        <Pie
                                          data={dashboardData.sentimentDistribution}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          outerRadius={80}
                                          innerRadius={40}
                                          fill="#4c3794"
                                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                          labelLine={false}
                                        >
                                          {dashboardData.sentimentDistribution.map((entry: ChartDataItem, index: number) => (
                                            <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                entry.name === 'Positive' ? '#4c3794' :     // light purple for Positive
                                                  entry.name === 'Negative' ? '#b19cd9' :     // deep purple for Negative
                                                    '#d8bfd8'                                   // thistle (soft purple) for Neutral or others
                                              }
                                            />
                                          ))}
                                        </Pie>
                                        <Tooltip
                                          formatter={(value: number, name: string) => [`${value} reviews`, name]}
                                          contentStyle={{
                                            backgroundColor: '#ede7f6',
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                                            padding: '8px 12px',
                                            color: '#4c3794'
                                          }}
                                        />
                                        <Legend
                                          verticalAlign="bottom"
                                          height={36}
                                          formatter={(value) => (
                                            <span style={{ color: '#4c3794', fontSize: '12px' }}>{value}</span>
                                          )}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <Box sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: 250,
                                      backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                      borderRadius: '8px'
                                    }}>
                                      <Typography color="text.secondary">No sentiment data available</Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* Topic Distribution Chart */}
                            <Grid item xs={12} md={6}>
                              <Card sx={{
                                height: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',

                              }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                    <AssessmentIcon sx={{ color: '#4c3794' }} />
                                    <Typography variant="h6" sx={{
                                      fontWeight: 600,
                                      color: '#1a1a1a',
                                      fontSize: '1.1rem'
                                    }}>
                                      Topic Distribution
                                    </Typography>
                                  </Box>
                                  {dashboardData.topicDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                      <BarChart
                                        data={dashboardData.topicDistribution}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                      >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
                                        <XAxis
                                          dataKey="name"
                                          angle={-45}
                                          textAnchor="end"
                                          height={70}
                                          tick={{ fontSize: 12, fill: '#4c3794' }}
                                          tickLine={{ stroke: '#b39ddb' }}
                                        />
                                        <YAxis
                                          tick={{ fontSize: 12, fill: '#4c3794' }}
                                          tickLine={{ stroke: '#b39ddb' }}
                                          label={{
                                            value: 'Frequency',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fill: '#4c3794' }
                                          }}
                                        />
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: '#ede7f6',
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                                            padding: '8px 12px',
                                            color: '#4c3794'
                                          }}
                                          formatter={(value: number) => [`${value} reviews`, 'Count']}
                                        />
                                        <Bar
                                          dataKey="value"
                                          radius={[4, 4, 0, 0]}
                                          maxBarSize={50}
                                        >
                                          {dashboardData.topicDistribution.map((entry, index) => (
                                            <Cell
                                              key={`cell-${index}`}
                                              fill={`rgba(76, 55, 148, ${0.6 + (index * 0.1)})`}
                                            />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <Box sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: 250,
                                      backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                      borderRadius: '8px'
                                    }}>
                                      <Typography color="text.secondary">No topic data available</Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* Timeline Chart */}
                            <Grid item xs={12}>
                              <Card sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',

                              }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <TrendingUpIcon sx={{ color: '#4c3794' }} />
                                      <Typography variant="h6" sx={{
                                        fontWeight: 600,
                                        color: '#1a1a1a',
                                        fontSize: '1.1rem'
                                      }}>
                                        Feedback Volume & Sentiment Trends
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      {(['day', 'week', 'month', 'year'] as TimeRange[]).map((range) => (
                                        <Button
                                          key={range}
                                          variant={timeRange === range ? 'contained' : 'outlined'}
                                          onClick={() => setTimeRange(range)}
                                          size="small"
                                          sx={{
                                            minWidth: '60px',
                                            textTransform: 'capitalize',
                                            backgroundColor: timeRange === range ? '#4c3794' : 'transparent',
                                            color: timeRange === range ? '#fff' : '#4c3794',
                                            borderColor: '#4c3794',

                                          }}
                                        >
                                          {range}
                                        </Button>
                                      ))}
                                    </Box>
                                  </Box>
                                  {dashboardData.timelineData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={250}>
                                      <LineChart
                                        data={groupDataByTimeRange(dashboardData.timelineData, timeRange)}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                      >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
                                        <XAxis
                                          dataKey="date"
                                          angle={-45}
                                          textAnchor="end"
                                          height={70}
                                          tick={{ fontSize: 12, fill: '#4c3794' }}
                                          tickLine={{ stroke: '#b39ddb' }}
                                        />
                                        <YAxis
                                          yAxisId="left"
                                          tick={{ fontSize: 12, fill: '#4c3794' }}
                                          tickLine={{ stroke: '#b39ddb' }}
                                          label={{
                                            value: 'Number of Reviews',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fill: '#4c3794' }
                                          }}
                                        />
                                        <YAxis
                                          yAxisId="right"
                                          orientation="right"
                                          tick={{ fontSize: 12, fill: '#6a4caf' }}
                                          tickLine={{ stroke: '#b39ddb' }}
                                          label={{
                                            value: 'Sentiment',
                                            angle: 90,
                                            position: 'insideRight',
                                            style: { textAnchor: 'middle', fill: '#6a4caf' }
                                          }}
                                        />
                                        <Tooltip
                                          contentStyle={{
                                            backgroundColor: '#ede7f6',
                                            borderRadius: '8px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                                            padding: '8px 12px',
                                            color: '#4c3794'
                                          }}
                                          formatter={(value: number, name: string) => {
                                            if (name === 'Reviews') return [`${value} reviews`, name];
                                            return [`${value.toFixed(2)}`, 'Sentiment Score'];
                                          }}
                                          labelFormatter={(label) => `Date: ${label}`}
                                        />
                                        <Legend />
                                        <Line
                                          yAxisId="left"
                                          type="monotone"
                                          dataKey="value"
                                          name="Reviews"
                                          stroke="#4c3794"
                                          strokeWidth={2}
                                          dot={{
                                            r: 4,
                                            fill: '#4c3794',
                                            strokeWidth: 2,
                                            stroke: '#fff'
                                          }}
                                          activeDot={{
                                            r: 6,
                                            fill: '#4c3794',
                                            stroke: '#fff',
                                            strokeWidth: 2
                                          }}
                                          animationDuration={1500}
                                        />
                                        <Line
                                          yAxisId="right"
                                          type="monotone"
                                          dataKey="sentimentScore"
                                          name="Sentiment Score"
                                          stroke="#6a4caf"
                                          strokeWidth={2}
                                          strokeDasharray="5 5"
                                          dot={{
                                            r: 4,
                                            fill: '#6a4caf',
                                            strokeWidth: 2,
                                            stroke: '#fff'
                                          }}
                                          activeDot={{
                                            r: 6,
                                            fill: '#6a4caf',
                                            stroke: '#fff',
                                            strokeWidth: 2
                                          }}
                                          animationDuration={1500}
                                        />
                                        <Area
                                          yAxisId="left"
                                          type="monotone"
                                          dataKey="value"
                                          fill="#4c3794"
                                          fillOpacity={0.1}
                                          stroke="none"
                                        />
                                        <Area
                                          yAxisId="right"
                                          type="monotone"
                                          dataKey="sentimentScore"
                                          fill="#6a4caf"
                                          fillOpacity={0.1}
                                          stroke="none"
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <Box sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: 250,
                                      backgroundColor: 'rgba(76, 55, 148, 0.05)',
                                      borderRadius: '8px'
                                    }}>
                                      <Typography color="text.secondary">No timeline data available</Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Chat Messages - Replaced by reusable Chatbot */}

                  {/* Quick Action Chips */}



                </Box>
              )}

              {activeMainTab === 'segment' && detectedProjectType !== 'DATA_ANAL' && (
                <Box sx={{ p: 4, backgroundColor: '#fafbfc', minHeight: '100%' }}>
                  {loadingSegments ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', gap: 2 }}>
                      <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={48} />
                      <Typography sx={{ color: '#64748B', fontSize: '0.938rem', fontWeight: 500 }}>
                        Loading segments...
                      </Typography>
                    </Box>
                  ) : segmentGroups.length === 0 ? (
                    <Card sx={{ p: 6, textAlign: 'center', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e9ecef', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                      <Box sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(76, 55, 148, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                        <InsightsIcon sx={{ fontSize: 40, color: '#4c3794' }} />
                      </Box>
                      <Typography sx={{ color: '#131F32', fontSize: '1.25rem', fontWeight: 600, mb: 1 }}>
                        No Segment Analysis Available
                      </Typography>
                      <Typography sx={{ color: '#64748B', fontSize: '0.938rem' }}>
                        Segment data will appear here once available
                      </Typography>
                    </Card>
                  ) : (
                    activeSegmentView === 'list' ? (
                      <SegmentCardList
                        groups={segmentGroups}
                        onViewDetails={handleViewSegmentDetails}
                        selectedUIDs={selectedSegmentDocUIDs}
                        onSelectionChange={(uids, checked) => {
                          setSelectedSegmentDocUIDs(prev => {
                            const newSet = new Set(prev);
                            uids.forEach(uid => checked ? newSet.add(uid) : newSet.delete(uid));
                            return newSet;
                          });
                        }}
                        onAskAI={() => setIsChatOpen(true)}
                      />
                    ) : (
                      <SegmentDetailsView
                        data={selectedSegmentData}
                        onBack={handleBackToSegmentList}
                        onAskAI={() => setIsChatOpen(true)}
                      />
                    )
                  )}
                </Box>
              )}

              {activeMainTab === 'driver' && detectedProjectType !== 'DATA_ANAL' && (
                <Box sx={{ p: 4, backgroundColor: '#fafbfc', minHeight: '100%' }}>
                  {/* Loading State */}
                  {loadingDrivers ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '400px',
                      gap: 2
                    }}>
                      <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={48} />
                      <Typography sx={{
                        color: '#64748B',
                        fontSize: '0.938rem',
                        fontWeight: 500
                      }}>
                        Loading driver analysis...
                      </Typography>
                    </Box>
                  ) : driverGroups.length === 0 ? (
                    <Card sx={{
                      p: 6,
                      textAlign: 'center',
                      backgroundColor: '#fff',
                      borderRadius: '16px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}>
                      <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                      }}>
                        <ShowChartIcon sx={{ fontSize: 40, color: '#4c3794' }} />
                      </Box>
                      <Typography sx={{
                        color: '#131F32',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        mb: 1
                      }}>
                        No Driver Analysis Available
                      </Typography>
                      <Typography sx={{
                        color: '#64748B',
                        fontSize: '0.938rem'
                      }}>
                        Driver analysis data will appear here once available
                      </Typography>
                    </Card>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {/* Launch Chat and Export Buttons - Only show when sections are selected */}
                      {selectedDriverDocUIDs.size > 0 && (
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 2,
                          mb: 2,
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          backgroundColor: '#fafbfc',
                          p: 2,
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}>
                          {/* <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportAsIsFromSurveyInsights}
                          disabled={isExportingSurveyInsights}
                          sx={{
                            borderColor: '#4c3794',
                            color: '#4c3794',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: '10px',
                            '&:hover': {
                              borderColor: '#3d2d7a',
                              backgroundColor: 'rgba(76, 55, 148, 0.08)'
                            },
                            '&:disabled': {
                              borderColor: '#ccc',
                              color: '#999'
                            }
                          }}
                        >
                          {isExportingSurveyInsights ? 'Exporting...' : 'Export As-Is'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => setShowSurveyInsightsExportDialog(true)}
                          sx={{
                            borderColor: '#4c3794',
                            color: '#4c3794',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: '10px',
                            '&:hover': {
                              borderColor: '#3d2d7a',
                              backgroundColor: 'rgba(76, 55, 148, 0.08)'
                            }
                          }}
                        >
                          Generate Content
                        </Button> */}
                          <Button
                            variant="contained"
                            startIcon={<ChatBubbleIcon />}
                            onClick={() => {
                              setIsChatOpen(true);
                            }}
                            sx={{
                              backgroundColor: '#4c3794',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 3,
                              py: 1.5,
                              borderRadius: '10px',
                              boxShadow: '0 4px 14px rgba(76, 55, 148, 0.25)',
                              '&:hover': {
                                backgroundColor: '#3d2d7a',
                                boxShadow: '0 6px 20px rgba(76, 55, 148, 0.35)'
                              }
                            }}
                          >
                            Ask AI
                          </Button>
                        </Box>
                      )}
                      {driverGroups.map((group: any, groupIndex: number) => {
                        const driverDocUIDs = getDriverDocUIDs(group);
                        const isGroupSelected = driverDocUIDs.length > 0 && driverDocUIDs.every(uid => selectedDriverDocUIDs.has(uid));
                        const isPartiallySelected = driverDocUIDs.length > 0 && driverDocUIDs.some(uid => selectedDriverDocUIDs.has(uid));
                        const driverId = `driver-${groupIndex}`;
                        const isExpanded = expandedDriverId === driverId;
                        const groupName = group.groupName || `Outcome ${groupIndex + 1}`;

                        // Extract briefDescription from group or from first item's entries
                        let briefDescription = group.briefDescription;
                        if (!briefDescription && group.items && group.items.length > 0) {
                          // Try to find briefDescription in the first item's entries
                          const firstItem = group.items[0];
                          if (firstItem.entries) {
                            const briefDescEntry = firstItem.entries.find((e: any) => e.key === 'briefDescription');
                            if (briefDescEntry?.value) {
                              briefDescription = briefDescEntry.value;
                            }
                          }
                        }

                        // Count response curve items
                        const responseCurveCount = group.items?.filter((item: any) =>
                          item.objectType === 'MULTICOLUMNDICT' && item.value && item.value.data
                        ).length || 0;

                        return (
                          <Card
                            key={driverId}
                            sx={{
                              border: '1px solid #e9ecef',
                              borderRadius: '16px',
                              overflow: 'hidden',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                              '&:hover': {
                                boxShadow: '0 8px 24px rgba(76, 55, 148, 0.15)',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 2,
                              borderBottom: isExpanded ? '1px solid #e9ecef' : 'none',
                              backgroundColor: isExpanded ? 'rgba(76, 55, 148, 0.03)' : 'transparent',
                            }}>
                              {/* Checkbox for selection */}
                              <Checkbox
                                checked={isGroupSelected}
                                indeterminate={isPartiallySelected && !isGroupSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  driverDocUIDs.forEach(uid => {
                                    if (e.target.checked) {
                                      setSelectedDriverDocUIDs(prev => new Set([...prev, uid]));
                                    } else {
                                      setSelectedDriverDocUIDs(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(uid);
                                        return newSet;
                                      });
                                    }
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  color: '#4c3794',
                                  '&.Mui-checked': {
                                    color: '#4c3794',
                                  },
                                }}
                              />
                              <Button
                                onClick={() => setExpandedDriverId(isExpanded ? null : driverId)}
                                fullWidth
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  textTransform: 'none',
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                  }
                                }}
                              >
                                <Box sx={{ flex: 1, textAlign: 'left' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                    <Box sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '10px',
                                      background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: '#fff',
                                      fontWeight: 700,
                                      fontSize: '1rem',
                                      boxShadow: '0 4px 12px rgba(76, 55, 148, 0.3)'
                                    }}>
                                      {groupIndex + 1}
                                    </Box>
                                    <Typography sx={{
                                      fontSize: '1.125rem',
                                      fontWeight: 700,
                                      color: '#131F32',
                                      lineHeight: 1.3
                                    }}>
                                      {groupName}
                                    </Typography>
                                    {responseCurveCount > 0 && (
                                      <Chip
                                        label={`${responseCurveCount} driver${responseCurveCount !== 1 ? 's' : ''}`}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                          color: '#4c3794',
                                          fontWeight: 600,
                                          fontSize: '0.75rem',
                                          height: '24px'
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <ExpandMoreIcon
                                  sx={{
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease',
                                    color: '#4c3794'
                                  }}
                                />
                              </Button>
                            </Box>

                            {isExpanded && group.items && group.items.length > 0 && (
                              <Box sx={{ p: 3, backgroundColor: '#fff' }}>
                                {/* Brief Description Section */}
                                {briefDescription && (
                                  <Box sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                      <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <DescriptionIcon sx={{ fontSize: 20, color: '#4c3794' }} />
                                      </Box>
                                      <Typography sx={{
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        color: '#131F32',
                                        letterSpacing: '-0.01em'
                                      }}>
                                        Description
                                      </Typography>
                                    </Box>
                                    <Card sx={{
                                      backgroundColor: '#fff',
                                      p: 3,
                                      borderRadius: '12px',
                                      border: '1px solid #e9ecef',
                                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                    }}>
                                      <Typography sx={{
                                        fontSize: '0.938rem',
                                        color: '#475569',
                                        lineHeight: 1.7,
                                        fontWeight: 400
                                      }}>
                                        {briefDescription}
                                      </Typography>
                                    </Card>
                                  </Box>
                                )}
                                <Grid container spacing={3}>
                                  {group.items.map((item: any, itemIndex: number) => {
                                    // Check if this is a response curve item
                                    if (item.objectType === 'MULTICOLUMNDICT' && item.value && item.value.data) {
                                      const responseData = item.value;
                                      const title = responseData.title || item.label || `Driver ${itemIndex + 1}`;
                                      // Extract UUID from entries array (where key === "uuid")
                                      const uuidEntry = item.entries?.find((e: any) => e.key === 'uuid');
                                      const itemDocUID = uuidEntry?.value || item.docUID || item.key;
                                      const isItemSelected = itemDocUID ? selectedDriverDocUIDs.has(itemDocUID) : false;

                                      // Extract baseline_metrics from entries array
                                      const baselineMetricsEntry = item.entries?.find((e: any) => e.key === 'baseline_metrics');
                                      const baselineMetrics = baselineMetricsEntry?.value || {};

                                      // Transform data from API format (header names) to chart format (snake_case)
                                      const transformedData = (responseData.data || []).map((row: any) => ({
                                        driver_value: row['Driver Value'] ?? row.driver_value ?? 0,
                                        outcome_value: row['Outcome Value'] ?? row.outcome_value ?? 0,
                                        driver_value_scaled: row['Driver Value Scaled'] ?? row.driver_value_scaled,
                                        driver_change_std: row['Driver Change (Std Dev)'] ?? row.driver_change_std,
                                        outcome_type: (row['Outcome Type'] ?? row.outcome_type) || 'continuous'
                                      }));

                                      return (
                                        <Grid item xs={12} sm={6} key={itemIndex}>
                                          <Box sx={{ position: 'relative' }}>
                                            {itemDocUID && (
                                              <Checkbox
                                                checked={isItemSelected}
                                                onChange={(e) => {
                                                  if (e.target.checked) {
                                                    setSelectedDriverDocUIDs(prev => new Set([...prev, itemDocUID]));
                                                  } else {
                                                    setSelectedDriverDocUIDs(prev => {
                                                      const newSet = new Set(prev);
                                                      newSet.delete(itemDocUID);
                                                      return newSet;
                                                    });
                                                  }
                                                }}
                                                sx={{
                                                  position: 'absolute',
                                                  top: 16,
                                                  right: 16,
                                                  zIndex: 1,
                                                  color: '#4c3794',
                                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                  '&.Mui-checked': {
                                                    color: '#4c3794',
                                                  },
                                                }}
                                              />
                                            )}
                                            <InteractiveWhatIfChart
                                              dataset={{
                                                title: title,
                                                headers: responseData.headers || ['Driver Value', 'Outcome Value'],
                                                data: transformedData,
                                                baseline_metrics: baselineMetrics // Extract from entries array
                                              }}
                                            />
                                          </Box>
                                        </Grid>
                                      );
                                    }
                                    return null;
                                  })}
                                </Grid>
                                {responseCurveCount === 0 && (
                                  <Typography sx={{ color: '#64748B', fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                    No driver data available for this outcome
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {/* End Main Scrollable Content */}

            {/* Chat Panel - Available in all tabs (Executive Summary, Segment Analysis, and Driver Analysis) */}
            {
              isChatOpen && (
                <Box
                  sx={{
                    width: isFullScreen ? '100%' : '48%',
                    height: '100%',
                    backgroundColor: '#FAFBFC',
                    boxShadow: '-4px 0 20px rgba(76, 55, 148, 0.1)',
                    transition: 'width 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isFullScreen ? 0 : '20px 0 0 20px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Chat Panel Header with Close Button */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    p: 1.5,
                    borderBottom: '1px solid #e9ecef'
                  }}>
                    <IconButton
                      onClick={handleCloseChat}
                      sx={{
                        color: '#64748B',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 55, 148, 0.1)',
                          color: '#4c3794'
                        }
                      }}
                      aria-label="Close chat"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1, height: 'calc(100% - 50px)', marginLeft: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Chatbot
                      placeholder="Type your message..."
                      welcomeMessage="Hello! I'm your AI assistant. How can I help you analyze the survey data?"
                      enableVoice={true}
                      enableGovAPI={true}
                      apiConfig={chatbotApiConfig}
                      getAuthToken={async () => localStorage.getItem('token')}
                    />
                  </Box>
                </Box>
              )
            }
          </Box >
          {/* End Content Area */}
        </Box >
        {/* End Main Content Area */}
      </Box >
      {/* End Flex Container */}

      {/* Floating Chat Button - only show when chat is closed and not fullscreen to avoid overlap */}
      {
        !isChatOpen && !isFullScreen && (
          <Fab
            color="primary"
            aria-label="Open AI chat assistant"
            onClick={handleChatClick}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              backgroundColor: '#4c3794',
              // transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

              '&:active': {
                transform: 'scale(0.95)'
              },
              zIndex: 1000,
            }}
          >
            <img src={botIcon} alt="AI Chat Assistant" style={{ width: 74, height: 74 }} />
          </Fab>
        )
      }

      {/* Add Survey Dialog */}
      <Dialog
        open={addSurveyDialog}
        onClose={() => setAddSurveyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {/* <DialogTitle>Upload Survey Data</DialogTitle> */}
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              Please upload a CSV file containing your survey data. The file should include all required survey fields.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="survey-file-upload"
                type="file"
                onChange={handleDeviceFileUpload}
              />
              <label htmlFor="survey-file-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={uploadLoading}
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#3a2a73',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(76, 55, 148, 0.3)',
                      color: '#ffffff'
                    }
                  }}
                >
                  {uploadLoading ? 'Uploading...' : 'Upload CSV File'}
                </Button>
              </label>
              {uploadLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress sx={{
                    color: '#4c3794',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#4c3794'
                    }
                  }} />
                </Box>
              )}
              {success && (
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    '& .MuiAlert-icon': {
                      color: '#4c3794'
                    }
                  }}
                >
                  {success}
                </Alert>
              )}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    '& .MuiAlert-icon': {
                      color: '#4c3794'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddSurveyDialog(false);
              setSuccess('');
              setError('');
            }}
            sx={{ color: '#4c3794' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced View All Responses Dialog */}
      <Dialog
        open={isViewAllDialogOpen}
        onClose={() => setIsViewAllDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
          color: '#000',
          p: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              letterSpacing: '0.5px'
            }}>
              {detectedContext === 'banking' ? 'Customer Feedback Analysis' :
                detectedContext === 'gov' ? 'Citizen Feedback Analysis' :
                  'Employee Feedback Analysis'}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setIsViewAllDialogOpen(false)}
            sx={{
              color: '#000',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: '75vh' }}>
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Enhanced Sidebar */}
            <Box sx={{
              width: 380,
              borderRight: '1px solid #e0e0e0',
              background: '#fafbfc',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Search Header */}
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0', background: '#fff' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={detectedContext === 'banking' ? "Search customer feedback by name, ID, or keywords..." :
                    detectedContext === 'gov' ? "Search citizen feedback by name, ID, or keywords..." :
                      "Search employee feedback by name, ID, or keywords..."}
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#64748B', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4c3794',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4c3794',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
                <Typography variant="caption" sx={{
                  color: '#64748B',
                  fontSize: '0.7rem',
                  mt: 1,
                  display: 'block'
                }}>
                  {getCurrentPageDialogItems().length} of {surveyItems.length} responses
                </Typography>
              </Box>

              {/* Feedback List */}
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                p: 1.5
              }}>
                {getCurrentPageDialogItems().map((item, idx) => (
                  <Card
                    key={getEntryValue(item.entries, 'uuid')}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderColor: selectedDocUID === getEntryValue(item.entries, 'uuid') ? '#4c3794' : '#e0e0e0',
                      borderWidth: selectedDocUID === getEntryValue(item.entries, 'uuid') ? '2px' : '1px',
                      cursor: 'pointer',
                      background: selectedDocUID === getEntryValue(item.entries, 'uuid') ? '#fff' : '#fafbfc',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: '#4c3794',
                        boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                    onClick={() => setSelectedDocUID(getEntryValue(item.entries, 'uuid'))}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: '#1a1a1a',
                        mb: 1,
                        lineHeight: 1.3
                      }}>
                        {getEntryValue(item.entries, 'title')}
                      </Typography>

                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5
                      }}>
                        {(() => {
                          try {
                            const sentimentStr = getEntryValue(item.entries, 'contentSentiment');
                            if (!sentimentStr) return null;
                            const sentimentData = JSON.parse(sentimentStr);
                            return (
                              <>
                                <Typography sx={{
                                  fontWeight: 500,
                                  color: '#64748B',
                                  fontSize: '0.7rem'
                                }}>
                                  Sentiment: {sentimentData.score || 'N/A'}
                                </Typography>
                                <Box sx={{
                                  background: getSentimentColor(sentimentData.sentiment).background,
                                  color: getSentimentColor(sentimentData.sentiment).color,
                                  borderRadius: '12px',
                                  px: 1.5,
                                  py: 0.5,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {sentimentData.sentiment || 'Unknown'}
                                </Box>
                              </>
                            );
                          } catch (e) {
                            return (
                              <Typography sx={{
                                fontWeight: 500,
                                color: '#64748B',
                                fontSize: '0.7rem'
                              }}>
                                Sentiment: N/A
                              </Typography>
                            );
                          }
                        })()}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Enhanced Pagination */}
              <Box sx={{
                p: 2,
                borderTop: '1px solid #e0e0e0',
                background: '#fff'
              }}>
                {renderPagination()}
              </Box>
            </Box>

            {/* Enhanced Details Panel */}
            <Box sx={{
              flex: 1,
              background: '#fff',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {selectedDocUID ? (
                (() => {
                  const selectedItem = surveyItems.find(item => getEntryValue(item.entries, 'uuid') === selectedDocUID);
                  if (!selectedItem) return (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: '#64748B'
                    }}>
                      <Typography variant="body2">No details available.</Typography>
                    </Box>
                  );

                  const item = selectedItem as SurveyItem;
                  let sentiment = 'Unknown';
                  let sentimentScore = 'N/A';
                  try {
                    const sentimentStr = getEntryValue(item.entries, 'contentSentiment');
                    if (sentimentStr) {
                      const sentimentData = JSON.parse(sentimentStr);
                      sentiment = sentimentData.sentiment || 'Unknown';
                      sentimentScore = sentimentData.score || 'N/A';
                    }
                  } catch (e) {
                    console.error('Error parsing sentiment:', e);
                  }

                  return (
                    <Box sx={{ p: 3, overflowY: 'auto', height: '100%' }}>
                      {/* Header */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 3,
                        pb: 2,
                        borderBottom: '1px solid #e0e0e0'
                      }}>
                        <Box sx={{
                          background: 'linear-gradient(135deg, #4c3794 0%, #6b5b9a 100%)',
                          borderRadius: '12px',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <InfoIcon sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1a1a1a',
                            mb: 0.5
                          }}>
                            {getEntryValue(item.entries, 'title')}
                          </Typography>
                          <Typography sx={{
                            color: '#64748B',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            Sentiment Score: {sentimentScore}
                          </Typography>
                        </Box>
                        <Box sx={{
                          background: getSentimentColor(sentiment).background,
                          color: getSentimentColor(sentiment).color,
                          borderRadius: '8px',
                          px: 2,
                          py: 1,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          border: `1px solid ${getSentimentColor(sentiment).color}20`
                        }}>
                          {sentiment}
                        </Box>
                      </Box>

                      {/* Content Cards */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Summary Card */}
                        <Card sx={{
                          background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%)',
                          border: '1px solid #e0e0e0',
                          borderRadius: '12px'
                        }}>
                          <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Box sx={{
                                background: '#4c3794',
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>AI</Typography>
                              </Box>
                              <Typography sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#1a1a1a'
                              }}>
                                AI-Generated Summary
                              </Typography>
                            </Box>
                            <Typography sx={{
                              fontSize: '0.75rem',
                              lineHeight: 1.6,
                              color: '#374151'
                            }}>
                              Feedback from {getEntryValue(item.entries, 'title')} (Employee ID {getEntryValue(item.entries, 'employeeId')}) regarding their experience.
                              The analysis highlights key insights and areas for improvement based on their responses.
                            </Typography>
                          </CardContent>
                        </Card>

                        {/* Key Topics Card */}
                        <Card sx={{
                          background: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '12px'
                        }}>
                          <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Box sx={{
                                background: '#10b981',
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>K</Typography>
                              </Box>
                              <Typography sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#1a1a1a'
                              }}>
                                Key Topics Identified
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {getEntryValue(item.entries, 'highlights')
                                .split(',')
                                .map((topic: string, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={topic.trim()}
                                    size="small"
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: '24px',
                                      background: 'rgba(76, 55, 148, 0.1)',
                                      color: '#4c3794',
                                      fontWeight: 500,
                                      '& .MuiChip-label': {
                                        px: 1
                                      }
                                    }}
                                  />
                                ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    </Box>
                  );
                })()
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#64748B',
                  p: 4
                }}>
                  <Box sx={{
                    background: 'rgba(76, 55, 148, 0.1)',
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <InfoIcon sx={{ color: '#4c3794', fontSize: 32 }} />
                  </Box>
                  <Typography variant="h6" sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    mb: 1
                  }}>
                    Select a Feedback Entry
                  </Typography>
                  <Typography variant="body2" sx={{
                    fontSize: '0.75rem',
                    textAlign: 'center',
                    maxWidth: 300
                  }}>
                    Choose a feedback entry from the list to view detailed analysis and insights.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Authentication Iframe Dialog */}
      <Dialog
        open={showAuthIframe}
        onClose={() => {
          setShowAuthIframe(false);
          setIsAuthInProgress(false);
          // Send message to parent that auth was cancelled
          window.postMessage({ type: 'AUTH_COMPLETED', success: false }, '*');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Authentication Required
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowAuthIframe(false);
              setIsAuthInProgress(false);
              window.postMessage({ type: 'AUTH_COMPLETED', success: false }, '*');
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Please log in to access the file. Once you've successfully logged in, click the "Download File" button below.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              The file will be automatically downloaded and processed after authentication.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, position: 'relative' }}>
            {isAuthInProgress && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}>
                <CircularProgress sx={{ color: '#4c3794' }} />
              </Box>
            )}

            <iframe
              src={authUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '400px'
              }}
              onLoad={() => {
                setIsAuthInProgress(false);
              }}
              title="Authentication"
            />
          </Box>

          <Box sx={{
            p: 2,
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowAuthIframe(false);
                setIsAuthInProgress(false);
                window.postMessage({ type: 'AUTH_COMPLETED', success: false }, '*');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setIsAuthInProgress(true);
                window.postMessage({ type: 'AUTH_COMPLETED', success: true }, '*');
              }}
              sx={{
                backgroundColor: '#4c3794',
                '&:hover': {
                  backgroundColor: '#3a2a73',
                }
              }}
            >
              Download File
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SharePoint Authentication Dialog */}
      <Dialog
        open={showSharePointAuth}
        onClose={() => {
          setShowSharePointAuth(false);
          if (authWindow) {
            authWindow.close();
            setAuthWindow(null);
          }
          window.postMessage({ type: 'SHAREPOINT_AUTH_COMPLETED', success: false }, '*');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              SharePoint Authentication
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowSharePointAuth(false);
              if (authWindow) {
                authWindow.close();
                setAuthWindow(null);
              }
              window.postMessage({ type: 'SHAREPOINT_AUTH_COMPLETED', success: false }, '*');
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              SharePoint has been opened in a new tab for authentication.
            </Typography>

            <Box sx={{
              p: 2,
              backgroundColor: 'rgba(76, 55, 148, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(76, 55, 148, 0.1)',
              mb: 3
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                Follow these steps:
              </Typography>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Log in to SharePoint in the new tab that opened
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Navigate to your file and download it to your computer
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Close the SharePoint tab when done
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Click "File Downloaded" below, then upload the file from your device
                  </Typography>
                </li>
              </ol>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              This approach ensures secure authentication while avoiding iframe restrictions.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowSharePointAuth(false);
              if (authWindow) {
                authWindow.close();
                setAuthWindow(null);
              }
              window.postMessage({ type: 'SHAREPOINT_AUTH_COMPLETED', success: false }, '*');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowSharePointAuth(false);
              if (authWindow) {
                authWindow.close();
                setAuthWindow(null);
              }
              window.postMessage({ type: 'SHAREPOINT_AUTH_COMPLETED', success: true }, '*');
            }}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': {
                backgroundColor: '#3a2a73',
              }
            }}
          >
            File Downloaded
          </Button>
        </DialogActions>
      </Dialog>

      {/* Authentication Credentials Dialog */}
      <Dialog
        open={showAuthCredentialsDialog}
        onClose={() => {
          setShowAuthCredentialsDialog(false);
          window.postMessage({ type: 'AUTH_CREDENTIALS_PROVIDED', credentials: null }, '*');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Authentication Required
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowAuthCredentialsDialog(false);
              window.postMessage({ type: 'AUTH_CREDENTIALS_PROVIDED', credentials: null }, '*');
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              This URL requires authentication. Please provide your credentials.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Authentication Type</InputLabel>
              <Select
                value={authCredentials?.type || 'bearer'}
                onChange={(e) => setAuthCredentials(prev => ({
                  type: e.target.value as 'bearer' | 'basic',
                  token: prev?.token || ''
                }))}
                label="Authentication Type"
              >
                <MenuItem value="bearer">Bearer Token</MenuItem>
                <MenuItem value="basic">Basic Auth</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={authCredentials?.type === 'bearer' ? 'Bearer Token' : 'Base64 Encoded Credentials'}
              placeholder={authCredentials?.type === 'bearer' ? 'Enter your bearer token' : 'username:password (base64 encoded)'}
              value={authCredentials?.token || ''}
              onChange={(e) => setAuthCredentials(prev => ({
                type: prev?.type || 'bearer',
                token: e.target.value
              }))}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {authCredentials?.type === 'bearer'
                ? 'Enter your Bearer token for API authentication.'
                : 'Enter your username:password encoded in base64 format.'
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowAuthCredentialsDialog(false);
              window.postMessage({ type: 'AUTH_CREDENTIALS_PROVIDED', credentials: null }, '*');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (authCredentials?.token) {
                setShowAuthCredentialsDialog(false);
                window.postMessage({
                  type: 'AUTH_CREDENTIALS_PROVIDED',
                  credentials: authCredentials
                }, '*');
              }
            }}
            disabled={!authCredentials?.token}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': {
                backgroundColor: '#3a2a73',
              }
            }}
          >
            Authenticate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Drive/Sheets Authentication Dialog */}
      <Dialog
        open={showGoogleAuth}
        onClose={() => {
          setShowGoogleAuth(false);
          if (googleWindow) {
            googleWindow.close();
            setGoogleWindow(null);
          }
          window.postMessage({ type: 'GOOGLE_AUTH_COMPLETED', success: false }, '*');
        }}
        maxWidth="md"
      // fullWidth

      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Google Drive/Sheets Authentication
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowGoogleAuth(false);
              if (googleWindow) {
                googleWindow.close();
                setGoogleWindow(null);
              }
              window.postMessage({ type: 'GOOGLE_AUTH_COMPLETED', success: false }, '*');
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Google Drive/Sheets has been opened in a new tab for authentication.
            </Typography>

            <Box sx={{
              p: 2,
              backgroundColor: 'rgba(76, 55, 148, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(76, 55, 148, 0.1)',
              mb: 3
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                Follow these steps:
              </Typography>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Log in to Google in the new tab that opened
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Navigate to your file and download it (File → Download → CSV/Excel)
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Close the Google tab when done
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Click "File Downloaded" below, then upload the file from your device
                  </Typography>
                </li>
              </ol>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              This approach ensures secure authentication while avoiding OAuth complexity.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowGoogleAuth(false);
              if (googleWindow) {
                googleWindow.close();
                setGoogleWindow(null);
              }
              window.postMessage({ type: 'GOOGLE_AUTH_COMPLETED', success: false }, '*');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowGoogleAuth(false);
              if (googleWindow) {
                googleWindow.close();
                setGoogleWindow(null);
              }
              window.postMessage({ type: 'GOOGLE_AUTH_COMPLETED', success: true }, '*');
            }}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': {
                backgroundColor: '#3a2a73',
              }
            }}
          >
            File Downloaded
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Info Dialog */}
      <Dialog
        open={showFileInfo}
        onClose={() => setShowFileInfo(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              File Information
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowFileInfo(false)}
            size="small"
            sx={{
              color: '#1a1a1a',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isLoadingFileInfo ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#4c3794' }} />
            </Box>
          ) : fileInfo ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1.5rem' }}>{getFileIcon(fileInfo.mimeType)}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {fileInfo.name}
                </Typography>
              </Box>

              <Box sx={{
                p: 2,
                backgroundColor: 'rgba(76, 55, 148, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(76, 55, 148, 0.1)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                  File Details:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Size:</strong> {formatFileSize(fileInfo.size || '0')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {fileInfo.mimeType}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong> {formatDate(fileInfo.createdTime)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Modified:</strong> {formatDate(fileInfo.modifiedTime)}
                  </Typography>
                  {fileInfo.description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {fileInfo.description}
                    </Typography>
                  )}
                  {fileInfo.owners && fileInfo.owners.length > 0 && (
                    <Typography variant="body2">
                      <strong>Owner:</strong> {fileInfo.owners[0].displayName}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">No file information available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowFileInfo(false)}
            sx={{ color: '#4c3794' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Drive Authentication Dialog */}
      <Dialog
        open={showGoogleDriveAuthDialog}
        onClose={() => {
          setShowGoogleDriveAuthDialog(false);
          if (googleDriveAuthWindow) {
            googleDriveAuthWindow.close();
            setGoogleDriveAuthWindow(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Google Drive Authentication
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowGoogleDriveAuthDialog(false);
              if (googleDriveAuthWindow) {
                googleDriveAuthWindow.close();
                setGoogleDriveAuthWindow(null);
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Connect to Google Drive to access your files securely.
            </Typography>

            <Box sx={{
              p: 2,
              backgroundColor: 'rgba(76, 55, 148, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(76, 55, 148, 0.1)',
              mb: 3
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                What happens next:
              </Typography>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    A new tab will open with Google's secure authentication page
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    <strong>Select the correct Google account</strong> (onkarpatil0269@gmail.com)
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    Sign in and click "Allow" to grant access to your Google Drive
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    <strong>The tab will close automatically</strong> when authentication is complete
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body2">
                    You'll see a success message and can then browse your Google Drive files
                  </Typography>
                </li>
              </ol>
            </Box>

            {isGoogleDriveAuthInProgress && (
              <Box sx={{
                p: 2,
                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(76, 55, 148, 0.2)',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CircularProgress size={20} sx={{ color: '#4c3794' }} />
                  <Typography variant="body2" sx={{ color: '#4c3794', fontWeight: 600 }}>
                    Authentication in progress...
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#4c3794', fontSize: '0.875rem' }}>
                  Please complete the authentication in the new tab. This dialog will close automatically when done.
                </Typography>
              </Box>
            )}

            <Box sx={{
              p: 2,
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(16, 185, 129, 0.1)'
            }}>
              <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mb: 1 }}>
                🔒 Secure & Private
              </Typography>
              <Typography variant="body2" sx={{ color: '#10B981', fontSize: '0.875rem' }}>
                We only request access to view your Google Drive files. We cannot modify, delete, or access any other data.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowGoogleDriveAuthDialog(false);
              if (googleDriveAuthWindow) {
                googleDriveAuthWindow.close();
                setGoogleDriveAuthWindow(null);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={initiateGoogleDriveAuth}
            disabled={isGoogleDriveAuthInProgress}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': {
                backgroundColor: '#3a2a73',
              }
            }}
          >
            {isGoogleDriveAuthInProgress ? 'Authenticating...' : 'Start Authentication'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Drive Dialog */}
      <Dialog
        open={showGoogleDriveDialog}
        onClose={closeGoogleDriveDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            height: '700px'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#111827',
          py: 3,
          px: 4
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Select a file
          </Typography>
          <IconButton
            onClick={closeGoogleDriveDialog}
            size="small"
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#ffffff'
          }}>
            <Button
              onClick={() => handleTabChange('myDrive')}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: activeTab === 'myDrive' ? 600 : 400,
                color: activeTab === 'myDrive' ? '#4c3794' : '#6b7280',
                borderBottom: activeTab === 'myDrive' ? '2px solid #4c3794' : '2px solid transparent',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              My Drive
            </Button>
            <Button
              onClick={() => handleTabChange('shared')}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: activeTab === 'shared' ? 600 : 400,
                color: activeTab === 'shared' ? '#4c3794' : '#6b7280',
                borderBottom: activeTab === 'shared' ? '2px solid #4c3794' : '2px solid transparent',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              Shared with me
            </Button>
            <Button
              onClick={() => handleTabChange('teamDrives')}
              sx={{
                flex: 1,
                py: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: activeTab === 'teamDrives' ? 600 : 400,
                color: activeTab === 'teamDrives' ? '#4c3794' : '#6b7280',
                borderBottom: activeTab === 'teamDrives' ? '2px solid #4c3794' : '2px solid transparent',
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              Shared drives
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <TextField
              fullWidth
              placeholder="Search files..."
              value={googleDriveSearchQuery}
              onChange={(e) => setGoogleDriveSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Box>

          {/* Folders Section */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
              Folders
            </Typography>
            {isLoadingGoogleFiles ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
              }}>
                <CircularProgress sx={{ color: '#4c3794', mr: 2 }} size={20} />
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Loading folders...
                </Typography>
              </Box>
            ) : filteredGoogleDriveFiles.filter(file => file.mimeType === 'application/vnd.google-apps.folder').length > 0 ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {filteredGoogleDriveFiles
                  .filter(file => file.mimeType === 'application/vnd.google-apps.folder')
                  .map((folder) => (
                    <Box
                      key={folder.id}
                      onClick={() => navigateToFolder(folder.id)}
                      sx={{
                        p: 2,
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#f9fafb',
                          borderColor: '#4c3794'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon sx={{ color: '#4c3794', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                          {folder.name}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                textAlign: 'center'
              }}>
                <FolderIcon sx={{ color: '#9ca3af', fontSize: 48, mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                  No folders available
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                  This location doesn't contain any folders
                </Typography>
              </Box>
            )}
          </Box>

          {/* Content Section */}
          <Box sx={{ p: 3 }}>
            {activeTab === 'teamDrives' ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
                  Team Drives
                </Typography>
                {isLoadingGoogleFiles ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={32} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                      Loading team drives...
                    </Typography>
                  </Box>
                ) : teamDrives.length > 0 ? (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {teamDrives.map((drive) => (
                      <Box
                        key={drive.id}
                        onClick={() => handleTeamDriveSelect(drive.id)}
                        sx={{
                          p: 2,
                          border: selectedTeamDrive === drive.id
                            ? '2px solid #4c3794'
                            : '1px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: selectedTeamDrive === drive.id
                            ? 'rgba(76, 55, 148, 0.05)'
                            : '#ffffff',
                          '&:hover': {
                            backgroundColor: selectedTeamDrive === drive.id
                              ? 'rgba(156, 39, 176, 0.1)'
                              : '#f9fafb',
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        <Box sx={{
                          height: 120,
                          background: 'linear-gradient(135deg, #4c3794 0%, #6B46C1 100%)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          border: '1px solid #e5e7eb'
                        }}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            zIndex: 1,
                            position: 'relative'
                          }}>
                            <Typography sx={{
                              fontSize: '2.5rem',
                              mb: 0.5,
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                            }}>
                              🏢
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              opacity: 0.9,
                              textAlign: 'center'
                            }}>
                              TEAM DRIVE
                            </Typography>
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5
                          }}
                        >
                          {drive.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    textAlign: 'center'
                  }}>
                    <Typography sx={{
                      fontSize: '3rem',
                      mb: 2,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}>
                      🏢
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      No team drives available
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      You don't have access to any shared drives
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
                  Files
                </Typography>

                {/* File Grid */}
                {isLoadingGoogleFiles ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <CircularProgress sx={{ color: '#4c3794', mb: 2 }} size={32} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                      Loading files...
                    </Typography>
                  </Box>
                ) : filteredGoogleDriveFiles.filter(file => file.mimeType !== 'application/vnd.google-apps.folder').length > 0 ? (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 2,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {filteredGoogleDriveFiles
                      .filter(file => file.mimeType !== 'application/vnd.google-apps.folder')
                      .map((file) => (
                        <Box
                          key={file.id}
                          onClick={(event) => handleGoogleDriveFileSelect(file, event)}
                          sx={{
                            p: 2,
                            border: selectedGoogleFile?.id === file.id
                              ? '2px solid #4c3794'
                              : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: selectedGoogleFile?.id === file.id
                              ? 'rgba(76, 55, 148, 0.05)'
                              : '#ffffff',
                            '&:hover': {
                              backgroundColor: selectedGoogleFile?.id === file.id
                                ? 'rgba(156, 39, 176, 0.1)'
                                : '#f9fafb',
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          {/* File Preview */}
                          <Box sx={{
                            height: 120,
                            background: file.mimeType?.includes('image')
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : file.mimeType?.includes('pdf')
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : file.mimeType?.includes('word') || file.mimeType?.includes('document')
                                  ? 'linear-gradient(135deg, #2b6cb0 0%, #3182ce 100%)'
                                  : file.mimeType?.includes('excel') || file.mimeType?.includes('spreadsheet')
                                    ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                    : file.mimeType?.includes('powerpoint') || file.mimeType?.includes('presentation')
                                      ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                                      : 'linear-gradient(135deg, #4c3794 0%, #6B46C1 100%)',
                            backgroundSize: 'auto',
                            backgroundPosition: 'center',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            border: '1px solid #e5e7eb',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(1px)'
                            }
                          }}>
                            {file.mimeType?.includes('image') ? (
                              <ImagePreview file={file} />
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                zIndex: 1,
                                position: 'relative'
                              }}>
                                <Typography sx={{
                                  fontSize: '2.5rem',
                                  mb: 0.5,
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                }}>
                                  {getFileTypeIcon(file.mimeType)}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  opacity: 0.9,
                                  textAlign: 'center'
                                }}>
                                  {getFileTypeLabel(file.mimeType)}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* File Name */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: '#111827',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5
                            }}
                          >
                            {file.name}
                          </Typography>

                          {/* File Size */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6b7280',
                              fontSize: '0.75rem'
                            }}
                          >
                            {formatFileSize(file.size || '0')}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                ) : null}

                {filteredGoogleDriveFiles.filter(file => file.mimeType !== 'application/vnd.google-apps.folder').length === 0 && !isLoadingGoogleFiles && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    textAlign: 'center'
                  }}>
                    <Typography sx={{
                      fontSize: '3rem',
                      mb: 2,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}>
                      📄
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      {googleDriveSearchQuery ? 'No files match your search' : 'No files available'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      {googleDriveSearchQuery
                        ? 'Try adjusting your search terms or clear the search to see all files'
                        : 'This location doesn\'t contain any files'
                      }
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={closeGoogleDriveDialog}
            sx={{
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedGoogleFiles.length > 0) {
                downloadMultipleGoogleDriveFiles(selectedGoogleFiles);
              } else if (selectedGoogleFile) {
                downloadGoogleDriveFile(selectedGoogleFile);
              }
            }}
            disabled={!selectedGoogleFile && selectedGoogleFiles.length === 0 || isDownloadingGoogleFile || isDownloadingMultipleFiles}
            sx={{
              backgroundColor: '#4c3794',
              '&:hover': {
                backgroundColor: '#3d2e7a',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(76, 55, 148, 0.3)',
                color: '#ffffff'
              }
            }}
          >
            {isDownloadingGoogleFile || isDownloadingMultipleFiles ? 'Downloading...' : 'Select'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog
        open={showFilePreview}
        onClose={() => setShowFilePreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon sx={{ color: '#4c3794' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              File Preview: {filePreviewInfo?.name}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowFilePreview(false)}
            size="small"
            sx={{
              color: '#1a1a1a',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isLoadingFilePreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress sx={{ color: '#4c3794' }} />
            </Box>
          ) : filePreviewInfo ? (
            <Box>
              {/* File Info */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Type:</strong> {filePreviewInfo.previewType} •
                  <strong>Size:</strong> {formatFileSize(filePreviewInfo.size)} •
                  <strong>Modified:</strong> {formatDate(filePreviewInfo.modifiedTime)}
                </Typography>
              </Box>

              {/* Preview Content */}
              {filePreviewInfo.previewAvailable ? (
                <Box>
                  {filePreviewInfo.previewType === 'image' && (
                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={previewUrl}
                        alt={filePreviewInfo.name}
                        style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  {filePreviewInfo.previewType === 'video' && (
                    <Box sx={{ textAlign: 'center' }}>
                      <video
                        controls
                        style={{ maxWidth: '100%', maxHeight: '60vh' }}
                      >
                        <source src={previewUrl} type={filePreviewInfo.mimeType} />
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  )}
                  {filePreviewInfo.previewType === 'audio' && (
                    <Box sx={{ textAlign: 'center' }}>
                      <audio controls style={{ width: '100%' }}>
                        <source src={previewUrl} type={filePreviewInfo.mimeType} />
                        Your browser does not support the audio tag.
                      </audio>
                    </Box>
                  )}
                  {filePreviewInfo.previewType === 'pdf' && (
                    <Box sx={{ height: '60vh' }}>
                      <iframe
                        src={previewUrl}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={filePreviewInfo.name}
                      />
                    </Box>
                  )}
                  {filePreviewInfo.previewType === 'text' && (
                    <Box sx={{
                      backgroundColor: '#f8f9fa',
                      p: 2,
                      borderRadius: 1,
                      maxHeight: '60vh',
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {previewContent}
                      </pre>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Preview not available for this file type.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(filePreviewInfo.webViewLink, '_blank')}
                    sx={{ mt: 2, color: '#4c3794', borderColor: '#4c3794' }}
                  >
                    Open in Google Drive
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
              No preview information available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowFilePreview(false)}
            sx={{ color: '#4c3794' }}
          >
            Close
          </Button>
          {filePreviewInfo?.webViewLink && (
            <Button
              variant="contained"
              onClick={() => window.open(filePreviewInfo.webViewLink, '_blank')}
              sx={{
                backgroundColor: '#4c3794',
                '&:hover': {
                  backgroundColor: '#3a2a73',
                }
              }}
            >
              Open in Google Drive
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Folder Tree Dialog */}
      <Dialog
        open={showFolderTree}
        onClose={() => setShowFolderTree(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📁 Folder Structure
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowFolderTree(false)}
            size="small"
            sx={{
              color: '#1a1a1a',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {isLoadingFolderTree ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress sx={{ color: '#4c3794' }} />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Click on any folder to navigate to it.
              </Typography>
              {folderTree.length > 0 ? (
                <Box>
                  {folderTree.map((folder) => (
                    <FolderTreeItem
                      key={folder.id}
                      folder={folder}
                      onNavigate={navigateToFolder}
                      onCloseDialog={() => setShowFolderTree(false)}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  No folders found.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setShowFolderTree(false)}
            sx={{ color: '#4c3794' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Content Dialog */}
      {
        showExportDialog && (
          <Dialog
            open={showExportDialog}
            onClose={handleCloseExportDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{
              backgroundColor: '#F8F9FA',
              borderBottom: '1px solid #E5E9EF',
              p: 3
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DownloadIcon sx={{ color: '#4c3794', fontSize: 24 }} />
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontSize: '1.25rem'
                  }}>
                    Export Content
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Step {currentStep} of 2
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {currentStep === 1 && (
                <Box sx={{ space: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Brand Details
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      These details help us create content that matches your brand voice and style.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Company Website */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Company Website
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                          fullWidth
                          type="url"
                          value={brandDetails.website}
                          onChange={(e) => handleBrandDetailsChange('website', e.target.value)}
                          disabled={brandDetails.websiteNotApplicable}
                          placeholder="https://www.example.com"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4c3794'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4c3794'
                              }
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="checkbox"
                            checked={brandDetails.websiteNotApplicable}
                            onChange={(e) => setBrandDetails(prev => ({ ...prev, websiteNotApplicable: e.target.checked }))}
                            style={{ accentColor: '#4c3794' }}
                          />
                          <Typography variant="body2" sx={{ color: '#64748B' }}>
                            Not applicable / No website
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Industry Keywords */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Industry Keywords
                      </Typography>
                      <TextField
                        fullWidth
                        value={brandDetails.industryKeywords}
                        onChange={(e) => handleBrandDetailsChange('industryKeywords', e.target.value)}
                        placeholder="fintech, banking, financial services, digital payments, public sector"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }
                        }}
                      />
                    </Box>

                    {/* Target Audience */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Target Audience / Customer Segments
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Selected audiences */}
                        {brandDetails.targetAudience.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {brandDetails.targetAudience.map((audience) => (
                              <Chip
                                key={audience}
                                label={audience}
                                onDelete={() => handleRemoveAudience(audience)}
                                sx={{
                                  backgroundColor: '#F3E5F5',
                                  color: '#4c3794',
                                  '& .MuiChip-deleteIcon': {
                                    color: '#4c3794',
                                    '&:hover': {
                                      color: '#7B1FA2'
                                    }
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        )}

                        {/* Add custom audience */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={newAudience}
                            onChange={(e) => setNewAudience(e.target.value)}
                            placeholder="Add custom audience"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustomAudience()}
                          />
                          <Button
                            onClick={handleAddCustomAudience}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: '#4c3794',
                              color: '#4c3794',
                              '&:hover': {
                                borderColor: '#7B1FA2',
                                backgroundColor: 'rgba(76, 55, 148, 0.05)'
                              }
                            }}
                          >
                            Add
                          </Button>
                        </Box>

                        {/* Audience options */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {audienceOptions.map((option) => (
                            <Chip
                              key={option}
                              label={option}
                              onClick={() => handleAudienceToggle(option)}
                              variant={brandDetails.targetAudience.includes(option) ? 'filled' : 'outlined'}
                              sx={{
                                backgroundColor: brandDetails.targetAudience.includes(option) ? '#F3E5F5' : 'transparent',
                                color: brandDetails.targetAudience.includes(option) ? '#4c3794' : '#64748B',
                                borderColor: '#4c3794',
                                '&:hover': {
                                  backgroundColor: 'rgba(76, 55, 148, 0.05)'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    {/* Brand Voice */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Brand Voice & Tone
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={brandDetails.brandVoice}
                        onChange={(e) => handleBrandDetailsChange('brandVoice', e.target.value)}
                        placeholder="bold, warm, authoritative, casual, formal, friendly, professional"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block' }}>
                        Describe your brand's voice and tone. You can select specific tones for each content piece during generation.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {currentStep === 2 && (
                <Box sx={{ space: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Content Preferences
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Choose your preferred content format and style.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Content Category */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Content Category
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={contentPreferences.category}
                          onChange={(e) => handleContentPreferenceChange('category', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E0E0E0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          {contentTypes.map((contentType) => (
                            <MenuItem key={contentType.contentType} value={contentType.contentType}>
                              {contentType.contentTypeDescription}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Content Format */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Content Format
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={contentPreferences.format}
                          onChange={(e) => handleContentPreferenceChange('format', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E0E0E0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          <MenuItem value="Text Only">Text Only</MenuItem>
                          <MenuItem value="Image Only">Image Only</MenuItem>
                          <MenuItem value="Infographic">Infographic</MenuItem>
                          <MenuItem value="Video">Video</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Content Length */}
                    {isContentLengthApplicable() && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                          Content Length
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={contentPreferences.length}
                            onChange={(e) => handleContentPreferenceChange('length', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E0E0E0'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4c3794'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4c3794'
                              }
                            }}
                          >
                            <MenuItem value="Short (100-200 words)">Short (100-200 words)</MenuItem>
                            <MenuItem value="Medium (300-500 words)">Medium (300-500 words)</MenuItem>
                            <MenuItem value="Long (600+ words)">Long (600+ words)</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    {/* Writing Style */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Writing Style
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={contentPreferences.style}
                          onChange={(e) => handleContentPreferenceChange('style', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E0E0E0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          <MenuItem value="Professional">Professional</MenuItem>
                          <MenuItem value="Casual">Casual</MenuItem>
                          <MenuItem value="Technical">Technical</MenuItem>
                          <MenuItem value="Creative">Creative</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Additional Instructions */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Additional Instructions (Optional)
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={contentPreferences.instructions}
                        onChange={(e) => handleContentPreferenceChange('instructions', e.target.value)}
                        placeholder="Don't use emojis, don't use em-dash, avoid technical jargon, include call-to-action..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
              <Button
                onClick={handleCloseExportDialog}
                sx={{ color: '#64748B' }}
              >
                Cancel
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {currentStep > 1 && (
                  <Button
                    onClick={handlePrevStep}
                    variant="outlined"
                    sx={{
                      borderColor: '#4c3794',
                      color: '#4c3794',
                      '&:hover': {
                        borderColor: '#7B1FA2',
                        backgroundColor: 'rgba(76, 55, 148, 0.05)'
                      }
                    }}
                  >
                    Previous
                  </Button>
                )}

                {currentStep < 2 ? (
                  <Button
                    onClick={handleNextStep}
                    variant="contained"
                    sx={{
                      backgroundColor: '#4c3794',
                      '&:hover': {
                        backgroundColor: '#3d2e7a'
                      }
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerateContent}
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{
                      backgroundColor: '#4c3794',
                      '&:hover': {
                        backgroundColor: '#3d2e7a'
                      }
                    }}
                  >
                    Generate Content
                  </Button>
                )}
              </Box>
            </DialogActions>
          </Dialog>
        )
      }

      {/* Generated Content Display Dialog */}
      {
        showContentDisplay && generatedContent && (
          <Dialog
            open={showContentDisplay}
            onClose={handleCloseContentDisplay}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{
              backgroundColor: '#F8F9FA',
              borderBottom: '1px solid #E5E9EF',
              p: 3
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: '#4CAF50',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '16px' }}>✓</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 600,
                      color: '#1a1a1a',
                      fontSize: '1.25rem'
                    }}>
                      Generated Content
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      {generatedContent.category} • {generatedContent.format}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseContentDisplay}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              {isGeneratingContent ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  gap: 2
                }}>
                  <CircularProgress size={40} sx={{ color: '#4c3794' }} />
                  <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
                    Generating Content...
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center' }}>
                    Please wait while we create your content based on the selected conversation pairs.
                  </Typography>
                </Box>
              ) : (['Image Only', 'Infographic'].includes(generatedContent.format)) ? (
                <Box sx={{
                  backgroundColor: '#F8F9FA',
                  borderRadius: '12px',
                  p: 3,
                  border: '1px solid #E5E9EF',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a1a1a', fontWeight: 600 }}>
                    {generatedContent.title}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <img
                      src={generatedContent.content}
                      alt="Generated content visualization"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        maxHeight: '500px'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    Generated {generatedContent.format.toLowerCase()} with charts and metrics based on your survey data and brand preferences
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  backgroundColor: '#F8F9FA',
                  borderRadius: '12px',
                  p: 3,
                  border: '1px solid #E5E9EF'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1a1a1a', fontWeight: 600 }}>
                    {generatedContent.title}
                  </Typography>

                  {/* Main Content */}
                  <Box sx={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    p: 2,
                    border: '1px solid #E0E0E0',
                    mb: 2
                  }}>
                    <MarkdownMessage text={generatedContent.content} />
                  </Box>

                  {/* Visualization */}
                  {generatedContent.visualization && (
                    <Box sx={{ mb: 2 }}>
                      {generatedContent.visualization.type === 'table' && (
                        <DataTable data={generatedContent.visualization.data} />
                      )}
                      {generatedContent.visualization.type === 'chart' && (
                        <DataChart data={generatedContent.visualization.data} />
                      )}
                      {generatedContent.visualization.type === 'heatmap' && (
                        <DataHeatmap data={generatedContent.visualization.data} />
                      )}
                    </Box>
                  )}

                  {/* Follow-up Questions */}
                  {generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Follow-up Questions
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        p: 2,
                        border: '1px solid #E0E0E0'
                      }}>
                        {generatedContent.followupQuestions.map((question, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 1, color: '#1a1a1a' }}>
                            • {question}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Source Links */}
                  {generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Source Links
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        p: 2,
                        border: '1px solid #E0E0E0'
                      }}>
                        {generatedContent.sourceLinks.map((link, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 1, color: '#4c3794' }}>
                            <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                              {link}
                            </a>
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
              <Button
                onClick={handleCloseContentDisplay}
                sx={{ color: '#64748B' }}
              >
                Close
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Copy button for text-based content */}
                {(generatedContent.format === 'Text Only' || generatedContent.format === 'Video') && (
                  <Button
                    onClick={handleCopyContent}
                    variant="outlined"
                    startIcon={<FileText />}
                    sx={{
                      borderColor: '#4c3794',
                      color: '#4c3794',
                      '&:hover': {
                        borderColor: '#7B1FA2',
                        backgroundColor: 'rgba(76, 55, 148, 0.05)'
                      }
                    }}
                  >
                    Copy Content
                  </Button>
                )}

                <Button
                  onClick={handleDownloadContent}
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#7B1FA2'
                    }
                  }}
                >
                  Download {
                    generatedContent.format === 'Image Only' || generatedContent.format === 'Infographic' ? 'PNG' :
                      generatedContent.format === 'Video' ? 'MP4' :
                        'Content'
                  }
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        )
      }

      {/* Success/Error Snackbars */}
      {
        success && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              backgroundColor: '#4CAF50',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {success}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSuccess('')}
              sx={{ color: 'white', ml: 1 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )
      }

      {
        error && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              backgroundColor: '#F44336',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {error}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setError('')}
              sx={{ color: 'white', ml: 1 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )
      }

      {/* Reasoning Log Notification Popover */}
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 450,
            maxWidth: '90vw',
            maxHeight: 600,
            mt: 1,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: '12px',
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            pb: 1.5,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4c3794' }}>
              Upload Progress
            </Typography>
            <IconButton
              size="small"
              onClick={() => setNotificationAnchorEl(null)}
              sx={{ color: '#666', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          {reasoningEntries.size === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
              <NotificationsIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body2" sx={{ color: '#999' }}>
                No active uploads. Upload a file to see progress updates.
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 500, overflow: 'auto', p: 0 }}>
              {Array.from(reasoningEntries.entries())
                .sort((a, b) => b[1].timestamp.getTime() - a[1].timestamp.getTime()) // Sort by timestamp, newest first
                .map(([interactionId, entry], index, array) => {
                  const progress = entry.progressPercent !== undefined ? entry.progressPercent : 0;
                  const isComplete = progress >= 100;

                  return (
                    <React.Fragment key={`${interactionId}-${entry.timestamp.getTime()}`}>
                      <ListItem
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 2,
                          px: 2.5,
                          backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.04)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 55, 148, 0.05)',
                          },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#4c3794',
                                fontWeight: 600,
                                fontSize: '0.813rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                              title={entry.interactionName || interactionId}
                            >
                              {entry.interactionName ? (
                                entry.interactionName
                              ) : (
                                `Upload ${interactionId.substring(0, 4)}...`
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Progress Bar */}
                        {entry.progressPercent !== undefined && (
                          <Box sx={{ width: '100%', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, fontSize: '0.7rem' }}>
                                Progress
                              </Typography>
                              <Typography variant="caption" sx={{ color: isComplete ? '#4caf50' : '#4c3794', fontWeight: 600, fontSize: '0.75rem' }}>
                                {progress}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  backgroundColor: isComplete ? '#4caf50' : '#4c3794',
                                  transition: 'width 0.3s ease-in-out'
                                }
                              }}
                            />
                          </Box>
                        )}

                        {/* Reasoning Text */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#333',
                            wordBreak: 'break-word',
                            lineHeight: 1.6,
                            fontSize: '0.875rem',
                            width: '100%'
                          }}
                        >
                          {entry.reasoning}
                        </Typography>
                      </ListItem>
                      {index < array.length - 1 && <Divider sx={{ mx: 2 }} />}
                    </React.Fragment>
                  );
                })}
            </List>
          )}
        </Box>
      </Popover>

      {/* Chart Expand Dialog */}
      <Dialog
        open={expandedChartDialog?.open || false}
        onClose={() => setExpandedChartDialog(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid #e9ecef'
        }}>
          <Typography variant="h6" sx={{
            color: '#131F32',
            fontWeight: 700,
            fontSize: '1.25rem'
          }}>
            {expandedChartDialog?.title}
          </Typography>
          <IconButton
            onClick={() => setExpandedChartDialog(null)}
            sx={{
              color: '#64748B',
              '&:hover': {
                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                color: '#4c3794'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {expandedChartDialog && (() => {
            // Custom tooltip for dialog
            const CustomTooltip = ({ active, payload, label }: any) => {
              if (active && payload && payload.length) {
                const dataPoint = payload[0].payload;
                const allFields = expandedChartDialog.headers.filter((header: string) => {
                  if (header === expandedChartDialog.labelHeader) return false;
                  const val = dataPoint[header];
                  return val !== null && val !== undefined && val !== '';
                });

                return (
                  <Box sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px 16px',
                    minWidth: '150px',
                    maxWidth: '300px'
                  }}>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: '#131F32',
                      mb: 1.5
                    }}>
                      {label}
                    </Typography>
                    {allFields.map((header: string, index: number) => {
                      const value = dataPoint[header];
                      if (value === null || value === undefined || value === '') return null;

                      const headerLabel = header
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())
                        .trim();

                      const isNumeric = typeof value === 'number';
                      const isPercentage = isNumeric && (
                        header.toLowerCase().includes('percent') ||
                        header.toLowerCase().includes('%') ||
                        header.toLowerCase() === 'percentage'
                      );

                      return (
                        <Box
                          key={header}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: index < allFields.length - 1 ? 0.5 : 0,
                            gap: 1
                          }}
                        >
                          <Typography sx={{
                            fontSize: '0.813rem',
                            color: '#64748B',
                            fontWeight: 500,
                            flexShrink: 0
                          }}>
                            {headerLabel}:
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.813rem',
                            color: '#131F32',
                            fontWeight: 600,
                            textAlign: 'right',
                            wordBreak: 'break-word'
                          }}>
                            {isNumeric
                              ? (isPercentage
                                ? `${value}%`
                                : value.toLocaleString())
                              : String(value)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              }
              return null;
            };

            // Treemap tooltip for dialog
            const TreemapTooltip = ({ active, payload }: any) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <Box sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '12px 16px',
                    minWidth: '200px',
                    maxWidth: '400px'
                  }}>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: '#131F32',
                      mb: 1.5
                    }}>
                      {data.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Size:</Typography>
                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.size}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Percentage:</Typography>
                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.percentage.toFixed(1)}%</Typography>
                      </Box>
                      {data.performanceScore !== null && data.performanceScore !== undefined && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Score:</Typography>
                          <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>
                            {typeof data.performanceScore === 'number' ? data.performanceScore.toFixed(1) : data.performanceScore}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Category:</Typography>
                        <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.performanceCategory}</Typography>
                      </Box>
                      {data.summary && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e9ecef' }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 }}>
                            {data.summary}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              }
              return null;
            };

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Display Analysis if available */}
                {expandedChartDialog.analysis && (
                  <Card sx={{
                    boxShadow: 'none',
                    border: 'none',
                    backgroundColor: 'transparent'
                  }}>
                    <MarkdownMessage text={expandedChartDialog.analysis} />
                  </Card>
                )}

                {/* Chart - Treemap for Segments Overview, Bubble Chart for Driver Priority Matrix, BarChart for others */}
                <Box sx={{ width: '100%', height: '600px', overflow: 'visible', position: 'relative' }}>
                  {expandedChartDialog.isDriverPriorityMatrix ? (
                    (() => {
                      // Use the same logic as the main Driver Priority Matrix chart
                      const getValue = (row: any, possibleKeys: string[]): any => {
                        for (const key of possibleKeys) {
                          if (row[key] !== undefined && row[key] !== null) {
                            return row[key];
                          }
                        }
                        const keyLower = possibleKeys[0]?.toLowerCase();
                        for (const rowKey in row) {
                          if (rowKey.toLowerCase() === keyLower) {
                            return row[rowKey];
                          }
                        }
                        const normalized = possibleKeys[0]?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                        for (const rowKey in row) {
                          const normalizedRowKey = rowKey.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                          if (normalizedRowKey === normalized) {
                            return row[rowKey];
                          }
                        }
                        return null;
                      };

                      const rawData = expandedChartDialog.chartData
                        .map((row: any) => {
                          const driver = getValue(row, ['driver', 'Driver']) || '';
                          const currentPerformance = getValue(row, ['current_performance_score', 'Current Performance Score', 'Current Performance', 'currentPerformanceScore']) || 0;
                          const impactScore = getValue(row, ['impact_score', 'Impact Score', 'impactScore']) || 0;
                          const performanceGap = getValue(row, ['performance_gap', 'Performance Gap', 'performanceGap']) || 0;
                          const direction = getValue(row, ['direction', 'Direction']) || 'positive';
                          const uplift = getValue(row, ['uplift', 'Uplift']);
                          const correlation = getValue(row, ['correlation', 'Correlation']);

                          const x = typeof currentPerformance === 'number' ? currentPerformance : parseFloat(String(currentPerformance)) || 0;
                          const y = typeof impactScore === 'number' ? impactScore : parseFloat(String(impactScore)) || 0;
                          const gap = Math.abs(typeof performanceGap === 'number' ? performanceGap : parseFloat(String(performanceGap)) || 0);

                          return {
                            driver,
                            x,
                            y,
                            gap,
                            direction,
                            uplift: uplift !== null && uplift !== undefined ? (typeof uplift === 'number' ? uplift : parseFloat(String(uplift))) : null,
                            correlation: correlation !== null && correlation !== undefined ? (typeof correlation === 'number' ? correlation : parseFloat(String(correlation))) : null,
                          };
                        })
                        .filter((item: any) => item.driver && !isNaN(item.x) && !isNaN(item.y) && item.x !== null && item.y !== null);

                      // Calculate chart domain
                      const xValuesRaw = rawData.map((d: any) => d.x);
                      const yValuesRaw = rawData.map((d: any) => d.y);
                      const chartXMin = Math.min(0, ...xValuesRaw);
                      const chartXMax = Math.max(100, ...xValuesRaw);
                      const chartYMin = Math.min(0, ...yValuesRaw);
                      const chartYMax = Math.max(...yValuesRaw);
                      const chartXPadding = 5;
                      const chartYPadding = (chartYMax - chartYMin) * 0.1 || 5;

                      const visibleXMin = chartXMin - chartXPadding;
                      const visibleXMax = chartXMax + chartXPadding;
                      const visibleYMin = Math.max(0, chartYMin - chartYPadding);
                      const visibleYMax = chartYMax + chartYPadding;

                      const performanceThreshold = (visibleXMin + visibleXMax) / 2;
                      const centerImpact = (visibleYMin + visibleYMax) / 2;

                      const getQuadrant = (x: number, y: number): string => {
                        if (x < performanceThreshold && y >= centerImpact) return 'Priority Fix';
                        if (x >= performanceThreshold && y >= centerImpact) return 'Maintain Excellence';
                        if (x >= performanceThreshold && y < centerImpact) return 'Over-Investing';
                        return 'Low Priority';
                      };

                      const bubbleData = rawData.map((item: any) => {
                        const quadrant = getQuadrant(item.x, item.y);
                        return {
                          driver: item.driver,
                          x: item.x,
                          y: item.y,
                          z: item.gap * 1000,
                          quadrant,
                          direction: item.direction,
                          uplift: item.uplift,
                          correlation: item.correlation,
                          performanceGap: item.gap,
                          currentPerformance: item.x,
                          impactScore: item.y
                        };
                      }).sort((a: any, b: any) => {
                        const quadrantOrder: Record<string, number> = {
                          'Priority Fix': 1,
                          'Maintain Excellence': 2,
                          'Over-Investing': 3,
                          'Low Priority': 4
                        };
                        const aQuadrant = quadrantOrder[a.quadrant] || 99;
                        const bQuadrant = quadrantOrder[b.quadrant] || 99;
                        if (aQuadrant !== bQuadrant) return aQuadrant - bQuadrant;
                        return b.y - a.y;
                      });

                      const quadrantColors: Record<string, string> = {
                        'Priority Fix': '#f44336',
                        'Maintain Excellence': '#4caf50',
                        'Over-Investing': '#ff9800',
                        'Low Priority': '#9e9e9e'
                      };

                      const BubbleTooltip = ({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box sx={{
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              padding: '12px 16px',
                              minWidth: '200px',
                              maxWidth: '350px'
                            }}>
                              <Typography sx={{
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                color: '#131F32',
                                mb: 1.5
                              }}>
                                {data.driver}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.currentPerformance.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Impact Score:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.impactScore.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Gap:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.performanceGap.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Quadrant:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.quadrant}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Direction:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.direction}</Typography>
                                </Box>
                                {data.uplift !== null && data.uplift !== undefined && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Uplift:</Typography>
                                    <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.uplift === 'number' ? data.uplift.toFixed(2) : data.uplift}</Typography>
                                  </Box>
                                )}
                                {data.correlation !== null && data.correlation !== undefined && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Correlation:</Typography>
                                    <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.correlation === 'number' ? data.correlation.toFixed(3) : data.correlation}</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        }
                        return null;
                      };

                      if (bubbleData.length === 0) {
                        return (
                          <Card sx={{
                            backgroundColor: '#fff',
                            p: 3,
                            borderRadius: '12px',
                            border: '1px solid #e9ecef',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                          }}>
                            <Typography sx={{
                              fontSize: '0.938rem',
                              color: '#64748B',
                              textAlign: 'center',
                              py: 4
                            }}>
                              No driver priority data available.
                            </Typography>
                          </Card>
                        );
                      }

                      const xMin = chartXMin;
                      const xMax = chartXMax;
                      const yMin = chartYMin;
                      const yMax = chartYMax;
                      const xPadding = chartXPadding;
                      const yPadding = chartYPadding;

                      const quadrantGroups: Record<string, any[]> = {
                        'Priority Fix': [],
                        'Maintain Excellence': [],
                        'Over-Investing': [],
                        'Low Priority': []
                      };
                      bubbleData.forEach((item: any) => {
                        const quadrant = item.quadrant;
                        if (quadrantGroups[quadrant]) {
                          quadrantGroups[quadrant].push(item);
                        } else {
                          quadrantGroups['Low Priority'].push(item);
                        }
                      });

                      return (
                        <Card sx={{
                          backgroundColor: '#fff',
                          p: 3,
                          borderRadius: '12px',
                          border: '1px solid #e9ecef',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                        }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                              fontSize: '0.875rem',
                              color: '#64748B',
                              fontStyle: 'italic',
                              mb: 1
                            }}>
                              Bubble size = performance gap. Quadrants divided at center: X = {performanceThreshold.toFixed(1)}, Y = {centerImpact.toFixed(2)}.
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                            {Object.entries(quadrantColors).map(([quadrant, color]) => (
                              <Box key={quadrant} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: color,
                                  border: '2px solid #fff',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500 }}>
                                  {quadrant} ({quadrantGroups[quadrant]?.length || 0})
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Box sx={{ width: '100%', height: '500px', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ScatterChart
                                margin={{ top: 40, right: 30, bottom: 60, left: 60 }}
                              >
                                <ReferenceArea
                                  x1={xMin - xPadding}
                                  x2={performanceThreshold}
                                  y1={centerImpact}
                                  y2={yMax + yPadding}
                                  fill="rgba(244, 67, 54, 0.08)"
                                  fillOpacity={1}
                                />
                                <ReferenceArea
                                  x1={performanceThreshold}
                                  x2={xMax + xPadding}
                                  y1={centerImpact}
                                  y2={yMax + yPadding}
                                  fill="rgba(76, 175, 80, 0.08)"
                                  fillOpacity={1}
                                />
                                <ReferenceArea
                                  x1={xMin - xPadding}
                                  x2={performanceThreshold}
                                  y1={Math.max(0, yMin - yPadding)}
                                  y2={centerImpact}
                                  fill="rgba(158, 158, 158, 0.08)"
                                  fillOpacity={1}
                                />
                                <ReferenceArea
                                  x1={performanceThreshold}
                                  x2={xMax + xPadding}
                                  y1={Math.max(0, yMin - yPadding)}
                                  y2={centerImpact}
                                  fill="rgba(255, 152, 0, 0.08)"
                                  fillOpacity={1}
                                />

                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                <ReferenceLine
                                  x={performanceThreshold}
                                  stroke="#4c3794"
                                  strokeWidth={2}
                                  strokeDasharray="5 5"
                                  label={{
                                    value: `Performance Center = ${performanceThreshold.toFixed(1)}`,
                                    position: 'top',
                                    fill: '#4c3794',
                                    fontSize: 11,
                                    fontWeight: 600
                                  }}
                                />

                                <ReferenceLine
                                  y={centerImpact}
                                  stroke="#4c3794"
                                  strokeWidth={2}
                                  strokeDasharray="5 5"
                                  label={{
                                    value: `Impact Center = ${centerImpact.toFixed(2)}`,
                                    position: 'right',
                                    fill: '#4c3794',
                                    fontSize: 11,
                                    fontWeight: 600
                                  }}
                                />

                                <XAxis
                                  type="number"
                                  dataKey="x"
                                  name="Current Performance Score"
                                  label={{ value: 'Current Performance Score', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#64748B', fontSize: '0.875rem' } }}
                                  domain={[xMin - xPadding, xMax + xPadding]}
                                  tick={{ fontSize: 12, fill: '#64748B' }}
                                  tickFormatter={(value: number) => value.toFixed(0)}
                                  allowDecimals={true}
                                />
                                <YAxis
                                  type="number"
                                  dataKey="y"
                                  name="Impact Score"
                                  label={{ value: 'Impact Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748B', fontSize: '0.875rem' } }}
                                  domain={[Math.max(0, yMin - yPadding), yMax + yPadding]}
                                  tick={{ fontSize: 12, fill: '#64748B' }}
                                  tickFormatter={(value: number) => value.toFixed(2)}
                                  allowDecimals={true}
                                />
                                <ZAxis
                                  type="number"
                                  dataKey="z"
                                  range={[100, 800]}
                                  name="Performance Gap"
                                />
                                <Tooltip content={<BubbleTooltip />} cursor={{ strokeDasharray: '3 3' }} />

                                {Object.entries(quadrantGroups).map(([quadrant, data]) => (
                                  data.length > 0 && (
                                    <Scatter
                                      key={quadrant}
                                      name={quadrant}
                                      data={data}
                                      fill={quadrantColors[quadrant] || '#4c3794'}
                                      shape={(props: any) => {
                                        const { cx, cy, payload } = props;
                                        const radius = Math.max(8, Math.sqrt(payload.z / Math.PI) * 0.5);
                                        const isPositive = payload.direction === 'positive';

                                        return (
                                          <g>
                                            <circle
                                              cx={cx}
                                              cy={cy}
                                              r={radius}
                                              fill={quadrantColors[payload.quadrant] || '#4c3794'}
                                              // stroke={isPositive ? '#fff' : '#333'}
                                              // strokeWidth={isPositive ? 2 : 3}
                                              // strokeDasharray={isPositive ? 'none' : '3 2'}
                                              opacity={0.8}
                                            />
                                          </g>
                                        );
                                      }}
                                    />
                                  )
                                ))}
                              </ScatterChart>
                            </ResponsiveContainer>
                          </Box>
                        </Card>
                      );
                    })()
                  ) : expandedChartDialog.isSegmentsOverview ? (
                    (() => {
                      // Filter out segments that are too small (less than 2% or size < 5)
                      const filteredData = expandedChartDialog.chartData.filter((item: any) => {
                        const minPercentage = 2;
                        const minSize = 5;
                        return item.percentage >= minPercentage && item.size >= minSize;
                      });

                      // Transform data for @nivo/treemap format with rich, varied colors
                      const richColors = [
                        '#4c3794', // Brand purple
                        '#1e3a5f', // Deep navy blue
                        '#2d5a4a', // Forest green
                        '#6b3a3a', // Burgundy
                        '#3d5a80', // Steel blue
                        '#5c4033', // Dark brown
                        '#2f4858', // Charcoal blue
                        '#4a3728', // Coffee
                        '#1f4e5f', // Teal
                        '#553555'  // Plum
                      ];

                      const nivoData = {
                        name: 'Segments',
                        children: filteredData.map((item: any, index: number) => ({
                          id: item.name,
                          value: item.size,
                          color: richColors[index % richColors.length],
                          percentage: item.percentage,
                          performanceScore: item.performanceScore,
                          performanceCategory: item.performanceCategory,
                          summary: item.summary,
                          outcomes: item.outcomes
                        }))
                      };

                      // Custom tooltip for @nivo treemap dialog
                      const NivoTooltipDialog = ({ node }: any) => {
                        if (!node) return null;
                        const data = node.data;
                        return (
                          <Box sx={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                            padding: '12px 16px',
                            minWidth: '200px',
                            maxWidth: '450px',
                            zIndex: 99999
                          }}>
                            <Typography sx={{
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: '#131F32',
                              mb: 1.5
                            }}>
                              {node.id || 'Unknown'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Size:</Typography>
                                <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{node.value}</Typography>
                              </Box>
                              {data.percentage !== undefined && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Percentage:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.percentage.toFixed(1)}%</Typography>
                                </Box>
                              )}
                              {data.performanceScore !== null && data.performanceScore !== undefined && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Score:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>
                                    {typeof data.performanceScore === 'number' ? data.performanceScore.toFixed(1) : data.performanceScore}
                                  </Typography>
                                </Box>
                              )}
                              {data.performanceCategory && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Category:</Typography>
                                  <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.performanceCategory}</Typography>
                                </Box>
                              )}
                              {data.summary && (
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e9ecef' }}>
                                  <Typography sx={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 }}>
                                    {data.summary}
                                  </Typography>
                                </Box>
                              )}
                              {/* Outcomes Section */}
                              {data.outcomes && typeof data.outcomes === 'object' && Object.keys(data.outcomes).length > 0 && (
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(76, 55, 148, 0.3)' }}>
                                  <Typography sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#4c3794',
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Outcomes
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {Object.entries(data.outcomes).map(([key, value], idx) => (
                                      <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <Typography sx={{
                                          fontSize: '0.7rem',
                                          color: '#64748B',
                                          flex: 1,
                                          lineHeight: 1.4
                                        }}>
                                          {key}:
                                        </Typography>
                                        <Typography sx={{
                                          fontSize: '0.75rem',
                                          color: '#131F32',
                                          fontWeight: 600,
                                          minWidth: '50px',
                                          textAlign: 'right'
                                        }}>
                                          {String(value)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      };

                      return (
                        <ResponsiveTreeMap
                          data={nivoData}
                          value="value"
                          valueFormat=".0s"
                          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                          tile="squarify"
                          enableLabel={true}
                          // theme={{
                          //   labels: {
                          //     text: {
                          //       fontSize: 16,
                          //       fontWeight: 700
                          //     }
                          //   }
                          // }}
                          nodeOpacity={1}
                          parentLabelPosition="left"
                          parentLabelTextColor="black"
                          colors={{ scheme: 'oranges' }}
                          borderWidth={3}
                          borderColor="black"
                          // borderColor={{ from: 'color', modifiers: [['darker', 0.1]] }}

                          tooltip={NivoTooltipDialog}
                          animate={true}
                          motionConfig="gentle"

                          enableParentLabel={false}
                          orientLabel={true}
                          layers={['nodes', ({ nodes }: any) => {
                            // Custom layer for all labels with multi-line support
                            if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return null;

                            return (
                              <g>
                                {nodes.filter((node: any) => !node.isParent).map((node: any) => {
                                  if (!node || node.width < 80 || node.height < 45) return null;

                                  const name = String(node.id || node.data?.name || '');
                                  if (!name || name === 'undefined' || name === 'null' || name === 'Segments') return null;

                                  const charsPerLine = Math.max(10, Math.floor((node.width - 20) / 9));
                                  const lineHeight = 22;
                                  const maxLines = Math.max(1, Math.floor((node.height - 16) / lineHeight));

                                  const words = name.split(' ');
                                  const lines: string[] = [];
                                  let currentLine = '';

                                  for (const word of words) {
                                    if (lines.length >= maxLines) break;
                                    const testLine = currentLine ? `${currentLine} ${word}` : word;
                                    if (testLine.length <= charsPerLine) {
                                      currentLine = testLine;
                                    } else {
                                      if (currentLine) {
                                        lines.push(currentLine);
                                        if (lines.length >= maxLines) break;
                                        currentLine = word.length > charsPerLine ? word.substring(0, charsPerLine - 2) + '..' : word;
                                      } else {
                                        currentLine = word.substring(0, charsPerLine - 2) + '..';
                                      }
                                    }
                                  }

                                  if (currentLine && lines.length < maxLines) {
                                    lines.push(currentLine);
                                  }

                                  if (lines.length === 0) return null;

                                  const totalHeight = lines.length * lineHeight;
                                  const startY = node.y + (node.height - totalHeight) / 2 + lineHeight / 2;

                                  return (
                                    <g key={`label-${node.id}`}>
                                      {lines.map((line, index) => (
                                        <text
                                          key={index}
                                          x={node.x + node.width / 2}
                                          y={startY + index * lineHeight}
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          fill="#000000"
                                          fontSize={20}                    // ← MUST be a number
                                          fontWeight={700}
                                          style={{
                                            fontSize: 20,                  // ← REQUIRED for override
                                            pointerEvents: 'none',
                                          }}
                                        >
                                          {line}
                                        </text>

                                      ))}
                                    </g>
                                  );
                                })}
                              </g>
                            );
                          }]}
                        />
                      );
                    })()
                  ) : (
                    (() => {
                      // Determine the data key to use for display
                      // For Distribution of Survey Outcomes, use percentage; otherwise use count
                      const barDataKey = expandedChartDialog.isDistributionOfSurveyOutcomes ? 'percentage' : 'count';

                      // Calculate max value from ALL data for consistent scale
                      const allDataMax = Math.max(...expandedChartDialog.chartData.map((d: any) => d[barDataKey] || 0));

                      return (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={expandedChartDialog.chartData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: -10, bottom: 5 }}
                            barCategoryGap={20}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                            <XAxis
                              type="number"
                              tick={{ fontSize: 12, fill: '#64748B' }}
                              domain={expandedChartDialog.isKeyDrivers ? [0, 5] : expandedChartDialog.isDistributionOfSurveyOutcomes ? [0, 100] : [0, Math.ceil(allDataMax * 1.1)]}
                              tickCount={6}
                              allowDecimals={false}
                              tickFormatter={expandedChartDialog.isDistributionOfSurveyOutcomes ? (value: number) => `${value}%` : undefined}
                            />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={200}
                              tick={{ fontSize: 12, fill: '#64748B' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar
                              dataKey={barDataKey}
                              fill="#4c3794"
                              radius={[0, 8, 8, 0]}
                              maxBarSize={15}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    })()
                  )}
                </Box>

                {/* Display Recommendations if available */}
                {expandedChartDialog.recommendations && expandedChartDialog.recommendations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>💡</Typography>
                      </Box>
                      <Typography sx={{
                        fontSize: '0.938rem',
                        fontWeight: 700,
                        color: '#131F32',
                        letterSpacing: '-0.01em'
                      }}>
                        Recommendations
                      </Typography>
                      {/* <Chip
                        label={expandedChartDialog.recommendations.length}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          color: '#3b82f6',
                          ml: 'auto'
                        }}
                      /> */}
                    </Box>
                    <Card sx={{
                      backgroundColor: '#fff',
                      p: 2.5,
                      borderRadius: '12px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {expandedChartDialog.recommendations.map((rec: string, idx: number) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 2.5,
                              backgroundColor: 'rgba(59, 130, 246, 0.03)',
                              borderRadius: '10px',
                              border: '1px solid rgba(59, 130, 246, 0.1)',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.06)',
                                borderColor: 'rgba(59, 130, 246, 0.2)',
                                transform: 'translateX(4px)'
                              }
                            }}
                          >
                            <Box sx={{
                              fontSize: '0.875rem',
                              color: '#495057',
                              lineHeight: 1.6
                            }}>
                              <MarkdownMessage text={rec.trim()} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Card>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Multicolumn Data Expand Dialog */}
      {
        Object.keys(multicolumnDialogOpen).map((key) => (
          <Dialog
            key={key}
            open={multicolumnDialogOpen[key] || false}
            onClose={() => setMulticolumnDialogOpen(prev => ({ ...prev, [key]: false }))}
            maxWidth={multicolumnDialogData?.isSegmentDetails ? "lg" : "md"}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                maxHeight: '90vh'
              }
            }}
          >
            <DialogTitle sx={{
              backgroundColor: '#4c3794',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BarChartIcon sx={{ fontSize: 24 }} />
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
                  {multicolumnDialogData?.title || 'Data Analysis'}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setMulticolumnDialogOpen(prev => ({ ...prev, [key]: false }))}
                sx={{ color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              {multicolumnDialogData && multicolumnDialogData.isSegmentDetails ? (
                // Render full segment details
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Segment Description */}
                  {multicolumnDialogData.segmentData?.segmentSummary && (
                    <Box>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 1.5 }}>
                        Segment Description
                      </Typography>
                      <Card sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e9ecef' }}>
                        <Typography sx={{ fontSize: '0.938rem', color: '#475569', lineHeight: 1.7 }}>
                          {multicolumnDialogData.segmentData.segmentSummary}
                        </Typography>
                      </Card>
                    </Box>
                  )}

                  {/* All Multicolumn Data */}
                  {multicolumnDialogData.segmentData?.multicolumnData &&
                    multicolumnDialogData.segmentData.multicolumnData
                      .filter((tableData: any) => {
                        // Filter out What-If Analysis table (we show it as visualization instead)
                        const titleLower = tableData.title?.toLowerCase() || '';
                        const keyLower = tableData.key?.toLowerCase() || '';
                        const subcategory = tableData.entries?.find((e: any) => e.key === 'informationSubcategory')?.value || '';

                        const isWhatIfAnalysis =
                          titleLower.includes('what-if') ||
                          titleLower.includes('what if') ||
                          keyLower.includes('what-if') ||
                          keyLower.includes('whatif') ||
                          subcategory.toLowerCase().includes('what-if');

                        if (isWhatIfAnalysis) return false;
                        return true;
                      })
                      .map((tableData: any, tableIndex: number) => {
                        // Detect chart types
                        const titleLower = tableData.title?.toLowerCase() || '';
                        const keyLower = tableData.key?.toLowerCase() || '';
                        const subcategory = tableData.entries?.find((e: any) => e.key === 'informationSubcategory')?.value || '';

                        const isDriverPriorityMatrix =
                          titleLower.includes('driver priority matrix') ||
                          titleLower.includes('strategic focus areas') ||
                          titleLower.includes('performance vs impact') ||
                          keyLower === 'driverprioritmatrix' ||
                          keyLower.includes('strategicfocusareas') ||
                          subcategory.toLowerCase().includes('driver priority matrix');

                        const isOpportunityHeatmap =
                          titleLower.includes('opportunity heatmap') ||
                          titleLower.includes('top priority actions') ||
                          titleLower.includes('ranked improvement') ||
                          keyLower === 'opportunityheatmap' ||
                          keyLower.includes('toppriorityactions') ||
                          subcategory.toLowerCase().includes('opportunity heatmap');

                        return (
                          <Box key={tableIndex}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32', mb: 1.5 }}>
                              {tableData.title || 'Data Analysis'}
                            </Typography>

                            {/* Analysis if available */}
                            {tableData.analysis && (
                              <Card sx={{ p: 2, mb: 2, borderRadius: '10px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                <MarkdownMessage text={tableData.analysis} />
                              </Card>
                            )}

                            {/* Render Driver Priority Matrix as Bubble Chart - SAME AS MAIN VIEW */}
                            {isDriverPriorityMatrix && tableData.data && tableData.data.length > 0 ? (
                              (() => {
                                const getValue = (row: any, possibleKeys: string[]): any => {
                                  for (const key of possibleKeys) {
                                    if (row[key] !== undefined && row[key] !== null) return row[key];
                                  }
                                  for (const possibleKey of possibleKeys) {
                                    const keyLower = possibleKey.toLowerCase();
                                    for (const rowKey in row) {
                                      if (rowKey.toLowerCase() === keyLower) return row[rowKey];
                                    }
                                  }
                                  for (const possibleKey of possibleKeys) {
                                    const normalized = possibleKey.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                                    for (const rowKey in row) {
                                      const normalizedRowKey = rowKey.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                                      if (normalizedRowKey === normalized) return row[rowKey];
                                    }
                                  }
                                  return null;
                                };

                                // First pass: extract raw data with impact scores to calculate center
                                const rawData = tableData.data
                                  .map((row: any) => {
                                    const driver = getValue(row, ['driver', 'Driver']) || '';
                                    const currentPerformance = getValue(row, ['current_performance_score', 'Current Performance Score', 'Current Performance', 'currentPerformanceScore']) || 0;
                                    const impactScore = getValue(row, ['impact_score', 'Impact Score', 'impactScore']) || 0;
                                    const performanceGap = getValue(row, ['performance_gap', 'Performance Gap', 'performanceGap']) || 0;
                                    const direction = getValue(row, ['direction', 'Direction']) || 'positive';
                                    const uplift = getValue(row, ['uplift', 'Uplift']);
                                    const correlation = getValue(row, ['correlation', 'Correlation']);

                                    const x = typeof currentPerformance === 'number' ? currentPerformance : parseFloat(String(currentPerformance)) || 0;
                                    const y = typeof impactScore === 'number' ? impactScore : parseFloat(String(impactScore)) || 0;
                                    const gap = Math.abs(typeof performanceGap === 'number' ? performanceGap : parseFloat(String(performanceGap)) || 0);

                                    return {
                                      driver,
                                      x,
                                      y,
                                      gap,
                                      direction,
                                      uplift: uplift !== null && uplift !== undefined ? (typeof uplift === 'number' ? uplift : parseFloat(String(uplift))) : null,
                                      correlation: correlation !== null && correlation !== undefined ? (typeof correlation === 'number' ? correlation : parseFloat(String(correlation))) : null,
                                    };
                                  })
                                  .filter((item: any) => item.driver && !isNaN(item.x) && !isNaN(item.y) && item.x !== null && item.y !== null);

                                // Calculate chart domain
                                const xValuesRaw = rawData.map((d: any) => d.x);
                                const yValuesRaw = rawData.map((d: any) => d.y);
                                const chartXMin = Math.min(0, ...xValuesRaw);
                                const chartXMax = Math.max(100, ...xValuesRaw);
                                const chartYMin = Math.min(0, ...yValuesRaw);
                                const chartYMax = Math.max(...yValuesRaw);
                                const chartXPadding = 5;
                                const chartYPadding = (chartYMax - chartYMin) * 0.1 || 5;

                                const visibleXMin = chartXMin - chartXPadding;
                                const visibleXMax = chartXMax + chartXPadding;
                                const visibleYMin = Math.max(0, chartYMin - chartYPadding);
                                const visibleYMax = chartYMax + chartYPadding;

                                const performanceThreshold = (visibleXMin + visibleXMax) / 2;
                                const centerImpact = (visibleYMin + visibleYMax) / 2;

                                const getQuadrant = (x: number, y: number): string => {
                                  if (x < performanceThreshold && y >= centerImpact) return 'Priority Fix';
                                  if (x >= performanceThreshold && y >= centerImpact) return 'Maintain Excellence';
                                  if (x >= performanceThreshold && y < centerImpact) return 'Over-Investing';
                                  return 'Low Priority';
                                };

                                const bubbleData = rawData.map((item: any) => {
                                  const quadrant = getQuadrant(item.x, item.y);
                                  return {
                                    driver: item.driver,
                                    x: item.x,
                                    y: item.y,
                                    z: item.gap * 1000,
                                    quadrant,
                                    direction: item.direction,
                                    uplift: item.uplift,
                                    correlation: item.correlation,
                                    performanceGap: item.gap,
                                    currentPerformance: item.x,
                                    impactScore: item.y
                                  };
                                }).sort((a: any, b: any) => {
                                  const quadrantOrder: Record<string, number> = {
                                    'Priority Fix': 1,
                                    'Maintain Excellence': 2,
                                    'Over-Investing': 3,
                                    'Low Priority': 4
                                  };
                                  const aQuadrant = quadrantOrder[a.quadrant] || 99;
                                  const bQuadrant = quadrantOrder[b.quadrant] || 99;
                                  if (aQuadrant !== bQuadrant) return aQuadrant - bQuadrant;
                                  return b.y - a.y;
                                });

                                const quadrantColors: Record<string, string> = {
                                  'Priority Fix': '#f44336',
                                  'Maintain Excellence': '#4caf50',
                                  'Over-Investing': '#ff9800',
                                  'Low Priority': '#9e9e9e'
                                };

                                const BubbleTooltipDialog = ({ active, payload }: any) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <Box sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: '8px',
                                        border: '1px solid #e9ecef',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        padding: '12px 16px',
                                        minWidth: '200px',
                                        maxWidth: '350px'
                                      }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#131F32', mb: 1.5 }}>
                                          {data.driver}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance:</Typography>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.currentPerformance.toFixed(2)}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Impact Score:</Typography>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.impactScore.toFixed(2)}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Performance Gap:</Typography>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.performanceGap.toFixed(2)}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Quadrant:</Typography>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.quadrant}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Direction:</Typography>
                                            <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{data.direction}</Typography>
                                          </Box>
                                          {data.uplift !== null && data.uplift !== undefined && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Uplift:</Typography>
                                              <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.uplift === 'number' ? data.uplift.toFixed(2) : data.uplift}</Typography>
                                            </Box>
                                          )}
                                          {data.correlation !== null && data.correlation !== undefined && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <Typography sx={{ fontSize: '0.813rem', color: '#64748B' }}>Correlation:</Typography>
                                              <Typography sx={{ fontSize: '0.813rem', color: '#131F32', fontWeight: 600 }}>{typeof data.correlation === 'number' ? data.correlation.toFixed(3) : data.correlation}</Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  }
                                  return null;
                                };

                                if (bubbleData.length === 0) {
                                  return (
                                    <Card sx={{ backgroundColor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e9ecef', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' }}>
                                      <Typography sx={{ fontSize: '0.938rem', color: '#64748B', textAlign: 'center', py: 4 }}>
                                        No driver priority data available for this segment.
                                      </Typography>
                                    </Card>
                                  );
                                }

                                const xMin = chartXMin;
                                const xMax = chartXMax;
                                const yMin = chartYMin;
                                const yMax = chartYMax;
                                const xPadding = chartXPadding;
                                const yPadding = chartYPadding;

                                const quadrantGroups: Record<string, any[]> = {
                                  'Priority Fix': [],
                                  'Maintain Excellence': [],
                                  'Over-Investing': [],
                                  'Low Priority': []
                                };
                                bubbleData.forEach((item: any) => {
                                  const quadrant = item.quadrant;
                                  if (quadrantGroups[quadrant]) {
                                    quadrantGroups[quadrant].push(item);
                                  } else {
                                    quadrantGroups['Low Priority'].push(item);
                                  }
                                });

                                return (
                                  <Card sx={{ backgroundColor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e9ecef', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', mb: 2 }}>
                                    <Box sx={{ mb: 2 }}>
                                      <Typography sx={{ fontSize: '0.875rem', color: '#64748B', fontStyle: 'italic', mb: 1 }}>
                                        Bubble size = performance gap. Quadrants divided at center: X = {performanceThreshold.toFixed(1)}, Y = {centerImpact.toFixed(2)}.
                                      </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                                      {Object.entries(quadrantColors).map(([quadrant, color]) => (
                                        <Box key={quadrant} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                          <Box sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                            border: '2px solid #fff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                          }} />
                                          <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 500 }}>
                                            {quadrant} ({quadrantGroups[quadrant]?.length || 0})
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>

                                    <Box sx={{ width: '100%', height: '500px', position: 'relative' }}>
                                      <ResponsiveContainer width="100%" height="100%">
                                        <ScatterChart margin={{ top: 40, right: 30, bottom: 60, left: 60 }}>
                                          <ReferenceArea
                                            x1={xMin - xPadding}
                                            x2={performanceThreshold}
                                            y1={centerImpact}
                                            y2={yMax + yPadding}
                                            fill="rgba(244, 67, 54, 0.08)"
                                            fillOpacity={1}
                                          />
                                          <ReferenceArea
                                            x1={performanceThreshold}
                                            x2={xMax + xPadding}
                                            y1={centerImpact}
                                            y2={yMax + yPadding}
                                            fill="rgba(76, 175, 80, 0.08)"
                                            fillOpacity={1}
                                          />
                                          <ReferenceArea
                                            x1={xMin - xPadding}
                                            x2={performanceThreshold}
                                            y1={Math.max(0, yMin - yPadding)}
                                            y2={centerImpact}
                                            fill="rgba(158, 158, 158, 0.08)"
                                            fillOpacity={1}
                                          />
                                          <ReferenceArea
                                            x1={performanceThreshold}
                                            x2={xMax + xPadding}
                                            y1={Math.max(0, yMin - yPadding)}
                                            y2={centerImpact}
                                            fill="rgba(255, 152, 0, 0.08)"
                                            fillOpacity={1}
                                          />

                                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                                          <ReferenceLine
                                            x={performanceThreshold}
                                            stroke="#4c3794"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            label={{
                                              value: `Performance Center = ${performanceThreshold.toFixed(1)}`,
                                              position: 'top',
                                              fill: '#4c3794',
                                              fontSize: 11,
                                              fontWeight: 600
                                            }}
                                          />

                                          <ReferenceLine
                                            y={centerImpact}
                                            stroke="#4c3794"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            label={{
                                              value: `Impact Center = ${centerImpact.toFixed(2)}`,
                                              position: 'right',
                                              fill: '#4c3794',
                                              fontSize: 11,
                                              fontWeight: 600
                                            }}
                                          />

                                          <XAxis
                                            type="number"
                                            dataKey="x"
                                            name="Current Performance Score"
                                            label={{ value: 'Current Performance Score', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#64748B', fontSize: '0.875rem' } }}
                                            domain={[xMin - xPadding, xMax + xPadding]}
                                            tick={{ fontSize: 12, fill: '#64748B' }}
                                            tickFormatter={(value: number) => value.toFixed(0)}
                                            allowDecimals={true}
                                          />
                                          <YAxis
                                            type="number"
                                            dataKey="y"
                                            name="Impact Score"
                                            label={{ value: 'Impact Score', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748B', fontSize: '0.875rem' } }}
                                            domain={[Math.max(0, yMin - yPadding), yMax + yPadding]}
                                            tick={{ fontSize: 12, fill: '#64748B' }}
                                            tickFormatter={(value: number) => value.toFixed(2)}
                                            allowDecimals={true}
                                          />
                                          <ZAxis
                                            type="number"
                                            dataKey="z"
                                            range={[100, 800]}
                                            name="Performance Gap"
                                          />
                                          <Tooltip content={<BubbleTooltipDialog />} cursor={{ strokeDasharray: '3 3' }} />

                                          {Object.entries(quadrantGroups).map(([quadrant, data]) => (
                                            data.length > 0 && (
                                              <Scatter
                                                key={quadrant}
                                                name={quadrant}
                                                data={data}
                                                fill={quadrantColors[quadrant] || '#4c3794'}
                                                shape={(props: any) => {
                                                  const { cx, cy, payload } = props;
                                                  const radius = Math.max(8, Math.sqrt(payload.z / Math.PI) * 0.5);
                                                  const isPositive = payload.direction === 'positive';

                                                  return (
                                                    <g>
                                                      <circle
                                                        cx={cx}
                                                        cy={cy}
                                                        r={radius}
                                                        fill={quadrantColors[payload.quadrant] || '#4c3794'}
                                                        // stroke={isPositive ? '#fff' : '#333'}
                                                        // strokeWidth={isPositive ? 2 : 3}
                                                        // strokeDasharray={isPositive ? 'none' : '3 2'}
                                                        opacity={0.8}
                                                      />
                                                    </g>
                                                  );
                                                }}
                                              />
                                            )
                                          ))}
                                        </ScatterChart>
                                      </ResponsiveContainer>
                                    </Box>
                                  </Card>
                                );
                              })()
                            ) : isOpportunityHeatmap && tableData.data && tableData.data.length > 0 ? (
                              /* Render Opportunity Heatmap - SAME AS MAIN VIEW */
                              (() => {
                                const getValue = (row: any, possibleKeys: string[]): any => {
                                  for (const key of possibleKeys) {
                                    if (row[key] !== undefined && row[key] !== null) return row[key];
                                  }
                                  const keyLower = possibleKeys[0]?.toLowerCase();
                                  for (const rowKey in row) {
                                    if (rowKey.toLowerCase() === keyLower) return row[rowKey];
                                  }
                                  const normalized = possibleKeys[0]?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                                  for (const rowKey in row) {
                                    const normalizedRowKey = rowKey.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
                                    if (normalizedRowKey === normalized) return row[rowKey];
                                  }
                                  return null;
                                };

                                // Process and sort data by priority_rank
                                const heatmapData = tableData.data
                                  .map((row: any) => {
                                    const priorityRank = getValue(row, ['priority_rank', 'Priority Rank', 'priorityRank']) || 999;
                                    const driver = getValue(row, ['driver', 'Driver']) || '';
                                    const opportunityScore = getValue(row, ['opportunity_score', 'Opportunity Score', 'opportunityScore']) || 0;
                                    const currentPerformance = getValue(row, ['current_performance_score', 'Current Performance', 'Current Performance Score', 'currentPerformanceScore']) || 0;
                                    const performanceGap = getValue(row, ['performance_gap', 'Performance Gap', 'performanceGap']) || 0;
                                    const impactScore = getValue(row, ['impact_score', 'Impact Score', 'impactScore']) || 0;
                                    const leverageScore = getValue(row, ['leverage_score', 'Leverage Score', 'leverageScore']) || 0;
                                    const quadrant = getValue(row, ['quadrant', 'Quadrant']) || 'Unknown';
                                    const direction = getValue(row, ['direction', 'Direction']) || 'positive';

                                    return {
                                      priorityRank: typeof priorityRank === 'number' ? priorityRank : parseFloat(String(priorityRank)) || 999,
                                      driver,
                                      opportunityScore: typeof opportunityScore === 'number' ? opportunityScore : parseFloat(String(opportunityScore)) || 0,
                                      currentPerformance: typeof currentPerformance === 'number' ? currentPerformance : parseFloat(String(currentPerformance)) || 0,
                                      performanceGap: typeof performanceGap === 'number' ? performanceGap : parseFloat(String(performanceGap)) || 0,
                                      impactScore: typeof impactScore === 'number' ? impactScore : parseFloat(String(impactScore)) || 0,
                                      leverageScore: typeof leverageScore === 'number' ? leverageScore : parseFloat(String(leverageScore)) || 0,
                                      quadrant,
                                      direction
                                    };
                                  })
                                  .filter((item: any) => item.driver && !isNaN(item.priorityRank))
                                  .sort((a: any, b: any) => a.priorityRank - b.priorityRank);

                                // Get opportunity score color - same as main view
                                // Low: < 5, Medium: 5-15, High: 15-25, Critical: > 25
                                const getOpportunityScoreColor = (score: number) => {
                                  if (score > 25) return { bg: 'rgba(244, 67, 54, 0.15)', text: '#f44336' }; // Critical - Red
                                  if (score >= 15) return { bg: 'rgba(255, 152, 0, 0.15)', text: '#ff9800' }; // High - Orange
                                  if (score >= 5) return { bg: 'rgba(255, 193, 7, 0.15)', text: '#f59e0b' }; // Medium - Yellow
                                  return { bg: 'rgba(76, 175, 80, 0.15)', text: '#4caf50' }; // Low - Green
                                };

                                // Get performance score color (higher is better)
                                const getPerformanceColor = (score: number) => {
                                  if (score >= 80) return { bg: 'rgba(34, 197, 94, 0.15)', text: '#16a34a' };
                                  if (score >= 60) return { bg: 'rgba(34, 197, 94, 0.1)', text: '#22c55e' };
                                  if (score >= 40) return { bg: 'rgba(245, 158, 11, 0.15)', text: '#d97706' };
                                  if (score >= 20) return { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' };
                                  return { bg: 'rgba(239, 68, 68, 0.1)', text: '#ef4444' };
                                };

                                // Get gap color (lower gap is better)
                                const getGapColor = (gap: number) => {
                                  const absGap = Math.abs(gap);
                                  if (absGap <= 5) return { bg: 'rgba(34, 197, 94, 0.15)', text: '#16a34a' };
                                  if (absGap <= 10) return { bg: 'rgba(34, 197, 94, 0.1)', text: '#22c55e' };
                                  if (absGap <= 15) return { bg: 'rgba(245, 158, 11, 0.15)', text: '#d97706' };
                                  if (absGap <= 20) return { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' };
                                  return { bg: 'rgba(239, 68, 68, 0.15)', text: '#dc2626' };
                                };

                                // Get impact score color
                                const getImpactColor = (score: number) => {
                                  if (score >= 0.5) return { bg: 'rgba(239, 68, 68, 0.15)', text: '#dc2626' };
                                  if (score >= 0.3) return { bg: 'rgba(245, 158, 11, 0.15)', text: '#d97706' };
                                  if (score >= 0.1) return { bg: 'rgba(34, 197, 94, 0.15)', text: '#16a34a' };
                                  return { bg: 'rgba(59, 130, 246, 0.1)', text: '#2563eb' };
                                };

                                // Get leverage score color
                                const getLeverageColor = (score: number) => {
                                  if (score >= 2) return { bg: 'rgba(139, 92, 246, 0.15)', text: '#7c3aed' };
                                  if (score >= 1) return { bg: 'rgba(59, 130, 246, 0.15)', text: '#2563eb' };
                                  return { bg: 'rgba(107, 114, 128, 0.1)', text: '#6b7280' };
                                };

                                // Get quadrant color
                                const getQuadrantColor = (quadrant: string) => {
                                  const q = quadrant.toLowerCase();
                                  if (q.includes('priority') && q.includes('fix')) return { bg: 'rgba(239, 68, 68, 0.12)', text: '#dc2626', border: '#fca5a5' };
                                  if (q.includes('low') && q.includes('priority')) return { bg: 'rgba(245, 158, 11, 0.12)', text: '#d97706', border: '#fcd34d' };
                                  if (q.includes('maintain')) return { bg: 'rgba(34, 197, 94, 0.12)', text: '#16a34a', border: '#86efac' };
                                  if (q.includes('leverage')) return { bg: 'rgba(59, 130, 246, 0.12)', text: '#2563eb', border: '#93c5fd' };
                                  return { bg: 'rgba(107, 114, 128, 0.08)', text: '#6b7280', border: '#d1d5db' };
                                };

                                // Get unique quadrants for filtering
                                const uniqueQuadrants: string[] = Array.from(new Set(heatmapData.map((item: any) => String(item.quadrant)))).filter((q): q is string => Boolean(q) && q !== 'undefined');

                                return (
                                  <Card sx={{ backgroundColor: '#fff', p: 2, borderRadius: '12px', border: '1px solid #e9ecef', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', mb: 2 }}>
                                    {/* Quadrant Filter */}
                                    {uniqueQuadrants.length > 0 && (
                                      <Box sx={{ mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                          <InputLabel>Filter by Quadrant</InputLabel>
                                          <Select
                                            defaultValue="All"
                                            label="Filter by Quadrant"
                                          >
                                            <MenuItem value="All">All Quadrants</MenuItem>
                                            {uniqueQuadrants.map((quadrant: string) => (
                                              <MenuItem key={quadrant} value={quadrant}>{quadrant}</MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Box>
                                    )}
                                    <Box sx={{ minWidth: '700px' }}>
                                      <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
                                        {/* Header Row - Simplified */}
                                        <Grid item xs={0.8}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)', borderRadius: '4px 0 0 4px' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Rank</Typography>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={4.4}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794' }}>Driver</Typography>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={2}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Opportunity Score</Typography>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={2}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Current Performance</Typography>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={1.6}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Impact Score</Typography>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={1.2}>
                                          <Box sx={{ p: 0.75, backgroundColor: 'rgba(76, 55, 148, 0.08)', borderRadius: '0 4px 4px 0' }}>
                                            <Typography sx={{ fontSize: '0.813rem', fontWeight: 700, color: '#4c3794', textAlign: 'center' }}>Direction</Typography>
                                          </Box>
                                        </Grid>
                                      </Grid>

                                      {/* Data Rows - Simplified */}
                                      {heatmapData.map((row: any, idx: number) => {
                                        const oppColor = getOpportunityScoreColor(row.opportunityScore);
                                        const perfColor = getPerformanceColor(row.currentPerformance);
                                        const impactColor = getImpactColor(row.impactScore);
                                        const isNegative = row.direction?.toLowerCase() === 'negative';

                                        return (
                                          <Grid container spacing={0.5} key={idx} sx={{ mb: 0.5 }}>
                                            <Grid item xs={0.8}>
                                              <Box sx={{
                                                p: 0.75,
                                                // backgroundColor: '#4c3794', 
                                                // borderRadius: '4px 0 0 4px',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff' }}>
                                                  {row.priorityRank}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={4.4}>
                                              <Box sx={{ p: 0.75, backgroundColor: idx % 2 === 0 ? 'rgba(76, 55, 148, 0.02)' : '#fff', height: '100%' }}>
                                                <Typography sx={{
                                                  fontSize: '0.875rem',
                                                  color: '#131F32',
                                                  fontWeight: 600,
                                                  lineHeight: 1.4
                                                }}>
                                                  {row.driver}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={2}>
                                              <Box sx={{
                                                p: 0.75,
                                                backgroundColor: oppColor.bg,
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: oppColor.text }}>
                                                  {row.opportunityScore.toFixed(1)}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={2}>
                                              <Box sx={{
                                                p: 0.75,
                                                backgroundColor: perfColor.bg,
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: perfColor.text }}>
                                                  {row.currentPerformance.toFixed(1)}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={1.6}>
                                              <Box sx={{
                                                p: 0.75,
                                                backgroundColor: impactColor.bg,
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                <Typography sx={{ fontSize: '0.938rem', fontWeight: 600, color: impactColor.text }}>
                                                  {row.impactScore.toFixed(2)}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={1.2}>
                                              <Box sx={{
                                                p: 0.75,
                                                backgroundColor: isNegative ? 'rgba(239, 68, 68, 0.08)' : 'rgba(34, 197, 94, 0.08)',
                                                borderRadius: '0 4px 4px 0',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5
                                              }}>
                                                <Typography sx={{
                                                  fontSize: '1.25rem',
                                                  color: isNegative ? '#ef4444' : '#22c55e',
                                                  fontWeight: 700,
                                                  lineHeight: 1
                                                }}>
                                                  {isNegative ? '↓' : '↑'}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        );
                                      })}
                                    </Box>
                                  </Card>
                                );
                              })()
                            ) : tableData.data && tableData.data.length > 0 && tableData.headers ? (
                              /* Regular Table */
                              <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e9ecef', boxShadow: 'none' }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
                                      {tableData.headers.map((header: string, headerIndex: number) => (
                                        <TableCell
                                          key={headerIndex}
                                          sx={{
                                            fontWeight: 700,
                                            color: '#4c3794',
                                            fontSize: '0.813rem',
                                            borderBottom: '2px solid rgba(76, 55, 148, 0.2)'
                                          }}
                                        >
                                          {header}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {tableData.data.map((row: any, rowIndex: number) => (
                                      <TableRow key={rowIndex} sx={{ '&:hover': { backgroundColor: 'rgba(76, 55, 148, 0.02)' } }}>
                                        {tableData.headers.map((header: string, headerIndex: number) => {
                                          const value = row[header] !== undefined ? row[header] :
                                            (row[header.toLowerCase()] !== undefined ? row[header.toLowerCase()] :
                                              row[header.toUpperCase()] !== undefined ? row[header.toUpperCase()] : null);
                                          return (
                                            <TableCell key={headerIndex} sx={{ fontSize: '0.813rem', color: '#131F32', py: 1.5 }}>
                                              {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : null}
                          </Box>
                        );
                      })
                  }

                  {/* What-If Analysis Visualization - Inserted after all tables */}
                  {(() => {
                    // Find What-If Analysis data
                    const whatIfAnalysisData = multicolumnDialogData.segmentData?.multicolumnData?.find((tableData: any) => {
                      const titleLower = tableData.title?.toLowerCase() || '';
                      const keyLower = tableData.key?.toLowerCase() || '';
                      const subcategory = tableData.entries?.find((e: any) => e.key === 'informationSubcategory')?.value || '';

                      return (
                        titleLower.includes('what-if') ||
                        titleLower.includes('what if') ||
                        keyLower.includes('what-if') ||
                        keyLower.includes('whatif') ||
                        subcategory.toLowerCase().includes('what-if')
                      );
                    });

                    if (!whatIfAnalysisData || !whatIfAnalysisData.data || whatIfAnalysisData.data.length === 0) {
                      return null;
                    }

                    // Check if Strategic Focus Areas section exists (to place after it)
                    const hasStrategicFocusAreas = multicolumnDialogData.segmentData?.multicolumnData?.some((tableData: any) => {
                      const titleLower = tableData.title?.toLowerCase() || '';
                      const keyLower = tableData.key?.toLowerCase() || '';
                      return (
                        titleLower.includes('strategic focus areas') ||
                        titleLower.includes('performance vs impact') ||
                        keyLower.includes('strategicfocusareas')
                      );
                    });

                    // Only render if Strategic Focus Areas exists
                    if (!hasStrategicFocusAreas) {
                      return null;
                    }

                    return (
                      <Box key="what-if-analysis" sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(76, 55, 148, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <ShowChartIcon sx={{ fontSize: 20, color: '#4c3794' }} />
                          </Box>
                          <Typography sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#131F32',
                            letterSpacing: '-0.01em'
                          }}>
                            {whatIfAnalysisData.title || 'What-If Analysis'}
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          {whatIfAnalysisData.data.map((driverOutcomeData: any, index: number) => {
                            // Check if this is the new format with baseline_metrics and headers
                            const hasNewFormat = driverOutcomeData.baseline_metrics !== undefined ||
                              (whatIfAnalysisData.headers && whatIfAnalysisData.headers.length > 0);

                            if (hasNewFormat) {
                              // New format: Use InteractiveWhatIfChart component
                              return (
                                <Grid item xs={12} sm={6} key={index}>
                                  <InteractiveWhatIfChart
                                    dataset={{
                                      title: driverOutcomeData.title || whatIfAnalysisData.title || 'What-If Analysis',
                                      headers: driverOutcomeData.headers || whatIfAnalysisData.headers || ['Driver Value', 'Outcome Value'],
                                      data: driverOutcomeData.data || driverOutcomeData.response_curve || [],
                                      baseline_metrics: driverOutcomeData.baseline_metrics || driverOutcomeData.summary || {}
                                    }}
                                  />
                                </Grid>
                              );
                            }

                            // Legacy format: Extract data from each driver-outcome pair
                            const summary = driverOutcomeData.summary || {};
                            const responseCurve = driverOutcomeData.response_curve || [];
                            const driver = driverOutcomeData.driver || summary.driver_name || 'Driver';
                            const outcome = driverOutcomeData.outcome || summary.outcome_name || 'Outcome';

                            if (!responseCurve || responseCurve.length === 0) {
                              return null;
                            }

                            // Use InteractiveWhatIfChart with legacy data structure
                            return (
                              <Grid item xs={12} sm={6} key={index}>
                                <InteractiveWhatIfChart
                                  dataset={{
                                    title: `${driver} → ${outcome}`,
                                    headers: [driver, outcome],
                                    data: responseCurve,
                                    baseline_metrics: summary,
                                    driver: driver,
                                    outcome: outcome,
                                    summary: summary,
                                    response_curve: responseCurve
                                  }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    );
                  })()}

                  {/* Recommendations */}
                  {multicolumnDialogData.segmentData?.segmentRecommendations &&
                    multicolumnDialogData.segmentData.segmentRecommendations.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <Typography sx={{ fontSize: '1.25rem' }}>💡</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#131F32' }}>
                            Recommendations
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {multicolumnDialogData.segmentData.segmentRecommendations.map((rec: string, idx: number) => (
                            <Card key={idx} sx={{
                              p: 2,
                              borderRadius: '10px',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              backgroundColor: 'rgba(59, 130, 246, 0.03)'
                            }}>
                              <Box sx={{ fontSize: '0.875rem', color: '#495057', lineHeight: 1.6 }}>
                                <MarkdownMessage text={rec.trim()} />
                              </Box>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    )}
                </Box>
              ) : multicolumnDialogData && (
                // Regular table display
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
                        {multicolumnDialogData.headers?.map((header: string, headerIndex: number) => {
                          const isPositiveTopics = header.toLowerCase().includes('positive');
                          const isNegativeTopics = header.toLowerCase().includes('negative');

                          return (
                            <TableCell
                              key={headerIndex}
                              sx={{
                                fontWeight: 700,
                                color: '#4c3794',
                                fontSize: '0.875rem',
                                borderBottom: '2px solid rgba(76, 55, 148, 0.2)',
                                backgroundColor: isPositiveTopics
                                  ? 'rgba(16, 185, 129, 0.08)'
                                  : isNegativeTopics
                                    ? 'rgba(249, 115, 22, 0.08)'
                                    : 'transparent'
                              }}
                            >
                              {header}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {multicolumnDialogData.data?.map((row: any, rowIndex: number) => (
                        <TableRow
                          key={rowIndex}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(76, 55, 148, 0.02)'
                            },
                            '&:last-child td': {
                              borderBottom: 0
                            }
                          }}
                        >
                          {multicolumnDialogData.headers?.map((header: string, headerIndex: number) => {
                            const value = row[header] !== undefined
                              ? row[header]
                              : (row[header.toLowerCase()] !== undefined
                                ? row[header.toLowerCase()]
                                : row[header.toUpperCase()] !== undefined
                                  ? row[header.toUpperCase()]
                                  : null);

                            const isPositiveTopics = header.toLowerCase().includes('positive');
                            const isNegativeTopics = header.toLowerCase().includes('negative');

                            return (
                              <TableCell
                                key={headerIndex}
                                sx={{
                                  fontSize: '0.875rem',
                                  color: '#131F32',
                                  py: 2,
                                  backgroundColor: isPositiveTopics
                                    ? 'rgba(16, 185, 129, 0.05)'
                                    : isNegativeTopics
                                      ? 'rgba(249, 115, 22, 0.05)'
                                      : 'transparent'
                                }}
                              >
                                {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #e9ecef' }}>
              <Button
                onClick={() => setMulticolumnDialogOpen(prev => ({ ...prev, [key]: false }))}
                variant="contained"
                sx={{
                  backgroundColor: '#4c3794',
                  '&:hover': {
                    backgroundColor: '#3d2d7a'
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        ))
      }

      {/* Export Dialog for Survey Insights (Segment/Driver Analysis) */}
      <ExportDialog
        isOpen={showSurveyInsightsExportDialog}
        onClose={() => setShowSurveyInsightsExportDialog(false)}
        onGenerate={handleExportDialogGenerate}
        contentTypes={contentTypes}
      />
    </FullScreenContainer >
  );
};

export default SurveyInsights; 